// This file is auto-generated by @hey-api/openapi-ts

export const AIFeedbackCategorySchema = {
    type: 'string',
    enum: [
        'factual_error',
        'not_actionable',
        'patient_safety'
    ],
    title: 'AIFeedbackCategory',
    description: 'What kind of wrong an AI output was.\n\nThree categories: `factual_error` because every AI feature has it,\n`not_actionable` because an accurate summary nobody needs is this\nfeature\'s biggest risk, and `patient_safety` because it is the one\nworth alerting on same-day and the one an auditor asks about.\n`patient_safety` is a severity axis, so it combines with the other two.\nOn a triage, a wrong class is the verdict\'s `expected`; `factual_error`\nthere means the reason states something the record does not support,\nand `not_actionable` that the class was right but the reason did not\nhelp anyone act.\n\nThe vocabulary grows from free-text comments that repeat, and a value\nalready used stays defined - dropping one orphans the rows tagged with\nit, so a retired value is only taken out of what the UI offers.'
} as const;

export const AIFeedbackResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        kind: {
            $ref: '#/components/schemas/AIOutputKind'
        },
        output_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Output Uuid'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        rating: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Rating'
        },
        categories: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/AIFeedbackCategory'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Categories'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        expected: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Expected'
        },
        target: {
            additionalProperties: true,
            type: 'object',
            title: 'Target'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'kind',
        'output_uuid',
        'patient_id',
        'user_uuid',
        'rating',
        'categories',
        'comment',
        'expected',
        'target',
        'created_at'
    ],
    title: 'AIFeedbackResponse',
    description: 'One stored AI feedback row.'
} as const;

export const AIOutputKindSchema = {
    type: 'string',
    enum: [
        'summary',
        'triage'
    ],
    title: 'AIOutputKind',
    description: 'Kinds of AI output about a patient, discriminating what the stored\n`output` holds. db-api validates the payload per kind, so a new kind is\na pydantic model there.\n\n`triage` is its own kind, not part of the summary, so its prompt and its\nevaluation can change without touching the summary clinicians read.'
} as const;

export const AIOutputResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        kind: {
            $ref: '#/components/schemas/AIOutputKind'
        },
        output: {
            additionalProperties: true,
            type: 'object',
            title: 'Output'
        },
        metadata: {
            additionalProperties: true,
            type: 'object',
            title: 'Metadata'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'patient_id',
        'kind',
        'output',
        'metadata',
        'created_at'
    ],
    title: 'AIOutputResponse',
    description: 'One stored AI output.'
} as const;

export const AITriagePrioritySchema = {
    type: 'string',
    enum: [
        'low',
        'medium',
        'urgent'
    ],
    title: 'AITriagePriority',
    description: 'How soon the monitoring team should look at a patient, per the AI\ntriage: team priority, not how ill the patient is. Follows the\nmonitoring handbook: `urgent` = Level 3, `medium` = Level 2, `low` =\nLevel 1 or none. Stored on every triage output, so a used value stays\ndefined.'
} as const;

export const APIInfoResponseSchema = {
    properties: {
        environment: {
            type: 'string',
            title: 'Environment'
        },
        version: {
            type: 'string',
            title: 'Version'
        },
        base_url: {
            type: 'string',
            maxLength: 2083,
            minLength: 1,
            format: 'uri',
            title: 'Base Url'
        },
        server_time_utc: {
            type: 'string',
            format: 'date-time',
            title: 'Server Time Utc'
        },
        server_time_local: {
            type: 'string',
            format: 'date-time',
            title: 'Server Time Local'
        }
    },
    type: 'object',
    required: [
        'environment',
        'version',
        'base_url',
        'server_time_utc',
        'server_time_local'
    ],
    title: 'APIInfoResponse'
} as const;

export const AboutSeerlinqTextSchema = {
    type: 'string',
    enum: [
        'investigators',
        'intended-purpose',
        'user-profile',
        'clinical-evidence'
    ],
    title: 'AboutSeerlinqText'
} as const;

export const AboutSeerlinqTextResponseSchema = {
    properties: {
        text: {
            $ref: '#/components/schemas/AboutSeerlinqText'
        },
        language: {
            $ref: '#/components/schemas/LocaleTag'
        },
        content: {
            type: 'string',
            title: 'Content'
        }
    },
    type: 'object',
    required: [
        'text',
        'language',
        'content'
    ],
    title: 'AboutSeerlinqTextResponse'
} as const;

export const AccessLimiterResponseSchema = {
    properties: {
        limiter_table: {
            $ref: '#/components/schemas/AccessLimiterTables'
        },
        limiter_type: {
            $ref: '#/components/schemas/AccessLimiterType'
        },
        limit_permission_bit: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PermissionBit'
                },
                {
                    type: 'null'
                }
            ]
        },
        limit_username: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Limit Username'
        },
        limit_whole_table: {
            type: 'boolean',
            title: 'Limit Whole Table',
            default: false
        },
        allowed_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Allowed Items'
        },
        forbidden_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Forbidden Items'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'limiter_table',
        'limiter_type',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AccessLimiterResponse'
} as const;

export const AccessLimiterTablesSchema = {
    type: 'string',
    enum: [
        'diagnoses',
        'medicaldata',
        'medications',
        'symptoms',
        'ppg',
        'ecg',
        'sq_computed',
        'ppg_derived',
        'ecg_derived'
    ],
    title: 'AccessLimiterTables'
} as const;

export const AccessLimiterTypeSchema = {
    type: 'string',
    enum: [
        'user',
        'permission_bit'
    ],
    title: 'AccessLimiterType'
} as const;

export const AccountOutSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        balance: {
            type: 'integer',
            minimum: 0,
            title: 'Balance'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'balance',
        'created_at'
    ],
    title: 'AccountOut'
} as const;

export const AdverseEventLevelSchema = {
    type: 'string',
    enum: [
        'mild',
        'moderate',
        'severe'
    ],
    title: 'AdverseEventLevel'
} as const;

export const AdverseEventSchemaSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/SimpleAdverseEventSchema'
        },
        {
            $ref: '#/components/schemas/LeveledAdverseEventSchema'
        }
    ],
    discriminator: {
        propertyName: 'event_category_type',
        mapping: {
            hyperkalemia: '#/components/schemas/LeveledAdverseEventSchema',
            hypokalemia: '#/components/schemas/LeveledAdverseEventSchema',
            hyponatremia: '#/components/schemas/LeveledAdverseEventSchema',
            symptomatic_hypotension: '#/components/schemas/SimpleAdverseEventSchema'
        }
    }
} as const;

export const AdverseEventTypeSchema = {
    type: 'string',
    enum: [
        'hypokalemia',
        'hyperkalemia',
        'hyponatremia',
        'symptomatic_hypotension'
    ],
    title: 'AdverseEventType'
} as const;

export const AlertKindSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'AlertKind',
    'x-enum-varnames': [
        'medical',
        'technical',
        'compliance'
    ],
    description: ' - 0: medical\n - 1: technical\n - 2: compliance'
} as const;

export const AlertTablesSchema = {
    type: 'string',
    enum: [
        'patients',
        'medicaldata',
        'symptoms',
        'ppg',
        'ppg_derived',
        'sq_computed',
        'ecg_derived'
    ],
    title: 'AlertTables'
} as const;

export const AlertTypeSchema = {
    type: 'integer',
    enum: [
        -3,
        -2,
        -1,
        0,
        1,
        2,
        3
    ],
    title: 'AlertType',
    'x-enum-varnames': [
        'low_critical',
        'low_alert',
        'low_warning',
        'normal',
        'high_warning',
        'high_alert',
        'high_critical'
    ],
    description: ' - -3: low_critical\n - -2: low_alert\n - -1: low_warning\n - 0: normal\n - 1: high_warning\n - 2: high_alert\n - 3: high_critical'
} as const;

export const AlgorithmDataTypeSchema = {
    type: 'string',
    enum: [
        'PPG',
        'BeatsPPG',
        'WindowedPPG',
        'EMD_PPG',
        'DerivativesPPG',
        'SignalQuality',
        'OutputPPG',
        'ECG'
    ],
    title: 'AlgorithmDataType'
} as const;

export const AlgorithmInfoSchema = {
    properties: {
        seerlinq_algorithm: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        algorithm_version: {
            type: 'string',
            title: 'Algorithm Version'
        },
        algorithm_type: {
            $ref: '#/components/schemas/AlgorithmType'
        },
        environment: {
            $ref: '#/components/schemas/SeerlinqEnvironments'
        },
        algorithm_pipeline: {
            $ref: '#/components/schemas/PipelineSchema'
        }
    },
    type: 'object',
    required: [
        'seerlinq_algorithm',
        'algorithm_version',
        'algorithm_type',
        'environment',
        'algorithm_pipeline'
    ],
    title: 'AlgorithmInfo'
} as const;

export const AlgorithmTypeSchema = {
    type: 'string',
    enum: [
        'PreprocessingAlgorithm',
        'ComputationAlgorithm',
        'RegressionAlgorithm',
        'ClassificationAlgorithm',
        'QuickLookAlgorithm',
        'AlertingAlgorithm'
    ],
    title: 'AlgorithmType',
    description: 'Defines types of deployed algorithm.'
} as const;

export const AliveCorAlgorithmDeterminationSchema = {
    type: 'string',
    enum: [
        'SINUS_RHYTHM',
        'SINUS_RHYTHM_WIDE_QRS',
        'SINUS_RHYTHM_PVCS',
        'SINUS_RHYTHM_PACS',
        'NORMAL',
        'BRADYCARDIA',
        'TACHYCARDIA',
        'AFIB',
        'UNCLASSIFIED',
        'UNREADABLE',
        'SHORT',
        'NO_ANALYSIS'
    ],
    title: 'AliveCorAlgorithmDetermination'
} as const;

export const AliveCorAvgBeatBlockSchema = {
    properties: {
        block_length: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Block Length'
        },
        ecg_samples: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ecg Samples'
        },
        avg: {
            type: 'string',
            enum: [
                'lead-I',
                'lead-II'
            ],
            title: 'Avg'
        }
    },
    type: 'object',
    required: [
        'block_length',
        'ecg_samples',
        'avg'
    ],
    title: 'AliveCorAvgBeatBlock'
} as const;

export const AliveCorBeatAnnotationSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3
    ],
    title: 'AliveCorBeatAnnotation',
    'x-enum-varnames': [
        'unknown',
        'normal_beat',
        'ventricular_ectopic_beat',
        'atrial_ectopic_beat'
    ],
    description: ' - 0: unknown\n - 1: normal_beat\n - 2: ventricular_ectopic_beat\n - 3: atrial_ectopic_beat'
} as const;

export const AliveCorBeatAnnotationsBlockSchema = {
    properties: {
        tick_offset: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Tick Offset'
        },
        beat_annotation: {
            $ref: '#/components/schemas/AliveCorBeatAnnotation'
        }
    },
    type: 'object',
    required: [
        'tick_offset',
        'beat_annotation'
    ],
    title: 'AliveCorBeatAnnotationsBlock'
} as const;

export const AliveCorDataBlockSchema = {
    properties: {
        block_length: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Block Length'
        },
        ecg_samples: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ecg Samples'
        },
        lead: {
            type: 'string',
            enum: [
                'lead-I',
                'lead-II',
                'lead-III',
                'lead-aVR',
                'lead-aVL',
                'lead-aVF'
            ],
            title: 'Lead'
        }
    },
    type: 'object',
    required: [
        'block_length',
        'ecg_samples',
        'lead'
    ],
    title: 'AliveCorDataBlock'
} as const;

export const AliveCorDataFilteringSchema = {
    properties: {
        mains_frequency: {
            type: 'integer',
            enum: [
                50,
                60
            ],
            title: 'Mains Frequency'
        },
        mains_filter: {
            type: 'boolean',
            title: 'Mains Filter'
        },
        baseline_filter: {
            type: 'boolean',
            title: 'Baseline Filter'
        },
        notch_mains_filter: {
            type: 'boolean',
            title: 'Notch Mains Filter'
        },
        enhanced_filter: {
            type: 'boolean',
            title: 'Enhanced Filter'
        }
    },
    type: 'object',
    required: [
        'mains_frequency',
        'mains_filter',
        'baseline_filter',
        'notch_mains_filter',
        'enhanced_filter'
    ],
    title: 'AliveCorDataFiltering'
} as const;

export const AliveCorECGDeviceStatusSchema = {
    properties: {
        recorder_software: {
            type: 'string',
            title: 'Recorder Software'
        },
        phone_model: {
            type: 'string',
            title: 'Phone Model'
        },
        serial_number: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Serial Number'
        },
        amplitude_resolution: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Amplitude Resolution'
        },
        data_filtering: {
            $ref: '#/components/schemas/AliveCorDataFiltering'
        }
    },
    type: 'object',
    required: [
        'recorder_software',
        'phone_model',
        'amplitude_resolution',
        'data_filtering'
    ],
    title: 'AliveCorECGDeviceStatus'
} as const;

export const AnemiaSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Anemia',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Anemia'
} as const;

export const Antiaggregants__ASASchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Antiaggregants: ASA',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Antiaggregants: ASA'
} as const;

export const Antiaggregants__P2Y12_inhibitorsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Antiaggregants: P2Y12 inhibitors',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Antiaggregants: P2Y12 inhibitors'
} as const;

export const AntiarrhythmicsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Antiarrhythmics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Antiarrhythmics'
} as const;

export const Anticoagulants__LMWHSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Anticoagulants: LMWH',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Anticoagulants: LMWH'
} as const;

export const Anticoagulants__NOACSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Anticoagulants: NOAC',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Anticoagulants: NOAC'
} as const;

export const Anticoagulants__warfarinSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Anticoagulants: warfarin',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Anticoagulants: warfarin'
} as const;

export const Antidepressants_AnxiolyticsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Antidepressants/Anxiolytics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Antidepressants/Anxiolytics'
} as const;

export const AorSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'AoR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AorSchemaResponse'
} as const;

export const AosSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'AoS',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AosSchemaResponse'
} as const;

export const ApiKeyResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        key_prefix: {
            type: 'string',
            title: 'Key Prefix'
        },
        expires_at: {
            type: 'string',
            format: 'date-time',
            title: 'Expires At'
        },
        last_used_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Used At'
        },
        revoked_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Revoked At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'name',
        'key_prefix',
        'expires_at',
        'last_used_at',
        'revoked_at'
    ],
    title: 'ApiKeyResponse'
} as const;

export const ArrhythmiaTreatmentSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Arrhythmia treatment',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Cardioversion',
                        'Interventional',
                        'Surgical'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ArrhythmiaTreatmentSchemaResponse'
} as const;

export const ArterialHypertensionSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Arterial hypertension',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ArterialHypertensionSchemaResponse'
} as const;

export const AscitesSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'ascites',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Minor',
                        'Major'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AscitesSchemaResponse'
} as const;

export const AsthmaSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Asthma',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AsthmaSchemaResponse'
} as const;

export const AtrialFibrillationSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Atrial fibrillation',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Paroxysmal',
                        'Persistent',
                        'Permanent',
                        'Unknown',
                        'Other',
                        'Seerlinq-suspected'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 6,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AtrialFibrillationSchemaResponse'
} as const;

export const AtrialFlutterSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Atrial flutter',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Paroxysmal',
                        'Persistent',
                        'Permanent',
                        'Unknown',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 5,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'AtrialFlutterSchemaResponse'
} as const;

export const AuditActionsSchema = {
    type: 'string',
    enum: [
        'delete',
        'update',
        'background',
        'note',
        'create'
    ],
    title: 'AuditActions'
} as const;

export const AuditItemResponseSchema = {
    properties: {
        audit_table: {
            $ref: '#/components/schemas/AuditTables'
        },
        audit_item_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Audit Item Uuid'
        },
        audit_item_type: {
            $ref: '#/components/schemas/AuditActions'
        },
        audit_item_action: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Audit Item Action'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        audit_item_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Audit Item Timestamp'
        }
    },
    type: 'object',
    required: [
        'audit_table',
        'audit_item_uuid',
        'audit_item_type',
        'uuid',
        'audit_item_timestamp'
    ],
    title: 'AuditItemResponse'
} as const;

export const AuditRoomResponseSchema = {
    properties: {
        fetched: {
            type: 'string',
            format: 'date-time',
            title: 'Fetched'
        },
        audit_room: {
            items: {
                $ref: '#/components/schemas/AuditRoomRow'
            },
            type: 'array',
            title: 'Audit Room'
        },
        audit_room_timespan: {
            type: 'string',
            format: 'duration',
            title: 'Audit Room Timespan'
        }
    },
    type: 'object',
    required: [
        'fetched',
        'audit_room',
        'audit_room_timespan'
    ],
    title: 'AuditRoomResponse'
} as const;

export const AuditRoomRowSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            minLength: 1,
            title: 'Name'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region'
        },
        patient_state: {
            $ref: '#/components/schemas/PatientState'
        },
        clinical_risk_score: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Clinical Risk Score'
        },
        audit_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Audit Alerts'
        },
        last_events: {
            items: {
                $ref: '#/components/schemas/WaitingRoomEvents'
            },
            type: 'array',
            title: 'Last Events'
        },
        monitoring_watch_kind: {
            $ref: '#/components/schemas/PatientMonitoringWatchKind'
        },
        monitoring_watch_status: {
            $ref: '#/components/schemas/PatientMonitoringWatchStatus'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'name',
        'country',
        'region',
        'patient_state',
        'clinical_risk_score',
        'audit_alerts',
        'last_events',
        'monitoring_watch_kind',
        'monitoring_watch_status'
    ],
    title: 'AuditRoomRow'
} as const;

export const AuditTablesSchema = {
    type: 'string',
    enum: [
        'users',
        'physicians',
        'clinics',
        'patients',
        'diagnoses',
        'medicaldata',
        'medications',
        'symptoms',
        'filled_questionnaires',
        'ppg',
        'ppg_derived',
        'ecg',
        'ecg_derived',
        'access_limiter',
        'sq_computed',
        'patient_schedule',
        'simple_messaging',
        'study_texts',
        'patient_alerts',
        'patient_thresholds',
        'patient_events',
        'medical_reports',
        'clinical_study_admin_procedure',
        'clinical_study_visits',
        'clinical_study_enrollments',
        'clinical_study_visit_diagnoses_association',
        'clinical_study_visit_medicaldata_association',
        'clinical_study_visit_medical_reports_association',
        'clinical_study_visit_medications_association',
        'clinical_study_visit_symptoms_association',
        'clinical_study_visit_filled_questionnaires_association',
        'clinical_study_visit_patient_events_association',
        'clinical_study_visit_ppg_association',
        'clinical_study_visit_sq_computed_association',
        'clinical_study_visit_ppg_derived_association',
        'user_access_policies',
        'api_keys',
        'auxiliary_time_series',
        'monitoring_settings',
        'questionnaire_automaton_settings',
        'ai_feedback',
        'patient_ai_outputs',
        'questionnaire_skips',
        'extraction_inputs',
        'extraction_results',
        'patient_notifications',
        'user_webauthn_credentials',
        'audit'
    ],
    title: 'AuditTables'
} as const;

export const AuxiliaryTimeSeriesRelatedResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        time_series_type: {
            $ref: '#/components/schemas/AuxiliaryTimeSeriesType'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        sampling_frequency: {
            type: 'number',
            title: 'Sampling Frequency'
        },
        dimensions: {
            items: {
                type: 'string',
                enum: [
                    'X',
                    'Y',
                    'Z',
                    'MAG'
                ]
            },
            type: 'array',
            maxItems: 3,
            minItems: 1,
            title: 'Dimensions'
        },
        length: {
            type: 'number',
            title: 'Length'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'time_series_type',
        'measurement_datetime',
        'sampling_frequency',
        'dimensions',
        'length'
    ],
    title: 'AuxiliaryTimeSeriesRelatedResponse'
} as const;

export const AuxiliaryTimeSeriesTypeSchema = {
    type: 'string',
    enum: [
        'accelerometer',
        'gyrometer',
        'ecg_average_beat'
    ],
    title: 'AuxiliaryTimeSeriesType'
} as const;

export const BBoxSchema = {
    properties: {
        page: {
            type: 'integer',
            title: 'Page'
        },
        x0: {
            type: 'number',
            title: 'X0'
        },
        y0: {
            type: 'number',
            title: 'Y0'
        },
        x1: {
            type: 'number',
            title: 'X1'
        },
        y1: {
            type: 'number',
            title: 'Y1'
        }
    },
    type: 'object',
    required: [
        'page',
        'x0',
        'y0',
        'x1',
        'y1'
    ],
    title: 'BBox',
    description: 'A word-span bounding box in PDF-point coordinates of the displayed page\n(top-left origin, after the page\'s ``/Rotate``). The frontend scales these\nto the rendered page size to draw a highlight overlay.'
} as const;

export const BLinesOnLungUltrasoundSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'B-lines on lung-ultrasound',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'BLinesOnLungUltrasoundSchemaResponse'
} as const;

export const BackupCodesResponseSchema = {
    properties: {
        backup_codes: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Backup Codes'
        }
    },
    type: 'object',
    required: [
        'backup_codes'
    ],
    title: 'BackupCodesResponse'
} as const;

export const BaseAlgorithmResultSchema = {
    properties: {
        algorithm_info: {
            $ref: '#/components/schemas/AlgorithmInfo'
        },
        dataset: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGDatasetMetadata'
                },
                {
                    $ref: '#/components/schemas/ECGDatasetMetadata'
                },
                {
                    $ref: '#/components/schemas/PatientDatasetMetadata'
                }
            ],
            title: 'Dataset'
        },
        started: {
            type: 'string',
            format: 'date-time',
            title: 'Started'
        },
        execution_time: {
            type: 'number',
            title: 'Execution Time'
        },
        computed: {
            type: 'boolean',
            title: 'Computed'
        }
    },
    type: 'object',
    required: [
        'algorithm_info',
        'dataset',
        'started',
        'execution_time',
        'computed'
    ],
    title: 'BaseAlgorithmResult'
} as const;

export const BaseCountsItemSchema = {
    properties: {
        timestamp: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'string',
                    format: 'date'
                }
            ],
            title: 'Timestamp'
        },
        data_count: {
            type: 'integer',
            minimum: 0,
            title: 'Data Count'
        },
        data_type: {
            type: 'string',
            title: 'Data Type'
        }
    },
    type: 'object',
    required: [
        'timestamp',
        'data_count',
        'data_type'
    ],
    title: 'BaseCountsItem'
} as const;

export const BaseHealthSchema = {
    properties: {
        status: {
            $ref: '#/components/schemas/ServiceHealth'
        },
        latency_ms: {
            type: 'number',
            minimum: 0,
            title: 'Latency Ms'
        }
    },
    type: 'object',
    required: [
        'status',
        'latency_ms'
    ],
    title: 'BaseHealth'
} as const;

export const BaseTableDataMetadataSchema = {
    properties: {
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Patient Ids'
        },
        total_count: {
            type: 'integer',
            minimum: 0,
            title: 'Total Count'
        },
        count_per_patient: {
            additionalProperties: {
                type: 'integer'
            },
            type: 'object',
            title: 'Count Per Patient'
        }
    },
    type: 'object',
    required: [
        'patient_ids',
        'total_count',
        'count_per_patient'
    ],
    title: 'BaseTableDataMetadata',
    description: 'Basic metadata structure for all tables.'
} as const;

export const BeatAnnotationSchema = {
    properties: {
        seconds: {
            type: 'number',
            title: 'Seconds'
        },
        value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value'
        },
        kind: {
            type: 'string',
            enum: [
                'beat_start',
                'systolic_peak',
                'diastolic_peak',
                'rpeak',
                'beat_curve'
            ],
            title: 'Kind'
        },
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        },
        used_for_auc: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Used For Auc'
        },
        auc_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auc Value'
        },
        auc_med_diff: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auc Med Diff'
        }
    },
    type: 'object',
    required: [
        'seconds',
        'kind'
    ],
    title: 'BeatAnnotation',
    description: 'A beat/peak overlay on a raw signal plot.'
} as const;

export const BeatAnnotationWindowTypeSchemaSchema = {
    properties: {
        tick_offset: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Tick Offset'
        },
        tick_sampling: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Tick Sampling'
        }
    },
    type: 'object',
    required: [
        'tick_offset',
        'tick_sampling'
    ],
    title: 'BeatAnnotationWindowTypeSchema'
} as const;

export const BerryOxiDeviceStatusSchema = {
    properties: {
        battery: {
            type: 'integer',
            maximum: 100,
            minimum: 0,
            title: 'Battery'
        },
        pkg_status_stats: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            propertyNames: {
                enum: [
                    'ok',
                    'spo2_sensor_off',
                    'no_finger',
                    'no_pulse_signal',
                    'pulse_beat_tips',
                    'pulse_searching',
                    'signal_disturb',
                    'signal_weak'
                ]
            },
            type: 'object',
            title: 'Pkg Status Stats'
        },
        signal_gaps: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SignalGaps'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'battery',
        'pkg_status_stats'
    ],
    title: 'BerryOxiDeviceStatus'
} as const;

export const BerryPacketStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        4,
        8,
        16,
        32,
        64
    ],
    title: 'BerryPacketStatus',
    'x-enum-varnames': [
        'ok',
        'spo2_sensor_off',
        'no_finger',
        'no_pulse_signal',
        'pulse_beat_tips',
        'pulse_searching',
        'signal_disturb',
        'signal_weak'
    ],
    description: ' - 0: ok\n - 1: spo2_sensor_off\n - 2: no_finger\n - 4: no_pulse_signal\n - 8: pulse_beat_tips\n - 16: pulse_searching\n - 32: signal_disturb\n - 64: signal_weak'
} as const;

export const BetablockersSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Betablockers',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Betablockers'
} as const;

export const BloodPressureDiastolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'blood pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'BloodPressureDiastolicSchemaResponse'
} as const;

export const BloodPressureSystolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'blood pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'BloodPressureSystolicSchemaResponse'
} as const;

export const BnpSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'BNP',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100000,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'pg/ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'BnpSchemaResponse'
} as const;

export const Body_create_medical_report_api_v1_medical_reports_postSchema = {
    properties: {
        file: {
            anyOf: [
                {
                    type: 'string',
                    contentMediaType: 'application/octet-stream'
                },
                {
                    type: 'null'
                }
            ],
            title: 'File'
        }
    },
    type: 'object',
    title: 'Body_create_medical_report_api_v1_medical_reports_post'
} as const;

export const Body_create_multifile_medical_report_api_v1_medical_reports_multifile_postSchema = {
    properties: {
        files: {
            items: {
                type: 'string',
                contentMediaType: 'application/octet-stream'
            },
            type: 'array',
            title: 'Files'
        }
    },
    type: 'object',
    required: [
        'files'
    ],
    title: 'Body_create_multifile_medical_report_api_v1_medical_reports_multifile_post'
} as const;

export const Body_create_smartcare_ppg_api_v1_ppg_smartcare_postSchema = {
    properties: {
        ppg_file: {
            type: 'string',
            contentMediaType: 'application/octet-stream',
            title: 'Ppg File'
        }
    },
    type: 'object',
    required: [
        'ppg_file'
    ],
    title: 'Body_create_smartcare_ppg_api_v1_ppg_smartcare_post'
} as const;

export const BoxTraceSchema = {
    properties: {
        values: {
            items: {
                type: 'number'
            },
            type: 'array',
            title: 'Values'
        },
        label: {
            type: 'string',
            title: 'Label'
        },
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        }
    },
    type: 'object',
    required: [
        'values',
        'label'
    ],
    title: 'BoxTrace',
    description: 'A single box in a box plot, carrying its facet identity.'
} as const;

export const Bronchodilators__AnticholinergicsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Bronchodilators: Anticholinergics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Bronchodilators: Anticholinergics'
} as const;

export const Bronchodilators__B2_agonistSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Bronchodilators: B2 agonist',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Bronchodilators: B2 agonist'
} as const;

export const Bronchodilators__MethylxanthinesSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Bronchodilators: Methylxanthines',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Bronchodilators: Methylxanthines'
} as const;

export const Bronchodilators__MontelukastSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Bronchodilators: Montelukast',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Bronchodilators: Montelukast'
} as const;

export const BuildWorkflowSchemaSchema = {
    properties: {
        workflow: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ChainSchema'
                },
                {
                    $ref: '#/components/schemas/GroupSchema'
                },
                {
                    $ref: '#/components/schemas/ChordSchema'
                }
            ],
            title: 'Workflow'
        }
    },
    type: 'object',
    required: [
        'workflow'
    ],
    title: 'BuildWorkflowSchema',
    description: 'Build a workflow of seerlinq celery tasks.'
} as const;

export const Ca_channel_blockers__DihydropyridineSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Ca channel blockers: Dihydropyridine',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Ca channel blockers: Dihydropyridine'
} as const;

export const Ca_channel_blockers__Non_DihydropyridineSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Ca channel blockers: Non-Dihydropyridine',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Ca channel blockers: Non-Dihydropyridine'
} as const;

export const CategoricalDataDescriptionSchema = {
    properties: {
        count: {
            type: 'integer',
            minimum: 0,
            title: 'Count'
        },
        unique: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Unique'
        },
        mode: {
            type: 'string',
            title: 'Mode'
        },
        freq: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Freq'
        }
    },
    type: 'object',
    required: [
        'count',
        'unique',
        'mode',
        'freq'
    ],
    title: 'CategoricalDataDescription'
} as const;

export const CeleryTaskSchema = {
    properties: {
        name: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        queue: {
            $ref: '#/components/schemas/SeerlinqQueues'
        },
        input_type: {
            $ref: '#/components/schemas/TaskInputTypes'
        },
        results_cache: {
            type: 'boolean',
            title: 'Results Cache',
            default: false
        },
        available_report_formats: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Available Report Formats'
        }
    },
    type: 'object',
    required: [
        'name',
        'queue',
        'input_type'
    ],
    title: 'CeleryTask'
} as const;

export const CeleryTaskStatesSchema = {
    type: 'string',
    enum: [
        'pending',
        'started',
        'retry',
        'failure',
        'success'
    ],
    title: 'CeleryTaskStates'
} as const;

export const ChainSchemaSchema = {
    properties: {
        type: {
            type: 'string',
            const: 'Chain',
            title: 'Type'
        },
        tasks: {
            items: {
                $ref: '#/components/schemas/TaskSchema'
            },
            type: 'array',
            title: 'Tasks'
        }
    },
    type: 'object',
    required: [
        'type',
        'tasks'
    ],
    title: 'ChainSchema',
    description: 'Chain of seerlinq celery tasks.'
} as const;

export const ChallengeScopedRequestSchemaSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id'
    ],
    title: 'ChallengeScopedRequestSchema',
    description: 'Base for requests that always carry a mandatory challenge_id: the\nlogin-time, not-yet-authenticated flows (verify, passkey auth).'
} as const;

export const ChangeBasedThresholdSettingsSchema = {
    properties: {
        threshold_trigger: {
            type: 'string',
            const: 'change_based',
            title: 'Threshold Trigger',
            default: 'change_based'
        },
        change_type: {
            $ref: '#/components/schemas/ChangeThresholdsType'
        },
        look_back_days: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Look Back Days'
        },
        maximum_measurements: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Maximum Measurements'
        },
        aggregation: {
            $ref: '#/components/schemas/ChangeThresholdsAggregation',
            description: 'Default for `increase` is take maximum; default for `decrease` is take minimum'
        }
    },
    type: 'object',
    required: [
        'change_type',
        'look_back_days',
        'maximum_measurements'
    ],
    title: 'ChangeBasedThresholdSettings'
} as const;

export const ChangeMedicationDosageSchemaSchema = {
    properties: {
        medication_change_date: {
            type: 'string',
            format: 'date',
            title: 'Medication Change Date',
            default: '2026-11-02'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            anyOf: [
                {
                    items: {
                        type: 'number',
                        multipleOf: 0.25,
                        minimum: 0
                    },
                    type: 'array',
                    maxItems: 4,
                    minItems: 4
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every'
        },
        medication_frequency_regular_repeat_every_unit: {
            anyOf: [
                {
                    $ref: '#/components/schemas/FrequencyUnit'
                },
                {
                    type: 'null'
                }
            ]
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'ChangeMedicationDosageSchema'
} as const;

export const ChangePasswordSchemaSchema = {
    properties: {
        new_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'New Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        repeat_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Repeat Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        old_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Old Password',
            writeOnly: true
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'new_password',
        'repeat_password',
        'old_password'
    ],
    title: 'ChangePasswordSchema'
} as const;

export const ChangeThresholdsAggregationSchema = {
    type: 'string',
    enum: [
        'max',
        'min',
        'mean',
        'median'
    ],
    title: 'ChangeThresholdsAggregation'
} as const;

export const ChangeThresholdsTypeSchema = {
    type: 'string',
    enum: [
        'increase',
        'decrease'
    ],
    title: 'ChangeThresholdsType'
} as const;

export const ChordSchemaSchema = {
    properties: {
        type: {
            type: 'string',
            const: 'Chord',
            title: 'Type'
        },
        tasks: {
            $ref: '#/components/schemas/GroupSchema'
        },
        callback: {
            $ref: '#/components/schemas/TaskSchema'
        }
    },
    type: 'object',
    required: [
        'type',
        'tasks',
        'callback'
    ],
    title: 'ChordSchema',
    description: 'Chord of seerlinq celery tasks.'
} as const;

export const CkdSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'CKD',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'K/DIGO: G1',
                        'K/DIGO: G2',
                        'K/DIGO: G3a',
                        'K/DIGO: G3b',
                        'K/DIGO: G4',
                        'K/DIGO: G5',
                        'K/DIGO: Unknown'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 7,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'CkdSchemaResponse'
} as const;

export const ClinicResponseSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            type: 'string',
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'country',
        'name',
        'address',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicResponse'
} as const;

export const ClinicWithAssignedUsersResponseSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            type: 'string',
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        physicians: {
            items: {
                $ref: '#/components/schemas/PhysicianResponse'
            },
            type: 'array',
            title: 'Physicians'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/SimpleUserResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        }
    },
    type: 'object',
    required: [
        'country',
        'name',
        'address',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicWithAssignedUsersResponse'
} as const;

export const ClinicalEventSchema = {
    properties: {
        date: {
            type: 'string',
            format: 'date-time',
            title: 'Date'
        },
        timestamp: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Timestamp'
        },
        event_category: {
            $ref: '#/components/schemas/EventCategory'
        },
        event_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Type'
        },
        event_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Details'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        date_unix: {
            type: 'integer',
            title: 'Date Unix',
            readOnly: true
        },
        timestamp_unix: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Timestamp Unix',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'date',
        'event_category',
        'date_unix',
        'timestamp_unix'
    ],
    title: 'ClinicalEvent',
    description: 'Clinical event marker.'
} as const;

export const ClinicalStudiesSchema = {
    type: 'string',
    enum: [
        'seerlinq-lhc'
    ],
    title: 'ClinicalStudies'
} as const;

export const ClinicalStudyAdminProcedureResponseSchema = {
    properties: {
        procedure: {
            type: 'string',
            title: 'Procedure'
        },
        procedure_date: {
            type: 'string',
            format: 'date',
            title: 'Procedure Date'
        },
        procedure_done: {
            type: 'boolean',
            title: 'Procedure Done'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        procedure_done_by_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure Done By Uuid'
        },
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        procedure_done_by: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleUserResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        side_effects_completed: {
            type: 'boolean',
            title: 'Side Effects Completed'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'procedure',
        'procedure_date',
        'procedure_done',
        'study_visit_uuid',
        'patient_id',
        'uuid',
        'side_effects_completed',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicalStudyAdminProcedureResponse'
} as const;

export const ClinicalStudyAdminProcedureUpdateResponseSchema = {
    properties: {
        procedure: {
            type: 'string',
            title: 'Procedure'
        },
        procedure_date: {
            type: 'string',
            format: 'date',
            title: 'Procedure Date'
        },
        procedure_done: {
            type: 'boolean',
            title: 'Procedure Done'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        procedure_done_by_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure Done By Uuid'
        },
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        procedure_done_by: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleUserResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        side_effects_completed: {
            type: 'boolean',
            title: 'Side Effects Completed'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        side_effect_results: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SideEffectExecutionResult'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Side Effect Results',
            description: 'Side effect execution results if completed'
        }
    },
    type: 'object',
    required: [
        'procedure',
        'procedure_date',
        'procedure_done',
        'study_visit_uuid',
        'patient_id',
        'uuid',
        'side_effects_completed',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicalStudyAdminProcedureUpdateResponse',
    description: 'Extended response for admin procedure update with side effects.\n\nside_effect_results:\n    - null: procedure not completed (no side effects triggered)\n    - []: procedure completed but no side effects configured\n    - [{...}]: side effects executed with metrics'
} as const;

export const ClinicalStudyDefinitionSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        full_title: {
            type: 'string',
            title: 'Full Title'
        },
        protocol_version: {
            type: 'string',
            title: 'Protocol Version'
        },
        protocol_date: {
            type: 'string',
            format: 'date',
            title: 'Protocol Date'
        },
        study_category: {
            $ref: '#/components/schemas/StudyCategory'
        },
        study_temporal_dimension: {
            $ref: '#/components/schemas/StudyTemporalDimension'
        },
        study_control_structure: {
            $ref: '#/components/schemas/StudyControlStructure'
        },
        study_randomization: {
            $ref: '#/components/schemas/StudyRandomization'
        },
        study_stage: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Stage'
        },
        planned_start: {
            type: 'string',
            format: 'date',
            title: 'Planned Start'
        },
        planned_end: {
            type: 'string',
            format: 'date',
            title: 'Planned End'
        },
        inclusion_criteria: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Inclusion Criteria'
        },
        exclusion_criteria: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Exclusion Criteria'
        },
        planned_patients: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Planned Patients'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        admin_procedures: {
            anyOf: [
                {
                    items: {
                        additionalProperties: true,
                        type: 'object'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Admin Procedures'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitDefinition'
            },
            type: 'array',
            title: 'Study Visits'
        },
        all_data_names: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'All Data Names',
            description: 'Get list of all data names in the study.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'full_title',
        'protocol_version',
        'protocol_date',
        'study_category',
        'study_temporal_dimension',
        'study_control_structure',
        'study_randomization',
        'planned_start',
        'planned_end',
        'inclusion_criteria',
        'exclusion_criteria',
        'planned_patients',
        'study_visits',
        'all_data_names'
    ],
    title: 'ClinicalStudyDefinition'
} as const;

export const ClinicalStudyEnrollmentPrefillResponseSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        patient_country: {
            type: 'string',
            title: 'Patient Country'
        },
        enrollment_date: {
            type: 'string',
            format: 'date',
            title: 'Enrollment Date'
        },
        enrollment_active: {
            type: 'boolean',
            title: 'Enrollment Active',
            default: true
        },
        schedule: {
            items: {
                $ref: '#/components/schemas/ProposedStudyVisitSchedule'
            },
            type: 'array',
            title: 'Schedule'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'patient_country',
        'enrollment_date',
        'schedule'
    ],
    title: 'ClinicalStudyEnrollmentPrefillResponse'
} as const;

export const ClinicalStudyEnrollmentResponseSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        enrollment_date: {
            type: 'string',
            format: 'date',
            title: 'Enrollment Date'
        },
        enrollment_active: {
            type: 'boolean',
            title: 'Enrollment Active'
        },
        enrollment_activity_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Enrollment Activity Reason'
        },
        schedule: {
            items: {
                $ref: '#/components/schemas/StudyVisitSchedule-Output'
            },
            type: 'array',
            title: 'Schedule'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitResponse'
            },
            type: 'array',
            title: 'Study Visits'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        schedule_validation: {
            additionalProperties: {
                $ref: '#/components/schemas/ProposedScheduleStatus'
            },
            type: 'object',
            title: 'Schedule Validation',
            description: 'Validate scheduled study visits per study definition.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'enrollment_date',
        'enrollment_active',
        'schedule',
        'patient_id',
        'uuid',
        'study_visits',
        'created_at',
        'updated_at',
        'schedule_validation'
    ],
    title: 'ClinicalStudyEnrollmentResponse'
} as const;

export const ClinicalStudyVisit_InputSchema = {
    properties: {
        visit_type: {
            $ref: '#/components/schemas/StudyVisitType'
        },
        followup_index: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Followup Index'
        }
    },
    type: 'object',
    required: [
        'visit_type'
    ],
    title: 'ClinicalStudyVisit'
} as const;

export const ClinicalStudyVisit_OutputSchema = {
    type: 'string'
} as const;

export const ClinicalStudyVisitDefinitionSchema = {
    properties: {
        parent_study: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_schedule: {
            type: 'string',
            format: 'duration',
            title: 'Visit Schedule'
        },
        visit_schedule_limits: {
            prefixItems: [
                {
                    anyOf: [
                        {
                            type: 'string',
                            format: 'duration'
                        },
                        {
                            type: 'null'
                        }
                    ]
                },
                {
                    anyOf: [
                        {
                            type: 'string',
                            format: 'duration'
                        },
                        {
                            type: 'null'
                        }
                    ]
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Visit Schedule Limits'
        },
        visit_location_type: {
            $ref: '#/components/schemas/StudyVisitLocation'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        data_list: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/VisitAdminRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitDiagnosisRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitMedicalRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitSymptomRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitQuestionnaireRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitPPGRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitComputedRequiredData'
                    },
                    {
                        $ref: '#/components/schemas/VisitPPGDerivedRequiredData'
                    }
                ],
                discriminator: {
                    propertyName: 'data_type',
                    mapping: {
                        admin: '#/components/schemas/VisitAdminRequiredData',
                        computed: '#/components/schemas/VisitComputedRequiredData',
                        derived: '#/components/schemas/VisitPPGDerivedRequiredData',
                        diagnosis: '#/components/schemas/VisitDiagnosisRequiredData',
                        'medical-data': '#/components/schemas/VisitMedicalRequiredData',
                        ppg: '#/components/schemas/VisitPPGRequiredData',
                        questionnaire: '#/components/schemas/VisitQuestionnaireRequiredData',
                        symptom: '#/components/schemas/VisitSymptomRequiredData'
                    }
                }
            },
            type: 'array',
            title: 'Data List'
        }
    },
    type: 'object',
    required: [
        'parent_study',
        'visit_type',
        'visit_schedule',
        'visit_schedule_limits',
        'visit_location_type',
        'data_list'
    ],
    title: 'ClinicalStudyVisitDefinition'
} as const;

export const ClinicalStudyVisitDiagnosisAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        diagnosis_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Diagnosis Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'diagnosis_uuid'
    ],
    title: 'ClinicalStudyVisitDiagnosisAssociationResponse'
} as const;

export const ClinicalStudyVisitEventAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        event_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Event Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'event_uuid'
    ],
    title: 'ClinicalStudyVisitEventAssociationResponse'
} as const;

export const ClinicalStudyVisitMedicalDataAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        medical_data_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Medical Data Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'medical_data_uuid'
    ],
    title: 'ClinicalStudyVisitMedicalDataAssociationResponse'
} as const;

export const ClinicalStudyVisitMedicalReportAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        medical_report_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Medical Report Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'medical_report_uuid'
    ],
    title: 'ClinicalStudyVisitMedicalReportAssociationResponse'
} as const;

export const ClinicalStudyVisitMedicationAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        medication_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Medication Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'medication_uuid'
    ],
    title: 'ClinicalStudyVisitMedicationAssociationResponse'
} as const;

export const ClinicalStudyVisitPPGAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'ppg_uuid'
    ],
    title: 'ClinicalStudyVisitPPGAssociationResponse'
} as const;

export const ClinicalStudyVisitPPGDerivedAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        derived_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Derived Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'derived_uuid'
    ],
    title: 'ClinicalStudyVisitPPGDerivedAssociationResponse'
} as const;

export const ClinicalStudyVisitPrefillDataResponseSchema = {
    properties: {
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Visit Datetime'
        },
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        data_list: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/VisitAdminProcedure'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableDiagnoses'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableMedicalData'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableSymptoms'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableQuestionnaires'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailablePPGs'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableComputedData'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailablePPGDerivedData'
                    }
                ],
                discriminator: {
                    propertyName: 'data_type',
                    mapping: {
                        admin: '#/components/schemas/VisitAdminProcedure',
                        computed: '#/components/schemas/VisitAvailableComputedData',
                        derived: '#/components/schemas/VisitAvailablePPGDerivedData',
                        diagnosis: '#/components/schemas/VisitAvailableDiagnoses',
                        'medical-data': '#/components/schemas/VisitAvailableMedicalData',
                        ppg: '#/components/schemas/VisitAvailablePPGs',
                        questionnaire: '#/components/schemas/VisitAvailableQuestionnaires',
                        symptom: '#/components/schemas/VisitAvailableSymptoms'
                    }
                }
            },
            type: 'array',
            title: 'Data List'
        }
    },
    type: 'object',
    required: [
        'visit_type',
        'visit_datetime',
        'study_visit_uuid',
        'patient_id',
        'data_list'
    ],
    title: 'ClinicalStudyVisitPrefillDataResponse'
} as const;

export const ClinicalStudyVisitQuestionnaireAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        questionnaire_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Questionnaire Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'questionnaire_uuid'
    ],
    title: 'ClinicalStudyVisitQuestionnaireAssociationResponse'
} as const;

export const ClinicalStudyVisitResponseSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        protocol_version: {
            type: 'string',
            title: 'Protocol Version'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Visit Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        study_enrollment_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Enrollment Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        schedule_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Schedule Timedelta'
        },
        admin_procedures: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyAdminProcedureResponse'
            },
            type: 'array',
            title: 'Admin Procedures'
        },
        diagnoses: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medical_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Medical Data'
        },
        medical_reports: {
            items: {
                $ref: '#/components/schemas/MedicalReportResponse'
            },
            type: 'array',
            title: 'Medical Reports'
        },
        medications: {
            items: {
                $ref: '#/components/schemas/DefaultMedicationSchemaResponse'
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Symptoms'
        },
        questionnaires: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponse'
            },
            type: 'array',
            title: 'Questionnaires'
        },
        events: {
            items: {
                $ref: '#/components/schemas/DefaultPatientEventResponse'
            },
            type: 'array',
            title: 'Events'
        },
        ppgs: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Ppgs'
        },
        sq_computed_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponse'
            },
            type: 'array',
            title: 'Sq Computed Data'
        },
        ppg_derived_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponse'
            },
            type: 'array',
            title: 'Ppg Derived Data'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'protocol_version',
        'visit_type',
        'visit_datetime',
        'study_enrollment_uuid',
        'patient_id',
        'uuid',
        'schedule_timedelta',
        'admin_procedures',
        'diagnoses',
        'medical_data',
        'medical_reports',
        'medications',
        'symptoms',
        'questionnaires',
        'events',
        'ppgs',
        'sq_computed_data',
        'ppg_derived_data',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicalStudyVisitResponse'
} as const;

export const ClinicalStudyVisitSeerlinqComputedAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        computed_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Computed Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'computed_uuid'
    ],
    title: 'ClinicalStudyVisitSeerlinqComputedAssociationResponse'
} as const;

export const ClinicalStudyVisitSymptomAssociationResponseSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        symptom_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Symptom Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'include',
        'created_at',
        'updated_at',
        'symptom_uuid'
    ],
    title: 'ClinicalStudyVisitSymptomAssociationResponse'
} as const;

export const ClinicalStudyVisitsAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        protocol_version: {
            type: 'string',
            title: 'Protocol Version'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Visit Datetime'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        user: {
            $ref: '#/components/schemas/SimpleAccountResponse'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'study_acronym',
        'protocol_version',
        'visit_type',
        'visit_datetime',
        'patient_id',
        'comment',
        'include',
        'user_uuid',
        'user',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicalStudyVisitsAssociation'
} as const;

export const CodeListCellSchema = {
    properties: {
        value: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Value'
        },
        ecg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ecg Uuid'
        },
        measured_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measured At'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QualityFlagInfo'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'value'
    ],
    title: 'CodeListCell',
    description: 'A cell holding a list of codes (ECG rhythm determination).'
} as const;

export const ComplianceResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        date_from: {
            type: 'string',
            format: 'date',
            title: 'Date From'
        },
        date_to: {
            type: 'string',
            format: 'date',
            title: 'Date To'
        },
        timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Timezone'
        },
        schedules: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/FullMeasurementCompliance'
                    },
                    {
                        $ref: '#/components/schemas/SingleScheduleCompliance'
                    }
                ]
            },
            type: 'array',
            title: 'Schedules'
        },
        overall_compliance: {
            type: 'number',
            title: 'Overall Compliance',
            description: 'Weighted average across all schedules.',
            readOnly: true
        },
        overall_soft_compliance: {
            type: 'number',
            title: 'Overall Soft Compliance',
            description: 'Weighted average of the soft ratios across all schedules.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'date_from',
        'date_to',
        'timezone',
        'schedules',
        'overall_compliance',
        'overall_soft_compliance'
    ],
    title: 'ComplianceResponse'
} as const;

export const ComputedGroundTruthTablesSchema = {
    type: 'string',
    enum: [
        'diagnoses',
        'medicaldata',
        'symptoms'
    ],
    title: 'ComputedGroundTruthTables'
} as const;

export const ConsentHistorySchema = {
    properties: {
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'informed_consent',
        'updated_at'
    ],
    title: 'ConsentHistory'
} as const;

export const ConsentLedgerItemSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        consent_type: {
            $ref: '#/components/schemas/ConsentType'
        },
        granted: {
            type: 'boolean',
            title: 'Granted'
        },
        method: {
            $ref: '#/components/schemas/ConsentMethod'
        },
        study_text_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Text Uuid'
        },
        content_hash: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Content Hash'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        recorded_by: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Recorded By'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'consent_type',
        'granted',
        'method',
        'created_at'
    ],
    title: 'ConsentLedgerItem'
} as const;

export const ConsentMethodSchema = {
    type: 'string',
    enum: [
        'app',
        'paper',
        'admin'
    ],
    title: 'ConsentMethod'
} as const;

export const ConsentStateSchema = {
    type: 'string',
    enum: [
        'granted',
        'not_granted',
        'revoked',
        'stale'
    ],
    title: 'ConsentState'
} as const;

export const ConsentStateItemSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType'
        },
        state: {
            $ref: '#/components/schemas/ConsentState'
        }
    },
    type: 'object',
    required: [
        'consent_type',
        'state'
    ],
    title: 'ConsentStateItem'
} as const;

export const ConsentTextReferenceSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        version: {
            type: 'string',
            title: 'Version'
        },
        language: {
            type: 'string',
            title: 'Language'
        },
        content_hash: {
            type: 'string',
            title: 'Content Hash'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'version',
        'language',
        'content_hash'
    ],
    title: 'ConsentTextReference'
} as const;

export const ConsentTypeSchema = {
    type: 'string',
    enum: [
        'telemonitoring',
        'research'
    ],
    title: 'ConsentType'
} as const;

export const ContinuousQuestionSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_text: {
            type: 'string',
            title: 'Question Text'
        },
        question_type: {
            type: 'string',
            const: 'continuous',
            title: 'Question Type',
            default: 'continuous'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        value_ge: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Ge'
        },
        value_gt: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Gt'
        },
        value_le: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Le'
        },
        value_lt: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Lt'
        },
        value_multiple_of: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Multiple Of'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_text'
    ],
    title: 'ContinuousQuestion'
} as const;

export const ContinuousQuestionnaireResponseSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_type: {
            type: 'string',
            const: 'continuous',
            title: 'Question Type'
        },
        question_response: {
            type: 'number',
            title: 'Question Response'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_type',
        'question_response'
    ],
    title: 'ContinuousQuestionnaireResponse'
} as const;

export const CopdSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'COPD',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'CopdSchemaResponse'
} as const;

export const CoronaryArteryDiseaseSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Coronary artery disease',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Chronic coronary syndrome',
                        'Previous ACS',
                        'Previous PCI',
                        'Previous CABG'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'CoronaryArteryDiseaseSchemaResponse'
} as const;

export const CountryRegionsResponseSchema = {
    properties: {
        countries: {
            additionalProperties: {
                $ref: '#/components/schemas/CountrySchema'
            },
            type: 'object',
            title: 'Countries'
        }
    },
    type: 'object',
    required: [
        'countries'
    ],
    title: 'CountryRegionsResponse'
} as const;

export const CountrySchemaSchema = {
    properties: {
        country_name: {
            type: 'string',
            title: 'Country Name'
        },
        regions: {
            items: {
                $ref: '#/components/schemas/RegionSchema'
            },
            type: 'array',
            title: 'Regions'
        },
        num_regions: {
            type: 'integer',
            minimum: 0,
            title: 'Num Regions',
            description: 'Return number of first-level regions recognized in the country.',
            readOnly: true
        },
        country_alpha2: {
            type: 'string',
            title: 'Country Alpha2',
            description: 'Return country as per ISO 3166-1 alpha-2.',
            readOnly: true
        },
        country_alpha3: {
            type: 'string',
            title: 'Country Alpha3',
            description: 'Return country as per ISO 3166-1 alpha-3.',
            readOnly: true
        },
        country_numeric_code: {
            type: 'string',
            title: 'Country Numeric Code',
            description: 'Return country as per ISO 3166-1 numeric code.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'country_name',
        'regions',
        'num_regions',
        'country_alpha2',
        'country_alpha3',
        'country_numeric_code'
    ],
    title: 'CountrySchema'
} as const;

export const CountsWithPatientsSchema = {
    properties: {
        timestamp: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'string',
                    format: 'date'
                }
            ],
            title: 'Timestamp'
        },
        data_count: {
            type: 'integer',
            minimum: 0,
            title: 'Data Count'
        },
        data_type: {
            type: 'string',
            title: 'Data Type'
        },
        unique_patients: {
            type: 'integer',
            minimum: 0,
            title: 'Unique Patients'
        }
    },
    type: 'object',
    required: [
        'timestamp',
        'data_count',
        'data_type',
        'unique_patients'
    ],
    title: 'CountsWithPatients'
} as const;

export const CreateAIFeedbackSchemaSchema = {
    properties: {
        kind: {
            type: 'string',
            enum: [
                'summary',
                'triage'
            ],
            title: 'Kind'
        },
        output_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Output Uuid'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 2147483647,
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        rating: {
            anyOf: [
                {
                    type: 'integer',
                    enum: [
                        1,
                        5
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Rating'
        },
        categories: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/AIFeedbackCategory'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Categories'
        },
        comment: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 2000,
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        expected: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CreateAISummarySchema'
                },
                {
                    $ref: '#/components/schemas/CreateAITriageSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Expected'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'kind'
    ],
    title: 'CreateAIFeedbackSchema',
    description: 'Schema for writing one reviewer\'s verdict on AI output.\n\n`output_uuid` judges that output, `patient_id` the patient\'s outputs in\ngeneral, and neither the feature named by `kind`. `target` is server-set,\nso a body carrying it is a 422. `rating` takes 5 for like and 1 for\ndislike, the only two values the UI offers. It is required with\n`output_uuid`: a verdict on an output supersedes the caller\'s earlier one\nwhole.'
} as const;

export const CreateAISummaryOutputSchemaSchema = {
    properties: {
        source_run_id: {
            type: 'string',
            title: 'Source Run Id'
        },
        metadata: {
            additionalProperties: true,
            type: 'object',
            title: 'Metadata'
        },
        kind: {
            type: 'string',
            const: 'summary',
            title: 'Kind'
        },
        output: {
            $ref: '#/components/schemas/CreateAISummarySchema'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'source_run_id',
        'kind',
        'output'
    ],
    title: 'CreateAISummaryOutputSchema'
} as const;

export const CreateAISummarySchemaSchema = {
    properties: {
        heading: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 200
                },
                {
                    type: 'null'
                }
            ],
            title: 'Heading'
        },
        reasoning: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 2000
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reasoning'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'CreateAISummarySchema',
    description: 'Schema for writing the summary payload.\n\nThe write refuses unknown keys, so a body carrying a fact another route\nalready serves comes back as a 422. The length caps live here, not on the\nread model.'
} as const;

export const CreateAITriageOutputSchemaSchema = {
    properties: {
        source_run_id: {
            type: 'string',
            title: 'Source Run Id'
        },
        metadata: {
            additionalProperties: true,
            type: 'object',
            title: 'Metadata'
        },
        kind: {
            type: 'string',
            const: 'triage',
            title: 'Kind'
        },
        output: {
            $ref: '#/components/schemas/CreateAITriageSchema'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'source_run_id',
        'kind',
        'output'
    ],
    title: 'CreateAITriageOutputSchema'
} as const;

export const CreateAITriageSchemaSchema = {
    properties: {
        priority: {
            $ref: '#/components/schemas/AITriagePriority'
        },
        reason: {
            type: 'string',
            maxLength: 500,
            minLength: 1,
            title: 'Reason'
        }
    },
    additionalProperties: true,
    type: 'object',
    required: [
        'priority',
        'reason'
    ],
    title: 'CreateAITriageSchema',
    description: 'Write shape of a triage: caps `reason`, limits harness\'s extra keys.'
} as const;

export const CreateAccelerometerTimeSeriesSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        dimensions: {
            items: {
                type: 'string',
                enum: [
                    'X',
                    'Y',
                    'Z',
                    'MAG'
                ]
            },
            type: 'array',
            maxItems: 3,
            minItems: 1,
            title: 'Dimensions'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        time_series: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Time Series'
        },
        ppg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppg Uuid'
        },
        ecg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ecg Uuid'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'sampling_frequency',
        'dimensions',
        'patient_id',
        'time_series'
    ],
    title: 'CreateAccelerometerTimeSeriesSchema',
    description: 'Schema for creating accelerometer auxiliary time series data.'
} as const;

export const CreateAccessLimiterSchema = {
    properties: {
        limiter_table: {
            $ref: '#/components/schemas/AccessLimiterTables'
        },
        limiter_type: {
            $ref: '#/components/schemas/AccessLimiterType'
        },
        limit_permission_bit: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PermissionBit'
                },
                {
                    type: 'null'
                }
            ]
        },
        limit_username: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Limit Username'
        },
        limit_whole_table: {
            type: 'boolean',
            title: 'Limit Whole Table',
            default: false
        },
        allowed_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Allowed Items'
        },
        forbidden_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Forbidden Items'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'limiter_table',
        'limiter_type'
    ],
    title: 'CreateAccessLimiter'
} as const;

export const CreateAliveCorAuxiliaryAvgBeatSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        dimensions: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Dimensions'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_device: {
            type: 'string',
            pattern: '^AliveCor ',
            title: 'Measurement Device'
        },
        avg_beat_block: {
            items: {
                $ref: '#/components/schemas/AliveCorAvgBeatBlock'
            },
            type: 'array',
            maxItems: 2,
            minItems: 1,
            title: 'Avg Beat Block'
        },
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'sampling_frequency',
        'dimensions',
        'patient_id',
        'measurement_device',
        'avg_beat_block',
        'ecg_uuid'
    ],
    title: 'CreateAliveCorAuxiliaryAvgBeatSchema'
} as const;

export const CreateAliveCorECGDataSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_device: {
            type: 'string',
            pattern: '^AliveCor ',
            title: 'Measurement Device'
        },
        leads_placement: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Leads Placement'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_mode: {
            $ref: '#/components/schemas/ECGMeasurementMode',
            default: 3
        },
        measurement_device_status: {
            $ref: '#/components/schemas/AliveCorECGDeviceStatus'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        ecg_data_block: {
            items: {
                $ref: '#/components/schemas/AliveCorDataBlock'
            },
            type: 'array',
            maxItems: 6,
            minItems: 1,
            title: 'Ecg Data Block'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_device',
        'leads_placement',
        'patient_id',
        'measurement_device_status',
        'sampling_frequency',
        'ecg_data_block'
    ],
    title: 'CreateAliveCorECGDataSchema'
} as const;

export const CreateAorSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'AoR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateAorSchema'
} as const;

export const CreateAosSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'AoS',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateAosSchema'
} as const;

export const CreateArrhythmiaTreatmentSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Arrhythmia treatment',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Cardioversion',
                        'Interventional',
                        'Surgical'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateArrhythmiaTreatmentSchema'
} as const;

export const CreateArterialHypertensionSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Arterial hypertension',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateArterialHypertensionSchema'
} as const;

export const CreateAscitesSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'ascites',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Minor',
                        'Major'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateAscitesSchema'
} as const;

export const CreateAsthmaSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Asthma',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateAsthmaSchema'
} as const;

export const CreateAtrialFibrillationSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Atrial fibrillation',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Paroxysmal',
                        'Persistent',
                        'Permanent',
                        'Unknown',
                        'Other',
                        'Seerlinq-suspected'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 6,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateAtrialFibrillationSchema'
} as const;

export const CreateAtrialFlutterSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Atrial flutter',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Paroxysmal',
                        'Persistent',
                        'Permanent',
                        'Unknown',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 5,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateAtrialFlutterSchema'
} as const;

export const CreateBLinesOnLungUltrasoundSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'B-lines on lung-ultrasound',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateBLinesOnLungUltrasoundSchema'
} as const;

export const CreateBloodPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'blood pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateBloodPressureDiastolicSchema'
} as const;

export const CreateBloodPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'blood pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateBloodPressureSystolicSchema'
} as const;

export const CreateBnpSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'BNP',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100000,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'pg/ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateBnpSchema'
} as const;

export const CreateCkdSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'CKD',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'K/DIGO: G1',
                        'K/DIGO: G2',
                        'K/DIGO: G3a',
                        'K/DIGO: G3b',
                        'K/DIGO: G4',
                        'K/DIGO: G5',
                        'K/DIGO: Unknown'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 7,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateCkdSchema'
} as const;

export const CreateClinicSchemaSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.',
            default: 0
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            type: 'string',
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        physicians_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians Uuids'
        },
        assigned_users_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Users Uuids'
        }
    },
    type: 'object',
    required: [
        'country',
        'name',
        'address'
    ],
    title: 'CreateClinicSchema'
} as const;

export const CreateClinicalStudyAdminProcedureSchema = {
    properties: {
        procedure: {
            type: 'string',
            title: 'Procedure'
        },
        procedure_date: {
            type: 'string',
            format: 'date',
            title: 'Procedure Date',
            default: '2026-11-02'
        },
        procedure_done: {
            type: 'boolean',
            title: 'Procedure Done',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        procedure_done_by_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure Done By Uuid'
        },
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'procedure',
        'study_visit_uuid',
        'patient_id'
    ],
    title: 'CreateClinicalStudyAdminProcedure'
} as const;

export const CreateClinicalStudyAdminProcedureBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        admin_procedures: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyAdminProcedure'
            },
            type: 'array',
            title: 'Admin Procedures'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'admin_procedures'
    ],
    title: 'CreateClinicalStudyAdminProcedureBulk'
} as const;

export const CreateClinicalStudyVisitSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        protocol_version: {
            type: 'string',
            title: 'Protocol Version',
            description: 'Default protocol version is the latest'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Input'
        },
        visit_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Visit Datetime',
            default: '2026-11-02T16:17:20.137577Z'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        study_enrollment_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Enrollment Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        schedule_timedelta: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Timedelta'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'visit_type',
        'study_enrollment_uuid',
        'patient_id'
    ],
    title: 'CreateClinicalStudyVisit'
} as const;

export const CreateClinicalStudyVisitDiagnosisAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        diagnosis_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Diagnosis Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'diagnosis_uuid'
    ],
    title: 'CreateClinicalStudyVisitDiagnosisAssociation'
} as const;

export const CreateClinicalStudyVisitEventAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        event_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Event Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'event_uuid'
    ],
    title: 'CreateClinicalStudyVisitEventAssociation'
} as const;

export const CreateClinicalStudyVisitMedicalDataAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medical_data_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Medical Data Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'medical_data_uuid'
    ],
    title: 'CreateClinicalStudyVisitMedicalDataAssociation'
} as const;

export const CreateClinicalStudyVisitMedicalReportAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medical_report_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Medical Report Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'medical_report_uuid'
    ],
    title: 'CreateClinicalStudyVisitMedicalReportAssociation'
} as const;

export const CreateClinicalStudyVisitMedicationAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Medication Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'medication_uuid'
    ],
    title: 'CreateClinicalStudyVisitMedicationAssociation'
} as const;

export const CreateClinicalStudyVisitPPGAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'ppg_uuid'
    ],
    title: 'CreateClinicalStudyVisitPPGAssociation'
} as const;

export const CreateClinicalStudyVisitPPGDerivedAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        derived_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Derived Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'derived_uuid'
    ],
    title: 'CreateClinicalStudyVisitPPGDerivedAssociation'
} as const;

export const CreateClinicalStudyVisitQuestionnaireAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        questionnaire_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Questionnaire Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'questionnaire_uuid'
    ],
    title: 'CreateClinicalStudyVisitQuestionnaireAssociation'
} as const;

export const CreateClinicalStudyVisitSeerlinqComputedAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        computed_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Computed Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'computed_uuid'
    ],
    title: 'CreateClinicalStudyVisitSeerlinqComputedAssociation'
} as const;

export const CreateClinicalStudyVisitSymptomAssociationSchema = {
    properties: {
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            type: 'boolean',
            title: 'Include',
            default: true
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        symptom_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Symptom Uuid'
        }
    },
    type: 'object',
    required: [
        'study_visit_uuid',
        'symptom_uuid'
    ],
    title: 'CreateClinicalStudyVisitSymptomAssociation'
} as const;

export const CreateCopdSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'COPD',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateCopdSchema'
} as const;

export const CreateCoronaryArteryDiseaseSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Coronary artery disease',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Chronic coronary syndrome',
                        'Previous ACS',
                        'Previous PCI',
                        'Previous CABG'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateCoronaryArteryDiseaseSchema'
} as const;

export const CreateCreatinineSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'creatinine',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 1500,
            minimum: 10,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'umol/l',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateCreatinineSchema'
} as const;

export const CreateDefaultDiagnosisSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateDefaultDiagnosisSchema'
} as const;

export const CreateDefaultECGDerivedDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Task Id'
        }
    },
    type: 'object',
    required: [
        'status',
        'task_id'
    ],
    title: 'CreateDefaultECGDerivedDataResponse'
} as const;

export const CreateDefaultMedicalDataSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'patient_id'
    ],
    title: 'CreateDefaultMedicalDataSchema'
} as const;

export const CreateDefaultPPGDerivedDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Task Id'
        }
    },
    type: 'object',
    required: [
        'status',
        'task_id'
    ],
    title: 'CreateDefaultPPGDerivedDataResponse'
} as const;

export const CreateDefaultSymptomSchemaSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id'
    ],
    title: 'CreateDefaultSymptomSchema'
} as const;

export const CreateDiabetesMellitusSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Diabetes mellitus',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Type 1',
                        'Type 2'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateDiabetesMellitusSchema'
} as const;

export const CreateDiagnosisBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        diagnoses: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/CreateHfEtiologySchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHfTypeSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreatePreviousHfhSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateCoronaryArteryDiseaseSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateArterialHypertensionSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateAtrialFibrillationSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateAtrialFlutterSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateDiabetesMellitusSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateDyslipidemiaSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateCkdSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateThyreopathySchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateIronDeficiency_AnemiaSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreatePreviousStroke_TiaSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreatePeripheralArterialDiseaseSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateCopdSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateAsthmaSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHistoryOfSmokingSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHistoryOfAlcoholAbuseSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateMalignancySchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHistoryOfCardiotoxicDrugsSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateImplantedDevicesSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateValvularInterventionSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateArrhythmiaTreatmentSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHistoryOfHtxSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateDefaultDiagnosisSchema'
                    }
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'diagnoses'
    ],
    title: 'CreateDiagnosisBulk',
    description: 'Schema for creating diagnoses data entries in bulk.'
} as const;

export const CreateDryWeightSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'dry weight',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 30,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'kg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateDryWeightSchema'
} as const;

export const CreateDyslipidemiaSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Dyslipidemia',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateDyslipidemiaSchema'
} as const;

export const CreateECGDataGenericSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        leads_placement: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Leads Placement'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_mode: {
            $ref: '#/components/schemas/ECGMeasurementMode',
            default: 3
        },
        measurement_device_status: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        ecg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            maxItems: 12,
            minItems: 1,
            title: 'Ecg'
        },
        leads: {
            items: {
                $ref: '#/components/schemas/ECGLead'
            },
            type: 'array',
            maxItems: 12,
            minItems: 1,
            title: 'Leads'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_device',
        'leads_placement',
        'patient_id',
        'ecg',
        'leads',
        'sampling_frequency'
    ],
    title: 'CreateECGDataGenericSchema'
} as const;

export const CreateECGDerivedAliveCorSchemaSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        beat_tick_sampling: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Beat Tick Sampling'
        },
        beat_annotations_block: {
            items: {
                $ref: '#/components/schemas/AliveCorBeatAnnotationsBlock'
            },
            type: 'array',
            minItems: 1,
            title: 'Beat Annotations Block'
        },
        alivecor_algorithm: {
            $ref: '#/components/schemas/AliveCorAlgorithmDetermination'
        },
        average_hr: {
            type: 'number',
            title: 'Average Hr'
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'beat_tick_sampling',
        'beat_annotations_block',
        'alivecor_algorithm',
        'average_hr'
    ],
    title: 'CreateECGDerivedAliveCorSchema'
} as const;

export const CreateESchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'E',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 500,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'cm/s',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateESchema'
} as const;

export const CreateE_ASchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'E/A',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateE/ASchema'
} as const;

export const CreateE_E_AverageSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'E/e\' average',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateE/E\'AverageSchema'
} as const;

export const CreateEcgPrematureContractionsSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'ECG premature contractions',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateEcgPrematureContractionsSchema'
} as const;

export const CreateEcgRhythmSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'ECG rhythm',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'SR',
                        'AF',
                        'Paced',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateEcgRhythmSchema'
} as const;

export const CreateElevatedLvFillingPressureFinalOutcomeSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'elevated LV filling pressure final outcome',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateElevatedLvFillingPressureFinalOutcomeSchema'
} as const;

export const CreateElevatedLvFillingPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'elevated LV filling pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateElevatedLvFillingPressureSchema'
} as const;

export const CreateFatigueScoreSchemaSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            const: 'fatigue score',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id'
    ],
    title: 'CreateFatigueScoreSchema'
} as const;

export const CreateFilledQuestionnaireSchemaSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        questionnaire_language: {
            type: 'string',
            enum: [
                'ar',
                'az',
                'cs',
                'da',
                'de',
                'el',
                'en',
                'es',
                'fr',
                'he',
                'hi',
                'hu',
                'it',
                'ja',
                'lt',
                'lv',
                'nb',
                'pl',
                'pt',
                'ro',
                'ru',
                'sk',
                'sv',
                'tr',
                'uk',
                'zh-Hans',
                'zh-Hant'
            ],
            title: 'SupportedLanguages',
            description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
            'x-seerlinq-languages': [
                {
                    alpha2: 'ar',
                    alpha3: 'ara',
                    code: 'ar',
                    dir: 'rtl',
                    name: 'Arabic',
                    native_name: 'العربية',
                    regions: [
                        'SA'
                    ]
                },
                {
                    alpha2: 'az',
                    alpha3: 'aze',
                    code: 'az',
                    dir: 'ltr',
                    name: 'Azerbaijani',
                    native_name: 'Azərbaycan dili',
                    regions: []
                },
                {
                    alpha2: 'cs',
                    alpha3: 'ces',
                    code: 'cs',
                    dir: 'ltr',
                    name: 'Czech',
                    native_name: 'Čeština',
                    regions: []
                },
                {
                    alpha2: 'da',
                    alpha3: 'dan',
                    code: 'da',
                    dir: 'ltr',
                    name: 'Danish',
                    native_name: 'Dansk',
                    regions: []
                },
                {
                    alpha2: 'de',
                    alpha3: 'deu',
                    code: 'de',
                    dir: 'ltr',
                    name: 'German',
                    native_name: 'Deutsch',
                    regions: [
                        'DE',
                        'AT'
                    ]
                },
                {
                    alpha2: 'el',
                    alpha3: 'ell',
                    code: 'el',
                    dir: 'ltr',
                    name: 'Greek',
                    native_name: 'Ελληνικά',
                    regions: []
                },
                {
                    alpha2: 'en',
                    alpha3: 'eng',
                    code: 'en',
                    dir: 'ltr',
                    name: 'English',
                    native_name: 'English',
                    regions: [
                        'GB'
                    ]
                },
                {
                    alpha2: 'es',
                    alpha3: 'spa',
                    code: 'es',
                    dir: 'ltr',
                    name: 'Spanish',
                    native_name: 'Español',
                    regions: [
                        'ES'
                    ]
                },
                {
                    alpha2: 'fr',
                    alpha3: 'fra',
                    code: 'fr',
                    dir: 'ltr',
                    name: 'French',
                    native_name: 'Français',
                    regions: [
                        'FR'
                    ]
                },
                {
                    alpha2: 'he',
                    alpha3: 'heb',
                    code: 'he',
                    dir: 'rtl',
                    name: 'Hebrew',
                    native_name: 'עברית',
                    regions: []
                },
                {
                    alpha2: 'hi',
                    alpha3: 'hin',
                    code: 'hi',
                    dir: 'ltr',
                    name: 'Hindi',
                    native_name: 'हिन्दी',
                    regions: []
                },
                {
                    alpha2: 'hu',
                    alpha3: 'hun',
                    code: 'hu',
                    dir: 'ltr',
                    name: 'Hungarian',
                    native_name: 'Magyar',
                    regions: []
                },
                {
                    alpha2: 'it',
                    alpha3: 'ita',
                    code: 'it',
                    dir: 'ltr',
                    name: 'Italian',
                    native_name: 'Italiano',
                    regions: []
                },
                {
                    alpha2: 'ja',
                    alpha3: 'jpn',
                    code: 'ja',
                    dir: 'ltr',
                    name: 'Japanese',
                    native_name: '日本語',
                    regions: []
                },
                {
                    alpha2: 'lt',
                    alpha3: 'lit',
                    code: 'lt',
                    dir: 'ltr',
                    name: 'Lithuanian',
                    native_name: 'Lietuvių',
                    regions: []
                },
                {
                    alpha2: 'lv',
                    alpha3: 'lav',
                    code: 'lv',
                    dir: 'ltr',
                    name: 'Latvian',
                    native_name: 'Latviešu',
                    regions: []
                },
                {
                    alpha2: 'nb',
                    alpha3: 'nob',
                    code: 'nb',
                    dir: 'ltr',
                    name: 'Norwegian Bokmål',
                    native_name: 'Norsk bokmål',
                    regions: []
                },
                {
                    alpha2: 'pl',
                    alpha3: 'pol',
                    code: 'pl',
                    dir: 'ltr',
                    name: 'Polish',
                    native_name: 'Polski',
                    regions: []
                },
                {
                    alpha2: 'pt',
                    alpha3: 'por',
                    code: 'pt',
                    dir: 'ltr',
                    name: 'Portuguese',
                    native_name: 'Português',
                    regions: [
                        'PT'
                    ]
                },
                {
                    alpha2: 'ro',
                    alpha3: 'ron',
                    code: 'ro',
                    dir: 'ltr',
                    name: 'Romanian',
                    native_name: 'Română',
                    regions: []
                },
                {
                    alpha2: 'ru',
                    alpha3: 'rus',
                    code: 'ru',
                    dir: 'ltr',
                    name: 'Russian',
                    native_name: 'Русский',
                    regions: []
                },
                {
                    alpha2: 'sk',
                    alpha3: 'slk',
                    code: 'sk',
                    dir: 'ltr',
                    name: 'Slovak',
                    native_name: 'Slovenčina',
                    regions: []
                },
                {
                    alpha2: 'sv',
                    alpha3: 'swe',
                    code: 'sv',
                    dir: 'ltr',
                    name: 'Swedish',
                    native_name: 'Svenska',
                    regions: []
                },
                {
                    alpha2: 'tr',
                    alpha3: 'tur',
                    code: 'tr',
                    dir: 'ltr',
                    name: 'Turkish',
                    native_name: 'Türkçe',
                    regions: []
                },
                {
                    alpha2: 'uk',
                    alpha3: 'ukr',
                    code: 'uk',
                    dir: 'ltr',
                    name: 'Ukrainian',
                    native_name: 'Українська',
                    regions: []
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hans',
                    dir: 'ltr',
                    name: 'Chinese (Simplified)',
                    native_name: '简体中文',
                    regions: [
                        'CN'
                    ]
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hant',
                    dir: 'ltr',
                    name: 'Chinese (Traditional)',
                    native_name: '繁體中文',
                    regions: [
                        'TW'
                    ]
                }
            ]
        },
        questionnaire_version: {
            type: 'string',
            title: 'Questionnaire Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        filling_start: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Filling Start'
        },
        filling_end: {
            type: 'string',
            format: 'date-time',
            title: 'Filling End'
        },
        responses: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/OrdinalQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/MultiChoiceQuestionnaireResponse-Input'
                    },
                    {
                        $ref: '#/components/schemas/ContinuousQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/OpenTextQuestionnaireResponse'
                    }
                ],
                discriminator: {
                    propertyName: 'question_type',
                    mapping: {
                        continuous: '#/components/schemas/ContinuousQuestionnaireResponse',
                        dichotomous: '#/components/schemas/SingleChoiceQuestionnaireResponse',
                        open_text: '#/components/schemas/OpenTextQuestionnaireResponse',
                        ordinal: '#/components/schemas/OrdinalQuestionnaireResponse',
                        polytomous_multi_choice: '#/components/schemas/MultiChoiceQuestionnaireResponse-Input',
                        polytomous_single_choice: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                    }
                }
            },
            type: 'array',
            minItems: 1,
            title: 'Responses'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'questionnaire_language',
        'questionnaire_version',
        'filling_end',
        'responses',
        'patient_id'
    ],
    title: 'CreateFilledQuestionnaireSchema'
} as const;

export const CreateGyrometerTimeSeriesSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        dimensions: {
            items: {
                type: 'string',
                enum: [
                    'X',
                    'Y',
                    'Z',
                    'MAG'
                ]
            },
            type: 'array',
            maxItems: 3,
            minItems: 1,
            title: 'Dimensions'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        time_series: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Time Series'
        },
        ppg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppg Uuid'
        },
        ecg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ecg Uuid'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'sampling_frequency',
        'dimensions',
        'patient_id',
        'time_series'
    ],
    title: 'CreateGyrometerTimeSeriesSchema',
    description: 'Schema for creating gyrometer/gyroscope auxiliary time series data.'
} as const;

export const CreateHeartRateSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'heart rate',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 250,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'bpm',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateHeartRateSchema'
} as const;

export const CreateHematocritSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'hematocrit',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 1,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateHematocritSchema'
} as const;

export const CreateHemoglobinSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'hemoglobin',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 500,
            minimum: 10,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'g/l',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateHemoglobinSchema'
} as const;

export const CreateHfEtiologySchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'HF etiology',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Ischemic',
                        'Non-ischemic',
                        'Unknown'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateHfEtiologySchema'
} as const;

export const CreateHfTypeSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'HF type',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'HFpEF',
                        'HFmrEF',
                        'HFrEF'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateHfTypeSchema'
} as const;

export const CreateHistoryOfAlcoholAbuseSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of alcohol abuse',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateHistoryOfAlcoholAbuseSchema'
} as const;

export const CreateHistoryOfCardiotoxicDrugsSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of cardiotoxic drugs',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateHistoryOfCardiotoxicDrugsSchema'
} as const;

export const CreateHistoryOfHtxSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of HTx',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateHistoryOfHtxSchema'
} as const;

export const CreateHistoryOfSmokingSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of smoking',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateHistoryOfSmokingSchema'
} as const;

export const CreateImplantedDevicesSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Implanted devices',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'PM',
                        'ICD',
                        'CRT-D',
                        'CRT-P',
                        'PA-sensor',
                        'VAD'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 6,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateImplantedDevicesSchema'
} as const;

export const CreateIronDeficiency_AnemiaSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Iron deficiency/anemia',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateIronDeficiency/AnemiaSchema'
} as const;

export const CreateJugularVenousDistentionSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'jugular venous distention',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateJugularVenousDistentionSchema'
} as const;

export const CreateLaviSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LAVi',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLaviSchema'
} as const;

export const CreateLegEdemaSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'leg edema',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'integer',
            maximum: 4,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLegEdemaSchema'
} as const;

export const CreateLhcAorticDiastolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic diastolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 150,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcAorticDiastolicPressureSchema'
} as const;

export const CreateLhcAorticMeanPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic mean pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 200,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcAorticMeanPressureSchema'
} as const;

export const CreateLhcAorticOxygenSaturationSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic oxygen saturation',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcAorticOxygenSaturationSchema'
} as const;

export const CreateLhcAorticSystolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic systolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcAorticSystolicPressureSchema'
} as const;

export const CreateLhcCardiacIndexSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC cardiac index',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcCardiacIndexSchema'
} as const;

export const CreateLhcCardiacOutputSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC cardiac output',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 15,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcCardiacOutputSchema'
} as const;

export const CreateLhcDp_DtSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC dP/dt',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 6000,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg/s',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcDp/DtSchema'
} as const;

export const CreateLhcEjectionFractionSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC ejection fraction',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcEjectionFractionSchema'
} as const;

export const CreateLhcLeftAtrialMeanPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC left atrial mean pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcLeftAtrialMeanPressureSchema'
} as const;

export const CreateLhcLeftVentricularEndDiastolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC left ventricular end-diastolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcLeftVentricularEndDiastolicPressureSchema'
} as const;

export const CreateLhcLeftVentricularSystolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC left ventricular systolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcLeftVentricularSystolicPressureSchema'
} as const;

export const CreateLhcMeanAorticValveGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC mean aortic valve gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 150,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcMeanAorticValveGradientSchema'
} as const;

export const CreateLhcMeanMitralValveGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC mean mitral valve gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLhcMeanMitralValveGradientSchema'
} as const;

export const CreateLvefSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LVEF',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 75,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateLvefSchema'
} as const;

export const CreateMalignancySchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Malignancy',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateMalignancySchema'
} as const;

export const CreateMedicalDataBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        medical_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/CreateHeartRateSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateBloodPressureSystolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateBloodPressureDiastolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateSpo2Schema'
                    },
                    {
                        $ref: '#/components/schemas/CreateWeightSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateDryWeightSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateTemperatureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRespiratoryRateSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateBnpSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateNtProbnpSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateUreaSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateCreatinineSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHemoglobinSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateHematocritSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateEcgRhythmSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateEcgPrematureContractionsSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLegEdemaSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateJugularVenousDistentionSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateNyhaClassSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLvefSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateESchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateE_ASchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateE_E_AverageSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateTrVelocitySchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateTrGradientSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLaviSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateElevatedLvFillingPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateStrokeVolumeOnEchoSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateAosSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateAorSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateMrSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateMsSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateTrSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreatePurSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRvspSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateBLinesOnLungUltrasoundSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreatePleuralEffusionSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateAscitesSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcRightAtrialPressureMeanSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcVentricularPressureSystolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcVentricularPressureDiastolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryCapillaryWedgePressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryArteryPressureSystolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryArteryPressureDiastolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryArteryPressureMeanSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcStrokeVolumeFickSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcStrokeVolumeTdSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcStrokeVolumeIndexSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcCardiacOutputFickSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcCardiacOutputTdSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcCardiacIndexFickSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcCardiacIndexTdSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryVascularResistanceFickSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryVascularResistanceTdSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryVascularResistanceIndexSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcTotalPulmonaryResistanceSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcPulmonaryArterialComplianceSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcTranspulmonaryGradientSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcDiastolicTranspulmonaryGradientSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcMixedVenousOxygenSaturationSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcArterialOxygenSaturationSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcSystemicBloodPressureSystolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateRhcSystemicBloodPressureDiastolicSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcLeftVentricularSystolicPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcLeftVentricularEndDiastolicPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcAorticSystolicPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcAorticDiastolicPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcAorticMeanPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcMeanAorticValveGradientSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcMeanMitralValveGradientSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcLeftAtrialMeanPressureSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcCardiacOutputSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcCardiacIndexSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcAorticOxygenSaturationSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcDp_DtSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateLhcEjectionFractionSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateElevatedLvFillingPressureFinalOutcomeSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateDefaultMedicalDataSchema'
                    }
                ]
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'medical_data'
    ],
    title: 'CreateMedicalDataBulk',
    description: 'Schema for creating medical data entries in bulk.'
} as const;

export const CreateMedicationsBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        medications: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/Betablockers'
                    },
                    {
                        $ref: '#/components/schemas/RAAS_inhibitors__ARNI'
                    },
                    {
                        $ref: '#/components/schemas/RAAS_inhibitors__ACE_inhibitors'
                    },
                    {
                        $ref: '#/components/schemas/RAAS_inhibitors__ARB'
                    },
                    {
                        $ref: '#/components/schemas/MRAs'
                    },
                    {
                        $ref: '#/components/schemas/SGLT2_inhibitors'
                    },
                    {
                        $ref: '#/components/schemas/Loop_diuretics'
                    },
                    {
                        $ref: '#/components/schemas/Thiazide_diuretics'
                    },
                    {
                        $ref: '#/components/schemas/Other_diuretics'
                    },
                    {
                        $ref: '#/components/schemas/Antiaggregants__ASA'
                    },
                    {
                        $ref: '#/components/schemas/Anticoagulants__NOAC'
                    },
                    {
                        $ref: '#/components/schemas/Anticoagulants__LMWH'
                    },
                    {
                        $ref: '#/components/schemas/Anticoagulants__warfarin'
                    },
                    {
                        $ref: '#/components/schemas/Antiaggregants__P2Y12_inhibitors'
                    },
                    {
                        $ref: '#/components/schemas/Other_HF_therapy'
                    },
                    {
                        $ref: '#/components/schemas/Ca_channel_blockers__Dihydropyridine'
                    },
                    {
                        $ref: '#/components/schemas/Ca_channel_blockers__Non-Dihydropyridine'
                    },
                    {
                        $ref: '#/components/schemas/Lipid_lowering__Statin'
                    },
                    {
                        $ref: '#/components/schemas/Lipid_lowering__PCSK9i'
                    },
                    {
                        $ref: '#/components/schemas/Lipid_lowering__Other'
                    },
                    {
                        $ref: '#/components/schemas/Antiarrhythmics'
                    },
                    {
                        $ref: '#/components/schemas/Other_antihypertensives'
                    },
                    {
                        $ref: '#/components/schemas/Vasodilators'
                    },
                    {
                        $ref: '#/components/schemas/PH_1_3_4__Specific_therapy'
                    },
                    {
                        $ref: '#/components/schemas/GLP1-RA'
                    },
                    {
                        $ref: '#/components/schemas/Glycaemia'
                    },
                    {
                        $ref: '#/components/schemas/Bronchodilators__B2_agonist'
                    },
                    {
                        $ref: '#/components/schemas/Bronchodilators__Anticholinergics'
                    },
                    {
                        $ref: '#/components/schemas/Bronchodilators__Methylxanthines'
                    },
                    {
                        $ref: '#/components/schemas/Bronchodilators__Montelukast'
                    },
                    {
                        $ref: '#/components/schemas/Antidepressants_Anxiolytics'
                    },
                    {
                        $ref: '#/components/schemas/Thyroid__thyrostatics'
                    },
                    {
                        $ref: '#/components/schemas/Thyroid__substitute_therapy'
                    },
                    {
                        $ref: '#/components/schemas/PPI'
                    },
                    {
                        $ref: '#/components/schemas/Anemia'
                    },
                    {
                        $ref: '#/components/schemas/Potassium_binder'
                    },
                    {
                        $ref: '#/components/schemas/Oncology_therapy'
                    },
                    {
                        $ref: '#/components/schemas/Other'
                    }
                ],
                discriminator: {
                    propertyName: 'medication_group',
                    mapping: {
                        Anemia: '#/components/schemas/Anemia',
                        'Antiaggregants: ASA': '#/components/schemas/Antiaggregants__ASA',
                        'Antiaggregants: P2Y12 inhibitors': '#/components/schemas/Antiaggregants__P2Y12_inhibitors',
                        Antiarrhythmics: '#/components/schemas/Antiarrhythmics',
                        'Anticoagulants: LMWH': '#/components/schemas/Anticoagulants__LMWH',
                        'Anticoagulants: NOAC': '#/components/schemas/Anticoagulants__NOAC',
                        'Anticoagulants: warfarin': '#/components/schemas/Anticoagulants__warfarin',
                        'Antidepressants/Anxiolytics': '#/components/schemas/Antidepressants_Anxiolytics',
                        Betablockers: '#/components/schemas/Betablockers',
                        'Bronchodilators: Anticholinergics': '#/components/schemas/Bronchodilators__Anticholinergics',
                        'Bronchodilators: B2 agonist': '#/components/schemas/Bronchodilators__B2_agonist',
                        'Bronchodilators: Methylxanthines': '#/components/schemas/Bronchodilators__Methylxanthines',
                        'Bronchodilators: Montelukast': '#/components/schemas/Bronchodilators__Montelukast',
                        'Ca channel blockers: Dihydropyridine': '#/components/schemas/Ca_channel_blockers__Dihydropyridine',
                        'Ca channel blockers: Non-Dihydropyridine': '#/components/schemas/Ca_channel_blockers__Non-Dihydropyridine',
                        'GLP1-RA': '#/components/schemas/GLP1-RA',
                        Glycaemia: '#/components/schemas/Glycaemia',
                        'Lipid lowering: Other': '#/components/schemas/Lipid_lowering__Other',
                        'Lipid lowering: PCSK9i': '#/components/schemas/Lipid_lowering__PCSK9i',
                        'Lipid lowering: Statin': '#/components/schemas/Lipid_lowering__Statin',
                        'Loop diuretics': '#/components/schemas/Loop_diuretics',
                        MRAs: '#/components/schemas/MRAs',
                        'Oncology therapy': '#/components/schemas/Oncology_therapy',
                        Other: '#/components/schemas/Other',
                        'Other HF therapy': '#/components/schemas/Other_HF_therapy',
                        'Other antihypertensives': '#/components/schemas/Other_antihypertensives',
                        'Other diuretics': '#/components/schemas/Other_diuretics',
                        'PH 1,3,4: Specific therapy': '#/components/schemas/PH_1_3_4__Specific_therapy',
                        PPI: '#/components/schemas/PPI',
                        'Potassium binder': '#/components/schemas/Potassium_binder',
                        'RAAS inhibitors: ACE inhibitors': '#/components/schemas/RAAS_inhibitors__ACE_inhibitors',
                        'RAAS inhibitors: ARB': '#/components/schemas/RAAS_inhibitors__ARB',
                        'RAAS inhibitors: ARNI': '#/components/schemas/RAAS_inhibitors__ARNI',
                        'SGLT2 inhibitors': '#/components/schemas/SGLT2_inhibitors',
                        'Thiazide diuretics': '#/components/schemas/Thiazide_diuretics',
                        'Thyroid: substitute therapy': '#/components/schemas/Thyroid__substitute_therapy',
                        'Thyroid: thyrostatics': '#/components/schemas/Thyroid__thyrostatics',
                        Vasodilators: '#/components/schemas/Vasodilators'
                    }
                }
            },
            type: 'array',
            title: 'Medications'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'medications'
    ],
    title: 'CreateMedicationsBulk',
    description: 'Schema for creating medication data entries in bulk.'
} as const;

export const CreateMonitoringSettingsSchema = {
    properties: {
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        new_patient_status: {
            $ref: '#/components/schemas/PatientStatus',
            default: 1
        },
        auto_schedule: {
            type: 'boolean',
            title: 'Auto Schedule',
            default: false
        },
        requires_measurement_config: {
            type: 'boolean',
            title: 'Requires Measurement Config',
            default: false
        },
        auto_alerts: {
            type: 'boolean',
            title: 'Auto Alerts',
            default: false
        },
        patient_push_notifications: {
            type: 'boolean',
            title: 'Patient Push Notifications',
            default: false
        },
        consent_type: {
            type: 'integer',
            enum: [
                2,
                1
            ],
            title: 'Consent Type',
            description: '**Deprecated** - use `required_consents`.',
            default: 2,
            deprecated: true
        },
        required_consents: {
            items: {
                $ref: '#/components/schemas/ConsentType'
            },
            type: 'array',
            title: 'Required Consents',
            default: [
                'research'
            ]
        },
        is_monitored: {
            type: 'boolean',
            title: 'Is Monitored',
            default: false
        },
        any_new_data_activate: {
            type: 'boolean',
            title: 'Any New Data Activate',
            default: false
        },
        ppg_ecg_new_data_activate: {
            type: 'boolean',
            title: 'Ppg Ecg New Data Activate',
            default: true
        },
        auto_deactivate_after: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auto Deactivate After',
            default: 'P60D'
        },
        status_change_email: {
            type: 'boolean',
            title: 'Status Change Email',
            default: false
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        }
    },
    type: 'object',
    required: [
        'monitoring_type'
    ],
    title: 'CreateMonitoringSettings'
} as const;

export const CreateMrSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'MR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateMrSchema'
} as const;

export const CreateMsSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'MS',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateMsSchema'
} as const;

export const CreateNtProbnpSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'NT-proBNP',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100000,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'pg/ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateNtProbnpSchema'
} as const;

export const CreateNyhaClassSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'NYHA class',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'I',
                        'II',
                        'III',
                        'IV'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateNyhaClassSchema'
} as const;

export const CreatePPGDataBerryOxiSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            pattern: '^Berry',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        timestamp: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp',
            description: 'Timestamps in ms'
        },
        pkg_index: {
            items: {
                type: 'integer'
            },
            type: 'array',
            title: 'Pkg Index'
        },
        ppg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Ppg',
            description: 'Outer list - wavelengths (`red`, `ir`); inner list - time'
        },
        battery: {
            type: 'integer',
            maximum: 100,
            minimum: 0,
            title: 'Battery'
        },
        pkg_status: {
            items: {
                $ref: '#/components/schemas/BerryPacketStatus'
            },
            type: 'array',
            title: 'Pkg Status'
        },
        session_id: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Session Id',
            description: 'Client-generated id of one paid-screening attempt, sent on both its Condition 1 and Condition 2 recordings. When a screening account sends it, the Condition 2 recording pairs only with a Condition 1 recording of the same session; without it, only with one that has none either, by the usual time-window matching. Other accounts store it but pair as usual.'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'timestamp',
        'pkg_index',
        'ppg',
        'battery',
        'pkg_status'
    ],
    title: 'CreatePPGDataBerryOxiSchema'
} as const;

export const CreatePPGDataGenericSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        ppg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Ppg'
        },
        wavelengths: {
            items: {
                $ref: '#/components/schemas/PPGWavelength'
            },
            type: 'array',
            minItems: 1,
            title: 'Wavelengths'
        },
        sampling_frequency: {
            items: {
                type: 'number',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Sampling Frequency'
        },
        run_automatic_celery_tasks: {
            type: 'boolean',
            title: 'Run Automatic Celery Tasks',
            default: false
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'ppg',
        'wavelengths',
        'sampling_frequency'
    ],
    title: 'CreatePPGDataGenericSchema',
    description: 'Schema for creating generic PPG data, i.e., passing also data itself.'
} as const;

export const CreatePPGDataMobileAppSchemaSchema = {
    properties: {
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        ppg: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ppg'
        },
        time: {
            items: {
                type: 'string',
                format: 'date-time'
            },
            type: 'array',
            title: 'Time'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'ppg',
        'time',
        'patient_id'
    ],
    title: 'CreatePPGDataMobileAppSchema',
    description: 'Schema for creating PPG data entry from mobile app.'
} as const;

export const CreatePPGDataSamsungWatchSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        timestamp_green: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp Green'
        },
        timestamp_red: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp Red'
        },
        timestamp_ir: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp Ir'
        },
        ppg_green: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ppg Green'
        },
        ppg_red: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ppg Red'
        },
        ppg_ir: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ppg Ir'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'timestamp_green',
        'timestamp_red',
        'timestamp_ir',
        'ppg_green',
        'ppg_red',
        'ppg_ir'
    ],
    title: 'CreatePPGDataSamsungWatchSchema'
} as const;

export const CreatePPGDataViatomSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            pattern: '^Viatom',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        timestamp: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp',
            description: 'Timestamps in ms'
        },
        ir: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Ir',
            description: 'Infrared channel, raw ADC counts'
        },
        red: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Red',
            description: 'Red channel, raw ADC counts'
        },
        state_bytes: {
            items: {
                type: 'integer',
                maximum: 255,
                minimum: 0
            },
            type: 'array',
            title: 'State Bytes',
            description: 'State bytes from the interleaved param polls; may be empty and is independent of the PPG length'
        },
        battery: {
            type: 'integer',
            maximum: 100,
            minimum: 0,
            title: 'Battery'
        },
        battery_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ViatomBatteryState'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'timestamp',
        'ir',
        'red',
        'battery'
    ],
    title: 'CreatePPGDataViatomSchema'
} as const;

export const CreatePPGDerivedDataBerryOxiSchemaSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        timestamp: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp',
            description: 'Timestamps in ms'
        },
        spo2: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Spo2'
        },
        hr: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Hr'
        },
        rr_intervals: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Rr Intervals'
        },
        pkg_status: {
            items: {
                $ref: '#/components/schemas/BerryPacketStatus'
            },
            type: 'array',
            title: 'Pkg Status'
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'timestamp',
        'spo2',
        'hr',
        'rr_intervals',
        'pkg_status'
    ],
    title: 'CreatePPGDerivedDataBerryOxiSchema'
} as const;

export const CreatePPGDerivedDataViatomSchemaSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        timestamp: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Timestamp',
            description: 'Timestamps of the parameter polls, in ms'
        },
        spo2: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Spo2',
            description: 'SpO2 in %; 0 and 255 both mean no value'
        },
        pulse_rate: {
            items: {
                type: 'number'
            },
            type: 'array',
            minItems: 1,
            title: 'Pulse Rate',
            description: 'Pulse rate in bpm; 0 and 65535 both mean no value'
        },
        pi: {
            anyOf: [
                {
                    items: {
                        type: 'number'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Pi',
            description: 'Perfusion index in %, already scaled by the app (raw device byte / 10); accepted but not currently aggregated'
        },
        state_bytes: {
            items: {
                type: 'integer',
                maximum: 255,
                minimum: 0
            },
            type: 'array',
            title: 'State Bytes',
            description: 'State byte per sample; `lead_off` samples are dropped'
        },
        alarm_bytes: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        maximum: 255,
                        minimum: 0
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alarm Bytes',
            description: 'Optional alarm byte per sample (device byte 12). Only the invalid-value bits are used; threshold alarms are ignored'
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'timestamp',
        'spo2',
        'pulse_rate',
        'state_bytes'
    ],
    title: 'CreatePPGDerivedDataViatomSchema'
} as const;

export const CreatePatientAlertSchemaSchema = {
    properties: {
        alert_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Alert Timestamp'
        },
        alert_kind: {
            $ref: '#/components/schemas/AlertKind'
        },
        alert_variable: {
            type: 'string',
            title: 'Alert Variable'
        },
        alert_type: {
            $ref: '#/components/schemas/AlertType'
        },
        alert_value: {
            type: 'number',
            title: 'Alert Value'
        },
        alert_reason: {
            type: 'string',
            title: 'Alert Reason'
        },
        alert_table: {
            $ref: '#/components/schemas/AlertTables'
        },
        alert_item_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Alert Item Uuid'
        },
        alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'alert_timestamp',
        'alert_kind',
        'alert_variable',
        'alert_type',
        'alert_value',
        'alert_reason',
        'alert_table',
        'alert_item_uuid',
        'patient_id'
    ],
    title: 'CreatePatientAlertSchema'
} as const;

export const CreatePatientAndUserResponseSchema = {
    properties: {
        patient: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DefaultPatientResponse'
                },
                {
                    $ref: '#/components/schemas/Level3PatientResponse'
                }
            ],
            title: 'Patient'
        },
        new_user: {
            $ref: '#/components/schemas/UserResponse'
        },
        qr_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^data:image/png;base64,.*'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Qr Code'
        },
        ses_send_mail_task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Ses Send Mail Task Id'
        }
    },
    type: 'object',
    required: [
        'patient',
        'new_user',
        'ses_send_mail_task_id'
    ],
    title: 'CreatePatientAndUserResponse'
} as const;

export const CreatePatientAndUserSchemaSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.',
            default: 0
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType',
            default: 0
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            type: 'string',
            format: 'date',
            title: 'Date Of Birth'
        },
        sex: {
            $ref: '#/components/schemas/Sex'
        },
        race: {
            $ref: '#/components/schemas/Race'
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            type: 'null',
            title: 'Patient Status'
        },
        residence: {
            type: 'string',
            title: 'Residence'
        },
        health_insurance: {
            type: 'string',
            title: 'Health Insurance'
        },
        id_number: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 11,
                    minLength: 7,
                    pattern: '^[0-9,\\/]*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Id Number'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Input'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config',
            description: 'Default config is based on patient\'s type and country'
        },
        append_to_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append To Physicians'
        },
        append_to_managing_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append To Managing Users'
        },
        welcome_email_type: {
            type: 'string',
            enum: [
                'patient_in_person',
                'patient_remote_send'
            ],
            title: 'Welcome Email Type'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        token_expiry_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Token Expiry Timedelta',
            default: 'P1D'
        },
        return_qr_code: {
            type: 'boolean',
            title: 'Return Qr Code',
            default: false
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'date_of_birth',
        'sex',
        'race',
        'residence',
        'health_insurance',
        'email',
        'welcome_email_type'
    ],
    title: 'CreatePatientAndUserSchema'
} as const;

export const CreatePatientMessageSchemaSchema = {
    properties: {
        message_body: {
            type: 'string',
            title: 'Message Body'
        },
        message_active: {
            type: 'boolean',
            title: 'Message Active',
            default: true
        },
        message_expiry: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Expiry'
        },
        message_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'message_body',
        'patient_id'
    ],
    title: 'CreatePatientMessageSchema'
} as const;

export const CreatePatientScheduleSchema = {
    properties: {
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        specific_schedule_data_name: {
            items: {
                type: 'string'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Specific Schedule Data Name'
        },
        schedule_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Frequency Regular Repeat Every'
        },
        schedule_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Frequency On Days'
        },
        schedule_times: {
            items: {
                type: 'string',
                format: 'time'
            },
            type: 'array',
            minItems: 1,
            uniqueItems: true,
            title: 'Schedule Times'
        },
        start_date: {
            type: 'string',
            format: 'date',
            title: 'Start Date',
            default: '2026-11-02'
        },
        schedule_active: {
            type: 'boolean',
            title: 'Schedule Active',
            default: true
        },
        schedule_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'schedule_type',
        'specific_schedule_data_name',
        'schedule_times',
        'patient_id'
    ],
    title: 'CreatePatientSchedule'
} as const;

export const CreatePatientSchemaSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.',
            default: 0
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType',
            default: 0
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            type: 'string',
            format: 'date',
            title: 'Date Of Birth'
        },
        sex: {
            $ref: '#/components/schemas/Sex'
        },
        race: {
            $ref: '#/components/schemas/Race'
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            type: 'null',
            title: 'Patient Status'
        },
        residence: {
            type: 'string',
            title: 'Residence'
        },
        health_insurance: {
            type: 'string',
            title: 'Health Insurance'
        },
        id_number: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 11,
                    minLength: 7,
                    pattern: '^[0-9,\\/]*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Id Number'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Input'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config',
            description: 'Default config is based on patient\'s type and country'
        },
        append_to_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append To Physicians'
        },
        append_to_managing_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append To Managing Users'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'date_of_birth',
        'sex',
        'race',
        'residence',
        'health_insurance'
    ],
    title: 'CreatePatientSchema',
    description: 'Schema for creating new patient.'
} as const;

export const CreatePatientThresholdSchemaSchema = {
    properties: {
        threshold_variable: {
            type: 'string',
            title: 'Threshold Variable'
        },
        threshold_type: {
            $ref: '#/components/schemas/ThresholdTypes'
        },
        threshold_trigger: {
            $ref: '#/components/schemas/ThresholdTriggers'
        },
        threshold_settings: {
            oneOf: [
                {
                    $ref: '#/components/schemas/ValueBasedThresholdSettings'
                },
                {
                    $ref: '#/components/schemas/ChangeBasedThresholdSettings'
                }
            ],
            title: 'Threshold Settings',
            discriminator: {
                propertyName: 'threshold_trigger',
                mapping: {
                    change_based: '#/components/schemas/ChangeBasedThresholdSettings',
                    value_based: '#/components/schemas/ValueBasedThresholdSettings'
                }
            }
        },
        threshold_value: {
            type: 'number',
            title: 'Threshold Value'
        },
        threshold_variable_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ThresholdTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        threshold_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Threshold Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'threshold_variable',
        'threshold_type',
        'threshold_trigger',
        'threshold_settings',
        'threshold_value',
        'patient_id'
    ],
    title: 'CreatePatientThresholdSchema'
} as const;

export const CreatePaymentUserRequestSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        initial_credits: {
            type: 'integer',
            maximum: 1000000,
            minimum: 0,
            title: 'Initial Credits',
            description: 'Credits the account is opened with.',
            default: 101
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'username',
        'email'
    ],
    title: 'CreatePaymentUserRequest',
    description: 'A paid-screening user: the login and its starting credits. The patient\nand physician rows the account needs are filled in by provisioning, since\nnone of it describes a real person.'
} as const;

export const CreatePerceivedHealthChangeSchemaSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            const: 'perceived health change',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 4,
            minimum: 1,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id'
    ],
    title: 'CreatePerceivedHealthChangeSchema'
} as const;

export const CreatePeripheralArterialDiseaseSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Peripheral arterial disease',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreatePeripheralArterialDiseaseSchema'
} as const;

export const CreatePhysicianSchemaSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.',
            default: 0
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            minItems: 1,
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        connected_user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected User Uuid'
        },
        clinics_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinics Uuids'
        },
        patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Ids'
        },
        assigned_users_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Users Uuids'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone'
    ],
    title: 'CreatePhysicianSchema'
} as const;

export const CreatePleuralEffusionSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'pleural effusion',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Minor',
                        'Major'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreatePleuralEffusionSchema'
} as const;

export const CreatePreviousHfhSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Previous HFH',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreatePreviousHfhSchema'
} as const;

export const CreatePreviousStroke_TiaSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Previous stroke/TIA',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreatePreviousStroke/TiaSchema'
} as const;

export const CreatePurSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'PuR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreatePurSchema'
} as const;

export const CreateQuestionnaireAutomatonSettingsSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        first_when_active: {
            type: 'boolean',
            title: 'First When Active'
        },
        filling_frequency: {
            items: {
                $ref: '#/components/schemas/FrequencyIntervalItem'
            },
            type: 'array',
            minItems: 1,
            title: 'Filling Frequency'
        },
        first_after_active: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        stop_after_n: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Stop After N'
        },
        stop_after_time_from_first: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        skippable: {
            type: 'boolean',
            title: 'Skippable',
            default: false
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'monitoring_type',
        'first_when_active',
        'filling_frequency'
    ],
    title: 'CreateQuestionnaireAutomatonSettings'
} as const;

export const CreateRespiratoryRateSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'respiratory rate',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 70,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'bpm',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRespiratoryRateSchema'
} as const;

export const CreateRhcArterialOxygenSaturationSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC arterial oxygen saturation',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcArterialOxygenSaturationSchema'
} as const;

export const CreateRhcCardiacIndexFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac index - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 20,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcCardiacIndexFickSchema'
} as const;

export const CreateRhcCardiacIndexTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac index - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 20,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcCardiacIndexTdSchema'
} as const;

export const CreateRhcCardiacOutputFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac output - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 25,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcCardiacOutputFickSchema'
} as const;

export const CreateRhcCardiacOutputTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac output - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 25,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcCardiacOutputTdSchema'
} as const;

export const CreateRhcDiastolicTranspulmonaryGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC diastolic transpulmonary gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcDiastolicTranspulmonaryGradientSchema'
} as const;

export const CreateRhcMixedVenousOxygenSaturationSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC mixed venous oxygen saturation',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcMixedVenousOxygenSaturationSchema'
} as const;

export const CreateRhcPulmonaryArterialComplianceSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary arterial compliance',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml/mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryArterialComplianceSchema'
} as const;

export const CreateRhcPulmonaryArteryPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary artery pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryArteryPressureDiastolicSchema'
} as const;

export const CreateRhcPulmonaryArteryPressureMeanSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary artery pressure mean',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryArteryPressureMeanSchema'
} as const;

export const CreateRhcPulmonaryArteryPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary artery pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 150,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryArteryPressureSystolicSchema'
} as const;

export const CreateRhcPulmonaryCapillaryWedgePressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary capillary wedge pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryCapillaryWedgePressureSchema'
} as const;

export const CreateRhcPulmonaryVascularResistanceFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary vascular resistance - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 30,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryVascularResistanceFickSchema'
} as const;

export const CreateRhcPulmonaryVascularResistanceIndexSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary vascular resistance index',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 20,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryVascularResistanceIndexSchema'
} as const;

export const CreateRhcPulmonaryVascularResistanceTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary vascular resistance - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 30,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcPulmonaryVascularResistanceTdSchema'
} as const;

export const CreateRhcRightAtrialPressureMeanSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC right atrial pressure mean',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcRightAtrialPressureMeanSchema'
} as const;

export const CreateRhcStrokeVolumeFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC stroke volume - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 400,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcStrokeVolumeFickSchema'
} as const;

export const CreateRhcStrokeVolumeIndexSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC stroke volume index',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcStrokeVolumeIndexSchema'
} as const;

export const CreateRhcStrokeVolumeTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC stroke volume - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 400,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcStrokeVolumeTdSchema'
} as const;

export const CreateRhcSystemicBloodPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC systemic blood pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcSystemicBloodPressureDiastolicSchema'
} as const;

export const CreateRhcSystemicBloodPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC systemic blood pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcSystemicBloodPressureSystolicSchema'
} as const;

export const CreateRhcTotalPulmonaryResistanceSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC total pulmonary resistance',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 30,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcTotalPulmonaryResistanceSchema'
} as const;

export const CreateRhcTranspulmonaryGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC transpulmonary gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcTranspulmonaryGradientSchema'
} as const;

export const CreateRhcVentricularPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC ventricular pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcVentricularPressureDiastolicSchema'
} as const;

export const CreateRhcVentricularPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC ventricular pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 200,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRhcVentricularPressureSystolicSchema'
} as const;

export const CreateRvspSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RVSP',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateRvspSchema'
} as const;

export const CreateSeerlinqComputedSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        seerlinq_measurement_quality_index: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Index'
        },
        ground_truth_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ComputedGroundTruthTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        ground_truth_data_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ground Truth Data Name'
        },
        connected_ppg_uuids: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                }
            ],
            title: 'Connected Ppg Uuids'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'connected_ppg_uuids'
    ],
    title: 'CreateSeerlinqComputedSchema'
} as const;

export const CreateSeerlinqStudyTextSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType',
            default: 'telemonitoring'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        study_text: {
            type: 'string',
            title: 'Study Text'
        },
        study_text_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StudyTextLanguage-Input'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Study Text Language'
        },
        study_text_markup: {
            $ref: '#/components/schemas/StudyTextMarkup'
        },
        study_text_version: {
            type: 'string',
            title: 'Study Text Version'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'study_text',
        'study_text_language',
        'study_text_markup',
        'study_text_version'
    ],
    title: 'CreateSeerlinqStudyText'
} as const;

export const CreateServiceAccountAdminSchemaSchema = {
    properties: {
        regions: {
            anyOf: [
                {
                    additionalProperties: {
                        items: {
                            type: 'integer',
                            minimum: 0
                        },
                        type: 'array',
                        uniqueItems: true
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Regions'
        },
        username: {
            type: 'string',
            pattern: '^svc-[a-z0-9-]{3,50}$',
            title: 'Username'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        owner_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Owner Uuid'
        },
        permissions: {
            items: {
                $ref: '#/components/schemas/PermissionBit'
            },
            type: 'array',
            title: 'Permissions'
        },
        visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patient Ids'
        },
        clinic_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuids'
        },
        physician_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuids'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'username'
    ],
    title: 'CreateServiceAccountAdminSchema',
    description: 'Admin-only service account creation: custom bits/visibility, optional\nowner (None => org-owned).'
} as const;

export const CreateServiceAccountSelfSchemaSchema = {
    properties: {
        username: {
            type: 'string',
            pattern: '^svc-[a-z0-9-]{3,50}$',
            title: 'Username'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'username'
    ],
    title: 'CreateServiceAccountSelfSchema',
    description: 'Self-service creation: owner is always the caller, permissions are\nthe template intersected with the caller\'s own bits, visibility is\ncopied from the caller\'s policy — none of that is client-supplied.'
} as const;

export const CreateShortnessOfBreathSchemaSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            const: 'shortness of breath',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 4,
            minimum: 1,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id'
    ],
    title: 'CreateShortnessOfBreathSchema'
} as const;

export const CreateSpo2SchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'SpO2',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 30,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateSpo2Schema'
} as const;

export const CreateStrokeVolumeOnEchoSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'stroke volume on echo',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 400,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateStrokeVolumeOnEchoSchema'
} as const;

export const CreateStudyEnrollmentSchemaSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        enrollment_date: {
            type: 'string',
            format: 'date',
            title: 'Enrollment Date',
            default: '2026-11-02'
        },
        enrollment_active: {
            type: 'boolean',
            title: 'Enrollment Active',
            default: true
        },
        enrollment_activity_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Enrollment Activity Reason'
        },
        schedule: {
            items: {
                $ref: '#/components/schemas/StudyVisitSchedule-Input'
            },
            type: 'array',
            title: 'Schedule'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        strict_limits_check: {
            type: 'boolean',
            title: 'Strict Limits Check',
            default: true
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'schedule',
        'patient_id'
    ],
    title: 'CreateStudyEnrollmentSchema'
} as const;

export const CreateSymptomsBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        symptoms: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/CreateShortnessOfBreathSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateFatigueScoreSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreatePerceivedHealthChangeSchema'
                    },
                    {
                        $ref: '#/components/schemas/CreateDefaultSymptomSchema'
                    }
                ]
            },
            type: 'array',
            title: 'Symptoms'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'symptoms'
    ],
    title: 'CreateSymptomsBulk'
} as const;

export const CreateTemperatureSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'temperature',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 20,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'C',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateTemperatureSchema'
} as const;

export const CreateThyreopathySchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Thyreopathy',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateThyreopathySchema'
} as const;

export const CreateTrGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'TR gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateTrGradientSchema'
} as const;

export const CreateTrSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'TR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateTrSchema'
} as const;

export const CreateTrVelocitySchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'TR velocity',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'm/s',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateTrVelocitySchema'
} as const;

export const CreateUreaSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'urea',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmol/l',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateUreaSchema'
} as const;

export const CreateUserForExistingPatientSchemaSchema = {
    properties: {
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        new_monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        welcome_email_type: {
            type: 'string',
            enum: [
                'patient_in_person',
                'patient_remote_send'
            ],
            title: 'Welcome Email Type'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        token_expiry_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Token Expiry Timedelta',
            default: 'P1D'
        },
        return_qr_code: {
            type: 'boolean',
            title: 'Return Qr Code',
            default: false
        }
    },
    type: 'object',
    required: [
        'email',
        'new_monitoring_type',
        'welcome_email_type'
    ],
    title: 'CreateUserForExistingPatientSchema'
} as const;

export const CreateUserSchemaSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patient Ids'
        },
        assigned_physicians_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Physicians Uuids'
        },
        assigned_clinics_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Clinics Uuids'
        }
    },
    type: 'object',
    required: [
        'username',
        'role'
    ],
    title: 'CreateUserSchema'
} as const;

export const CreateValvularInterventionSchemaSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Valvular intervention',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'SAVR',
                        'TAVR',
                        'MVR',
                        'MVP',
                        'TEER',
                        'TVR',
                        'TVP',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 8,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id'
    ],
    title: 'CreateValvularInterventionSchema'
} as const;

export const CreateWeightSchemaSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'weight',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 30,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'kg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        study_visit_link: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Visit Link'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id'
    ],
    title: 'CreateWeightSchema'
} as const;

export const CreatinineSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'creatinine',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 1500,
            minimum: 10,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'umol/l',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'CreatinineSchemaResponse'
} as const;

export const CustomECGStatementSchema = {
    properties: {
        code: {
            type: 'string',
            maxLength: 7,
            minLength: 2,
            title: 'Code'
        },
        statement_type: {
            $ref: '#/components/schemas/ECGStatementType'
        },
        description: {
            type: 'string',
            title: 'Description'
        },
        statement_category: {
            type: 'string',
            title: 'Statement Category'
        },
        aha_code: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 999,
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aha Code'
        },
        aecg_refid_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^MDC_ECG[A-Z0-9_]*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aecg Refid Code'
        },
        cdisc_code: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 7,
                    minLength: 6,
                    pattern: '^C.*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Cdisc Code'
        },
        dicom_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^(D3|F|R)-[A-Z0-9]{5,6}$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Dicom Code'
        },
        related_default_codes: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        maxLength: 7,
                        minLength: 2
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Related Default Codes'
        }
    },
    type: 'object',
    required: [
        'code',
        'statement_type',
        'description',
        'statement_category'
    ],
    title: 'CustomECGStatement'
} as const;

export const DBHealthSchema = {
    properties: {
        status: {
            $ref: '#/components/schemas/ServiceHealth'
        },
        latency_ms: {
            type: 'number',
            minimum: 0,
            title: 'Latency Ms'
        },
        pool_size: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Pool Size'
        }
    },
    type: 'object',
    required: [
        'status',
        'latency_ms',
        'pool_size'
    ],
    title: 'DBHealth'
} as const;

export const DailyCountSchema = {
    properties: {
        date: {
            type: 'string',
            format: 'date',
            title: 'Date'
        },
        expected: {
            type: 'integer',
            minimum: 0,
            title: 'Expected'
        },
        completed: {
            type: 'integer',
            minimum: 0,
            title: 'Completed'
        },
        missed: {
            type: 'integer',
            minimum: 0,
            title: 'Missed',
            default: 0
        },
        pending: {
            type: 'integer',
            minimum: 0,
            title: 'Pending',
            default: 0
        }
    },
    type: 'object',
    required: [
        'date',
        'expected',
        'completed'
    ],
    title: 'DailyCount'
} as const;

export const DataTableSchema = {
    properties: {
        name: {
            type: 'string',
            title: 'Name'
        },
        index_columns: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Index Columns'
        },
        data_columns: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Data Columns'
        },
        rows: {
            items: {
                additionalProperties: true,
                type: 'object'
            },
            type: 'array',
            title: 'Rows'
        }
    },
    type: 'object',
    required: [
        'name',
        'index_columns',
        'data_columns',
        'rows'
    ],
    title: 'DataTable',
    description: 'Tabular data.'
} as const;

export const DataVisitStatusSchema = {
    type: 'string',
    enum: [
        'incomplete',
        'incomplete-optional',
        'done',
        'done-multiple'
    ],
    title: 'DataVisitStatus'
} as const;

export const DaysSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4,
        5,
        6
    ],
    title: 'Days',
    'x-enum-varnames': [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday'
    ],
    description: ' - 0: monday\n - 1: tuesday\n - 2: wednesday\n - 3: thursday\n - 4: friday\n - 5: saturday\n - 6: sunday'
} as const;

export const DeathEventReasonSchema = {
    type: 'string',
    enum: [
        'hf',
        'cv',
        'non_cv',
        'unknown'
    ],
    title: 'DeathEventReason'
} as const;

export const DeathEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'death',
            title: 'Event Category',
            default: 'death'
        },
        event_category_type: {
            $ref: '#/components/schemas/DeathEventReason'
        },
        event_category_details: {
            type: 'null',
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_type',
        'event_description',
        'patient_id'
    ],
    title: 'DeathEventSchema'
} as const;

export const DefaultAlertThresholdSchema = {
    properties: {
        threshold_variable: {
            type: 'string',
            title: 'Threshold Variable'
        },
        threshold_type: {
            $ref: '#/components/schemas/ThresholdTypes'
        },
        threshold_trigger: {
            $ref: '#/components/schemas/ThresholdTriggers'
        },
        threshold_settings: {
            oneOf: [
                {
                    $ref: '#/components/schemas/ValueBasedThresholdSettings'
                },
                {
                    $ref: '#/components/schemas/ChangeBasedThresholdSettings'
                }
            ],
            title: 'Threshold Settings',
            discriminator: {
                propertyName: 'threshold_trigger',
                mapping: {
                    change_based: '#/components/schemas/ChangeBasedThresholdSettings',
                    value_based: '#/components/schemas/ValueBasedThresholdSettings'
                }
            }
        },
        threshold_value: {
            type: 'number',
            title: 'Threshold Value'
        },
        threshold_variable_table: {
            $ref: '#/components/schemas/ThresholdTables'
        },
        cooldown: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Cooldown'
        },
        suppress_duplicate: {
            type: 'boolean',
            title: 'Suppress Duplicate',
            default: false
        },
        suppressing_diagnoses: {
            items: {
                $ref: '#/components/schemas/SuppressingDiagnosis'
            },
            type: 'array',
            title: 'Suppressing Diagnoses'
        }
    },
    type: 'object',
    required: [
        'threshold_variable',
        'threshold_type',
        'threshold_trigger',
        'threshold_settings',
        'threshold_value',
        'threshold_variable_table'
    ],
    title: 'DefaultAlertThreshold',
    description: 'Schema for default thresholds defined in `ALERT_THRESHOLDS_FILE`.'
} as const;

export const DefaultAuxResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        dimensions: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Dimensions'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        time_series_type: {
            $ref: '#/components/schemas/AuxiliaryTimeSeriesType'
        },
        ppg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppg Uuid'
        },
        ecg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ecg Uuid'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'sampling_frequency',
        'dimensions',
        'patient_id',
        'uuid',
        'length',
        'created_at',
        'updated_at',
        'time_series_type'
    ],
    title: 'DefaultAuxResponse',
    description: 'Default response schema for auxiliary time series metadata.'
} as const;

export const DefaultClinicalPatientResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientFacingPhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        diagnoses: {
            type: 'integer',
            title: 'Diagnoses'
        },
        medical_data: {
            type: 'integer',
            title: 'Medical Data'
        },
        medications: {
            type: 'integer',
            title: 'Medications'
        },
        symptoms: {
            type: 'integer',
            title: 'Symptoms'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Output'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'diagnoses',
        'medical_data',
        'medications',
        'symptoms',
        'full_name'
    ],
    title: 'DefaultClinicalPatientResponse'
} as const;

export const DefaultDiagnosisSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultDiagnosisSchemaResponse'
} as const;

export const DefaultECGDataSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        leads_placement: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Leads Placement'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_device_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AliveCorECGDeviceStatus'
                },
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        measurement_mode: {
            $ref: '#/components/schemas/ECGMeasurementMode',
            default: 3
        },
        leads: {
            items: {
                $ref: '#/components/schemas/ECGLead'
            },
            type: 'array',
            maxItems: 12,
            minItems: 1,
            title: 'Leads'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_device',
        'leads_placement',
        'patient_id',
        'uuid',
        'leads',
        'sampling_frequency',
        'length',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultECGDataSchemaResponse'
} as const;

export const DefaultECGDerivedDataSchemaResponseSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/ECGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    $ref: '#/components/schemas/BeatAnnotationWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Type'
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataAggregationFunction'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/ECGRelatedQualityFlag',
            default: 1
        },
        custom_statement: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CustomECGStatement'
                },
                {
                    type: 'null'
                }
            ]
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'window_type',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'custom_statement',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'DefaultECGDerivedDataSchemaResponse'
} as const;

export const DefaultECGStatementSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/DiagnosticECGStatement'
        },
        {
            $ref: '#/components/schemas/FormRhythmECGStatement'
        }
    ],
    discriminator: {
        propertyName: 'statement_type',
        mapping: {
            diagnostic: '#/components/schemas/DiagnosticECGStatement',
            form: '#/components/schemas/FormRhythmECGStatement',
            rhythm: '#/components/schemas/FormRhythmECGStatement'
        }
    }
} as const;

export const DefaultFullAuxResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        dimensions: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Dimensions'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        time_series_type: {
            $ref: '#/components/schemas/AuxiliaryTimeSeriesType'
        },
        ppg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppg Uuid'
        },
        ecg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ecg Uuid'
        },
        time_series: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Time Series'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'sampling_frequency',
        'dimensions',
        'patient_id',
        'uuid',
        'length',
        'created_at',
        'updated_at',
        'time_series_type',
        'time_series'
    ],
    title: 'DefaultFullAuxResponse',
    description: 'Default response schema for auxiliary time series data with\ntime series values.'
} as const;

export const DefaultFullECGDataSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        leads_placement: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Leads Placement'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_device_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AliveCorECGDeviceStatus'
                },
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        measurement_mode: {
            $ref: '#/components/schemas/ECGMeasurementMode',
            default: 3
        },
        leads: {
            items: {
                $ref: '#/components/schemas/ECGLead'
            },
            type: 'array',
            maxItems: 12,
            minItems: 1,
            title: 'Leads'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        ecg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Ecg'
        },
        auxiliary_time_series: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/AuxiliaryTimeSeriesRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auxiliary Time Series'
        },
        derived_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ECGDerivedDataResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_device',
        'leads_placement',
        'patient_id',
        'uuid',
        'leads',
        'sampling_frequency',
        'length',
        'created_at',
        'updated_at',
        'ecg'
    ],
    title: 'DefaultFullECGDataSchemaResponse'
} as const;

export const DefaultFullPPGDataSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_device_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/BerryOxiDeviceStatus'
                },
                {
                    $ref: '#/components/schemas/ViatomDeviceStatus'
                },
                {
                    $ref: '#/components/schemas/SamsungWatchDeviceStatus'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        wavelengths: {
            items: {
                $ref: '#/components/schemas/PPGWavelength'
            },
            type: 'array',
            minItems: 1,
            title: 'Wavelengths'
        },
        sampling_frequency: {
            items: {
                type: 'number',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Sampling Frequency'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Ppg'
        },
        auxiliary_time_series: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/AuxiliaryTimeSeriesRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auxiliary Time Series'
        },
        sq_computed_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SeerlinqComputedRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Sq Computed Data'
        },
        derived_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PPGDerivedDataResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'uuid',
        'wavelengths',
        'sampling_frequency',
        'length',
        'created_at',
        'updated_at',
        'ppg'
    ],
    title: 'DefaultFullPPGDataSchemaResponse'
} as const;

export const DefaultMeasurementConfigResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        config: {
            items: {
                $ref: '#/components/schemas/MeasurementRequiredData-Output'
            },
            type: 'array',
            minItems: 1,
            title: 'Config'
        }
    },
    type: 'object',
    required: [
        'status',
        'config'
    ],
    title: 'DefaultMeasurementConfigResponse'
} as const;

export const DefaultMedicalDataSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultMedicalDataSchemaResponse'
} as const;

export const DefaultMedicationSchemaResponseSchema = {
    properties: {
        medication_group: {
            type: 'string',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Medication Dose'
        },
        medication_unit: {
            type: 'string',
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dose',
        'medication_unit',
        'medication_dosage',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultMedicationSchemaResponse'
} as const;

export const DefaultPPGDataSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_device_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/BerryOxiDeviceStatus'
                },
                {
                    $ref: '#/components/schemas/ViatomDeviceStatus'
                },
                {
                    $ref: '#/components/schemas/SamsungWatchDeviceStatus'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        wavelengths: {
            items: {
                $ref: '#/components/schemas/PPGWavelength'
            },
            type: 'array',
            minItems: 1,
            title: 'Wavelengths'
        },
        sampling_frequency: {
            items: {
                type: 'number',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Sampling Frequency'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'uuid',
        'wavelengths',
        'sampling_frequency',
        'length',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultPPGDataSchemaResponse'
} as const;

export const DefaultPPGDerivedDataSchemaResponseSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/PPGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            $ref: '#/components/schemas/DerivedDataAggregationFunction'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'DefaultPPGDerivedDataSchemaResponse'
} as const;

export const DefaultPatientAlertResponseSchema = {
    properties: {
        alert_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Alert Timestamp'
        },
        alert_kind: {
            $ref: '#/components/schemas/AlertKind'
        },
        alert_variable: {
            type: 'string',
            title: 'Alert Variable'
        },
        alert_type: {
            $ref: '#/components/schemas/AlertType'
        },
        alert_value: {
            type: 'number',
            title: 'Alert Value'
        },
        alert_reason: {
            type: 'string',
            title: 'Alert Reason'
        },
        alert_table: {
            $ref: '#/components/schemas/AlertTables'
        },
        alert_item_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Alert Item Uuid'
        },
        alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        alert_seen: {
            type: 'boolean',
            title: 'Alert Seen'
        },
        alert_validated_by: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        alert_validation: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AlertType'
                },
                {
                    type: 'null'
                }
            ]
        },
        alert_validation_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Validation Comment'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        alert_error: {
            type: 'integer',
            maximum: 6,
            minimum: -1,
            title: 'Alert Error',
            description: 'Compute alert error as an absolute value difference between the\nautomatic and user-validated alert value. The lowest alert error is 0 (=\nhighest accuracy of automatic alerting), the highest alert error is 6 (=\nvery low accuracy of automatic alerting).',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'alert_timestamp',
        'alert_kind',
        'alert_variable',
        'alert_type',
        'alert_value',
        'alert_reason',
        'alert_table',
        'alert_item_uuid',
        'patient_id',
        'uuid',
        'alert_seen',
        'created_at',
        'updated_at',
        'alert_error'
    ],
    title: 'DefaultPatientAlertResponse'
} as const;

export const DefaultPatientEventResponseSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp'
        },
        event_category: {
            $ref: '#/components/schemas/EventCategory'
        },
        event_category_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Type'
        },
        event_category_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        event_color_hex: {
            type: 'string',
            title: 'Event Color Hex',
            description: 'Event marker color as a HEX value.',
            readOnly: true
        },
        event_shape: {
            type: 'string',
            title: 'Event Shape',
            description: 'Event marker shape as a named string.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'event_date',
        'event_timestamp',
        'event_category',
        'event_description',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at',
        'event_color_hex',
        'event_shape'
    ],
    title: 'DefaultPatientEventResponse'
} as const;

export const DefaultPatientResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientFacingPhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Output'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'full_name'
    ],
    title: 'DefaultPatientResponse'
} as const;

export const DefaultPatientThresholdResponseSchema = {
    properties: {
        threshold_variable: {
            type: 'string',
            title: 'Threshold Variable'
        },
        threshold_type: {
            $ref: '#/components/schemas/ThresholdTypes'
        },
        threshold_trigger: {
            $ref: '#/components/schemas/ThresholdTriggers'
        },
        threshold_settings: {
            oneOf: [
                {
                    $ref: '#/components/schemas/ValueBasedThresholdSettings'
                },
                {
                    $ref: '#/components/schemas/ChangeBasedThresholdSettings'
                }
            ],
            title: 'Threshold Settings',
            discriminator: {
                propertyName: 'threshold_trigger',
                mapping: {
                    change_based: '#/components/schemas/ChangeBasedThresholdSettings',
                    value_based: '#/components/schemas/ValueBasedThresholdSettings'
                }
            }
        },
        threshold_value: {
            type: 'number',
            title: 'Threshold Value'
        },
        threshold_variable_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ThresholdTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        threshold_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Threshold Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        threshold_history: {
            items: {
                $ref: '#/components/schemas/ThresholdHistory'
            },
            type: 'array',
            title: 'Threshold History'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'threshold_variable',
        'threshold_type',
        'threshold_trigger',
        'threshold_settings',
        'threshold_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultPatientThresholdResponse'
} as const;

export const DefaultSeerlinqComputedBasicResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'DefaultSeerlinqComputedBasicResponse'
} as const;

export const DefaultSeerlinqComputedFullResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppgs: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PPGDataRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppgs'
        },
        seerlinq_measurement_quality_index: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Index'
        },
        ground_truth_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ComputedGroundTruthTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        ground_truth_data_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ground Truth Data Name'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'DefaultSeerlinqComputedFullResponse'
} as const;

export const DefaultSymptomSchemaResponseSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultSymptomSchemaResponse'
} as const;

export const DeleteBlockedErrorSchema = {
    properties: {
        detail: {
            type: 'string',
            title: 'Detail'
        },
        data: {
            $ref: '#/components/schemas/DeleteImpactReport'
        }
    },
    type: 'object',
    required: [
        'detail',
        'data'
    ],
    title: 'DeleteBlockedError',
    description: 'The 409 body of a refused user delete: `data` is the same report\n`delete-impact` returns, so the client needs no second call.',
    example: {
        detail: '<Detail of the exception.>'
    }
} as const;

export const DeleteImpactReportSchema = {
    properties: {
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        blockers: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Blockers'
        },
        loses_attribution: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Loses Attribution'
        },
        links_cleared: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Links Cleared'
        },
        transfers_to_shadow: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Transfers To Shadow'
        },
        deleted: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Deleted'
        },
        unlinked: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Unlinked'
        },
        requires_archive_first: {
            type: 'boolean',
            title: 'Requires Archive First'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'user_uuid',
        'username',
        'requires_archive_first'
    ],
    title: 'DeleteImpactReport',
    description: 'What a hard delete of this user would do, one mapping per fate of the\nreferencing rows. Zero-count tables are omitted.'
} as const;

export const DeleteImpactResponseSchema = {
    properties: {
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        blockers: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Blockers'
        },
        loses_attribution: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Loses Attribution'
        },
        links_cleared: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Links Cleared'
        },
        transfers_to_shadow: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Transfers To Shadow'
        },
        deleted: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Deleted'
        },
        unlinked: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            type: 'object',
            title: 'Unlinked'
        },
        requires_archive_first: {
            type: 'boolean',
            title: 'Requires Archive First'
        },
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'user_uuid',
        'username',
        'requires_archive_first',
        'status'
    ],
    title: 'DeleteImpactResponse',
    description: 'The report as `GET /users/{uuid}/delete-impact` serves it.'
} as const;

export const DeltaColorRuleSchema = {
    properties: {
        threshold_pct: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Threshold Pct'
        },
        positive_change_bad: {
            type: 'boolean',
            title: 'Positive Change Bad'
        }
    },
    type: 'object',
    required: [
        'threshold_pct',
        'positive_change_bad'
    ],
    title: 'DeltaColorRule',
    description: 'When a % change is coloured: from `threshold_pct` (absolute) up, red if it\nis a deterioration, green if an improvement.'
} as const;

export const DerivedDataAggregationFunctionSchema = {
    type: 'string',
    enum: [
        'mean',
        'median',
        'std',
        'min',
        'max',
        'sdnn',
        'sdsd',
        'rmssd',
        'pnn50',
        'pnn20',
        'psd_lf',
        'psd_hf',
        'sample_entropy'
    ],
    title: 'DerivedDataAggregationFunction'
} as const;

export const DerivedDataRollingTypeSchema = {
    type: 'string',
    enum: [
        'full',
        'window'
    ],
    title: 'DerivedDataRollingType'
} as const;

export const DerivedDataWindowTypeSchemaSchema = {
    properties: {
        window_function: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    prefixItems: [
                        {
                            type: 'string'
                        },
                        {
                            type: 'number'
                        }
                    ],
                    type: 'array',
                    maxItems: 2,
                    minItems: 2
                }
            ],
            title: 'Window Function'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        step: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Step'
        }
    },
    type: 'object',
    required: [
        'window_function',
        'length',
        'step'
    ],
    title: 'DerivedDataWindowTypeSchema'
} as const;

export const DiabetesMellitusSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Diabetes mellitus',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Type 1',
                        'Type 2'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DiabetesMellitusSchemaResponse'
} as const;

export const DiagnosticECGStatementSchema = {
    properties: {
        code: {
            type: 'string',
            maxLength: 7,
            minLength: 2,
            title: 'Code'
        },
        statement_type: {
            type: 'string',
            const: 'diagnostic',
            title: 'Statement Type',
            default: 'diagnostic'
        },
        description: {
            type: 'string',
            title: 'Description'
        },
        statement_category: {
            type: 'string',
            title: 'Statement Category'
        },
        aha_code: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 999,
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aha Code'
        },
        aecg_refid_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^MDC_ECG[A-Z0-9_]*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aecg Refid Code'
        },
        cdisc_code: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 7,
                    minLength: 6,
                    pattern: '^C.*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Cdisc Code'
        },
        dicom_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^(D3|F|R)-[A-Z0-9]{5,6}$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Dicom Code'
        },
        diagnostic_class: {
            $ref: '#/components/schemas/ECGDiagnosticClass'
        }
    },
    type: 'object',
    required: [
        'code',
        'description',
        'statement_category',
        'diagnostic_class'
    ],
    title: 'DiagnosticECGStatement'
} as const;

export const DichotomousQuestionSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_text: {
            type: 'string',
            title: 'Question Text'
        },
        question_type: {
            type: 'string',
            const: 'dichotomous',
            title: 'Question Type',
            default: 'dichotomous'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        question_options: {
            items: {
                $ref: '#/components/schemas/QuestionOption'
            },
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Question Options'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_text',
        'question_options'
    ],
    title: 'DichotomousQuestion'
} as const;

export const DryWeightSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'dry weight',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 30,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'kg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DryWeightSchemaResponse'
} as const;

export const DyslipidemiaSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Dyslipidemia',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DyslipidemiaSchemaResponse'
} as const;

export const ECGDataRelatedResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        leads_placement: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Leads Placement'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        leads: {
            items: {
                $ref: '#/components/schemas/ECGLead'
            },
            type: 'array',
            maxItems: 12,
            minItems: 1,
            title: 'Leads'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'measurement_datetime',
        'measurement_device',
        'leads_placement',
        'leads',
        'sampling_frequency'
    ],
    title: 'ECGDataRelatedResponse'
} as const;

export const ECGDatasetMetadataSchema = {
    properties: {
        dataset_name: {
            type: 'string',
            title: 'Dataset Name'
        },
        length: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created: {
            type: 'string',
            format: 'date-time',
            title: 'Created',
            default: '2026-11-02T16:17:18.445267Z'
        },
        ecg_data: {
            items: {
                $ref: '#/components/schemas/ECGMetadata'
            },
            type: 'array',
            title: 'Ecg Data'
        }
    },
    type: 'object',
    required: [
        'dataset_name',
        'length',
        'ecg_data'
    ],
    title: 'ECGDatasetMetadata'
} as const;

export const ECGDerivedDataGroupSchema = {
    type: 'string',
    enum: [
        'scp-default',
        'scp-custom',
        'ecg-annotation',
        'beat-annotation',
        'hr'
    ],
    title: 'ECGDerivedDataGroup'
} as const;

export const ECGDerivedDataResponseSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/ECGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    $ref: '#/components/schemas/BeatAnnotationWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Type'
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataAggregationFunction'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/ECGRelatedQualityFlag',
            default: 1
        },
        custom_statement: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CustomECGStatement'
                },
                {
                    type: 'null'
                }
            ]
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'window_type',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'custom_statement',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'ECGDerivedDataResponse'
} as const;

export const ECGDerivedDataSchemaWithECGResponseSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/ECGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    $ref: '#/components/schemas/BeatAnnotationWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Type'
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataAggregationFunction'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/ECGRelatedQualityFlag',
            default: 1
        },
        custom_statement: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CustomECGStatement'
                },
                {
                    type: 'null'
                }
            ]
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        ecg: {
            $ref: '#/components/schemas/ECGDataRelatedResponse'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'window_type',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'custom_statement',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'ecg',
        'final_value'
    ],
    title: 'ECGDerivedDataSchemaWithECGResponse'
} as const;

export const ECGDiagnosticClassSchema = {
    type: 'string',
    enum: [
        'normal',
        'myocardial-infarction',
        'st-t-change',
        'conduction-disturbance',
        'hypertrophy'
    ],
    title: 'ECGDiagnosticClass'
} as const;

export const ECGLeadSchema = {
    type: 'string',
    enum: [
        'lead-I',
        'lead-II',
        'lead-III',
        'lead-aVR',
        'lead-aVL',
        'lead-aVF',
        'lead-V1',
        'lead-V2',
        'lead-V3',
        'lead-V4',
        'lead-V5',
        'lead-V6'
    ],
    title: 'ECGLead'
} as const;

export const ECGMeasurementModeSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'ECGMeasurementMode',
    'x-enum-varnames': [
        'external_data',
        'seerlinq_supervision',
        'physician_supervison',
        'home_monitoring',
        'synthetic'
    ],
    description: ' - 0: external_data\n - 1: seerlinq_supervision\n - 2: physician_supervison\n - 3: home_monitoring\n - 4: synthetic'
} as const;

export const ECGMetadataSchema = {
    properties: {
        patient_id: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'integer'
                }
            ],
            title: 'Patient Id'
        },
        patient_group: {
            type: 'string',
            title: 'Patient Group'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag'
        },
        measurement_mode: {
            $ref: '#/components/schemas/ECGMeasurementMode'
        },
        file_reference: {
            type: 'string',
            title: 'File Reference'
        },
        uuid: {
            type: 'string',
            format: 'uuid',
            title: 'Uuid'
        },
        data_type: {
            $ref: '#/components/schemas/AlgorithmDataType'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'patient_group',
        'measurement_datetime',
        'measurement_origin',
        'measurement_device',
        'quality_flag',
        'measurement_mode',
        'file_reference',
        'uuid',
        'data_type'
    ],
    title: 'ECGMetadata'
} as const;

export const ECGQualityFlagSchema = {
    type: 'integer',
    enum: [
        -1,
        0,
        1,
        2
    ],
    title: 'ECGQualityFlag',
    'x-enum-varnames': [
        'unknown',
        'data_error',
        'ok',
        'low_signal_quality'
    ],
    description: ' - -1: unknown\n - 0: data_error\n - 1: ok\n - 2: low_signal_quality'
} as const;

export const ECGRelatedQualityFlagSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'ECGRelatedQualityFlag'
} as const;

export const ECGStatementTypeSchema = {
    type: 'string',
    enum: [
        'diagnostic',
        'form',
        'rhythm'
    ],
    title: 'ECGStatementType'
} as const;

export const ESchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'E',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 500,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'cm/s',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ESchemaResponse'
} as const;

export const E_ASchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'E/A',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'E/ASchemaResponse'
} as const;

export const E_E_AverageSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'E/e\' average',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'E/E\'AverageSchemaResponse'
} as const;

export const EcgPrematureContractionsSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'ECG premature contractions',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'EcgPrematureContractionsSchemaResponse'
} as const;

export const EcgRhythmSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'ECG rhythm',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'SR',
                        'AF',
                        'Paced',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'EcgRhythmSchemaResponse'
} as const;

export const ElectiveHospitalizationEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'hospitalization',
            title: 'Event Category',
            default: 'hospitalization'
        },
        event_category_type: {
            type: 'string',
            const: 'elective',
            title: 'Event Category Type',
            default: 'elective'
        },
        event_category_details: {
            $ref: '#/components/schemas/HospitalizationEventReason'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_details',
        'event_description',
        'patient_id'
    ],
    title: 'ElectiveHospitalizationEventSchema'
} as const;

export const ElevatedLvFillingPressureFinalOutcomeSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'elevated LV filling pressure final outcome',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
} as const;

export const ElevatedLvFillingPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'elevated LV filling pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ElevatedLvFillingPressureSchemaResponse'
} as const;

export const EnumDataSchemaSchema = {
    properties: {
        value_type: {
            type: 'string',
            const: 'enum',
            title: 'Value Type',
            default: 'enum'
        },
        values_enum: {
            items: {
                type: 'string'
            },
            type: 'array',
            minItems: 2,
            title: 'Values Enum'
        }
    },
    type: 'object',
    required: [
        'values_enum'
    ],
    title: 'EnumDataSchema'
} as const;

export const ErrorCodeSchema = {
    type: 'string',
    enum: [
        'USER_NOT_ACTIVE',
        'USER_NOT_FOUND',
        'USER_METADATA_NOT_FOUND',
        'RESOURCE_NOT_FOUND',
        'PATIENT_NOT_FOUND',
        'PATIENT_HF_TYPE_MISSING',
        'RESOURCE_NOT_AVAILABLE',
        'USER_INACTIVE',
        'ENROLMENT_INACTIVE',
        'BULK_ACTION_NOT_ALLOWED',
        'UPDATE_NOT_ALLOWED',
        'USER_PROTECTED',
        'USER_ARCHIVED',
        'DELETE_BLOCKED',
        'PATIENT_PROTECTED',
        'DATA_PROTECTED',
        'USER_IS_NOT_MANAGER',
        'PATIENT_DOES_NOT_MATCH',
        'CLINIC_DOES_NOT_MATCH',
        'RESOURCE_ALREADY_EXISTS',
        'USERNAME_ALREADY_EXISTS',
        'EMAIL_ALREADY_EXISTS',
        'TEMPORARY_PASSWORD_EXISTS',
        'BAD_INTENT',
        'UPLOAD_STATUS_NOT_SUCCESSFUL',
        'RESOURCE_FAILED',
        'DATABASE_CONNECTION_FAILED',
        'REDIS_CONNECTION_FAILED',
        'INVALID_CREDENTIALS',
        'INVALID_TOKEN',
        'INCORRECT_DATA',
        'MISSING_REQUIRED_DATA',
        'INVALID_FILE_TYPE',
        'INVALID_FILE_SIZE',
        'FILE_TOO_LARGE',
        'FILES_TOTAL_SIZE_TOO_LARGE',
        'MEASUREMENT_TYPE_NOT_SUPPORTED',
        'INCORRECT_GEOGRAPHY',
        'TASK_RESULT_CAN_NOT_BE_RENDERED',
        'TOKEN_EXPIRED',
        'TOKEN_ALREADY_USED',
        'MFA_INVALID_CODE',
        'MFA_LAST_METHOD',
        'SMS_SEND_FAILED',
        'SMS_UNAVAILABLE',
        'RATE_LIMIT_EXCEEDED',
        'PERMISSION_DENIED',
        'VALIDATION_ERROR',
        'INTERNAL_SERVER_ERROR',
        'SERVICE_UNAVAILABLE',
        'REQUEST_TIMEOUT',
        'EXTERNAL_SERVICE_TIMEOUT'
    ],
    title: 'ErrorCode',
    description: 'Unique error codes for specific error scenarios.\n\nThese codes provide a precise identifier for each error,\nCodes can be translated to user friendly messages by mapping them\nto JSON in `_locales` for specific language.'
} as const;

export const ErrorCodeResponseSchema = {
    additionalProperties: {
        type: 'string'
    },
    propertyNames: {
        $ref: '#/components/schemas/ErrorCode'
    },
    type: 'object',
    title: 'ErrorCodeResponse'
} as const;

export const EventCategorySchema = {
    type: 'string',
    enum: [
        'hospitalization',
        'death',
        'visit',
        'medical_call',
        'technical_call',
        'medication_change',
        'labs',
        'physician_notified',
        'patient_non_adherence',
        'adverse_event'
    ],
    title: 'EventCategory'
} as const;

export const EventTablesSchema = {
    type: 'string',
    enum: [
        'medications',
        'ppg_derived',
        'sq_computed'
    ],
    title: 'EventTables'
} as const;

export const ExactDateStatusSchema = {
    type: 'string',
    enum: [
        'ok',
        'weekend',
        'holiday'
    ],
    title: 'ExactDateStatus'
} as const;

export const ExceptionReprSchema = {
    properties: {
        exc_type: {
            type: 'string',
            title: 'Exc Type'
        },
        exc_message: {
            items: {},
            type: 'array',
            title: 'Exc Message'
        },
        exc_module: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Exc Module'
        },
        exc_cause: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ExceptionRepr'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Exc Cause'
        },
        exc_context: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ExceptionRepr'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Exc Context'
        },
        exc_suppress_context: {
            type: 'boolean',
            title: 'Exc Suppress Context',
            default: false
        }
    },
    type: 'object',
    required: [
        'exc_type',
        'exc_message',
        'exc_module'
    ],
    title: 'ExceptionRepr',
    description: 'Serializable exception model.'
} as const;

export const ExtractedDiagnosisSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            type: 'integer',
            enum: [
                0,
                25,
                50,
                75,
                100
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        source_context: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Source Context'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value'
    ],
    title: 'ExtractedDiagnosis'
} as const;

export const ExtractedMeasurementSchema = {
    properties: {
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        measurement_datetime: {
            type: 'string',
            title: 'Measurement Datetime'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        source_context: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Source Context'
        }
    },
    type: 'object',
    required: [
        'measurement_type',
        'measurement_value',
        'measurement_datetime'
    ],
    title: 'ExtractedMeasurement'
} as const;

export const ExtractedMedicationSchema = {
    properties: {
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_group: {
            type: 'string',
            title: 'Medication Group'
        },
        medication_dose: {
            type: 'number',
            title: 'Medication Dose'
        },
        medication_unit: {
            type: 'string',
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number'
            },
            type: 'array',
            title: 'Medication Dosage'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'days',
                        'months'
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every Unit'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        type: 'integer'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        source_context: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Source Context'
        }
    },
    type: 'object',
    required: [
        'medication_name',
        'medication_group',
        'medication_dose',
        'medication_unit'
    ],
    title: 'ExtractedMedication'
} as const;

export const ExtractedSymptomSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 4,
            minimum: 0,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M',
            default: false
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        source_context: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Source Context'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value'
    ],
    title: 'ExtractedSymptom'
} as const;

export const ExtractionBBoxesSchema = {
    properties: {
        diagnoses: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/BBox'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/BBox'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/BBox'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/BBox'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    title: 'ExtractionBBoxes',
    description: 'Bounding boxes per extracted entity, index-aligned with the matching list\nin ``ExtractionResult``. ``None`` where no span could be located (e.g. the\nsource_context wasn\'t found, or the PDF has no text layer / was scanned).'
} as const;

export const ExtractionDraftSchemaSchema = {
    properties: {
        extraction: {
            $ref: '#/components/schemas/ExtractionResult'
        },
        entity_ids: {
            $ref: '#/components/schemas/ExtractionEntityIds'
        },
        bboxes: {
            $ref: '#/components/schemas/ExtractionBBoxes'
        },
        entity_confidence: {
            $ref: '#/components/schemas/ExtractionEntityConfidence'
        },
        duplicates: {
            $ref: '#/components/schemas/ExtractionDuplicates'
        },
        entity_origin: {
            $ref: '#/components/schemas/ExtractionEntityOrigin'
        },
        excluded: {
            $ref: '#/components/schemas/ExtractionExcluded'
        },
        review_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Review Comment'
        },
        version: {
            type: 'integer',
            title: 'Version'
        }
    },
    type: 'object',
    required: [
        'extraction',
        'version'
    ],
    title: 'ExtractionDraftSchema',
    description: 'A reviewer\'s corrections, saved before they are ingested.\n\nReview is not a single sitting: a dense discharge letter takes minutes of\nedits, and the queue exists so documents are worked through in batches.\nHolding that only in the browser meant a reload, a misclick or a hand-off\nto a colleague threw it away, so the reviewed state is stored beside the\nmodel\'s own output and outlives the session.\n\nThe per-entity lists must stay index-aligned — the highlight boxes,\nconfidence badges, duplicate flags, origins and exclusions are matched to\nentities by position, so a draft that dropped an entity without dropping\nits box would point the reviewer at the wrong line in the PDF after a\nreload.\n\nDatetimes are deliberately NOT validated here. The PastAwareDatetime\ncontract is enforced per entity in the ingest build path (_parse_dt),\nwhere a violation lands in the response\'s ``skipped`` counter like every\nother per-entity failure — a request-level validator would 422 the whole\nautosave over one tz-naive string, losing the reviewer\'s other edits.\n\n``version`` is required here alone: a save must state which draft it was\nmade against, so a second reviewer\'s overlapping save gets a 409 rather\nthan quietly replacing the first\'s work.'
} as const;

export const ExtractionDuplicateRefSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        recorded_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Recorded At'
        },
        value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                },
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value'
        },
        unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Unit'
        }
    },
    type: 'object',
    required: [
        'uuid'
    ],
    title: 'ExtractionDuplicateRef',
    description: 'The existing clinical record an extracted entity possibly duplicates.\n\nAdvisory only — the reviewer decides. The frontend shows a tooltip naming\nthe existing record and defaults the entity to excluded, so a repeat of\nsomething already in the chart costs a glance rather than a deletion.'
} as const;

export const ExtractionDuplicatesSchema = {
    properties: {
        diagnoses: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ExtractionDuplicateRef'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ExtractionDuplicateRef'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ExtractionDuplicateRef'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ExtractionDuplicateRef'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    title: 'ExtractionDuplicates',
    description: 'Per-entity duplicate flags, index-aligned with the matching list in\n``ExtractionResult``. ``None`` where no existing record matched.'
} as const;

export const ExtractionEntityConfidenceSchema = {
    properties: {
        diagnoses: {
            items: {
                type: 'number'
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                type: 'number'
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                type: 'number'
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                type: 'number'
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    title: 'ExtractionEntityConfidence',
    description: 'Per-entity extraction-accuracy score, index-aligned with the matching\nlist in ``ExtractionResult``. Deterministic (never the model\'s\nself-reported confidence): 1.0 = the source quote was found in the\ndocument and the extracted value is visible in it; 0.5 = quote found but\nthe value is not in it (paraphrase / wobble / unit conversion — reviewer\nshould double-check); 0.0 = quote not found. Distinct from the clinical\n``diagnosis_confidence`` field (the clinician\'s certainty of the\ndiagnosis itself).'
} as const;

export const ExtractionEntityIdsSchema = {
    properties: {
        diagnoses: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    title: 'ExtractionEntityIds',
    description: 'The server-minted id of each extracted entity, index-aligned with the\nmatching list in ``ExtractionResult``. Unlike the other side-cars this is\nnever partial: an entity without an id cannot be matched between the\nmodel\'s output and the reviewer\'s, which is the whole point of keeping\nboth.'
} as const;

export const ExtractionEntityOriginSchema = {
    properties: {
        diagnoses: {
            items: {
                type: 'string',
                enum: [
                    'model',
                    'region',
                    'manual'
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                type: 'string',
                enum: [
                    'model',
                    'region',
                    'manual'
                ]
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                type: 'string',
                enum: [
                    'model',
                    'region',
                    'manual'
                ]
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                type: 'string',
                enum: [
                    'model',
                    'region',
                    'manual'
                ]
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    title: 'ExtractionEntityOrigin',
    description: 'Per-entity provenance, index-aligned with the matching list in\n``ExtractionResult``.'
} as const;

export const ExtractionExcludedSchema = {
    properties: {
        diagnoses: {
            items: {
                type: 'boolean'
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                type: 'boolean'
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                type: 'boolean'
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                type: 'boolean'
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    title: 'ExtractionExcluded',
    description: 'Per-entity ingest exclusion, index-aligned with the matching list in\n``ExtractionResult``. True keeps the entity in the draft but out of the\nclinical tables — how a reviewer resolves a flagged duplicate without\ndeleting what the document actually said.'
} as const;

export const ExtractionRejectionSchemaSchema = {
    properties: {
        rejected_reason: {
            type: 'string',
            minLength: 1,
            title: 'Rejected Reason'
        }
    },
    type: 'object',
    required: [
        'rejected_reason'
    ],
    title: 'ExtractionRejectionSchema',
    description: 'The reviewer\'s refusal of a stored draft.\n\nThe reason is required: a rejected run says the model\'s output was\nunusable, which nothing else in the dataset records, and without the why\nthe label cannot be acted on by a later prompt.'
} as const;

export const ExtractionResultSchema = {
    properties: {
        diagnoses: {
            items: {
                $ref: '#/components/schemas/ExtractedDiagnosis'
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medications: {
            items: {
                $ref: '#/components/schemas/ExtractedMedication'
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                $ref: '#/components/schemas/ExtractedSymptom'
            },
            type: 'array',
            title: 'Symptoms'
        },
        medical_data: {
            items: {
                $ref: '#/components/schemas/ExtractedMeasurement'
            },
            type: 'array',
            title: 'Medical Data'
        },
        report_type: {
            type: 'integer',
            maximum: 4,
            minimum: 0,
            title: 'Report Type',
            default: 0
        },
        report_date: {
            type: 'string',
            format: 'date',
            title: 'Report Date'
        },
        document_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Document Date'
        },
        confidence_score: {
            type: 'number',
            maximum: 1,
            minimum: 0,
            title: 'Confidence Score',
            default: 0.9
        },
        source_language: {
            type: 'string',
            pattern: '^\\w{2}$',
            title: 'Source Language',
            default: 'sk'
        }
    },
    type: 'object',
    required: [
        'report_date'
    ],
    title: 'ExtractionResult'
} as const;

export const ExtractionRetryRequestSchema = {
    properties: {
        rotations: {
            items: {
                $ref: '#/components/schemas/PageRotation'
            },
            type: 'array',
            title: 'Rotations',
            default: []
        },
        pages: {
            items: {
                type: 'integer',
                minimum: 1
            },
            type: 'array',
            title: 'Pages',
            default: []
        }
    },
    type: 'object',
    title: 'ExtractionRetryRequest',
    description: 'Page rotations to write into the stored PDF before the re-run reads it,\nand pages to read again, such as the latest run\'s ``failed_pages``.\n\nWith either, the re-run reads the rotated and listed pages, the pages\nthe latest run failed on and the pages whose words or their boxes\nchanged, and keeps the latest run\'s entities on every other page. A page\nOCR fails on this time keeps them too, unless it was rotated or listed.\nIt reads every page when that run was rejected, predates page-by-page\nreading or used another prompt. A listed page past the document\'s last\nis ignored. With neither, every page is read.'
} as const;

export const ExtractionStatusSchema = {
    type: 'string',
    enum: [
        'pending',
        'processing',
        'ready',
        'failed',
        'ingested',
        'rejected'
    ],
    title: 'ExtractionStatus'
} as const;

export const ExtractionStoredResultSchema = {
    properties: {
        extraction: {
            $ref: '#/components/schemas/ExtractionResult'
        },
        entity_ids: {
            $ref: '#/components/schemas/ExtractionEntityIds'
        },
        bboxes: {
            $ref: '#/components/schemas/ExtractionBBoxes'
        },
        entity_confidence: {
            $ref: '#/components/schemas/ExtractionEntityConfidence'
        },
        duplicates: {
            $ref: '#/components/schemas/ExtractionDuplicates'
        },
        entity_origin: {
            $ref: '#/components/schemas/ExtractionEntityOrigin'
        },
        excluded: {
            $ref: '#/components/schemas/ExtractionExcluded'
        },
        review_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Review Comment'
        },
        version: {
            type: 'integer',
            title: 'Version',
            default: 1
        },
        report_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Report Uuid'
        },
        extraction_status: {
            type: 'string',
            title: 'Extraction Status'
        },
        extracted_at: {
            type: 'string',
            title: 'Extracted At'
        },
        reviewed_at: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reviewed At'
        },
        review_status: {
            type: 'string',
            title: 'Review Status',
            default: 'unreviewed'
        },
        rejected_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Rejected Reason'
        },
        ingested_uuids: {
            additionalProperties: {
                type: 'string',
                format: 'uuid4'
            },
            type: 'object',
            title: 'Ingested Uuids'
        },
        skip_reasons: {
            additionalProperties: {
                type: 'string'
            },
            type: 'object',
            title: 'Skip Reasons'
        },
        unreadable_pages: {
            items: {
                type: 'integer'
            },
            type: 'array',
            title: 'Unreadable Pages'
        },
        failed_pages: {
            items: {
                type: 'integer'
            },
            type: 'array',
            title: 'Failed Pages'
        }
    },
    type: 'object',
    required: [
        'extraction',
        'report_uuid',
        'extraction_status',
        'extracted_at'
    ],
    title: 'ExtractionStoredResult',
    description: 'A stored run as the reviewer sees it. Everything below the reviewed\nextraction is server-written, so the frontend renders the review and\ningest state without a second call.'
} as const;

export const FatigueScoreSchemaResponseSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            const: 'fatigue score',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'FatigueScoreSchemaResponse'
} as const;

export const FeatureFlagStateSchema = {
    properties: {
        value: {
            type: 'boolean',
            title: 'Value',
            default: false
        },
        user_editable: {
            type: 'boolean',
            title: 'User Editable',
            default: true
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'FeatureFlagState',
    description: 'A flag\'s value plus whether the record\'s own user may change it\nthemselves (staff can always change either, regardless of this).\nFrozen: instances returned by `resolve_feature_flags` for untouched\nflags are the shared registry defaults, not copies.'
} as const;

export const FeatureFlagUpdateSchema = {
    properties: {
        value: {
            type: 'boolean',
            title: 'Value',
            default: false
        },
        user_editable: {
            type: 'boolean',
            title: 'User Editable',
            default: true
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'FeatureFlagUpdate',
    description: 'Partial update for one flag: either field may be omitted to leave it\nunchanged. Neither field accepts an explicit `null` - only `bool` is a\nvalid value, so a client that sends `null` gets a 422 instead of that\n`null` silently reaching storage.'
} as const;

export const FilledQuestionnaireResponseSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        questionnaire_language: {
            type: 'string',
            enum: [
                'ar',
                'az',
                'cs',
                'da',
                'de',
                'el',
                'en',
                'es',
                'fr',
                'he',
                'hi',
                'hu',
                'it',
                'ja',
                'lt',
                'lv',
                'nb',
                'pl',
                'pt',
                'ro',
                'ru',
                'sk',
                'sv',
                'tr',
                'uk',
                'zh-Hans',
                'zh-Hant'
            ],
            title: 'SupportedLanguages',
            description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
            'x-seerlinq-languages': [
                {
                    alpha2: 'ar',
                    alpha3: 'ara',
                    code: 'ar',
                    dir: 'rtl',
                    name: 'Arabic',
                    native_name: 'العربية',
                    regions: [
                        'SA'
                    ]
                },
                {
                    alpha2: 'az',
                    alpha3: 'aze',
                    code: 'az',
                    dir: 'ltr',
                    name: 'Azerbaijani',
                    native_name: 'Azərbaycan dili',
                    regions: []
                },
                {
                    alpha2: 'cs',
                    alpha3: 'ces',
                    code: 'cs',
                    dir: 'ltr',
                    name: 'Czech',
                    native_name: 'Čeština',
                    regions: []
                },
                {
                    alpha2: 'da',
                    alpha3: 'dan',
                    code: 'da',
                    dir: 'ltr',
                    name: 'Danish',
                    native_name: 'Dansk',
                    regions: []
                },
                {
                    alpha2: 'de',
                    alpha3: 'deu',
                    code: 'de',
                    dir: 'ltr',
                    name: 'German',
                    native_name: 'Deutsch',
                    regions: [
                        'DE',
                        'AT'
                    ]
                },
                {
                    alpha2: 'el',
                    alpha3: 'ell',
                    code: 'el',
                    dir: 'ltr',
                    name: 'Greek',
                    native_name: 'Ελληνικά',
                    regions: []
                },
                {
                    alpha2: 'en',
                    alpha3: 'eng',
                    code: 'en',
                    dir: 'ltr',
                    name: 'English',
                    native_name: 'English',
                    regions: [
                        'GB'
                    ]
                },
                {
                    alpha2: 'es',
                    alpha3: 'spa',
                    code: 'es',
                    dir: 'ltr',
                    name: 'Spanish',
                    native_name: 'Español',
                    regions: [
                        'ES'
                    ]
                },
                {
                    alpha2: 'fr',
                    alpha3: 'fra',
                    code: 'fr',
                    dir: 'ltr',
                    name: 'French',
                    native_name: 'Français',
                    regions: [
                        'FR'
                    ]
                },
                {
                    alpha2: 'he',
                    alpha3: 'heb',
                    code: 'he',
                    dir: 'rtl',
                    name: 'Hebrew',
                    native_name: 'עברית',
                    regions: []
                },
                {
                    alpha2: 'hi',
                    alpha3: 'hin',
                    code: 'hi',
                    dir: 'ltr',
                    name: 'Hindi',
                    native_name: 'हिन्दी',
                    regions: []
                },
                {
                    alpha2: 'hu',
                    alpha3: 'hun',
                    code: 'hu',
                    dir: 'ltr',
                    name: 'Hungarian',
                    native_name: 'Magyar',
                    regions: []
                },
                {
                    alpha2: 'it',
                    alpha3: 'ita',
                    code: 'it',
                    dir: 'ltr',
                    name: 'Italian',
                    native_name: 'Italiano',
                    regions: []
                },
                {
                    alpha2: 'ja',
                    alpha3: 'jpn',
                    code: 'ja',
                    dir: 'ltr',
                    name: 'Japanese',
                    native_name: '日本語',
                    regions: []
                },
                {
                    alpha2: 'lt',
                    alpha3: 'lit',
                    code: 'lt',
                    dir: 'ltr',
                    name: 'Lithuanian',
                    native_name: 'Lietuvių',
                    regions: []
                },
                {
                    alpha2: 'lv',
                    alpha3: 'lav',
                    code: 'lv',
                    dir: 'ltr',
                    name: 'Latvian',
                    native_name: 'Latviešu',
                    regions: []
                },
                {
                    alpha2: 'nb',
                    alpha3: 'nob',
                    code: 'nb',
                    dir: 'ltr',
                    name: 'Norwegian Bokmål',
                    native_name: 'Norsk bokmål',
                    regions: []
                },
                {
                    alpha2: 'pl',
                    alpha3: 'pol',
                    code: 'pl',
                    dir: 'ltr',
                    name: 'Polish',
                    native_name: 'Polski',
                    regions: []
                },
                {
                    alpha2: 'pt',
                    alpha3: 'por',
                    code: 'pt',
                    dir: 'ltr',
                    name: 'Portuguese',
                    native_name: 'Português',
                    regions: [
                        'PT'
                    ]
                },
                {
                    alpha2: 'ro',
                    alpha3: 'ron',
                    code: 'ro',
                    dir: 'ltr',
                    name: 'Romanian',
                    native_name: 'Română',
                    regions: []
                },
                {
                    alpha2: 'ru',
                    alpha3: 'rus',
                    code: 'ru',
                    dir: 'ltr',
                    name: 'Russian',
                    native_name: 'Русский',
                    regions: []
                },
                {
                    alpha2: 'sk',
                    alpha3: 'slk',
                    code: 'sk',
                    dir: 'ltr',
                    name: 'Slovak',
                    native_name: 'Slovenčina',
                    regions: []
                },
                {
                    alpha2: 'sv',
                    alpha3: 'swe',
                    code: 'sv',
                    dir: 'ltr',
                    name: 'Swedish',
                    native_name: 'Svenska',
                    regions: []
                },
                {
                    alpha2: 'tr',
                    alpha3: 'tur',
                    code: 'tr',
                    dir: 'ltr',
                    name: 'Turkish',
                    native_name: 'Türkçe',
                    regions: []
                },
                {
                    alpha2: 'uk',
                    alpha3: 'ukr',
                    code: 'uk',
                    dir: 'ltr',
                    name: 'Ukrainian',
                    native_name: 'Українська',
                    regions: []
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hans',
                    dir: 'ltr',
                    name: 'Chinese (Simplified)',
                    native_name: '简体中文',
                    regions: [
                        'CN'
                    ]
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hant',
                    dir: 'ltr',
                    name: 'Chinese (Traditional)',
                    native_name: '繁體中文',
                    regions: [
                        'TW'
                    ]
                }
            ]
        },
        questionnaire_version: {
            type: 'string',
            title: 'Questionnaire Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        filling_start: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Filling Start'
        },
        filling_end: {
            type: 'string',
            format: 'date-time',
            title: 'Filling End'
        },
        responses: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/OrdinalQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/MultiChoiceQuestionnaireResponse-Output'
                    },
                    {
                        $ref: '#/components/schemas/ContinuousQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/OpenTextQuestionnaireResponse'
                    }
                ],
                discriminator: {
                    propertyName: 'question_type',
                    mapping: {
                        continuous: '#/components/schemas/ContinuousQuestionnaireResponse',
                        dichotomous: '#/components/schemas/SingleChoiceQuestionnaireResponse',
                        open_text: '#/components/schemas/OpenTextQuestionnaireResponse',
                        ordinal: '#/components/schemas/OrdinalQuestionnaireResponse',
                        polytomous_multi_choice: '#/components/schemas/MultiChoiceQuestionnaireResponse-Output',
                        polytomous_single_choice: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                    }
                }
            },
            type: 'array',
            minItems: 1,
            title: 'Responses'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        filling_time: {
            type: 'number',
            title: 'Filling Time',
            description: 'Compute time it took to fill in the questionnaire.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'questionnaire_language',
        'questionnaire_version',
        'filling_end',
        'responses',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at',
        'filling_time'
    ],
    title: 'FilledQuestionnaireResponse'
} as const;

export const FinishRegistrationSchema = {
    properties: {
        token: {
            type: 'string',
            title: 'Token'
        },
        expiry_token: {
            type: 'string',
            minLength: 1,
            title: 'Expiry Token'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'token',
        'expiry_token'
    ],
    title: 'FinishRegistration'
} as const;

export const FinishRegistrationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        new_user: {
            $ref: '#/components/schemas/UserResponse'
        }
    },
    type: 'object',
    required: [
        'status',
        'new_user'
    ],
    title: 'FinishRegistrationResponse'
} as const;

export const FloatDataSchemaSchema = {
    properties: {
        value_type: {
            type: 'string',
            const: 'float',
            title: 'Value Type',
            default: 'float'
        },
        value_ge: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Ge'
        },
        value_gt: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Gt'
        },
        value_le: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Le'
        },
        value_lt: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Lt'
        },
        value_multiple_of: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Multiple Of'
        }
    },
    type: 'object',
    title: 'FloatDataSchema'
} as const;

export const ForgotPasswordResponseSchema = {
    properties: {
        password_reset: {
            type: 'boolean',
            const: true,
            title: 'Password Reset',
            default: true
        },
        ses_send_mail_task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Ses Send Mail Task Id'
        }
    },
    type: 'object',
    required: [
        'ses_send_mail_task_id'
    ],
    title: 'ForgotPasswordResponse'
} as const;

export const FormRhythmECGStatementSchema = {
    properties: {
        code: {
            type: 'string',
            maxLength: 7,
            minLength: 2,
            title: 'Code'
        },
        statement_type: {
            type: 'string',
            enum: [
                'form',
                'rhythm'
            ],
            title: 'Statement Type'
        },
        description: {
            type: 'string',
            title: 'Description'
        },
        statement_category: {
            type: 'string',
            title: 'Statement Category'
        },
        aha_code: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 999,
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aha Code'
        },
        aecg_refid_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^MDC_ECG[A-Z0-9_]*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aecg Refid Code'
        },
        cdisc_code: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 7,
                    minLength: 6,
                    pattern: '^C.*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Cdisc Code'
        },
        dicom_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^(D3|F|R)-[A-Z0-9]{5,6}$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Dicom Code'
        }
    },
    type: 'object',
    required: [
        'code',
        'statement_type',
        'description',
        'statement_category'
    ],
    title: 'FormRhythmECGStatement'
} as const;

export const FrequencyIntervalItemSchema = {
    properties: {
        delta: {
            $ref: '#/components/schemas/RelativeDeltaModel'
        },
        count: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Count'
        }
    },
    type: 'object',
    required: [
        'delta'
    ],
    title: 'FrequencyIntervalItem'
} as const;

export const FrequencyUnitSchema = {
    type: 'string',
    enum: [
        'days',
        'months'
    ],
    title: 'FrequencyUnit'
} as const;

export const FreshLoginResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        access_token: {
            type: 'string',
            title: 'Access Token'
        },
        mfa_enrollment_recommended: {
            type: 'boolean',
            title: 'Mfa Enrollment Recommended',
            default: false
        }
    },
    type: 'object',
    required: [
        'status',
        'access_token'
    ],
    title: 'FreshLoginResponse'
} as const;

export const FullClinicResponseSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            type: 'string',
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        physicians: {
            items: {
                $ref: '#/components/schemas/PhysicianResponse'
            },
            type: 'array',
            title: 'Physicians'
        }
    },
    type: 'object',
    required: [
        'country',
        'name',
        'address',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'FullClinicResponse'
} as const;

export const FullMeasurementComplianceSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            type: 'string',
            const: 'full_measurement',
            title: 'Schedule Type'
        },
        schedule_data_name: {
            type: 'string',
            const: 'full measurement',
            title: 'Schedule Data Name',
            default: 'full measurement'
        },
        scheduled_times: {
            items: {
                type: 'string',
                format: 'time'
            },
            type: 'array',
            title: 'Scheduled Times'
        },
        total_expected: {
            type: 'integer',
            minimum: 0,
            title: 'Total Expected'
        },
        total_complete_sessions: {
            type: 'integer',
            minimum: 0,
            title: 'Total Complete Sessions'
        },
        total_partial_sessions: {
            type: 'integer',
            minimum: 0,
            title: 'Total Partial Sessions'
        },
        total_pending_sessions: {
            type: 'integer',
            minimum: 0,
            title: 'Total Pending Sessions',
            default: 0
        },
        compliance_ratio: {
            type: 'number',
            title: 'Compliance Ratio'
        },
        soft_total_complete_sessions: {
            type: 'integer',
            minimum: 0,
            title: 'Soft Total Complete Sessions'
        },
        soft_compliance_ratio: {
            type: 'number',
            title: 'Soft Compliance Ratio'
        },
        soft_tolerance_days: {
            type: 'integer',
            minimum: 0,
            title: 'Soft Tolerance Days'
        },
        most_common_missing: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Most Common Missing'
        },
        daily_counts: {
            items: {
                $ref: '#/components/schemas/DailyCount'
            },
            type: 'array',
            title: 'Daily Counts'
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'scheduled_times',
        'total_expected',
        'total_complete_sessions',
        'total_partial_sessions',
        'compliance_ratio',
        'soft_total_complete_sessions',
        'soft_compliance_ratio',
        'soft_tolerance_days',
        'daily_counts'
    ],
    title: 'FullMeasurementCompliance'
} as const;

export const FullMeasurementScheduleCheckResponseSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_time: {
            type: 'string',
            format: 'time',
            title: 'Scheduled Time'
        },
        check_timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Check Timezone'
        },
        allowed_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Allowed Timedelta'
        },
        measured_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measured Datetime'
        },
        is_complete: {
            type: 'boolean',
            title: 'Is Complete'
        },
        missing_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Missing Items'
        },
        schedule_fulfilled: {
            type: 'boolean',
            title: 'Schedule Fulfilled',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_time',
        'check_timezone',
        'allowed_timedelta',
        'measured_datetime',
        'is_complete',
        'schedule_fulfilled'
    ],
    title: 'FullMeasurementScheduleCheckResponse'
} as const;

export const FullPhysicianResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicResponse'
            },
            type: 'array',
            title: 'Clinics'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/SimpleUserResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at',
        'full_name'
    ],
    title: 'FullPhysicianResponse'
} as const;

export const FullUserResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponse'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        added_by: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Added By'
        },
        connected_patient: {
            anyOf: [
                {
                    $ref: '#/components/schemas/UserConnectedPatientResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/FullPhysicianResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        assigned_physicians: {
            items: {
                $ref: '#/components/schemas/FullPhysicianResponse'
            },
            type: 'array',
            title: 'Assigned Physicians'
        },
        assigned_clinics: {
            items: {
                $ref: '#/components/schemas/FullClinicResponse'
            },
            type: 'array',
            title: 'Assigned Clinics'
        },
        visible_patients: {
            items: {
                $ref: '#/components/schemas/UserConnectedPatientResponse'
            },
            type: 'array',
            title: 'Visible Patients',
            description: 'Get managed patients from access policy.',
            readOnly: true
        },
        password_status: {
            $ref: '#/components/schemas/UserPasswordStatus',
            description: 'Return status of user\'s password.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at',
        'visible_patients',
        'password_status'
    ],
    title: 'FullUserResponse'
} as const;

export const GDMTConflictReasonSchema = {
    type: 'string',
    enum: [
        'medication_in_other_group',
        'medication_target_dosage_not_in_data',
        'medication_does_not_match_patient_hf_type',
        'multiple_medications_same_class',
        'multiple_drug_classes_in_raas_slot',
        'patient_weight_missing',
        'medication_not_gdmt_relevant'
    ],
    title: 'GDMTConflictReason',
    description: 'Per-medication flag on GDMTMedicationResult indicating why a medication\ncannot be scored or signals a conflict needing clinical review.\nSent as string values in API responses (EnumMixin pattern).'
} as const;

export const GDMTInfoSchema = {
    properties: {
        max_pts: {
            type: 'integer',
            title: 'Max Pts'
        },
        type: {
            type: 'string',
            enum: [
                'threshold',
                'presence'
            ],
            title: 'Type'
        },
        hf_types: {
            items: {
                type: 'string',
                enum: [
                    'HFrEF',
                    'HFmrEF',
                    'HFpEF'
                ]
            },
            type: 'array',
            title: 'Hf Types'
        },
        raas_slot: {
            anyOf: [
                {
                    type: 'integer',
                    enum: [
                        1,
                        2,
                        3
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Raas Slot'
        }
    },
    type: 'object',
    required: [
        'max_pts',
        'type',
        'hf_types'
    ],
    title: 'GDMTInfo'
} as const;

export const GDMTMedicationResultSchema = {
    properties: {
        drug_class: {
            type: 'string',
            title: 'Drug Class'
        },
        status: {
            $ref: '#/components/schemas/GDMTMedicationStatus'
        },
        drug_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Drug Name'
        },
        daily_dose: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Daily Dose',
            description: 'Daily dose in mg. Computed as the sum of tablet counts across dosage slots multiplied by the per-tablet dose (mg), then adjusted for weekly frequency or for the repeat-every-N schedule, where N counts days or calendar months counted from today.'
        },
        daily_dose_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Daily Dose Unit'
        },
        target_daily_dose: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Target Daily Dose',
            description: 'Target daily dose in mg per GDMT guidelines (from medications.json). Weight-adjusted for Carvedilol; sum of component dose x times_daily for compound medications.'
        },
        points: {
            type: 'integer',
            title: 'Points'
        },
        flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/GDMTConflictReason'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'drug_class',
        'status',
        'drug_name',
        'daily_dose',
        'daily_dose_unit',
        'target_daily_dose',
        'points'
    ],
    title: 'GDMTMedicationResult'
} as const;

export const GDMTMedicationStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'GDMTMedicationStatus'
} as const;

export const GDMTNotApplicableReasonSchema = {
    type: 'integer',
    enum: [
        0
    ],
    title: 'GDMTNotApplicableReason'
} as const;

export const GDMTResultSchema = {
    properties: {
        gdmt_score: {
            type: 'integer',
            maximum: 9,
            minimum: 0,
            title: 'Gdmt Score'
        },
        gdmt_max: {
            type: 'integer',
            maximum: 9,
            minimum: 0,
            title: 'Gdmt Max'
        },
        hf_type: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'HFrEF',
                        'HFmrEF',
                        'HFpEF'
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Hf Type'
        },
        weight_missing: {
            type: 'boolean',
            title: 'Weight Missing',
            default: false
        },
        not_applicable: {
            type: 'boolean',
            title: 'Not Applicable',
            default: false
        },
        not_applicable_reason: {
            anyOf: [
                {
                    $ref: '#/components/schemas/GDMTNotApplicableReason'
                },
                {
                    type: 'null'
                }
            ]
        },
        medications: {
            items: {
                $ref: '#/components/schemas/GDMTMedicationResult'
            },
            type: 'array',
            title: 'Medications'
        }
    },
    type: 'object',
    required: [
        'gdmt_score',
        'gdmt_max',
        'medications'
    ],
    title: 'GDMTResult'
} as const;

export const GLP1_RASchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'GLP1-RA',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'GLP1-RA'
} as const;

export const GlycaemiaSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Glycaemia',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Glycaemia'
} as const;

export const GroupCountSchema = {
    properties: {
        group_by: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        enum: [
                            'Patient Status',
                            'Monitoring Type',
                            'Country'
                        ]
                    },
                    type: 'array'
                },
                {
                    type: 'string',
                    const: 'Total'
                }
            ],
            title: 'Group By'
        },
        group_name: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Group Name'
        },
        count: {
            type: 'integer',
            minimum: 0,
            title: 'Count'
        }
    },
    type: 'object',
    required: [
        'group_by',
        'group_name',
        'count'
    ],
    title: 'GroupCount'
} as const;

export const GroupSchemaSchema = {
    properties: {
        type: {
            type: 'string',
            const: 'Group',
            title: 'Type'
        },
        tasks: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/TaskSchema'
                    },
                    {
                        $ref: '#/components/schemas/ChainSchema'
                    },
                    {
                        $ref: '#/components/schemas/GroupSchema'
                    },
                    {
                        $ref: '#/components/schemas/ChordSchema'
                    }
                ]
            },
            type: 'array',
            title: 'Tasks'
        }
    },
    type: 'object',
    required: [
        'type',
        'tasks'
    ],
    title: 'GroupSchema',
    description: 'Group of seerlinq celery tasks.'
} as const;

export const HTTPErrorSchema = {
    properties: {
        detail: {
            type: 'string',
            title: 'Detail'
        }
    },
    type: 'object',
    required: [
        'detail'
    ],
    title: 'HTTPError',
    example: {
        detail: '<Detail of the exception.>'
    }
} as const;

export const HTTPValidationErrorSchema = {
    properties: {
        detail: {
            items: {
                $ref: '#/components/schemas/ValidationError'
            },
            type: 'array',
            title: 'Detail'
        }
    },
    type: 'object',
    title: 'HTTPValidationError'
} as const;

export const HandledExceptionAlgorithmResultSchema = {
    properties: {
        component: {
            type: 'string',
            enum: [
                'ppg_data',
                'ecg_data',
                'patient_data',
                'pipeline',
                'result',
                'seerlinq_api'
            ],
            title: 'Component'
        },
        title: {
            type: 'string',
            title: 'Title'
        },
        details: {
            type: 'string',
            title: 'Details'
        },
        step_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Step Name'
        },
        traceback: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Traceback'
        }
    },
    type: 'object',
    required: [
        'component',
        'title',
        'details',
        'step_name',
        'traceback'
    ],
    title: 'HandledExceptionAlgorithmResult'
} as const;

export const HealthProPhysiciansSchema = {
    type: 'string',
    enum: [
        'diana_sevcikova',
        'allan_bohm'
    ],
    title: 'HealthProPhysicians'
} as const;

export const HealthProReportSchema = {
    properties: {
        report_main_diagnosis: {
            $ref: '#/components/schemas/HealthProReportDiagnosis'
        },
        report_service: {
            $ref: '#/components/schemas/HealthProServices'
        },
        report_physician: {
            $ref: '#/components/schemas/HealthProPhysicians'
        },
        report_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Report Datetime'
        },
        report_diag_conclusion: {
            type: 'string',
            title: 'Report Diag Conclusion'
        },
        report_text: {
            type: 'string',
            title: 'Report Text'
        }
    },
    type: 'object',
    required: [
        'report_main_diagnosis',
        'report_service',
        'report_physician',
        'report_datetime',
        'report_diag_conclusion',
        'report_text'
    ],
    title: 'HealthProReport'
} as const;

export const HealthProReportDiagnosisSchema = {
    type: 'string',
    enum: [
        'I50.9',
        'I50.11',
        'I50.12',
        'I50.13',
        'I50.14',
        'Z03.5'
    ],
    title: 'HealthProReportDiagnosis'
} as const;

export const HealthProReportTypeSchema = {
    type: 'string',
    enum: [
        'basic',
        'dovera',
        'vszp_basic',
        'vszp_monthly'
    ],
    title: 'HealthProReportType'
} as const;

export const HealthProServicesSchema = {
    type: 'string',
    enum: [
        '11a',
        '1A06009',
        '1A06021'
    ],
    title: 'HealthProServices'
} as const;

export const HeartCoreMonitoringSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        last_ppg: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Ppg'
        },
        last_ecg: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Ecg'
        },
        last_5_dris: {
            items: {
                prefixItems: [
                    {
                        type: 'string',
                        format: 'date-time'
                    },
                    {
                        type: 'number'
                    }
                ],
                type: 'array',
                maxItems: 2,
                minItems: 2
            },
            type: 'array',
            maxItems: 5,
            minItems: 0,
            title: 'Last 5 Dris'
        },
        last_5_lvfps: {
            items: {
                prefixItems: [
                    {
                        type: 'string',
                        format: 'date-time'
                    },
                    {
                        type: 'number'
                    }
                ],
                type: 'array',
                maxItems: 2,
                minItems: 2
            },
            type: 'array',
            maxItems: 5,
            minItems: 0,
            title: 'Last 5 Lvfps'
        },
        num_full_measurements: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/NumFullMeasurements'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Num Full Measurements'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'last_ppg',
        'last_ecg',
        'num_full_measurements'
    ],
    title: 'HeartCoreMonitoring'
} as const;

export const HeartCoreRangesMonthsSchema = {
    type: 'integer',
    enum: [
        3,
        6,
        9,
        12
    ]
} as const;

export const HeartRateSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'heart rate',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 250,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'bpm',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HeartRateSchemaResponse'
} as const;

export const HematocritSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'hematocrit',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 1,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HematocritSchemaResponse'
} as const;

export const HemoglobinSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'hemoglobin',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 500,
            minimum: 10,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'g/l',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HemoglobinSchemaResponse'
} as const;

export const HfEtiologySchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'HF etiology',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Ischemic',
                        'Non-ischemic',
                        'Unknown'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HfEtiologySchemaResponse'
} as const;

export const HfTypeSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'HF type',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'HFpEF',
                        'HFmrEF',
                        'HFrEF'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HfTypeSchemaResponse'
} as const;

export const HistoryOfAlcoholAbuseSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of alcohol abuse',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HistoryOfAlcoholAbuseSchemaResponse'
} as const;

export const HistoryOfCardiotoxicDrugsSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of cardiotoxic drugs',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HistoryOfCardiotoxicDrugsSchemaResponse'
} as const;

export const HistoryOfHtxSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of HTx',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HistoryOfHtxSchemaResponse'
} as const;

export const HistoryOfSmokingSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'History of smoking',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'HistoryOfSmokingSchemaResponse'
} as const;

export const HospitalizationEventReasonSchema = {
    type: 'string',
    enum: [
        'hfh',
        'cv',
        'non_cv'
    ],
    title: 'HospitalizationEventReason'
} as const;

export const HospitalizationEventSchemaSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/UnplannedHospitalizationEventSchema'
        },
        {
            $ref: '#/components/schemas/ElectiveHospitalizationEventSchema'
        }
    ],
    discriminator: {
        propertyName: 'event_category_type',
        mapping: {
            elective: '#/components/schemas/ElectiveHospitalizationEventSchema',
            unplanned: '#/components/schemas/UnplannedHospitalizationEventSchema'
        }
    }
} as const;

export const HospitalizationEventTypeSchema = {
    type: 'string',
    enum: [
        'unplanned',
        'elective'
    ],
    title: 'HospitalizationEventType'
} as const;

export const ImplantedDevicesSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Implanted devices',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'PM',
                        'ICD',
                        'CRT-D',
                        'CRT-P',
                        'PA-sensor',
                        'VAD'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 6,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ImplantedDevicesSchemaResponse'
} as const;

export const InformedConsentSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'InformedConsent',
    'x-enum-varnames': [
        'none',
        'telemonitoring',
        'paper'
    ],
    description: ' - 0: none\n - 1: telemonitoring\n - 2: paper'
} as const;

export const IngestApprovalSchemaSchema = {
    properties: {
        review_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Review Comment'
        }
    },
    type: 'object',
    title: 'IngestApprovalSchema',
    description: 'The reviewer\'s sign-off on a stored draft.\n\nCarries no entities: ingest reads the saved draft, so what is written is\nexactly what the reviewer last saw, and a stale browser tab cannot post an\nolder copy over it. Entities flagged in ``excluded`` are left out.'
} as const;

export const IntDataSchemaSchema = {
    properties: {
        value_type: {
            type: 'string',
            const: 'integer',
            title: 'Value Type',
            default: 'integer'
        },
        value_ge: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Ge'
        },
        value_gt: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Gt'
        },
        value_le: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Le'
        },
        value_lt: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Lt'
        },
        value_multiple_of: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value Multiple Of'
        }
    },
    type: 'object',
    title: 'IntDataSchema'
} as const;

export const IronDeficiency_AnemiaSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Iron deficiency/anemia',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'IronDeficiency/AnemiaSchemaResponse'
} as const;

export const IsTokenFreshResponseSchema = {
    properties: {
        access_token_fresh: {
            type: 'boolean',
            title: 'Access Token Fresh'
        }
    },
    type: 'object',
    required: [
        'access_token_fresh'
    ],
    title: 'IsTokenFreshResponse'
} as const;

export const IssueApiKeySchemaSchema = {
    properties: {
        name: {
            type: 'string',
            maxLength: 200,
            minLength: 1,
            title: 'Name'
        },
        expires_in_days: {
            type: 'integer',
            maximum: 365,
            minimum: 1,
            title: 'Expires In Days',
            default: 365
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'name'
    ],
    title: 'IssueApiKeySchema'
} as const;

export const IssuedApiKeyResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        key_prefix: {
            type: 'string',
            title: 'Key Prefix'
        },
        expires_at: {
            type: 'string',
            format: 'date-time',
            title: 'Expires At'
        },
        last_used_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Used At'
        },
        revoked_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Revoked At'
        },
        api_key: {
            type: 'string',
            title: 'Api Key'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'name',
        'key_prefix',
        'expires_at',
        'last_used_at',
        'revoked_at',
        'api_key'
    ],
    title: 'IssuedApiKeyResponse',
    description: 'Returned exactly once, at key creation — api_key is the plaintext\nvalue, never retrievable again afterwards.'
} as const;

export const ItemHistorySchema = {
    properties: {
        timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Timestamp'
        },
        action: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Action'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        username: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Username'
        }
    },
    type: 'object',
    required: [
        'timestamp',
        'action',
        'tags',
        'username'
    ],
    title: 'ItemHistory'
} as const;

export const JugularVenousDistentionSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'jugular venous distention',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'JugularVenousDistentionSchemaResponse'
} as const;

export const LVFPIndicationOutSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime',
            description: 'When the measurement this readout belongs to was taken.'
        },
        status: {
            type: 'string',
            enum: [
                'unpaired',
                'pending',
                'ready',
                'bad_quality',
                'too_short',
                'failed'
            ],
            title: 'Status',
            description: '`unpaired`: no pair is recorded yet - the normal state of a first recording; record its condition-matched partner. `pending`: the pair is recorded and the computation is running. `ready`: a value exists and `elevated` answers it; the flow is charged its one credit at this point (terminal). `bad_quality`: the computed value is unusable (terminal; re-record the pair). `too_short`: the recording is below the minimum signal length and was never analysed (terminal; record a longer one). `failed`: no value can arrive - no partner was found, the pairing window closed, the recording is excluded from analysis, or the computation did not deliver in time (terminal; re-record the pair). Nothing is charged for a flow that never reached `ready`.'
        },
        paired_with: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Paired With',
            description: 'The condition-matched measurement this one was paired with, as recorded when the computation was submitted. Non-null proves the pair was formed, independently of whether a value has arrived; null means no submission is recorded for this attempt.'
        },
        elevated: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Elevated',
            description: 'True when the computed value is at or above the threshold; null unless `status` is `ready`.'
        },
        threshold: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Threshold',
            description: 'The screening threshold it was compared against, in mmHg; not the LVFP alert threshold.'
        },
        threshold_variable: {
            type: 'string',
            title: 'Threshold Variable'
        },
        computed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed At',
            description: 'When the value was computed; null unless `status` is `ready`.'
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'patient_id',
        'measurement_datetime',
        'status',
        'elevated',
        'threshold',
        'threshold_variable'
    ],
    title: 'LVFPIndicationOut',
    description: 'Reduced LVFP readout for one recording: whether the value computed for\nits pair reaches the screening threshold. The value itself is never\npart of this response.'
} as const;

export const LabelStudioTaskResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        task: {
            additionalProperties: true,
            type: 'object',
            title: 'Task'
        },
        url: {
            type: 'string',
            maxLength: 2083,
            minLength: 1,
            format: 'uri',
            title: 'Url'
        }
    },
    type: 'object',
    required: [
        'status',
        'task',
        'url'
    ],
    title: 'LabelStudioTaskResponse'
} as const;

export const LabsEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'labs',
            title: 'Event Category',
            default: 'labs'
        },
        event_category_type: {
            $ref: '#/components/schemas/LabsEventType'
        },
        event_category_details: {
            type: 'null',
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_type',
        'event_description',
        'patient_id'
    ],
    title: 'LabsEventSchema'
} as const;

export const LabsEventTypeSchema = {
    type: 'string',
    enum: [
        'planned',
        'seerlinq',
        'unplanned'
    ],
    title: 'LabsEventType'
} as const;

export const LaviSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LAVi',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LaviSchemaResponse'
} as const;

export const LedgerEntryOutSchema = {
    properties: {
        id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Id'
        },
        delta: {
            type: 'integer',
            title: 'Delta'
        },
        reason: {
            $ref: '#/components/schemas/LedgerReason'
        },
        balance_after: {
            type: 'integer',
            minimum: 0,
            title: 'Balance After'
        },
        actor_user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Actor User Uuid'
        },
        note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Note'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        }
    },
    type: 'object',
    required: [
        'id',
        'delta',
        'reason',
        'balance_after',
        'actor_user_uuid',
        'note',
        'created_at'
    ],
    title: 'LedgerEntryOut'
} as const;

export const LedgerReasonSchema = {
    type: 'string',
    enum: [
        'spend',
        'admin_adjust'
    ],
    title: 'LedgerReason'
} as const;

export const LegEdemaSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'leg edema',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'integer',
            maximum: 4,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LegEdemaSchemaResponse'
} as const;

export const Level3ClinicalPatientResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        diagnoses: {
            type: 'integer',
            title: 'Diagnoses'
        },
        medical_data: {
            type: 'integer',
            title: 'Medical Data'
        },
        medications: {
            type: 'integer',
            title: 'Medications'
        },
        symptoms: {
            type: 'integer',
            title: 'Symptoms'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Output'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        insurance_policy_details: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Insurance Policy Details'
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        alerts: {
            type: 'integer',
            title: 'Alerts'
        },
        thresholds: {
            type: 'integer',
            title: 'Thresholds'
        },
        events: {
            type: 'integer',
            title: 'Events'
        },
        gdmt_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 9,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Gdmt Score'
        },
        ai_summary: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientAISummary'
                },
                {
                    type: 'null'
                }
            ]
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'diagnoses',
        'medical_data',
        'medications',
        'symptoms',
        'alerts',
        'thresholds',
        'events',
        'full_name'
    ],
    title: 'Level3ClinicalPatientResponse'
} as const;

export const Level3MinimalPatientResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        gdmt_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 9,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Gdmt Score'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'full_name'
    ],
    title: 'Level3MinimalPatientResponse'
} as const;

export const Level3PatientResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Output'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        insurance_policy_details: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Insurance Policy Details'
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        gdmt_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 9,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Gdmt Score'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'full_name'
    ],
    title: 'Level3PatientResponse'
} as const;

export const LeveledAdverseEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'adverse_event',
            title: 'Event Category',
            default: 'adverse_event'
        },
        event_category_type: {
            type: 'string',
            enum: [
                'hypokalemia',
                'hyperkalemia',
                'hyponatremia'
            ],
            title: 'Event Category Type'
        },
        event_category_details: {
            $ref: '#/components/schemas/AdverseEventLevel'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_type',
        'event_category_details',
        'event_description',
        'patient_id'
    ],
    title: 'LeveledAdverseEventSchema'
} as const;

export const LhcAorticDiastolicPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic diastolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 150,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcAorticDiastolicPressureSchemaResponse'
} as const;

export const LhcAorticMeanPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic mean pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 200,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcAorticMeanPressureSchemaResponse'
} as const;

export const LhcAorticOxygenSaturationSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic oxygen saturation',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcAorticOxygenSaturationSchemaResponse'
} as const;

export const LhcAorticSystolicPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC aortic systolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcAorticSystolicPressureSchemaResponse'
} as const;

export const LhcCardiacIndexSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC cardiac index',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcCardiacIndexSchemaResponse'
} as const;

export const LhcCardiacOutputSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC cardiac output',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 15,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcCardiacOutputSchemaResponse'
} as const;

export const LhcDp_DtSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC dP/dt',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 6000,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg/s',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcDp/DtSchemaResponse'
} as const;

export const LhcEjectionFractionSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC ejection fraction',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcEjectionFractionSchemaResponse'
} as const;

export const LhcLeftAtrialMeanPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC left atrial mean pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcLeftAtrialMeanPressureSchemaResponse'
} as const;

export const LhcLeftVentricularEndDiastolicPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC left ventricular end-diastolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcLeftVentricularEndDiastolicPressureSchemaResponse'
} as const;

export const LhcLeftVentricularSystolicPressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC left ventricular systolic pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcLeftVentricularSystolicPressureSchemaResponse'
} as const;

export const LhcMeanAorticValveGradientSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC mean aortic valve gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 150,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcMeanAorticValveGradientSchemaResponse'
} as const;

export const LhcMeanMitralValveGradientSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LHC mean mitral valve gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LhcMeanMitralValveGradientSchemaResponse'
} as const;

export const LineStatsSchema = {
    properties: {
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        },
        windows: {
            items: {
                $ref: '#/components/schemas/WindowStat'
            },
            type: 'array',
            title: 'Windows'
        }
    },
    type: 'object',
    required: [
        'windows'
    ],
    title: 'LineStats',
    description: 'Window statistics of one line, matched to traces by facet identity.'
} as const;

export const Lipid_lowering__OtherSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Lipid lowering: Other',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Lipid lowering: Other'
} as const;

export const Lipid_lowering__PCSK9iSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Lipid lowering: PCSK9i',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Lipid lowering: PCSK9i'
} as const;

export const Lipid_lowering__StatinSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Lipid lowering: Statin',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Lipid lowering: Statin'
} as const;

export const ListAIFeedbackResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        feedback: {
            items: {
                $ref: '#/components/schemas/AIFeedbackResponse'
            },
            type: 'array',
            title: 'Feedback'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'feedback'
    ],
    title: 'ListAIFeedbackResponse',
    description: 'Schema for listing AI feedback.'
} as const;

export const ListAccessLimiterResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        limiters: {
            items: {
                $ref: '#/components/schemas/AccessLimiterResponse'
            },
            type: 'array',
            title: 'Limiters'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'limiters'
    ],
    title: 'ListAccessLimiterResponse'
} as const;

export const ListAuditItemsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        audit_items: {
            items: {
                $ref: '#/components/schemas/AuditItemResponse'
            },
            type: 'array',
            title: 'Audit Items'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'audit_items'
    ],
    title: 'ListAuditItemsResponse'
} as const;

export const ListAuxiliaryTimeSeriesResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        aux_data: {
            items: {
                $ref: '#/components/schemas/DefaultAuxResponse'
            },
            type: 'array',
            title: 'Aux Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'aux_data'
    ],
    title: 'ListAuxiliaryTimeSeriesResponse',
    description: 'Response schema for listing auxiliary time series data.'
} as const;

export const ListBasicComputedDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        computed_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponse'
            },
            type: 'array',
            title: 'Computed Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'computed_data'
    ],
    title: 'ListBasicComputedDataResponse'
} as const;

export const ListCeleryTasksSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        tasks: {
            additionalProperties: {
                $ref: '#/components/schemas/CeleryTask'
            },
            type: 'object',
            title: 'Tasks'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'tasks'
    ],
    title: 'ListCeleryTasks'
} as const;

export const ListClinicalStudyAdminProcedureResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        admin_procedures: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyAdminProcedureResponse'
            },
            type: 'array',
            title: 'Admin Procedures'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'admin_procedures'
    ],
    title: 'ListClinicalStudyAdminProcedureResponse'
} as const;

export const ListClinicalStudyEnrollmentsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        study_enrollments: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyEnrollmentResponse'
            },
            type: 'array',
            title: 'Study Enrollments'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'study_enrollments'
    ],
    title: 'ListClinicalStudyEnrollmentsResponse'
} as const;

export const ListClinicalStudyVisitDiagnosisAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitDiagnosisAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitDiagnosisAssociationResponse'
} as const;

export const ListClinicalStudyVisitEventAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitEventAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitEventAssociationResponse'
} as const;

export const ListClinicalStudyVisitMedicalDataAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitMedicalDataAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitMedicalDataAssociationResponse'
} as const;

export const ListClinicalStudyVisitMedicalReportAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitMedicalReportAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitMedicalReportAssociationResponse'
} as const;

export const ListClinicalStudyVisitMedicationAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitMedicationAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitMedicationAssociationResponse'
} as const;

export const ListClinicalStudyVisitPPGAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitPPGAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitPPGAssociationResponse'
} as const;

export const ListClinicalStudyVisitPPGDerivedAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitPPGDerivedAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitPPGDerivedAssociationResponse'
} as const;

export const ListClinicalStudyVisitQuestionnaireAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitQuestionnaireAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitQuestionnaireAssociationResponse'
} as const;

export const ListClinicalStudyVisitResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitResponse'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'study_visits'
    ],
    title: 'ListClinicalStudyVisitResponse'
} as const;

export const ListClinicalStudyVisitSeerlinqComputedAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitSeerlinqComputedAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitSeerlinqComputedAssociationResponse'
} as const;

export const ListClinicalStudyVisitSymptomAssociationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitSymptomAssociationResponse'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'associations'
    ],
    title: 'ListClinicalStudyVisitSymptomAssociationResponse'
} as const;

export const ListClinicsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicWithAssignedUsersResponse'
            },
            type: 'array',
            title: 'Clinics'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'clinics'
    ],
    title: 'ListClinicsResponse'
} as const;

export const ListComputedWithPPGDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        computed_data: {
            items: {
                $ref: '#/components/schemas/SeerlinqComputedWithPPGResponse'
            },
            type: 'array',
            title: 'Computed Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'computed_data'
    ],
    title: 'ListComputedWithPPGDataResponse'
} as const;

export const ListCountsSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        counts: {
            items: {
                $ref: '#/components/schemas/BaseCountsItem'
            },
            type: 'array',
            title: 'Counts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'counts'
    ],
    title: 'ListCounts'
} as const;

export const ListCountsWithPatientsSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        counts: {
            items: {
                $ref: '#/components/schemas/CountsWithPatients'
            },
            type: 'array',
            title: 'Counts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'counts'
    ],
    title: 'ListCountsWithPatients'
} as const;

export const ListDefaultThresholdsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        thresholds: {
            items: {
                $ref: '#/components/schemas/DefaultAlertThreshold'
            },
            type: 'array',
            title: 'Thresholds'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'thresholds'
    ],
    title: 'ListDefaultThresholdsResponse'
} as const;

export const ListDiagnosesResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        diagnoses: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'diagnoses'
    ],
    title: 'ListDiagnosesResponse',
    description: 'Schema for listing diagnoses.'
} as const;

export const ListECGDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        ecg_data: {
            items: {
                $ref: '#/components/schemas/DefaultECGDataSchemaResponse'
            },
            type: 'array',
            title: 'Ecg Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'ecg_data'
    ],
    title: 'ListECGDataResponse',
    description: 'Schema for listing ECG data entries.'
} as const;

export const ListECGDerivedDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/DefaultECGDerivedDataSchemaResponse'
            },
            type: 'array',
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListECGDerivedDataResponse',
    description: 'Schema for listing ECG Derived data entries.'
} as const;

export const ListECGDerivedDataWithECGDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/ECGDerivedDataSchemaWithECGResponse'
            },
            type: 'array',
            title: 'Derived Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListECGDerivedDataWithECGDataResponse'
} as const;

export const ListEventCategoriesSchema = {
    properties: {
        hospitalization: {
            additionalProperties: {
                items: {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                type: 'array'
            },
            propertyNames: {
                $ref: '#/components/schemas/HospitalizationEventType'
            },
            type: 'object',
            title: 'Hospitalization'
        },
        death: {
            additionalProperties: {
                type: 'null'
            },
            propertyNames: {
                $ref: '#/components/schemas/DeathEventReason'
            },
            type: 'object',
            title: 'Death'
        },
        visit: {
            additionalProperties: {
                items: {
                    anyOf: [
                        {
                            $ref: '#/components/schemas/VisitEventUnplannedCategory'
                        },
                        {
                            $ref: '#/components/schemas/VisitEventPlannedCategory'
                        }
                    ]
                },
                type: 'array'
            },
            propertyNames: {
                $ref: '#/components/schemas/VisitEventType'
            },
            type: 'object',
            title: 'Visit'
        },
        medical_call: {
            type: 'null',
            title: 'Medical Call'
        },
        technical_call: {
            type: 'null',
            title: 'Technical Call'
        },
        medication_change: {
            type: 'null',
            title: 'Medication Change'
        },
        labs: {
            additionalProperties: {
                type: 'null'
            },
            propertyNames: {
                $ref: '#/components/schemas/LabsEventType'
            },
            type: 'object',
            title: 'Labs'
        },
        physician_notified: {
            additionalProperties: {
                type: 'null'
            },
            propertyNames: {
                $ref: '#/components/schemas/PhysicianNotifiedEventType'
            },
            type: 'object',
            title: 'Physician Notified'
        },
        patient_non_adherence: {
            type: 'null',
            title: 'Patient Non Adherence'
        },
        adverse_event: {
            additionalProperties: {
                anyOf: [
                    {
                        items: {
                            $ref: '#/components/schemas/AdverseEventLevel'
                        },
                        type: 'array'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            propertyNames: {
                $ref: '#/components/schemas/AdverseEventType'
            },
            type: 'object',
            title: 'Adverse Event'
        }
    },
    type: 'object',
    required: [
        'hospitalization',
        'death',
        'visit',
        'medical_call',
        'technical_call',
        'medication_change',
        'labs',
        'physician_notified',
        'patient_non_adherence',
        'adverse_event'
    ],
    title: 'ListEventCategories'
} as const;

export const ListFilledQuestionnaireResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        questionnaires: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponse'
            },
            type: 'array',
            title: 'Questionnaires'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'questionnaires'
    ],
    title: 'ListFilledQuestionnaireResponse'
} as const;

export const ListLedgerResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        ledger: {
            items: {
                $ref: '#/components/schemas/LedgerEntryOut'
            },
            type: 'array',
            title: 'Ledger'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'ledger'
    ],
    title: 'ListLedgerResponse'
} as const;

export const ListMQuestionnaireAutomatonSettingsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        questionnaire_automaton_settings: {
            items: {
                $ref: '#/components/schemas/QuestionnaireAutomatonSettingsResponse'
            },
            type: 'array',
            title: 'Questionnaire Automaton Settings'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'questionnaire_automaton_settings'
    ],
    title: 'ListMQuestionnaireAutomatonSettingsResponse'
} as const;

export const ListMaybeFullPPGDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        ppg_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/DefaultFullPPGDataSchemaResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
                    },
                    type: 'array'
                }
            ],
            title: 'Ppg Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'ppg_data'
    ],
    title: 'ListMaybeFullPPGDataResponse',
    description: 'Schema for listing (maybe) full PPG data entries.'
} as const;

export const ListMedicalDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        medical_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'medical_data'
    ],
    title: 'ListMedicalDataResponse',
    description: 'Schema for listing medical data entries.'
} as const;

export const ListMedicalReportResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        medical_reports: {
            items: {
                $ref: '#/components/schemas/MedicalReportResponse'
            },
            type: 'array',
            title: 'Medical Reports'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'medical_reports'
    ],
    title: 'ListMedicalReportResponse'
} as const;

export const ListMedicationGroupsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        medications: {
            additionalProperties: {
                $ref: '#/components/schemas/SupportedMedication'
            },
            type: 'object',
            title: 'Medications'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'medications'
    ],
    title: 'ListMedicationGroupsResponse'
} as const;

export const ListMedicationResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        medications: {
            items: {
                $ref: '#/components/schemas/DefaultMedicationSchemaResponse'
            },
            type: 'array',
            title: 'Medications'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'medications'
    ],
    title: 'ListMedicationResponse',
    description: 'Schema for listing medication entries.'
} as const;

export const ListMessagesResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        messages: {
            items: {
                $ref: '#/components/schemas/PatientMessageResponse'
            },
            type: 'array',
            title: 'Messages'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'messages'
    ],
    title: 'ListMessagesResponse'
} as const;

export const ListMonitoringSettingsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        monitoring_settings: {
            items: {
                $ref: '#/components/schemas/MonitoringSettingsResponse'
            },
            type: 'array',
            title: 'Monitoring Settings'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'monitoring_settings'
    ],
    title: 'ListMonitoringSettingsResponse'
} as const;

export const ListMonitoringUsersResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        users: {
            items: {
                $ref: '#/components/schemas/MonitoringUserResponse'
            },
            type: 'array',
            title: 'Users'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'users'
    ],
    title: 'ListMonitoringUsersResponse'
} as const;

export const ListPPGDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        ppg_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Ppg Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'ppg_data'
    ],
    title: 'ListPPGDataResponse',
    description: 'Schema for listing PPG data entries.'
} as const;

export const ListPPGDerivedDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponse'
            },
            type: 'array',
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListPPGDerivedDataResponse',
    description: 'Schema for listing PPG Derived data entries.'
} as const;

export const ListPPGDerivedDataWithPPGDataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/PPGDerivedDataSchemaWithPPGResponse'
            },
            type: 'array',
            title: 'Derived Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListPPGDerivedDataWithPPGDataResponse'
} as const;

export const ListPatientAlertsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Alerts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'alerts'
    ],
    title: 'ListPatientAlertsResponse'
} as const;

export const ListPatientEventsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        events: {
            items: {
                $ref: '#/components/schemas/DefaultPatientEventResponse'
            },
            type: 'array',
            title: 'Events'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'events'
    ],
    title: 'ListPatientEventsResponse'
} as const;

export const ListPatientNotificationsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        total: {
            type: 'integer',
            minimum: 0,
            title: 'Total',
            description: 'Rows matching the filters in total, of which this page carries `results`'
        },
        notifications: {
            items: {
                $ref: '#/components/schemas/PatientNotificationItem'
            },
            type: 'array',
            title: 'Notifications'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'total',
        'notifications'
    ],
    title: 'ListPatientNotificationsResponse',
    description: 'Ledger rows matching the filters.'
} as const;

export const ListPatientScheduleResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        schedules: {
            items: {
                $ref: '#/components/schemas/PatientScheduleResponse'
            },
            type: 'array',
            title: 'Schedules'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'schedules'
    ],
    title: 'ListPatientScheduleResponse'
} as const;

export const ListPatientThresholdsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        thresholds: {
            items: {
                $ref: '#/components/schemas/DefaultPatientThresholdResponse'
            },
            type: 'array',
            title: 'Thresholds'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'thresholds'
    ],
    title: 'ListPatientThresholdsResponse'
} as const;

export const ListPatientsMinimalResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Level3MinimalPatientResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/MinimalPatientResponse'
                    },
                    type: 'array'
                }
            ],
            title: 'Patients'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patients'
    ],
    title: 'ListPatientsMinimalResponse'
} as const;

export const ListPatientsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Level3ClinicalPatientResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/DefaultClinicalPatientResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/Level3PatientResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/DefaultPatientResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/Level3MinimalPatientResponse'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/MinimalPatientResponse'
                    },
                    type: 'array'
                }
            ],
            title: 'Patients'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patients'
    ],
    title: 'ListPatientsResponse'
} as const;

export const ListPendingConsentsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patients: {
            items: {
                $ref: '#/components/schemas/PendingConsentPatient'
            },
            type: 'array',
            title: 'Patients'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patients'
    ],
    title: 'ListPendingConsentsResponse'
} as const;

export const ListPhysiciansResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        physicians: {
            items: {
                $ref: '#/components/schemas/PhysicianWithPatientsResponse'
            },
            type: 'array',
            title: 'Physicians'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'physicians'
    ],
    title: 'ListPhysiciansResponse'
} as const;

export const ListScreeningUsersResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        screening_users: {
            items: {
                $ref: '#/components/schemas/ScreeningUserOut'
            },
            type: 'array',
            title: 'Screening Users'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'screening_users'
    ],
    title: 'ListScreeningUsersResponse'
} as const;

export const ListSeerlinqStudyTextResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        study_texts: {
            items: {
                $ref: '#/components/schemas/SeerlinqStudyTextResponse'
            },
            type: 'array',
            title: 'Study Texts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'study_texts'
    ],
    title: 'ListSeerlinqStudyTextResponse'
} as const;

export const ListServiceAccountsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        service_accounts: {
            items: {
                $ref: '#/components/schemas/ServiceAccountResponse'
            },
            type: 'array',
            title: 'Service Accounts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'service_accounts'
    ],
    title: 'ListServiceAccountsResponse'
} as const;

export const ListSupportedDiagnosesSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        diagnoses: {
            items: {
                $ref: '#/components/schemas/SupportedDiagnosis'
            },
            type: 'array',
            title: 'Diagnoses'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'diagnoses'
    ],
    title: 'ListSupportedDiagnoses'
} as const;

export const ListSupportedMedicalDataSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        medical_data: {
            items: {
                $ref: '#/components/schemas/SupportedMedicalData'
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'medical_data'
    ],
    title: 'ListSupportedMedicalData'
} as const;

export const ListSupportedSCPCodesSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        ecg_annotations: {
            items: {
                $ref: '#/components/schemas/DefaultECGStatement'
            },
            type: 'array',
            title: 'Ecg Annotations'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'ecg_annotations'
    ],
    title: 'ListSupportedSCPCodes'
} as const;

export const ListSupportedSymptomsSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        symptoms: {
            items: {
                $ref: '#/components/schemas/SupportedSymptom'
            },
            type: 'array',
            title: 'Symptoms'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'symptoms'
    ],
    title: 'ListSupportedSymptoms'
} as const;

export const ListSymptomResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        symptoms: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Symptoms'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'symptoms'
    ],
    title: 'ListSymptomResponse',
    description: 'Schema for listing symptom data entries.'
} as const;

export const ListTaskIDsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        task_ids: {
            items: {
                type: 'string',
                maxLength: 32,
                minLength: 32
            },
            type: 'array',
            title: 'Task Ids'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'task_ids'
    ],
    title: 'ListTaskIDsResponse'
} as const;

export const ListUserAccessPoliciesResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        policies: {
            items: {
                $ref: '#/components/schemas/UserAccessPolicyResponse'
            },
            type: 'array',
            title: 'Policies'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'policies'
    ],
    title: 'ListUserAccessPoliciesResponse',
    description: 'Schema for listing user access policies.'
} as const;

export const ListUsersResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        users: {
            items: {
                $ref: '#/components/schemas/FullUserResponse'
            },
            type: 'array',
            title: 'Users'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'users'
    ],
    title: 'ListUsersResponse'
} as const;

export const ListedConsentItemSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType'
        },
        state: {
            $ref: '#/components/schemas/ConsentState'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        }
    },
    type: 'object',
    required: [
        'consent_type',
        'state',
        'required'
    ],
    title: 'ListedConsentItem'
} as const;

export const LocaleTagSchema = {
    type: 'string',
    enum: [
        'ar',
        'ar-SA',
        'az',
        'cs',
        'da',
        'de',
        'de-DE',
        'de-AT',
        'el',
        'en',
        'en-GB',
        'es',
        'es-ES',
        'fr',
        'fr-FR',
        'he',
        'hi',
        'hu',
        'it',
        'ja',
        'lt',
        'lv',
        'nb',
        'pl',
        'pt',
        'pt-PT',
        'ro',
        'ru',
        'sk',
        'sv',
        'tr',
        'uk',
        'zh-Hans',
        'zh-Hans-CN',
        'zh-Hant',
        'zh-Hant-TW'
    ],
    title: 'LocaleTag',
    description: 'Supported language, optionally with one of its target regions (see `x-seerlinq-languages`).'
} as const;

export const LoginMFAEnrollmentRequiredSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        status: {
            type: 'string',
            const: 'mfa_enrollment_required',
            title: 'Status'
        }
    },
    type: 'object',
    required: [
        'challenge_id',
        'status'
    ],
    title: 'LoginMFAEnrollmentRequired'
} as const;

export const LoginMFARequiredSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        status: {
            type: 'string',
            const: 'mfa_required',
            title: 'Status'
        },
        methods: {
            items: {
                $ref: '#/components/schemas/MFAMethod'
            },
            type: 'array',
            title: 'Methods'
        }
    },
    type: 'object',
    required: [
        'challenge_id',
        'status',
        'methods'
    ],
    title: 'LoginMFARequired'
} as const;

export const LoginResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        access_token: {
            type: 'string',
            title: 'Access Token'
        },
        refresh_token: {
            type: 'string',
            title: 'Refresh Token'
        },
        mfa_enrollment_recommended: {
            type: 'boolean',
            title: 'Mfa Enrollment Recommended',
            default: false
        }
    },
    type: 'object',
    required: [
        'status',
        'access_token',
        'refresh_token'
    ],
    title: 'LoginResponse'
} as const;

export const LoginUserSchemaSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        }
    },
    type: 'object',
    required: [
        'username'
    ],
    title: 'LoginUserSchema'
} as const;

export const LogoutResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        }
    },
    type: 'object',
    required: [
        'status'
    ],
    title: 'LogoutResponse'
} as const;

export const Loop_diureticsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Loop diuretics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Loop diuretics'
} as const;

export const LvefSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'LVEF',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 75,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'LvefSchemaResponse'
} as const;

export const MFACompleteLoginResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        access_token: {
            type: 'string',
            title: 'Access Token'
        },
        refresh_token: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Refresh Token'
        }
    },
    type: 'object',
    required: [
        'status',
        'access_token'
    ],
    title: 'MFACompleteLoginResponse',
    description: 'Returned when completing a login-time challenge (verify or dual-mode\nenrollment-completes-login) - same shape as `LoginResponse`.'
} as const;

export const MFAMethodSchema = {
    type: 'string',
    enum: [
        'totp',
        'passkey',
        'backup_code',
        'sms'
    ],
    title: 'MFAMethod'
} as const;

export const MFAStatusResponseSchema = {
    properties: {
        required: {
            type: 'boolean',
            title: 'Required'
        },
        totp_enrolled: {
            type: 'boolean',
            title: 'Totp Enrolled'
        },
        passkeys: {
            items: {
                $ref: '#/components/schemas/WebAuthnCredentialResponse'
            },
            type: 'array',
            title: 'Passkeys'
        },
        backup_codes_remaining: {
            type: 'integer',
            title: 'Backup Codes Remaining'
        },
        sms_available: {
            type: 'boolean',
            title: 'Sms Available'
        },
        sms_enrolled: {
            type: 'boolean',
            title: 'Sms Enrolled'
        },
        sms_phone_masked: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Sms Phone Masked',
            description: 'The stored number, also after SMS is switched off; whether SMS is on is `sms_enrolled`.'
        }
    },
    type: 'object',
    required: [
        'required',
        'totp_enrolled',
        'passkeys',
        'backup_codes_remaining',
        'sms_available',
        'sms_enrolled',
        'sms_phone_masked'
    ],
    title: 'MFAStatusResponse'
} as const;

export const MFAVerifyBackupCodeSchemaSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        method: {
            type: 'string',
            const: 'backup_code',
            title: 'Method'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'method'
    ],
    title: 'MFAVerifyBackupCodeSchema'
} as const;

export const MFAVerifySmsSchemaSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        method: {
            type: 'string',
            const: 'sms',
            title: 'Method'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'method'
    ],
    title: 'MFAVerifySmsSchema'
} as const;

export const MFAVerifyTOTPSchemaSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        method: {
            type: 'string',
            const: 'totp',
            title: 'Method'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'method'
    ],
    title: 'MFAVerifyTOTPSchema'
} as const;

export const MRAsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'MRAs',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'MRAs'
} as const;

export const MalignancySchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Malignancy',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'MalignancySchemaResponse'
} as const;

export const MeasurementActionsSchema = {
    properties: {
        quicklook_ppg: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QuickLookAction'
                },
                {
                    type: 'null'
                }
            ]
        },
        open_ecg: {
            anyOf: [
                {
                    $ref: '#/components/schemas/OpenECGAction'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    title: 'MeasurementActions',
    description: 'Per-row actions.'
} as const;

export const MeasurementAliveCorECGRequiredData_InputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ecg',
            title: 'Data Type',
            default: 'ecg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 20,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 30
        },
        required_device: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^AliveCor '
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Device'
        },
        ecg_device_type: {
            type: 'string',
            const: 'alivecor',
            title: 'Ecg Device Type',
            default: 'alivecor'
        }
    },
    type: 'object',
    required: [
        'order'
    ],
    title: 'MeasurementAliveCorECGRequiredData'
} as const;

export const MeasurementAliveCorECGRequiredData_OutputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ecg',
            title: 'Data Type',
            default: 'ecg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 20,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 30
        },
        required_device: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^AliveCor '
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Device'
        },
        ecg_device_type: {
            type: 'string',
            const: 'alivecor',
            title: 'Ecg Device Type',
            default: 'alivecor'
        },
        post_endpoint: {
            type: 'string',
            title: 'Post Endpoint',
            description: 'Endpoint for posting the data.',
            readOnly: true
        },
        additional_post_endpoints: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Additional Post Endpoints',
            description: 'Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,\nor ECG-derived data. Note, that these always required newly submitted\nPPG or ECG UUID to be part of the post body.',
            readOnly: true
        },
        data_name: {
            type: 'string',
            title: 'Data Name',
            description: 'Simple string representation.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'post_endpoint',
        'additional_post_endpoints',
        'data_name'
    ],
    title: 'MeasurementAliveCorECGRequiredData'
} as const;

export const MeasurementBerryPPGRequiredData_InputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            type: 'string',
            const: 'BerryMed BM1000B',
            title: 'Required Device',
            default: 'BerryMed BM1000B'
        },
        ppg_device_type: {
            type: 'string',
            const: 'berry',
            title: 'Ppg Device Type',
            default: 'berry'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition'
    ],
    title: 'MeasurementBerryPPGRequiredData'
} as const;

export const MeasurementBerryPPGRequiredData_OutputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            type: 'string',
            const: 'BerryMed BM1000B',
            title: 'Required Device',
            default: 'BerryMed BM1000B'
        },
        ppg_device_type: {
            type: 'string',
            const: 'berry',
            title: 'Ppg Device Type',
            default: 'berry'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        },
        post_endpoint: {
            type: 'string',
            title: 'Post Endpoint',
            description: 'Endpoint for posting the data.',
            readOnly: true
        },
        additional_post_endpoints: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Additional Post Endpoints',
            description: 'Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,\nor ECG-derived data. Note, that these always required newly submitted\nPPG or ECG UUID to be part of the post body.',
            readOnly: true
        },
        data_name: {
            type: 'string',
            title: 'Data Name',
            description: 'Simple string representation.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition',
        'post_endpoint',
        'additional_post_endpoints',
        'data_name'
    ],
    title: 'MeasurementBerryPPGRequiredData'
} as const;

export const MeasurementCellSchema = {
    anyOf: [
        {
            $ref: '#/components/schemas/PairCell'
        },
        {
            $ref: '#/components/schemas/CodeListCell'
        },
        {
            $ref: '#/components/schemas/ValueCell'
        }
    ]
} as const;

export const MeasurementColumnSchema = {
    properties: {
        key: {
            type: 'string',
            title: 'Key'
        },
        configured: {
            type: 'boolean',
            title: 'Configured'
        },
        has_data: {
            type: 'boolean',
            title: 'Has Data'
        },
        threshold_flagging: {
            type: 'boolean',
            title: 'Threshold Flagging'
        },
        selector: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Selector'
        },
        condition_selector: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Condition Selector'
        }
    },
    type: 'object',
    required: [
        'key',
        'configured',
        'has_data',
        'threshold_flagging'
    ],
    title: 'MeasurementColumn',
    description: 'Metadata of one rendered column; labels, units, categories, ordering and\nformatting are frontend knowledge.'
} as const;

export const MeasurementDaySchema = {
    properties: {
        date: {
            type: 'string',
            format: 'date',
            title: 'Date'
        },
        primary_session_index: {
            type: 'integer',
            minimum: 0,
            title: 'Primary Session Index'
        },
        sessions: {
            items: {
                $ref: '#/components/schemas/MeasurementSession'
            },
            type: 'array',
            title: 'Sessions'
        }
    },
    type: 'object',
    required: [
        'date',
        'primary_session_index',
        'sessions'
    ],
    title: 'MeasurementDay',
    description: 'One day row of the measurements table.'
} as const;

export const MeasurementECGRequiredData_InputSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementAliveCorECGRequiredData-Input'
        }
    ],
    discriminator: {
        propertyName: 'ecg_device_type',
        mapping: {
            alivecor: '#/components/schemas/MeasurementAliveCorECGRequiredData-Input'
        }
    }
} as const;

export const MeasurementECGRequiredData_OutputSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementAliveCorECGRequiredData-Output'
        }
    ],
    discriminator: {
        propertyName: 'ecg_device_type',
        mapping: {
            alivecor: '#/components/schemas/MeasurementAliveCorECGRequiredData-Output'
        }
    }
} as const;

export const MeasurementMedicalRequiredData_InputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        }
    },
    type: 'object',
    required: [
        'order',
        'data_name'
    ],
    title: 'MeasurementMedicalRequiredData'
} as const;

export const MeasurementMedicalRequiredData_OutputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        post_endpoint: {
            type: 'string',
            title: 'Post Endpoint',
            description: 'Endpoint for posting the data.',
            readOnly: true
        },
        data_definition: {
            $ref: '#/components/schemas/SupportedMedicalData',
            description: 'Data definition (type, units, limits, etc.).',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'data_name',
        'post_endpoint',
        'data_definition'
    ],
    title: 'MeasurementMedicalRequiredData'
} as const;

export const MeasurementPPGRequiredData_InputSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementBerryPPGRequiredData-Input'
        },
        {
            $ref: '#/components/schemas/MeasurementSamsungPPGRequiredData-Input'
        },
        {
            $ref: '#/components/schemas/MeasurementSmartCarePPGRequiredData-Input'
        }
    ],
    discriminator: {
        propertyName: 'ppg_device_type',
        mapping: {
            berry: '#/components/schemas/MeasurementBerryPPGRequiredData-Input',
            samsung: '#/components/schemas/MeasurementSamsungPPGRequiredData-Input',
            smartcare: '#/components/schemas/MeasurementSmartCarePPGRequiredData-Input'
        }
    }
} as const;

export const MeasurementPPGRequiredData_OutputSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementBerryPPGRequiredData-Output'
        },
        {
            $ref: '#/components/schemas/MeasurementSamsungPPGRequiredData-Output'
        },
        {
            $ref: '#/components/schemas/MeasurementSmartCarePPGRequiredData-Output'
        }
    ],
    discriminator: {
        propertyName: 'ppg_device_type',
        mapping: {
            berry: '#/components/schemas/MeasurementBerryPPGRequiredData-Output',
            samsung: '#/components/schemas/MeasurementSamsungPPGRequiredData-Output',
            smartcare: '#/components/schemas/MeasurementSmartCarePPGRequiredData-Output'
        }
    }
} as const;

export const MeasurementRequiredData_InputSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementMedicalRequiredData-Input'
        },
        {
            $ref: '#/components/schemas/MeasurementSymptomRequiredData-Input'
        },
        {
            $ref: '#/components/schemas/MeasurementPPGRequiredData-Input'
        },
        {
            $ref: '#/components/schemas/MeasurementECGRequiredData-Input'
        }
    ],
    discriminator: {
        propertyName: 'data_type',
        mapping: {
            ecg: '#/components/schemas/MeasurementECGRequiredData-Input',
            'medical-data': '#/components/schemas/MeasurementMedicalRequiredData-Input',
            ppg: '#/components/schemas/MeasurementPPGRequiredData-Input',
            symptom: '#/components/schemas/MeasurementSymptomRequiredData-Input'
        }
    }
} as const;

export const MeasurementRequiredData_OutputSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementMedicalRequiredData-Output'
        },
        {
            $ref: '#/components/schemas/MeasurementSymptomRequiredData-Output'
        },
        {
            $ref: '#/components/schemas/MeasurementPPGRequiredData-Output'
        },
        {
            $ref: '#/components/schemas/MeasurementECGRequiredData-Output'
        }
    ],
    discriminator: {
        propertyName: 'data_type',
        mapping: {
            ecg: '#/components/schemas/MeasurementECGRequiredData-Output',
            'medical-data': '#/components/schemas/MeasurementMedicalRequiredData-Output',
            ppg: '#/components/schemas/MeasurementPPGRequiredData-Output',
            symptom: '#/components/schemas/MeasurementSymptomRequiredData-Output'
        }
    }
} as const;

export const MeasurementSamsungPPGRequiredData_InputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^Samsung '
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Device'
        },
        ppg_device_type: {
            type: 'string',
            const: 'samsung',
            title: 'Ppg Device Type',
            default: 'samsung'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition'
    ],
    title: 'MeasurementSamsungPPGRequiredData'
} as const;

export const MeasurementSamsungPPGRequiredData_OutputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^Samsung '
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Device'
        },
        ppg_device_type: {
            type: 'string',
            const: 'samsung',
            title: 'Ppg Device Type',
            default: 'samsung'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        },
        post_endpoint: {
            type: 'string',
            title: 'Post Endpoint',
            description: 'Endpoint for posting the data.',
            readOnly: true
        },
        additional_post_endpoints: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Additional Post Endpoints',
            description: 'Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,\nor ECG-derived data. Note, that these always required newly submitted\nPPG or ECG UUID to be part of the post body.',
            readOnly: true
        },
        data_name: {
            type: 'string',
            title: 'Data Name',
            description: 'Simple string representation.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition',
        'post_endpoint',
        'additional_post_endpoints',
        'data_name'
    ],
    title: 'MeasurementSamsungPPGRequiredData'
} as const;

export const MeasurementSessionSchema = {
    properties: {
        kind: {
            $ref: '#/components/schemas/SessionKind'
        },
        session_start: {
            type: 'string',
            format: 'date-time',
            title: 'Session Start'
        },
        session_end: {
            type: 'string',
            format: 'date-time',
            title: 'Session End'
        },
        missing_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Missing Items'
        },
        position: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Position'
        },
        cells: {
            additionalProperties: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/MeasurementCell'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'object',
            title: 'Cells',
            default: {}
        },
        quality: {
            additionalProperties: {
                $ref: '#/components/schemas/QualityFlagInfo'
            },
            type: 'object',
            title: 'Quality',
            default: {}
        },
        actions: {
            $ref: '#/components/schemas/MeasurementActions'
        },
        ppg_signals: {
            items: {
                $ref: '#/components/schemas/PPGSignalInfo'
            },
            type: 'array',
            title: 'Ppg Signals',
            default: []
        }
    },
    type: 'object',
    required: [
        'kind',
        'session_start',
        'session_end',
        'actions'
    ],
    title: 'MeasurementSession',
    description: 'One session (sub-row) of a day.'
} as const;

export const MeasurementSmartCarePPGRequiredData_InputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            type: 'string',
            enum: [
                'SmartCare',
                'SmartCare BM2000A'
            ],
            title: 'Required Device',
            default: 'SmartCare BM2000A'
        },
        ppg_device_type: {
            type: 'string',
            const: 'smartcare',
            title: 'Ppg Device Type',
            default: 'smartcare'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition'
    ],
    title: 'MeasurementSmartCarePPGRequiredData'
} as const;

export const MeasurementSmartCarePPGRequiredData_OutputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            type: 'string',
            enum: [
                'SmartCare',
                'SmartCare BM2000A'
            ],
            title: 'Required Device',
            default: 'SmartCare BM2000A'
        },
        ppg_device_type: {
            type: 'string',
            const: 'smartcare',
            title: 'Ppg Device Type',
            default: 'smartcare'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        },
        post_endpoint: {
            type: 'string',
            title: 'Post Endpoint',
            description: 'Endpoint for posting the data.',
            readOnly: true
        },
        additional_post_endpoints: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Additional Post Endpoints',
            description: 'Additional endpoints for timeseries data (PPG, ECG), e.g., PPG-derived,\nor ECG-derived data. Note, that these always required newly submitted\nPPG or ECG UUID to be part of the post body.',
            readOnly: true
        },
        data_name: {
            type: 'string',
            title: 'Data Name',
            description: 'Simple string representation.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition',
        'post_endpoint',
        'additional_post_endpoints',
        'data_name'
    ],
    title: 'MeasurementSmartCarePPGRequiredData'
} as const;

export const MeasurementSymptomRequiredData_InputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        }
    },
    type: 'object',
    required: [
        'order',
        'data_name'
    ],
    title: 'MeasurementSymptomRequiredData'
} as const;

export const MeasurementSymptomRequiredData_OutputSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        post_endpoint: {
            type: 'string',
            title: 'Post Endpoint',
            description: 'Endpoint for posting the data.',
            readOnly: true
        },
        data_definition: {
            $ref: '#/components/schemas/SupportedSymptom',
            description: 'Data definition (type, units, limits, etc.).',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'data_name',
        'post_endpoint',
        'data_definition'
    ],
    title: 'MeasurementSymptomRequiredData'
} as const;

export const MeasurementsResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        timezone: {
            type: 'string',
            title: 'Timezone'
        },
        selectors: {
            additionalProperties: {
                $ref: '#/components/schemas/Selector'
            },
            type: 'object',
            title: 'Selectors'
        },
        columns: {
            items: {
                $ref: '#/components/schemas/MeasurementColumn'
            },
            type: 'array',
            title: 'Columns'
        },
        measurements: {
            items: {
                $ref: '#/components/schemas/MeasurementDay'
            },
            type: 'array',
            title: 'Measurements'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patient_id',
        'timezone',
        'selectors',
        'columns',
        'measurements'
    ],
    title: 'MeasurementsResponse',
    description: 'Full response of the measurements endpoint.'
} as const;

export const MedicalDataCategorySchema = {
    type: 'string',
    enum: [
        'basic',
        'vitals',
        'labs',
        'exams_basic',
        'exams_echo',
        'exams_rhc',
        'exams_lhc',
        'exams_final'
    ],
    title: 'MedicalDataCategory'
} as const;

export const MedicalDataMetadataSchema = {
    properties: {
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Patient Ids'
        },
        total_count: {
            type: 'integer',
            minimum: 0,
            title: 'Total Count'
        },
        count_per_patient: {
            additionalProperties: {
                type: 'integer'
            },
            type: 'object',
            title: 'Count Per Patient'
        },
        earliest_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Earliest Datetime'
        },
        latest_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Latest Datetime'
        },
        data_description: {
            additionalProperties: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/NumericalDataDescription'
                    },
                    {
                        $ref: '#/components/schemas/CategoricalDataDescription'
                    }
                ]
            },
            type: 'object',
            title: 'Data Description'
        }
    },
    type: 'object',
    required: [
        'patient_ids',
        'total_count',
        'count_per_patient',
        'earliest_datetime',
        'latest_datetime',
        'data_description'
    ],
    title: 'MedicalDataMetadata',
    description: 'Additional fields for medical data, computed data, and symptoms.'
} as const;

export const MedicalReportResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        report_date: {
            type: 'string',
            format: 'date',
            title: 'Report Date'
        },
        report_type: {
            $ref: '#/components/schemas/ReportType'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        upload_status: {
            $ref: '#/components/schemas/UploadStatus'
        },
        extraction_status: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Extraction Status'
        },
        uploaded_by: {
            $ref: '#/components/schemas/UploadedBy',
            default: 'clinic'
        },
        object_key: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Object Key'
        },
        original_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Original Name'
        },
        size_bytes: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Size Bytes'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'user_uuid',
        'report_date',
        'report_type',
        'uuid',
        'upload_status',
        'object_key',
        'created_at',
        'updated_at'
    ],
    title: 'MedicalReportResponse'
} as const;

export const MedicalReportsDownloadUrlResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        report_date: {
            type: 'string',
            format: 'date',
            title: 'Report Date'
        },
        report_type: {
            $ref: '#/components/schemas/ReportType'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        upload_status: {
            $ref: '#/components/schemas/UploadStatus'
        },
        extraction_status: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Extraction Status'
        },
        uploaded_by: {
            $ref: '#/components/schemas/UploadedBy',
            default: 'clinic'
        },
        object_key: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Object Key'
        },
        original_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Original Name'
        },
        size_bytes: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Size Bytes'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        download_url: {
            type: 'string',
            title: 'Download Url'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'user_uuid',
        'report_date',
        'report_type',
        'uuid',
        'upload_status',
        'object_key',
        'created_at',
        'updated_at',
        'download_url'
    ],
    title: 'MedicalReportsDownloadUrlResponse'
} as const;

export const MedicalReportsUploadUrlResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        report_date: {
            type: 'string',
            format: 'date',
            title: 'Report Date'
        },
        report_type: {
            $ref: '#/components/schemas/ReportType'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        upload_status: {
            $ref: '#/components/schemas/UploadStatus'
        },
        extraction_status: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Extraction Status'
        },
        uploaded_by: {
            $ref: '#/components/schemas/UploadedBy',
            default: 'clinic'
        },
        object_key: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Object Key'
        },
        original_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Original Name'
        },
        size_bytes: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Size Bytes'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        upload_url: {
            type: 'string',
            title: 'Upload Url'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'user_uuid',
        'report_date',
        'report_type',
        'uuid',
        'upload_status',
        'object_key',
        'created_at',
        'updated_at',
        'upload_url'
    ],
    title: 'MedicalReportsUploadUrlResponse'
} as const;

export const MedicalWaitingRoomAlertsSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            minLength: 1,
            title: 'Name'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region'
        },
        patient_state: {
            $ref: '#/components/schemas/PatientState'
        },
        clinical_risk_score: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Clinical Risk Score'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        alert_risk_score: {
            type: 'number',
            minimum: 0,
            title: 'Alert Risk Score'
        },
        lvfp_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Lvfp Alerts'
        },
        vitals_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Vitals Alerts'
        },
        symptom_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Symptom Alerts'
        },
        ppg_quality_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Ppg Quality Alerts'
        },
        compliance_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Compliance Alerts'
        },
        last_events: {
            items: {
                $ref: '#/components/schemas/WaitingRoomEvents'
            },
            type: 'array',
            title: 'Last Events'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        monitoring_watch_kind: {
            $ref: '#/components/schemas/PatientMonitoringWatchKind'
        },
        monitoring_watch_status: {
            $ref: '#/components/schemas/PatientMonitoringWatchStatus'
        },
        monitoring_call_status: {
            $ref: '#/components/schemas/PatientMonitoringCallStatus'
        },
        monitoring_report_status: {
            $ref: '#/components/schemas/PatientMonitoringReportStatus'
        },
        ai_summary: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientAISummary'
                },
                {
                    type: 'null'
                }
            ]
        },
        ai_triage: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientAITriage'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'name',
        'country',
        'region',
        'patient_state',
        'clinical_risk_score',
        'alert_risk_score',
        'lvfp_alerts',
        'vitals_alerts',
        'symptom_alerts',
        'ppg_quality_alerts',
        'compliance_alerts',
        'last_events',
        'monitoring_watch_kind',
        'monitoring_watch_status',
        'monitoring_call_status',
        'monitoring_report_status'
    ],
    title: 'MedicalWaitingRoomAlerts'
} as const;

export const MedicalWaitingRoomResponseSchema = {
    properties: {
        fetched: {
            type: 'string',
            format: 'date-time',
            title: 'Fetched'
        },
        waiting_room: {
            items: {
                $ref: '#/components/schemas/MedicalWaitingRoomAlerts'
            },
            type: 'array',
            title: 'Waiting Room'
        },
        waiting_room_timespan: {
            type: 'string',
            format: 'duration',
            title: 'Waiting Room Timespan'
        },
        risk_factor_exponential_discount: {
            type: 'number',
            title: 'Risk Factor Exponential Discount'
        }
    },
    type: 'object',
    required: [
        'fetched',
        'waiting_room',
        'waiting_room_timespan',
        'risk_factor_exponential_discount'
    ],
    title: 'MedicalWaitingRoomResponse'
} as const;

export const MedicationComponentSchema = {
    properties: {
        name: {
            type: 'string',
            title: 'Name'
        },
        dose: {
            type: 'number',
            title: 'Dose'
        },
        times_daily: {
            type: 'integer',
            title: 'Times Daily'
        }
    },
    type: 'object',
    required: [
        'name',
        'dose',
        'times_daily'
    ],
    title: 'MedicationComponent'
} as const;

export const MedicationDosageEntrySchema = {
    properties: {
        dose: {
            type: 'number',
            title: 'Dose'
        },
        times_daily: {
            type: 'integer',
            title: 'Times Daily'
        },
        condition: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Condition'
        },
        lower_bound: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Lower Bound'
        },
        upper_bound: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Upper Bound'
        },
        max_dose: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Max Dose'
        }
    },
    type: 'object',
    required: [
        'dose',
        'times_daily'
    ],
    title: 'MedicationDosageEntry'
} as const;

export const MedicationEntrySchema = {
    properties: {
        gdmt_relevant: {
            type: 'boolean',
            title: 'Gdmt Relevant'
        },
        dosages: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MedicationDosageEntry'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Dosages'
        },
        components: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MedicationComponent'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Components'
        }
    },
    type: 'object',
    required: [
        'gdmt_relevant'
    ],
    title: 'MedicationEntry'
} as const;

export const MedicationGroupSchema = {
    type: 'string',
    enum: [
        'Betablockers',
        'RAAS inhibitors: ARNI',
        'RAAS inhibitors: ACE inhibitors',
        'RAAS inhibitors: ARB',
        'MRAs',
        'SGLT2 inhibitors',
        'Loop diuretics',
        'Thiazide diuretics',
        'Other diuretics',
        'Antiaggregants: ASA',
        'Anticoagulants: NOAC',
        'Anticoagulants: LMWH',
        'Anticoagulants: warfarin',
        'Antiaggregants: P2Y12 inhibitors',
        'Other HF therapy',
        'Ca channel blockers: Dihydropyridine',
        'Ca channel blockers: Non-Dihydropyridine',
        'Lipid lowering: Statin',
        'Lipid lowering: PCSK9i',
        'Lipid lowering: Other',
        'Antiarrhythmics',
        'Other antihypertensives',
        'Vasodilators',
        'PH 1,3,4: Specific therapy',
        'GLP1-RA',
        'Glycaemia',
        'Bronchodilators: B2 agonist',
        'Bronchodilators: Anticholinergics',
        'Bronchodilators: Methylxanthines',
        'Bronchodilators: Montelukast',
        'Antidepressants/Anxiolytics',
        'Thyroid: thyrostatics',
        'Thyroid: substitute therapy',
        'PPI',
        'Anemia',
        'Potassium binder',
        'Oncology therapy',
        'Other'
    ],
    title: 'MedicationGroup'
} as const;

export const MinimalPatientResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'full_name'
    ],
    title: 'MinimalPatientResponse'
} as const;

export const MissingConsentsSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        missing_consents: {
            items: {
                $ref: '#/components/schemas/ConsentStateItem'
            },
            type: 'array',
            title: 'Missing Consents'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'missing_consents'
    ],
    title: 'MissingConsents'
} as const;

export const MissingConsentsErrorSchema = {
    properties: {
        detail: {
            type: 'string',
            title: 'Detail'
        },
        data: {
            $ref: '#/components/schemas/MissingConsents'
        }
    },
    type: 'object',
    required: [
        'detail',
        'data'
    ],
    title: 'MissingConsentsError',
    description: 'The 451 body: `data` names the consents the patient misses and why, so a\nclient can route straight into the right consent flow.',
    example: {
        data: {
            missing_consents: [
                {
                    consent_type: 'telemonitoring',
                    state: 'stale'
                }
            ],
            patient_id: 1
        },
        detail: '<Detail of the exception.>'
    }
} as const;

export const MonitoringSettingsResponseSchema = {
    properties: {
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        new_patient_status: {
            $ref: '#/components/schemas/PatientStatus',
            default: 1
        },
        auto_schedule: {
            type: 'boolean',
            title: 'Auto Schedule',
            default: false
        },
        requires_measurement_config: {
            type: 'boolean',
            title: 'Requires Measurement Config',
            default: false
        },
        auto_alerts: {
            type: 'boolean',
            title: 'Auto Alerts',
            default: false
        },
        patient_push_notifications: {
            type: 'boolean',
            title: 'Patient Push Notifications',
            default: false
        },
        consent_type: {
            type: 'integer',
            enum: [
                2,
                1
            ],
            title: 'Consent Type',
            description: '**Deprecated** - use `required_consents`.',
            default: 2,
            deprecated: true
        },
        required_consents: {
            items: {
                $ref: '#/components/schemas/ConsentType'
            },
            type: 'array',
            title: 'Required Consents',
            default: [
                'research'
            ]
        },
        is_monitored: {
            type: 'boolean',
            title: 'Is Monitored',
            default: false
        },
        any_new_data_activate: {
            type: 'boolean',
            title: 'Any New Data Activate',
            default: false
        },
        ppg_ecg_new_data_activate: {
            type: 'boolean',
            title: 'Ppg Ecg New Data Activate',
            default: true
        },
        auto_deactivate_after: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auto Deactivate After',
            default: 'P60D'
        },
        status_change_email: {
            type: 'boolean',
            title: 'Status Change Email',
            default: false
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        is_protected: {
            type: 'boolean',
            title: 'Is Protected',
            description: 'Whether this monitoring setting instance is protected. Protected\nsettings are general settings that only depend on the `monitoring_type`\nand not on any other hierarchical attributes (country / region / clinic\n/ physician). At least one instance of settings per `monitoring_type`\nmust exist.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'uuid',
        'created_at',
        'updated_at',
        'is_protected'
    ],
    title: 'MonitoringSettingsResponse'
} as const;

export const MonitoringUserResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        medical_access: {
            type: 'boolean',
            title: 'Medical Access'
        },
        visible_patients_count: {
            type: 'integer',
            minimum: 0,
            title: 'Visible Patients Count'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'medical_access',
        'visible_patients_count'
    ],
    title: 'MonitoringUserResponse',
    description: 'A monitoring user as other staff see them: no contact details or access\npolicy.'
} as const;

export const MrSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'MR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'MrSchemaResponse'
} as const;

export const MsSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'MS',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'MsSchemaResponse'
} as const;

export const MultiChoiceQuestionSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_text: {
            type: 'string',
            title: 'Question Text'
        },
        question_type: {
            type: 'string',
            const: 'polytomous_multi_choice',
            title: 'Question Type',
            default: 'polytomous_multi_choice'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        question_options: {
            items: {
                $ref: '#/components/schemas/QuestionOption'
            },
            type: 'array',
            minItems: 2,
            title: 'Question Options'
        },
        ge_selected: {
            type: 'integer',
            minimum: 0,
            title: 'Ge Selected'
        },
        le_selected: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Le Selected'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_text',
        'question_options',
        'ge_selected',
        'le_selected'
    ],
    title: 'MultiChoiceQuestion'
} as const;

export const MultiChoiceQuestionnaireResponse_InputSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_type: {
            type: 'string',
            const: 'polytomous_multi_choice',
            title: 'Question Type'
        },
        question_responses: {
            items: {
                type: 'string',
                description: 'Question option ID as `${questionnaire_id}-${question}-${option}`.'
            },
            type: 'array',
            minItems: 1,
            uniqueItems: true,
            title: 'Question Responses'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_type',
        'question_responses'
    ],
    title: 'MultiChoiceQuestionnaireResponse'
} as const;

export const MultiChoiceQuestionnaireResponse_OutputSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_type: {
            type: 'string',
            const: 'polytomous_multi_choice',
            title: 'Question Type'
        },
        question_responses: {
            items: {
                $ref: '#/components/schemas/QuestionOptionId-Output'
            },
            type: 'array',
            title: 'Question Responses'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_type',
        'question_responses'
    ],
    title: 'MultiChoiceQuestionnaireResponse'
} as const;

export const NextMeasurementListResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        per_schedule_next_measurement: {
            items: {
                $ref: '#/components/schemas/PerScheduleNextMeasurement'
            },
            type: 'array',
            title: 'Per Schedule Next Measurement'
        },
        last_measurement: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Measurement',
            readOnly: true
        },
        closest_next_measurement: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Closest Next Measurement',
            readOnly: true
        },
        schedule_ok: {
            type: 'boolean',
            title: 'Schedule Ok',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'per_schedule_next_measurement',
        'last_measurement',
        'closest_next_measurement',
        'schedule_ok'
    ],
    title: 'NextMeasurementListResponse'
} as const;

export const NotificationKindSchema = {
    type: 'string',
    enum: [
        'planned_measurement',
        'compliance',
        'low_quality',
        'low_battery',
        'manual'
    ],
    title: 'NotificationKind',
    description: 'What a ledger row is about. `manual` is an admin send, which shares the\ntable but never the checks\' dedup keys.'
} as const;

export const NotificationMessageSchema = {
    type: 'string',
    enum: [
        'planned_measurement',
        'compliance_5',
        'compliance_10',
        'compliance_15',
        'low_quality',
        'low_battery'
    ],
    title: 'NotificationMessage',
    description: 'A message of `notifications.json` the engine can render; each compliance\nstage is its own copy.'
} as const;

export const NotificationPrioritySchema = {
    type: 'string',
    enum: [
        'default',
        'normal',
        'high'
    ],
    title: 'NotificationPriority',
    description: 'Priority levels for push notifications.'
} as const;

export const NotificationSoundSchema = {
    type: 'string',
    enum: [
        'default'
    ],
    title: 'NotificationSound',
    description: 'Sound options for push notifications.'
} as const;

export const NtProbnpSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'NT-proBNP',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100000,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'pg/ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'NtProbnpSchemaResponse'
} as const;

export const NumFullMeasurementsSchema = {
    properties: {
        report_length: {
            $ref: '#/components/schemas/HeartCoreRangesMonths'
        },
        report_start_date: {
            type: 'string',
            format: 'date-time',
            title: 'Report Start Date'
        },
        report_end_date: {
            type: 'string',
            format: 'date-time',
            title: 'Report End Date'
        },
        number_of_measurements: {
            type: 'integer',
            minimum: 0,
            title: 'Number Of Measurements'
        }
    },
    type: 'object',
    required: [
        'report_length',
        'report_start_date',
        'report_end_date',
        'number_of_measurements'
    ],
    title: 'NumFullMeasurements'
} as const;

export const NumericalDataDescriptionSchema = {
    properties: {
        count: {
            type: 'integer',
            minimum: 0,
            title: 'Count'
        },
        mean: {
            type: 'number',
            title: 'Mean'
        },
        std: {
            type: 'number',
            title: 'Std'
        },
        min: {
            type: 'number',
            title: 'Min'
        },
        max: {
            type: 'number',
            title: 'Max'
        },
        median: {
            type: 'number',
            title: 'Median'
        }
    },
    type: 'object',
    required: [
        'count',
        'mean',
        'std',
        'min',
        'max',
        'median'
    ],
    title: 'NumericalDataDescription'
} as const;

export const NyhaClassSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'NYHA class',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'I',
                        'II',
                        'III',
                        'IV'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'NyhaClassSchemaResponse'
} as const;

export const Oncology_therapySchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Oncology therapy',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Oncology therapy'
} as const;

export const OpenECGActionSchema = {
    properties: {
        ecg_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ecg Uuid'
        },
        report_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Report Uuid'
        }
    },
    type: 'object',
    title: 'OpenECGAction',
    description: 'Link to the ECG PDF uploaded as a medical report for the day.'
} as const;

export const OpenTextQuestionSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_text: {
            type: 'string',
            title: 'Question Text'
        },
        question_type: {
            type: 'string',
            const: 'open_text',
            title: 'Question Type',
            default: 'open_text'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_text'
    ],
    title: 'OpenTextQuestion'
} as const;

export const OpenTextQuestionnaireResponseSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_type: {
            type: 'string',
            const: 'open_text',
            title: 'Question Type'
        },
        question_response: {
            type: 'string',
            maxLength: 2000,
            title: 'Question Response'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_type',
        'question_response'
    ],
    title: 'OpenTextQuestionnaireResponse'
} as const;

export const OperationResponseSchema = {
    properties: {
        success: {
            type: 'boolean',
            title: 'Success'
        },
        message: {
            type: 'string',
            title: 'Message'
        }
    },
    type: 'object',
    required: [
        'success',
        'message'
    ],
    title: 'OperationResponse',
    description: 'Base model for operation responses.'
} as const;

export const OrdinalQuestionSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_text: {
            type: 'string',
            title: 'Question Text'
        },
        question_type: {
            type: 'string',
            const: 'ordinal',
            title: 'Question Type',
            default: 'ordinal'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        question_options: {
            items: {
                $ref: '#/components/schemas/OrdinalQuestionOption'
            },
            type: 'array',
            minItems: 2,
            title: 'Question Options'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_text',
        'question_options'
    ],
    title: 'OrdinalQuestion'
} as const;

export const OrdinalQuestionOptionSchema = {
    properties: {
        option_id: {
            type: 'string',
            description: 'Question option ID as `${questionnaire_id}-${question}-${option}`.'
        },
        option_text: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Option Text'
        },
        option_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                }
            ],
            title: 'Option Value'
        }
    },
    type: 'object',
    required: [
        'option_id',
        'option_value'
    ],
    title: 'OrdinalQuestionOption'
} as const;

export const OrdinalQuestionnaireResponseSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_type: {
            type: 'string',
            const: 'ordinal',
            title: 'Question Type'
        },
        question_response: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                }
            ],
            title: 'Question Response'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_type',
        'question_response'
    ],
    title: 'OrdinalQuestionnaireResponse'
} as const;

export const OtherSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Other',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Other'
} as const;

export const Other_HF_therapySchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Other HF therapy',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Other HF therapy'
} as const;

export const Other_antihypertensivesSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Other antihypertensives',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Other antihypertensives'
} as const;

export const Other_diureticsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Other diuretics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Other diuretics'
} as const;

export const OverallPerPatientStatsSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        per_question: {
            additionalProperties: {
                $ref: '#/components/schemas/QuestionnaireStatsResponse'
            },
            type: 'object',
            title: 'Per Question'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'per_question'
    ],
    title: 'OverallPerPatientStats'
} as const;

export const OverallQuestionnaireStatsResponseSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        per_patient: {
            type: 'string',
            enum: [
                'first',
                'last',
                'aggregate'
            ],
            title: 'Per Patient'
        },
        total_questionnaires: {
            type: 'integer',
            minimum: 0,
            title: 'Total Questionnaires'
        },
        responses_stats_per_patient: {
            items: {
                $ref: '#/components/schemas/OverallPerPatientStats'
            },
            type: 'array',
            title: 'Responses Stats Per Patient'
        },
        responses_stats_summary: {
            additionalProperties: {
                $ref: '#/components/schemas/QuestionnaireStatsResponse'
            },
            type: 'object',
            title: 'Responses Stats Summary'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'per_patient',
        'total_questionnaires',
        'responses_stats_per_patient',
        'responses_stats_summary'
    ],
    title: 'OverallQuestionnaireStatsResponse'
} as const;

export const OverallStudyOverviewResponseSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        full_title: {
            type: 'string',
            title: 'Full Title'
        },
        planned_patients: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Planned Patients'
        },
        no_planned_visits: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'No Planned Visits'
        },
        enrolled_patients: {
            type: 'integer',
            minimum: 0,
            title: 'Enrolled Patients'
        },
        visits_per_patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PerPatientStudyStatsSimpleDetailed'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/PerPatientStudyStatsSimple'
                    },
                    type: 'array'
                }
            ],
            title: 'Visits Per Patients'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'full_title',
        'planned_patients',
        'no_planned_visits',
        'enrolled_patients',
        'visits_per_patients'
    ],
    title: 'OverallStudyOverviewResponse'
} as const;

export const OverallVisitStatusSchema = {
    type: 'string',
    enum: [
        'not-scheduled',
        'scheduled',
        'started',
        'done-required',
        'done-incl-optional',
        'done-multiple'
    ],
    title: 'OverallVisitStatus'
} as const;

export const PH_1_3_4__Specific_therapySchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'PH 1,3,4: Specific therapy',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'PH 1,3,4: Specific therapy'
} as const;

export const PPGDataRelatedResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        wavelengths: {
            items: {
                $ref: '#/components/schemas/PPGWavelength'
            },
            type: 'array',
            minItems: 1,
            title: 'Wavelengths'
        },
        sampling_frequency: {
            items: {
                type: 'number',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Sampling Frequency'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'wavelengths',
        'sampling_frequency'
    ],
    title: 'PPGDataRelatedResponse'
} as const;

export const PPGDataTasksResponseSchema = {
    properties: {
        ppg_data: {
            $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
        },
        background_tasks: {
            additionalProperties: {
                anyOf: [
                    {
                        type: 'string',
                        maxLength: 64,
                        minLength: 32
                    },
                    {
                        type: 'string',
                        format: 'uuid4'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'object',
            title: 'Background Tasks'
        }
    },
    type: 'object',
    required: [
        'ppg_data',
        'background_tasks'
    ],
    title: 'PPGDataTasksResponse'
} as const;

export const PPGDatasetMetadataSchema = {
    properties: {
        dataset_name: {
            type: 'string',
            title: 'Dataset Name'
        },
        length: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created: {
            type: 'string',
            format: 'date-time',
            title: 'Created',
            default: '2026-11-02T16:17:18.445267Z'
        },
        ppg_data: {
            items: {
                $ref: '#/components/schemas/PPGMetadata'
            },
            type: 'array',
            title: 'Ppg Data'
        }
    },
    type: 'object',
    required: [
        'dataset_name',
        'length',
        'ppg_data'
    ],
    title: 'PPGDatasetMetadata'
} as const;

export const PPGDerivedDataGroupSchema = {
    type: 'string',
    enum: [
        'hr',
        'hrv',
        'spo2'
    ],
    title: 'PPGDerivedDataGroup'
} as const;

export const PPGDerivedDataResponseSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/PPGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            $ref: '#/components/schemas/DerivedDataAggregationFunction'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'PPGDerivedDataResponse'
} as const;

export const PPGDerivedDataSchemaWithPPGResponseSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/PPGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            $ref: '#/components/schemas/DerivedDataAggregationFunction'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppg: {
            $ref: '#/components/schemas/PPGWithSessionResponse'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'ppg',
        'final_value'
    ],
    title: 'PPGDerivedDataSchemaWithPPGResponse'
} as const;

export const PPGMeasurementModeSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'PPGMeasurementMode',
    'x-enum-varnames': [
        'external_data',
        'seerlinq_supervision',
        'physician_supervison',
        'home_monitoring',
        'synthetic'
    ],
    description: ' - 0: external_data\n - 1: seerlinq_supervision\n - 2: physician_supervison\n - 3: home_monitoring\n - 4: synthetic'
} as const;

export const PPGMetadataSchema = {
    properties: {
        patient_id: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'integer'
                }
            ],
            title: 'Patient Id'
        },
        patient_group: {
            type: 'string',
            title: 'Patient Group'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode'
        },
        file_reference: {
            type: 'string',
            title: 'File Reference'
        },
        uuid: {
            type: 'string',
            format: 'uuid',
            title: 'Uuid'
        },
        data_type: {
            $ref: '#/components/schemas/AlgorithmDataType'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'patient_group',
        'measurement_datetime',
        'measurement_origin',
        'measurement_device',
        'quality_flag',
        'measurement_mode',
        'file_reference',
        'uuid',
        'data_type',
        'measurement_condition'
    ],
    title: 'PPGMetadata'
} as const;

export const PPGQualityFlagSchema = {
    type: 'integer',
    enum: [
        -1,
        0,
        1,
        2,
        3,
        4,
        5,
        6
    ],
    title: 'PPGQualityFlag',
    'x-enum-varnames': [
        'unknown',
        'data_error',
        'ok',
        'low_signal_quality',
        'artefacts',
        'short_signal',
        'extra_circumstance',
        'test_measurement'
    ],
    description: ' - -1: unknown\n - 0: data_error\n - 1: ok\n - 2: low_signal_quality\n - 3: artefacts\n - 4: short_signal\n - 5: extra_circumstance\n - 6: test_measurement'
} as const;

export const PPGRelatedQualityFlagSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'PPGRelatedQualityFlag',
    'x-enum-varnames': [
        'wrong',
        'ok',
        'low_ppg_quality',
        'ignore_data',
        'manual_value_override'
    ],
    description: ' - 0: wrong\n - 1: ok\n - 2: low_ppg_quality\n - 3: ignore_data\n - 4: manual_value_override'
} as const;

export const PPGSignalInfoSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        position: {
            type: 'string',
            title: 'Position'
        },
        measured_at: {
            type: 'string',
            format: 'date-time',
            title: 'Measured At'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        length: {
            type: 'number',
            title: 'Length'
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        quality_flag: {
            $ref: '#/components/schemas/QualityFlagInfo'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        editable: {
            type: 'boolean',
            title: 'Editable'
        },
        derived: {
            additionalProperties: {
                additionalProperties: {
                    $ref: '#/components/schemas/ValueCell'
                },
                type: 'object'
            },
            type: 'object',
            title: 'Derived',
            default: {}
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_condition',
        'position',
        'measured_at',
        'measurement_device',
        'length',
        'quality_flag',
        'editable'
    ],
    title: 'PPGSignalInfo',
    description: 'Detail-dialog payload for a single PPG signal of a session.'
} as const;

export const PPGWavelengthSchema = {
    type: 'string',
    enum: [
        'red',
        'green',
        'ir',
        'pleth',
        'mobile_ppg'
    ],
    title: 'PPGWavelength'
} as const;

export const PPGWithSessionResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        wavelengths: {
            items: {
                $ref: '#/components/schemas/PPGWavelength'
            },
            type: 'array',
            minItems: 1,
            title: 'Wavelengths'
        },
        sampling_frequency: {
            items: {
                type: 'number',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Sampling Frequency'
        },
        session_id: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Session Id',
            description: 'Session of the paid-screening attempt the recording belongs to, when it was sent with one.'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'wavelengths',
        'sampling_frequency'
    ],
    title: 'PPGWithSessionResponse'
} as const;

export const PPISchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'PPI',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'PPI'
} as const;

export const PageRotationSchema = {
    properties: {
        page: {
            type: 'integer',
            minimum: 1,
            title: 'Page'
        },
        rotation: {
            type: 'integer',
            enum: [
                0,
                90,
                180,
                270
            ],
            title: 'Rotation'
        }
    },
    type: 'object',
    required: [
        'page',
        'rotation'
    ],
    title: 'PageRotation',
    description: 'An absolute clockwise page rotation, as the PDF ``/Rotate`` attribute\nholds it, so repeating a request changes nothing.'
} as const;

export const PaginationResponseSchema = {
    properties: {
        page: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Page'
        },
        page_size: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Page Size'
        },
        sort_by: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Sort By'
        },
        total_pages: {
            type: 'integer',
            minimum: 0,
            title: 'Total Pages'
        },
        total_items: {
            type: 'integer',
            minimum: 0,
            title: 'Total Items'
        }
    },
    type: 'object',
    required: [
        'page',
        'page_size',
        'sort_by',
        'total_pages',
        'total_items'
    ],
    title: 'PaginationResponse'
} as const;

export const PairCellSchema = {
    properties: {
        parts: {
            additionalProperties: {
                $ref: '#/components/schemas/ValueCell'
            },
            type: 'object',
            title: 'Parts'
        }
    },
    type: 'object',
    required: [
        'parts'
    ],
    title: 'PairCell',
    description: 'A two-part cell (blood pressure), one value cell per side.'
} as const;

export const PasskeyAuthBeginSchemaSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id'
    ],
    title: 'PasskeyAuthBeginSchema'
} as const;

export const PasskeyAuthCompleteSchemaSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        credential_json: {
            type: 'string',
            maxLength: 32768,
            minLength: 1,
            title: 'Credential Json'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'credential_json'
    ],
    title: 'PasskeyAuthCompleteSchema'
} as const;

export const PasskeyEnrollBeginSchemaSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        },
        device_name: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Device Name'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'PasskeyEnrollBeginSchema'
} as const;

export const PasskeyEnrollCompleteSchemaSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        },
        device_name: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Device Name'
        },
        credential_json: {
            type: 'string',
            maxLength: 32768,
            minLength: 1,
            title: 'Credential Json'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'credential_json'
    ],
    title: 'PasskeyEnrollCompleteSchema'
} as const;

export const PasskeyEnrollResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        backup_codes: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Backup Codes'
        },
        access_token: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Access Token'
        },
        refresh_token: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Refresh Token'
        }
    },
    type: 'object',
    required: [
        'status',
        'backup_codes'
    ],
    title: 'PasskeyEnrollResponse',
    description: 'Tokens are only present when this enrollment completed a login-time\nchallenge (mandatory first-time setup); absent for a voluntary opt-in\nby an already-authenticated user. `backup_codes` is only non-empty when\nthe account had none yet, same as `TOTPEnrollConfirmResponse`.'
} as const;

export const PasswordFormTypesSchema = {
    type: 'string',
    enum: [
        'reset_password',
        'finish_physician',
        'finish_patient'
    ],
    title: 'PasswordFormTypes'
} as const;

export const PatientAISummarySchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        generated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Generated At'
        },
        heading: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Heading'
        },
        reasoning: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reasoning'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'generated_at'
    ],
    title: 'PatientAISummary',
    description: 'The current summary as served on patient reads. `AISummary` stays the\nstored-payload shape the write schema inherits.'
} as const;

export const PatientAITriageSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        generated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Generated At'
        },
        priority: {
            $ref: '#/components/schemas/AITriagePriority'
        },
        reason: {
            type: 'string',
            title: 'Reason'
        }
    },
    additionalProperties: true,
    type: 'object',
    required: [
        'uuid',
        'generated_at',
        'priority',
        'reason'
    ],
    title: 'PatientAITriage',
    description: 'The current triage as served on waiting-room rows, harness\'s fields\nincluded.'
} as const;

export const PatientBaseSchemaSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType',
            default: 0
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'country',
        'patient_id'
    ],
    title: 'PatientBaseSchema',
    description: 'Minimal patient schema.'
} as const;

export const PatientConsentItemSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        state: {
            $ref: '#/components/schemas/ConsentState'
        },
        method: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ConsentMethod'
                },
                {
                    type: 'null'
                }
            ]
        },
        granted_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Granted At'
        },
        recorded_by: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Recorded By'
        },
        consented_text: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ConsentTextReference'
                },
                {
                    type: 'null'
                }
            ]
        },
        current_text: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ConsentTextReference'
                },
                {
                    type: 'null'
                }
            ]
        },
        available_languages: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Available Languages',
            description: 'The languages of the texts of this type at the patient\'s level, the most specific scope with a text in any language; signing in one of them makes it the consent language',
            default: []
        }
    },
    type: 'object',
    required: [
        'consent_type',
        'required',
        'state'
    ],
    title: 'PatientConsentItem'
} as const;

export const PatientConsentResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        consent_history: {
            items: {
                $ref: '#/components/schemas/ConsentHistory'
            },
            type: 'array',
            title: 'Consent History'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'informed_consent',
        'consent_history'
    ],
    title: 'PatientConsentResponse'
} as const;

export const PatientConsentsResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/PatientConsentItem'
            },
            type: 'array',
            title: 'Consents'
        },
        history: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ConsentLedgerItem'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'History'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'consents'
    ],
    title: 'PatientConsentsResponse'
} as const;

export const PatientCountsResponseSchema = {
    properties: {
        environment: {
            $ref: '#/components/schemas/SeerlinqEnvironments'
        },
        server_time_utc: {
            type: 'string',
            format: 'date-time',
            title: 'Server Time Utc'
        },
        grouped_counts: {
            items: {
                $ref: '#/components/schemas/GroupCount'
            },
            type: 'array',
            title: 'Grouped Counts'
        }
    },
    type: 'object',
    required: [
        'environment',
        'server_time_utc',
        'grouped_counts'
    ],
    title: 'PatientCountsResponse'
} as const;

export const PatientDataMetadataSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        sex: {
            $ref: '#/components/schemas/Sex'
        },
        age: {
            type: 'integer',
            minimum: 0,
            title: 'Age'
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        diagnoses: {
            $ref: '#/components/schemas/BaseTableDataMetadata'
        },
        medical_data: {
            $ref: '#/components/schemas/MedicalDataMetadata'
        },
        medications: {
            $ref: '#/components/schemas/BaseTableDataMetadata'
        },
        symptoms: {
            $ref: '#/components/schemas/MedicalDataMetadata'
        },
        sq_computed: {
            $ref: '#/components/schemas/MedicalDataMetadata'
        },
        derived_data: {
            $ref: '#/components/schemas/MedicalDataMetadata'
        },
        alerts: {
            $ref: '#/components/schemas/BaseTableDataMetadata'
        },
        thresholds: {
            $ref: '#/components/schemas/BaseTableDataMetadata'
        },
        events: {
            $ref: '#/components/schemas/BaseTableDataMetadata'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'sex',
        'age',
        'height',
        'diagnoses',
        'medical_data',
        'medications',
        'symptoms',
        'sq_computed',
        'derived_data',
        'alerts',
        'thresholds',
        'events'
    ],
    title: 'PatientDataMetadata'
} as const;

export const PatientDatasetMetadataSchema = {
    properties: {
        dataset_name: {
            type: 'string',
            title: 'Dataset Name'
        },
        length: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        patient_data: {
            items: {
                $ref: '#/components/schemas/PatientDataMetadata'
            },
            type: 'array',
            title: 'Patient Data'
        },
        created: {
            type: 'string',
            format: 'date-time',
            title: 'Created',
            default: '2026-11-02T16:17:18.450930Z'
        }
    },
    type: 'object',
    required: [
        'dataset_name',
        'length',
        'patient_data'
    ],
    title: 'PatientDatasetMetadata'
} as const;

export const PatientFacingPhysicianResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicResponse'
            },
            type: 'array',
            title: 'Clinics'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/UserReferenceResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at',
        'full_name'
    ],
    title: 'PatientFacingPhysicianResponse'
} as const;

export const PatientHealthProReportsResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        report_type: {
            $ref: '#/components/schemas/HealthProReportType'
        },
        generated: {
            type: 'string',
            format: 'date-time',
            title: 'Generated'
        },
        max_reports: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Max Reports'
        },
        from_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'From Date'
        },
        num_reports: {
            type: 'integer',
            minimum: 0,
            title: 'Num Reports'
        },
        reports: {
            items: {
                $ref: '#/components/schemas/HealthProReport'
            },
            type: 'array',
            title: 'Reports'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'report_type',
        'generated',
        'max_reports',
        'from_date',
        'num_reports',
        'reports'
    ],
    title: 'PatientHealthProReportsResponse'
} as const;

export const PatientInfoSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        full_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Full Name'
        },
        age: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Age'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        display_string: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Display String'
        }
    },
    type: 'object',
    required: [
        'patient_id'
    ],
    title: 'PatientInfo',
    description: 'Patient demographic info for the report header.'
} as const;

export const PatientMessageResponseSchema = {
    properties: {
        message_body: {
            type: 'string',
            title: 'Message Body'
        },
        message_active: {
            type: 'boolean',
            title: 'Message Active',
            default: true
        },
        message_expiry: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Expiry'
        },
        message_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'message_body',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PatientMessageResponse'
} as const;

export const PatientMonitoringCallStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'PatientMonitoringCallStatus',
    'x-enum-varnames': [
        'nothing',
        'to_call',
        'called'
    ],
    description: ' - 0: nothing\n - 1: to_call\n - 2: called'
} as const;

export const PatientMonitoringReportStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'PatientMonitoringReportStatus',
    'x-enum-varnames': [
        'nothing',
        'to_report',
        'reported'
    ],
    description: ' - 0: nothing\n - 1: to_report\n - 2: reported'
} as const;

export const PatientMonitoringTypeSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3
    ],
    title: 'PatientMonitoringType',
    'x-enum-varnames': [
        'self_measurement',
        'physician_supervision',
        'testing',
        'screening'
    ],
    description: ' - 0: self_measurement\n - 1: physician_supervision\n - 2: testing\n - 3: screening'
} as const;

export const PatientMonitoringWatchKindSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'PatientMonitoringWatchKind',
    'x-enum-varnames': [
        'none',
        'medical',
        'tech_compliance'
    ],
    description: ' - 0: none\n - 1: medical\n - 2: tech_compliance'
} as const;

export const PatientMonitoringWatchStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'PatientMonitoringWatchStatus',
    'x-enum-varnames': [
        'not_watching',
        'new_alert',
        'in_progress',
        'watching',
        'from_audit'
    ],
    description: ' - 0: not_watching\n - 1: new_alert\n - 2: in_progress\n - 3: watching\n - 4: from_audit'
} as const;

export const PatientNotificationItemSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        kind: {
            $ref: '#/components/schemas/NotificationKind'
        },
        dedup_key: {
            type: 'string',
            title: 'Dedup Key'
        },
        language: {
            $ref: '#/components/schemas/LocaleTag'
        },
        title: {
            type: 'string',
            title: 'Title'
        },
        body: {
            type: 'string',
            title: 'Body'
        },
        sent_at: {
            type: 'string',
            format: 'date-time',
            title: 'Sent At'
        },
        expo_ticket_ids: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Expo Ticket Ids'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'user_uuid',
        'patient_id',
        'kind',
        'dedup_key',
        'language',
        'title',
        'body',
        'sent_at',
        'expo_ticket_ids'
    ],
    title: 'PatientNotificationItem',
    description: 'One row of the notification ledger. Retiring a `kind` or `language` value\nneeds the historical rows migrated: the columns are plain strings.'
} as const;

export const PatientQuestionnairesStatsResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        filled_questionnaires: {
            type: 'integer',
            minimum: 0,
            title: 'Filled Questionnaires'
        },
        first_questionnaire: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'First Questionnaire'
        },
        last_questionnaire: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Questionnaire'
        },
        responses_stats: {
            items: {
                $ref: '#/components/schemas/PerPatientQuestionStats'
            },
            type: 'array',
            title: 'Responses Stats'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'questionnaire_id',
        'filled_questionnaires',
        'first_questionnaire',
        'last_questionnaire',
        'responses_stats'
    ],
    title: 'PatientQuestionnairesStatsResponse'
} as const;

export const PatientScheduleResponseSchema = {
    properties: {
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        specific_schedule_data_name: {
            items: {
                type: 'string'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Specific Schedule Data Name'
        },
        schedule_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Frequency Regular Repeat Every'
        },
        schedule_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Frequency On Days'
        },
        schedule_times: {
            items: {
                type: 'string',
                format: 'time'
            },
            type: 'array',
            minItems: 1,
            uniqueItems: true,
            title: 'Schedule Times'
        },
        start_date: {
            type: 'string',
            format: 'date',
            title: 'Start Date',
            default: '2026-11-02'
        },
        schedule_active: {
            type: 'boolean',
            title: 'Schedule Active',
            default: true
        },
        schedule_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'schedule_type',
        'specific_schedule_data_name',
        'schedule_times',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PatientScheduleResponse'
} as const;

export const PatientStateSchema = {
    type: 'integer',
    enum: [
        0,
        1
    ],
    title: 'PatientState',
    'x-enum-varnames': [
        'normal',
        'high_risk'
    ],
    description: ' - 0: normal\n - 1: high_risk'
} as const;

export const PatientStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4,
        5
    ],
    title: 'PatientStatus',
    'x-enum-varnames': [
        'test',
        'active',
        'not_active',
        'archived',
        'deceased',
        'onboarded'
    ],
    description: ' - 0: test\n - 1: active\n - 2: not_active\n - 3: archived\n - 4: deceased\n - 5: onboarded'
} as const;

export const PatientStudyOverviewResponseSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        visits: {
            items: {
                $ref: '#/components/schemas/PerVisitStudyStats'
            },
            type: 'array',
            title: 'Visits'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'patient_id',
        'visits'
    ],
    title: 'PatientStudyOverviewResponse'
} as const;

export const PatientThresholdSchema = {
    properties: {
        value: {
            type: 'number',
            title: 'Value'
        },
        threshold_type: {
            $ref: '#/components/schemas/ThresholdTypes'
        },
        variable_name: {
            type: 'string',
            title: 'Variable Name'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        }
    },
    type: 'object',
    required: [
        'value',
        'threshold_type',
        'variable_name',
        'patient_id'
    ],
    title: 'PatientThreshold',
    description: 'Patient-specific clinical threshold. Value only — no styling.'
} as const;

export const PatientWithUuidSchemaSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType',
            default: 0
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        }
    },
    type: 'object',
    required: [
        'country',
        'patient_id',
        'uuid'
    ],
    title: 'PatientWithUuidSchema',
    description: 'Patient schema with UUID for access policy responses.\nExtends PatientBaseSchema to include uuid field.'
} as const;

export const PaymentUserOutSchema = {
    properties: {
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        physician_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Physician Uuid'
        },
        account: {
            $ref: '#/components/schemas/AccountOut'
        }
    },
    type: 'object',
    required: [
        'user_uuid',
        'username',
        'role',
        'patient_id',
        'physician_uuid',
        'account'
    ],
    title: 'PaymentUserOut',
    description: 'What was created for a paid-screening user.'
} as const;

export const PendingConsentItemSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType'
        },
        state: {
            $ref: '#/components/schemas/ConsentState'
        },
        unsignable: {
            type: 'boolean',
            title: 'Unsignable',
            description: 'No text of the type resolves for the patient: nothing to sign until one is published in their scope.'
        }
    },
    type: 'object',
    required: [
        'consent_type',
        'state',
        'unsignable'
    ],
    title: 'PendingConsentItem'
} as const;

export const PendingConsentPatientSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region',
            default: 0
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        pending: {
            items: {
                $ref: '#/components/schemas/PendingConsentItem'
            },
            type: 'array',
            title: 'Pending'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'patient_id',
        'country',
        'pending',
        'full_name'
    ],
    title: 'PendingConsentPatient'
} as const;

export const PerPatientQuestionStatsSchema = {
    properties: {
        question: {
            type: 'string',
            title: 'Question'
        },
        per_submission: {
            additionalProperties: {
                $ref: '#/components/schemas/QuestionnaireStatsResponse'
            },
            propertyNames: {
                format: 'date'
            },
            type: 'object',
            title: 'Per Submission'
        },
        summary: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Summary'
        }
    },
    type: 'object',
    required: [
        'question',
        'per_submission',
        'summary'
    ],
    title: 'PerPatientQuestionStats'
} as const;

export const PerPatientStudyStatsSimpleSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_status: {
            $ref: '#/components/schemas/OverallVisitStatus'
        },
        status_color_hex: {
            type: 'string',
            title: 'Status Color Hex',
            description: 'Status marker color as HEX value.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'visit_type',
        'visit_status',
        'status_color_hex'
    ],
    title: 'PerPatientStudyStatsSimple'
} as const;

export const PerPatientStudyStatsSimpleDetailedSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_status: {
            $ref: '#/components/schemas/DataVisitStatus'
        },
        status_color_hex: {
            type: 'string',
            title: 'Status Color Hex',
            description: 'Status marker color as HEX value.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'visit_type',
        'data_name',
        'data_status',
        'status_color_hex'
    ],
    title: 'PerPatientStudyStatsSimpleDetailed'
} as const;

export const PerScheduleNextMeasurementSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_time: {
            type: 'string',
            format: 'time',
            title: 'Scheduled Time'
        },
        check_timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Check Timezone'
        },
        last_measurement: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Measurement'
        },
        next_measurement: {
            type: 'string',
            format: 'date-time',
            title: 'Next Measurement'
        },
        allowed_window: {
            type: 'string',
            format: 'duration',
            title: 'Allowed Window'
        },
        last_measurement_timedelta: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Measurement Timedelta',
            readOnly: true
        },
        schedule_ok: {
            type: 'boolean',
            title: 'Schedule Ok',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_time',
        'check_timezone',
        'last_measurement',
        'next_measurement',
        'allowed_window',
        'last_measurement_timedelta',
        'schedule_ok'
    ],
    title: 'PerScheduleNextMeasurement'
} as const;

export const PerVisitStudyStatsSchema = {
    properties: {
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visit Datetime'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DataVisitStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        status_color_hex: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Status Color Hex',
            description: 'Status marker color as HEX value.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'visit_type',
        'visit_datetime',
        'data_name',
        'data_status',
        'status_color_hex'
    ],
    title: 'PerVisitStudyStats'
} as const;

export const PerceivedHealthChangeSchemaResponseSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            const: 'perceived health change',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 4,
            minimum: 1,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PerceivedHealthChangeSchemaResponse'
} as const;

export const PeripheralArterialDiseaseSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Peripheral arterial disease',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PeripheralArterialDiseaseSchemaResponse'
} as const;

export const PermissionBitSchema = {
    type: 'integer',
    enum: [
        1,
        2,
        3,
        4,
        5,
        6,
        10,
        11,
        12,
        20,
        21,
        22,
        23,
        24,
        25,
        30,
        31,
        32,
        33,
        35,
        40,
        41,
        42,
        44,
        45,
        46,
        47,
        48,
        43,
        50,
        51,
        52,
        53,
        54,
        55,
        56
    ],
    title: 'PermissionBit',
    description: ' - 1: READ\n - 2: WRITE\n - 3: UPDATE_OWNER\n - 4: UPDATE_VISIBLE\n - 5: DELETE_OWNER\n - 6: DELETE_VISIBLE\n - 10: GROUP_MEDICAL_ROUTERS\n - 11: GROUP_MONITORING_ROUTERS\n - 12: GROUP_ADMIN_ROUTERS\n - 20: VISIBILITY_SELF\n - 21: VISIBILITY_MANAGED\n - 22: VISIBILITY_REGIONS\n - 23: VISIBILITY_CLINICS\n - 24: VISIBILITY_PHYSICIAN\n - 25: VISIBILITY_FULL\n - 30: VIEW_WAITING_ROOM\n - 31: VIEW_ALERTS\n - 32: VIEW_THRESHOLDS\n - 33: VIEW_EVENTS\n - 35: VIEW_ANALYTICS\n - 40: ACCESS_DRI\n - 41: RUN_HEART_CORE_QUICKLOOK\n - 42: PPG_RAW\n - 44: ACCESS_LVFP\n - 45: PPG_META\n - 46: ECG_META\n - 47: ECG_RAW\n - 48: AFIB\n - 43: BYPASS_CONSENT\n - 50: ACCESS_AI_CHAT\n - 51: CLINICAL_STUDIES\n - 52: CREATE_PATIENTS\n - 53: CREATE_SERVICE_ACCOUNTS\n - 54: AI_OUTPUT_WRITE\n - 55: PATIENT_SECRETS\n - 56: ALLOW_PAYMENT',
    'x-enum-varnames': [
        'READ',
        'WRITE',
        'UPDATE_OWNER',
        'UPDATE_VISIBLE',
        'DELETE_OWNER',
        'DELETE_VISIBLE',
        'GROUP_MEDICAL_ROUTERS',
        'GROUP_MONITORING_ROUTERS',
        'GROUP_ADMIN_ROUTERS',
        'VISIBILITY_SELF',
        'VISIBILITY_MANAGED',
        'VISIBILITY_REGIONS',
        'VISIBILITY_CLINICS',
        'VISIBILITY_PHYSICIAN',
        'VISIBILITY_FULL',
        'VIEW_WAITING_ROOM',
        'VIEW_ALERTS',
        'VIEW_THRESHOLDS',
        'VIEW_EVENTS',
        'VIEW_ANALYTICS',
        'ACCESS_DRI',
        'RUN_HEART_CORE_QUICKLOOK',
        'PPG_RAW',
        'ACCESS_LVFP',
        'PPG_META',
        'ECG_META',
        'ECG_RAW',
        'AFIB',
        'BYPASS_CONSENT',
        'ACCESS_AI_CHAT',
        'CLINICAL_STUDIES',
        'CREATE_PATIENTS',
        'CREATE_SERVICE_ACCOUNTS',
        'AI_OUTPUT_WRITE',
        'PATIENT_SECRETS',
        'ALLOW_PAYMENT'
    ]
} as const;

export const PermissionMetadataItemSchema = {
    properties: {
        bit: {
            type: 'integer',
            title: 'Bit'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        category: {
            type: 'string',
            title: 'Category'
        },
        description: {
            type: 'string',
            title: 'Description'
        }
    },
    type: 'object',
    required: [
        'bit',
        'name',
        'category',
        'description'
    ],
    title: 'PermissionMetadataItem',
    description: 'Metadata for a single permission bit.'
} as const;

export const PermissionMetadataResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        permissions: {
            additionalProperties: {
                items: {
                    $ref: '#/components/schemas/PermissionMetadataItem'
                },
                type: 'array'
            },
            type: 'object',
            title: 'Permissions'
        },
        implied_permissions: {
            additionalProperties: {
                items: {
                    type: 'integer'
                },
                type: 'array'
            },
            type: 'object',
            title: 'Implied Permissions',
            description: 'Mapping of parent permission bits to their implied bits.'
        },
        forbidden_permissions: {
            additionalProperties: {
                items: {
                    type: 'integer'
                },
                type: 'array'
            },
            type: 'object',
            title: 'Forbidden Permissions',
            description: 'Mapping of permission bits to mutually exclusive bits.'
        }
    },
    type: 'object',
    required: [
        'status',
        'permissions',
        'implied_permissions'
    ],
    title: 'PermissionMetadataResponse',
    description: 'Response schema for permission metadata grouped by category.'
} as const;

export const PermissionSetSchema = {
    items: {
        $ref: '#/components/schemas/PermissionBit'
    },
    type: 'array',
    uniqueItems: true,
    title: 'PermissionSet',
    description: 'Set of permissions.'
} as const;

export const PhysicianNotifiedEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'physician_notified',
            title: 'Event Category',
            default: 'physician_notified'
        },
        event_category_type: {
            $ref: '#/components/schemas/PhysicianNotifiedEventType'
        },
        event_category_details: {
            type: 'null',
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_type',
        'event_description',
        'patient_id'
    ],
    title: 'PhysicianNotifiedEventSchema'
} as const;

export const PhysicianNotifiedEventTypeSchema = {
    type: 'string',
    enum: [
        'regular',
        'seerlinq'
    ],
    title: 'PhysicianNotifiedEventType'
} as const;

export const PhysicianResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at',
        'full_name'
    ],
    title: 'PhysicianResponse'
} as const;

export const PhysicianWithPatientsResponseSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicResponse'
            },
            type: 'array',
            title: 'Clinics'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/SimpleUserResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        },
        connected_user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleUserResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        patients: {
            items: {
                $ref: '#/components/schemas/PatientBaseSchema'
            },
            type: 'array',
            title: 'Patients'
        },
        full_name: {
            type: 'string',
            title: 'Full Name',
            description: 'Return full name.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at',
        'full_name'
    ],
    title: 'PhysicianWithPatientsResponse'
} as const;

export const PipelineSchemaSchema = {
    properties: {
        name: {
            type: 'string',
            title: 'Name'
        },
        steps: {
            items: {
                additionalProperties: true,
                type: 'object'
            },
            type: 'array',
            title: 'Steps'
        },
        graph: {
            additionalProperties: {
                items: {
                    type: 'string'
                },
                type: 'array'
            },
            type: 'object',
            title: 'Graph'
        }
    },
    type: 'object',
    required: [
        'name',
        'steps',
        'graph'
    ],
    title: 'PipelineSchema'
} as const;

export const PlannedVisitEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'visit',
            title: 'Event Category',
            default: 'visit'
        },
        event_category_type: {
            type: 'string',
            const: 'planned',
            title: 'Event Category Type',
            default: 'planned'
        },
        event_category_details: {
            $ref: '#/components/schemas/VisitEventPlannedCategory'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_details',
        'event_description',
        'patient_id'
    ],
    title: 'PlannedVisitEventSchema'
} as const;

export const PleuralEffusionSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'pleural effusion',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Minor',
                        'Major'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PleuralEffusionSchemaResponse'
} as const;

export const PlotDataSchema = {
    properties: {
        plot_type: {
            type: 'string',
            enum: [
                'timeseries',
                'signal_annotated',
                'box',
                'bar'
            ],
            title: 'Plot Type'
        },
        timeseries: {
            anyOf: [
                {
                    $ref: '#/components/schemas/TimeSeriesPlot'
                },
                {
                    type: 'null'
                }
            ]
        },
        signal_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SignalTrace'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Signal Data'
        },
        beat_annotations: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/BeatAnnotation'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Beat Annotations'
        },
        quality_bands: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/QualityBand'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Quality Bands'
        },
        box_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/BoxTrace'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Box Data'
        },
        thresholds: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientThreshold'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Thresholds'
        },
        reference_lines: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ReferenceLine'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reference Lines'
        },
        reference_bands: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ReferenceBand'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reference Bands'
        },
        events: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ClinicalEvent'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Events'
        },
        afib_probability: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/TimeSeriesPoint'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Afib Probability'
        },
        afib_probability_recording: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Afib Probability Recording'
        },
        line_stats: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/LineStats'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Line Stats'
        },
        delta_color_rule: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DeltaColorRule'
                },
                {
                    type: 'null'
                }
            ]
        },
        last_value_threshold_gt: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Value Threshold Gt'
        }
    },
    type: 'object',
    required: [
        'plot_type'
    ],
    title: 'PlotData',
    description: 'Container for plot data.'
} as const;

export const Potassium_binderSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Potassium binder',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Potassium binder'
} as const;

export const PreprocessAlgorithmResultSchema = {
    properties: {
        algorithm_info: {
            $ref: '#/components/schemas/AlgorithmInfo'
        },
        dataset: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGDatasetMetadata'
                },
                {
                    $ref: '#/components/schemas/ECGDatasetMetadata'
                },
                {
                    $ref: '#/components/schemas/PatientDatasetMetadata'
                }
            ],
            title: 'Dataset'
        },
        started: {
            type: 'string',
            format: 'date-time',
            title: 'Started'
        },
        execution_time: {
            type: 'number',
            title: 'Execution Time'
        },
        computed: {
            type: 'boolean',
            title: 'Computed'
        },
        results: {
            additionalProperties: {
                items: {
                    additionalProperties: true,
                    type: 'object'
                },
                type: 'array'
            },
            type: 'object',
            title: 'Results'
        }
    },
    type: 'object',
    required: [
        'algorithm_info',
        'dataset',
        'started',
        'execution_time',
        'computed',
        'results'
    ],
    title: 'PreprocessAlgorithmResult'
} as const;

export const PreregisterPatientSchemaSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            type: 'string',
            const: 'patient',
            title: 'Role',
            default: 'patient'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        expiry_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Expiry Timedelta',
            default: 'P1D'
        },
        welcome_email_type: {
            type: 'string',
            enum: [
                'patient_in_person',
                'patient_remote_send'
            ],
            title: 'Welcome Email Type'
        },
        connected_patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Connected Patient Id'
        },
        return_qr_code: {
            type: 'boolean',
            title: 'Return Qr Code',
            default: false
        }
    },
    type: 'object',
    required: [
        'username',
        'email',
        'welcome_email_type',
        'connected_patient_id'
    ],
    title: 'PreregisterPatientSchema'
} as const;

export const PreregisterPhysicianSchemaSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            type: 'string',
            const: 'physician',
            title: 'Role',
            default: 'physician'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        expiry_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Expiry Timedelta',
            default: 'P1D'
        },
        welcome_email_type: {
            type: 'string',
            const: 'minimal',
            title: 'Welcome Email Type',
            default: 'minimal'
        },
        visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patient Ids'
        }
    },
    type: 'object',
    required: [
        'username',
        'email'
    ],
    title: 'PreregisterPhysicianSchema'
} as const;

export const PreregisterUserResponseSchema = {
    properties: {
        new_user: {
            $ref: '#/components/schemas/UserResponse'
        },
        qr_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^data:image/png;base64,.*'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Qr Code'
        },
        ses_send_mail_task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 32,
                    minLength: 32
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ses Send Mail Task Id'
        }
    },
    type: 'object',
    required: [
        'new_user'
    ],
    title: 'PreregisterUserResponse'
} as const;

export const PreviousHfhSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Previous HFH',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PreviousHfhSchemaResponse'
} as const;

export const PreviousStroke_TiaSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Previous stroke/TIA',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PreviousStroke/TiaSchemaResponse'
} as const;

export const ProposedScheduleStatusSchema = {
    type: 'integer',
    enum: [
        1,
        2,
        3,
        4,
        5
    ],
    title: 'ProposedScheduleStatus'
} as const;

export const ProposedStudyVisitScheduleSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_location_type: {
            $ref: '#/components/schemas/StudyVisitLocation'
        },
        exact_date: {
            type: 'string',
            format: 'date',
            title: 'Exact Date'
        },
        exact_date_status: {
            $ref: '#/components/schemas/ExactDateStatus'
        },
        corrected_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Corrected Date'
        },
        min_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Min Date'
        },
        max_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Max Date'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'visit_type',
        'visit_location_type',
        'exact_date',
        'exact_date_status'
    ],
    title: 'ProposedStudyVisitSchedule'
} as const;

export const PublicServiceHealthResponseSchema = {
    properties: {
        status: {
            $ref: '#/components/schemas/ServiceHealth'
        },
        latency_ms: {
            type: 'number',
            minimum: 0,
            title: 'Latency Ms'
        },
        database: {
            $ref: '#/components/schemas/DBHealth'
        },
        redis_cache: {
            $ref: '#/components/schemas/BaseHealth'
        },
        taskiq_backend: {
            $ref: '#/components/schemas/BaseHealth'
        },
        taskiq_broker: {
            $ref: '#/components/schemas/RedisStreamHealth'
        },
        taskiq_ocr_broker: {
            $ref: '#/components/schemas/RedisStreamHealth'
        },
        server_time_utc: {
            type: 'string',
            format: 'date-time',
            title: 'Server Time Utc'
        }
    },
    type: 'object',
    required: [
        'status',
        'latency_ms',
        'database',
        'redis_cache',
        'taskiq_backend',
        'taskiq_broker',
        'taskiq_ocr_broker',
        'server_time_utc'
    ],
    title: 'PublicServiceHealthResponse'
} as const;

export const PublishedStudyTextResponseSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType',
            default: 'telemonitoring'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        study_text: {
            type: 'string',
            title: 'Study Text'
        },
        study_text_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StudyTextLanguage-Output'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Study Text Language'
        },
        study_text_markup: {
            $ref: '#/components/schemas/StudyTextMarkup'
        },
        study_text_version: {
            type: 'string',
            title: 'Study Text Version'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        content_hash: {
            type: 'string',
            title: 'Content Hash'
        },
        study_text_template: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Text Template'
        },
        patient_specific: {
            type: 'boolean',
            title: 'Patient Specific',
            default: false
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        dry_run: {
            type: 'boolean',
            title: 'Dry Run'
        },
        patients_to_resign: {
            type: 'integer',
            minimum: 0,
            title: 'Patients To Resign'
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'study_text',
        'study_text_language',
        'study_text_markup',
        'study_text_version',
        'uuid',
        'content_hash',
        'created_at',
        'updated_at',
        'dry_run',
        'patients_to_resign'
    ],
    title: 'PublishedStudyTextResponse'
} as const;

export const PurSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'PuR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PurSchemaResponse'
} as const;

export const PushTokenRequestSchema = {
    properties: {
        expo_push_token: {
            type: 'string',
            pattern: '^ExponentPushToken\\[.+\\]$',
            title: 'Expo Push Token',
            description: 'Expo push token'
        },
        platform: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SupportedPushNotificationPlatform'
                },
                {
                    type: 'null'
                }
            ]
        },
        platform_version: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Platform Version'
        },
        device_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Device Name'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'expo_push_token'
    ],
    title: 'PushTokenRequest',
    description: 'Request model for push token registration/unregistration.'
} as const;

export const QLReportFormatSchema = {
    type: 'string',
    enum: [
        'html',
        'pdf',
        'json'
    ],
    title: 'QLReportFormat'
} as const;

export const QualityBandSchema = {
    properties: {
        t0: {
            type: 'number',
            title: 'T0'
        },
        t1: {
            type: 'number',
            title: 'T1'
        },
        level: {
            type: 'number',
            title: 'Level'
        },
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        }
    },
    type: 'object',
    required: [
        't0',
        't1',
        'level'
    ],
    title: 'QualityBand',
    description: 'A per-sample quality shading span on a raw signal plot.'
} as const;

export const QualityFlagInfoSchema = {
    properties: {
        flag: {
            type: 'integer',
            title: 'Flag'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        level: {
            $ref: '#/components/schemas/QualityLevel'
        }
    },
    type: 'object',
    required: [
        'flag',
        'name',
        'level'
    ],
    title: 'QualityFlagInfo',
    description: 'A PPG/ECG quality flag with its name and severity level.'
} as const;

export const QualityLevelSchema = {
    type: 'string',
    enum: [
        'ok',
        'warn',
        'bad'
    ],
    title: 'QualityLevel',
    description: 'Severity of a quality flag; the frontend owns the palette.'
} as const;

export const QuestionId_InputSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        question_id: {
            type: 'string',
            title: 'Question Id'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'question_id'
    ],
    title: 'QuestionId'
} as const;

export const QuestionId_OutputSchema = {
    type: 'string'
} as const;

export const QuestionOptionSchema = {
    properties: {
        option_id: {
            type: 'string',
            description: 'Question option ID as `${questionnaire_id}-${question}-${option}`.'
        },
        option_text: {
            type: 'string',
            title: 'Option Text'
        }
    },
    type: 'object',
    required: [
        'option_id',
        'option_text'
    ],
    title: 'QuestionOption'
} as const;

export const QuestionOptionId_InputSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        question_id: {
            type: 'string',
            title: 'Question Id'
        },
        option_id: {
            type: 'string',
            title: 'Option Id'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'question_id',
        'option_id'
    ],
    title: 'QuestionOptionId'
} as const;

export const QuestionOptionId_OutputSchema = {
    type: 'string'
} as const;

export const QuestionnaireSchema = {
    properties: {
        name: {
            type: 'string',
            title: 'Name'
        },
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        language: {
            type: 'string',
            enum: [
                'ar',
                'az',
                'cs',
                'da',
                'de',
                'el',
                'en',
                'es',
                'fr',
                'he',
                'hi',
                'hu',
                'it',
                'ja',
                'lt',
                'lv',
                'nb',
                'pl',
                'pt',
                'ro',
                'ru',
                'sk',
                'sv',
                'tr',
                'uk',
                'zh-Hans',
                'zh-Hant'
            ],
            title: 'SupportedLanguages',
            description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
            'x-seerlinq-languages': [
                {
                    alpha2: 'ar',
                    alpha3: 'ara',
                    code: 'ar',
                    dir: 'rtl',
                    name: 'Arabic',
                    native_name: 'العربية',
                    regions: [
                        'SA'
                    ]
                },
                {
                    alpha2: 'az',
                    alpha3: 'aze',
                    code: 'az',
                    dir: 'ltr',
                    name: 'Azerbaijani',
                    native_name: 'Azərbaycan dili',
                    regions: []
                },
                {
                    alpha2: 'cs',
                    alpha3: 'ces',
                    code: 'cs',
                    dir: 'ltr',
                    name: 'Czech',
                    native_name: 'Čeština',
                    regions: []
                },
                {
                    alpha2: 'da',
                    alpha3: 'dan',
                    code: 'da',
                    dir: 'ltr',
                    name: 'Danish',
                    native_name: 'Dansk',
                    regions: []
                },
                {
                    alpha2: 'de',
                    alpha3: 'deu',
                    code: 'de',
                    dir: 'ltr',
                    name: 'German',
                    native_name: 'Deutsch',
                    regions: [
                        'DE',
                        'AT'
                    ]
                },
                {
                    alpha2: 'el',
                    alpha3: 'ell',
                    code: 'el',
                    dir: 'ltr',
                    name: 'Greek',
                    native_name: 'Ελληνικά',
                    regions: []
                },
                {
                    alpha2: 'en',
                    alpha3: 'eng',
                    code: 'en',
                    dir: 'ltr',
                    name: 'English',
                    native_name: 'English',
                    regions: [
                        'GB'
                    ]
                },
                {
                    alpha2: 'es',
                    alpha3: 'spa',
                    code: 'es',
                    dir: 'ltr',
                    name: 'Spanish',
                    native_name: 'Español',
                    regions: [
                        'ES'
                    ]
                },
                {
                    alpha2: 'fr',
                    alpha3: 'fra',
                    code: 'fr',
                    dir: 'ltr',
                    name: 'French',
                    native_name: 'Français',
                    regions: [
                        'FR'
                    ]
                },
                {
                    alpha2: 'he',
                    alpha3: 'heb',
                    code: 'he',
                    dir: 'rtl',
                    name: 'Hebrew',
                    native_name: 'עברית',
                    regions: []
                },
                {
                    alpha2: 'hi',
                    alpha3: 'hin',
                    code: 'hi',
                    dir: 'ltr',
                    name: 'Hindi',
                    native_name: 'हिन्दी',
                    regions: []
                },
                {
                    alpha2: 'hu',
                    alpha3: 'hun',
                    code: 'hu',
                    dir: 'ltr',
                    name: 'Hungarian',
                    native_name: 'Magyar',
                    regions: []
                },
                {
                    alpha2: 'it',
                    alpha3: 'ita',
                    code: 'it',
                    dir: 'ltr',
                    name: 'Italian',
                    native_name: 'Italiano',
                    regions: []
                },
                {
                    alpha2: 'ja',
                    alpha3: 'jpn',
                    code: 'ja',
                    dir: 'ltr',
                    name: 'Japanese',
                    native_name: '日本語',
                    regions: []
                },
                {
                    alpha2: 'lt',
                    alpha3: 'lit',
                    code: 'lt',
                    dir: 'ltr',
                    name: 'Lithuanian',
                    native_name: 'Lietuvių',
                    regions: []
                },
                {
                    alpha2: 'lv',
                    alpha3: 'lav',
                    code: 'lv',
                    dir: 'ltr',
                    name: 'Latvian',
                    native_name: 'Latviešu',
                    regions: []
                },
                {
                    alpha2: 'nb',
                    alpha3: 'nob',
                    code: 'nb',
                    dir: 'ltr',
                    name: 'Norwegian Bokmål',
                    native_name: 'Norsk bokmål',
                    regions: []
                },
                {
                    alpha2: 'pl',
                    alpha3: 'pol',
                    code: 'pl',
                    dir: 'ltr',
                    name: 'Polish',
                    native_name: 'Polski',
                    regions: []
                },
                {
                    alpha2: 'pt',
                    alpha3: 'por',
                    code: 'pt',
                    dir: 'ltr',
                    name: 'Portuguese',
                    native_name: 'Português',
                    regions: [
                        'PT'
                    ]
                },
                {
                    alpha2: 'ro',
                    alpha3: 'ron',
                    code: 'ro',
                    dir: 'ltr',
                    name: 'Romanian',
                    native_name: 'Română',
                    regions: []
                },
                {
                    alpha2: 'ru',
                    alpha3: 'rus',
                    code: 'ru',
                    dir: 'ltr',
                    name: 'Russian',
                    native_name: 'Русский',
                    regions: []
                },
                {
                    alpha2: 'sk',
                    alpha3: 'slk',
                    code: 'sk',
                    dir: 'ltr',
                    name: 'Slovak',
                    native_name: 'Slovenčina',
                    regions: []
                },
                {
                    alpha2: 'sv',
                    alpha3: 'swe',
                    code: 'sv',
                    dir: 'ltr',
                    name: 'Swedish',
                    native_name: 'Svenska',
                    regions: []
                },
                {
                    alpha2: 'tr',
                    alpha3: 'tur',
                    code: 'tr',
                    dir: 'ltr',
                    name: 'Turkish',
                    native_name: 'Türkçe',
                    regions: []
                },
                {
                    alpha2: 'uk',
                    alpha3: 'ukr',
                    code: 'uk',
                    dir: 'ltr',
                    name: 'Ukrainian',
                    native_name: 'Українська',
                    regions: []
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hans',
                    dir: 'ltr',
                    name: 'Chinese (Simplified)',
                    native_name: '简体中文',
                    regions: [
                        'CN'
                    ]
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hant',
                    dir: 'ltr',
                    name: 'Chinese (Traditional)',
                    native_name: '繁體中文',
                    regions: [
                        'TW'
                    ]
                }
            ]
        },
        version: {
            type: 'string',
            title: 'Version'
        },
        questions: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/DichotomousQuestion'
                    },
                    {
                        $ref: '#/components/schemas/SingleChoiceQuestion'
                    },
                    {
                        $ref: '#/components/schemas/MultiChoiceQuestion'
                    },
                    {
                        $ref: '#/components/schemas/OrdinalQuestion'
                    },
                    {
                        $ref: '#/components/schemas/ContinuousQuestion'
                    },
                    {
                        $ref: '#/components/schemas/OpenTextQuestion'
                    }
                ],
                discriminator: {
                    propertyName: 'question_type',
                    mapping: {
                        continuous: '#/components/schemas/ContinuousQuestion',
                        dichotomous: '#/components/schemas/DichotomousQuestion',
                        open_text: '#/components/schemas/OpenTextQuestion',
                        ordinal: '#/components/schemas/OrdinalQuestion',
                        polytomous_multi_choice: '#/components/schemas/MultiChoiceQuestion',
                        polytomous_single_choice: '#/components/schemas/SingleChoiceQuestion'
                    }
                }
            },
            type: 'array',
            minItems: 1,
            title: 'Questions'
        }
    },
    type: 'object',
    required: [
        'name',
        'questionnaire_id',
        'language',
        'version',
        'questions'
    ],
    title: 'Questionnaire'
} as const;

export const QuestionnaireAutomatonSettingsResponseSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        first_when_active: {
            type: 'boolean',
            title: 'First When Active'
        },
        filling_frequency: {
            items: {
                $ref: '#/components/schemas/FrequencyIntervalItem'
            },
            type: 'array',
            minItems: 1,
            title: 'Filling Frequency'
        },
        first_after_active: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        stop_after_n: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Stop After N'
        },
        stop_after_time_from_first: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        skippable: {
            type: 'boolean',
            title: 'Skippable',
            default: false
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'monitoring_type',
        'first_when_active',
        'filling_frequency',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'QuestionnaireAutomatonSettingsResponse'
} as const;

export const QuestionnaireStatsResponseSchema = {
    anyOf: [
        {
            type: 'string'
        },
        {
            type: 'number'
        },
        {
            type: 'integer'
        },
        {
            items: {
                type: 'string'
            },
            type: 'array'
        }
    ]
} as const;

export const QuestionnaireTypesSchema = {
    type: 'string',
    enum: [
        'dummy',
        'eq_5d_5l',
        'customer_feedback'
    ],
    title: 'QuestionnaireTypes'
} as const;

export const QueueTypesSchema = {
    type: 'string',
    enum: [
        'low',
        'mid'
    ],
    title: 'QueueTypes'
} as const;

export const QuickLookActionSchema = {
    properties: {
        task: {
            type: 'string',
            title: 'Task'
        },
        ppg_uuids: {
            items: {
                type: 'string',
                format: 'uuid4'
            },
            type: 'array',
            title: 'Ppg Uuids',
            default: []
        }
    },
    type: 'object',
    required: [
        'task'
    ],
    title: 'QuickLookAction',
    description: 'QuickLook submission the frontend can trigger for a session.'
} as const;

export const QuickLookReportSchema = {
    properties: {
        report_id: {
            type: 'string',
            title: 'Report Id'
        },
        report_type: {
            type: 'string',
            enum: [
                'ppg_timeseries_ql',
                'ppg_auc_annotated_ql',
                'ppg_beats_investigation_ql',
                'ppg_afib_ql',
                'ecg_timeseries_ql',
                'hf_dri_timeseries_ql',
                'lvfp_timeseries_ql',
                'heartcore_report',
                'heartcore_master_ql'
            ],
            title: 'Report Type'
        },
        title: {
            type: 'string',
            title: 'Title'
        },
        patient_info: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientInfo'
                },
                {
                    type: 'null'
                }
            ]
        },
        algorithm_name: {
            type: 'string',
            title: 'Algorithm Name'
        },
        algorithm_version: {
            type: 'string',
            title: 'Algorithm Version'
        },
        generated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Generated At'
        },
        sections: {
            items: {
                $ref: '#/components/schemas/SectionContent'
            },
            type: 'array',
            title: 'Sections'
        },
        generated_at_unix: {
            type: 'integer',
            title: 'Generated At Unix',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'report_type',
        'title',
        'algorithm_name',
        'algorithm_version',
        'generated_at',
        'sections',
        'generated_at_unix'
    ],
    title: 'QuickLookReport',
    description: 'Complete QuickLook report.'
} as const;

export const QuickLookSectionSchema = {
    type: 'string',
    enum: [
        'dri',
        'lvfp',
        'lvfp_median',
        'clinical_events',
        'heart_rate',
        'heart_rate_weekly',
        'blood_pressure',
        'blood_pressure_weekly',
        'weight',
        'symptoms',
        'spo2',
        'afib'
    ],
    title: 'QuickLookSection',
    description: 'Canonical section identifiers for patient-level QuickLook reports.\n\nShared vocabulary between the algorithm workers (`codebase`, which renders\nonly the sections it is told to) and the API (`db-api`, which computes the\nper-user allowlist: permitted sections minus the user\'s skip preference).'
} as const;

export const QuickLookTasksSchema = {
    type: 'string',
    enum: [
        'ppg_timeseries_ql',
        'ppg_auc_annotated_ql',
        'ppg_beats_investigation_ql',
        'ppg_afib_ql',
        'hf_dri_timeseries_ql',
        'lvfp_timeseries_ql',
        'heartcore_report',
        'heartcore_master_ql'
    ],
    title: 'QuickLookTasks'
} as const;

export const RAAS_inhibitors__ACE_inhibitorsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'RAAS inhibitors: ACE inhibitors',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'RAAS inhibitors: ACE inhibitors'
} as const;

export const RAAS_inhibitors__ARBSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'RAAS inhibitors: ARB',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'RAAS inhibitors: ARB'
} as const;

export const RAAS_inhibitors__ARNISchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'RAAS inhibitors: ARNI',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'RAAS inhibitors: ARNI'
} as const;

export const RaceSchema = {
    type: 'string',
    enum: [
        'Caucasian',
        'Asian',
        'American Indian / Alaska Native',
        'Black or African American',
        'Hawaiian or Other Pacific Islander',
        'More Than One Race',
        'Arabic',
        'Unknown / Not Reported'
    ],
    title: 'Race'
} as const;

export const RebuildAlertsResponseSchema = {
    properties: {
        task_ids: {
            additionalProperties: {
                type: 'string'
            },
            type: 'object',
            title: 'Task Ids'
        }
    },
    type: 'object',
    required: [
        'task_ids'
    ],
    title: 'RebuildAlertsResponse'
} as const;

export const RebuildAlertsSchemaSchema = {
    properties: {
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            minItems: 1,
            uniqueItems: true,
            title: 'Patient Ids'
        },
        alert_variable: {
            type: 'string',
            title: 'Alert Variable'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'patient_ids',
        'alert_variable'
    ],
    title: 'RebuildAlertsSchema'
} as const;

export const RedisStreamHealthSchema = {
    properties: {
        status: {
            $ref: '#/components/schemas/ServiceHealth'
        },
        latency_ms: {
            type: 'number',
            minimum: 0,
            title: 'Latency Ms'
        },
        queue_length: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Queue Length'
        }
    },
    type: 'object',
    required: [
        'status',
        'latency_ms',
        'queue_length'
    ],
    title: 'RedisStreamHealth'
} as const;

export const ReferenceBandSchema = {
    properties: {
        orientation: {
            type: 'string',
            enum: [
                'horizontal',
                'vertical'
            ],
            title: 'Orientation'
        },
        start: {
            type: 'number',
            title: 'Start'
        },
        end: {
            type: 'number',
            title: 'End'
        },
        label: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Label'
        }
    },
    type: 'object',
    required: [
        'orientation',
        'start',
        'end'
    ],
    title: 'ReferenceBand',
    description: 'A constant reference band/zone.'
} as const;

export const ReferenceLineSchema = {
    properties: {
        orientation: {
            type: 'string',
            enum: [
                'horizontal',
                'vertical'
            ],
            title: 'Orientation'
        },
        value: {
            type: 'number',
            title: 'Value'
        },
        label: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Label'
        },
        applies_to: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Applies To'
        }
    },
    type: 'object',
    required: [
        'orientation',
        'value'
    ],
    title: 'ReferenceLine',
    description: 'A constant reference line.'
} as const;

export const RefreshResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        access_token: {
            type: 'string',
            title: 'Access Token'
        },
        refresh_token: {
            type: 'string',
            title: 'Refresh Token'
        }
    },
    type: 'object',
    required: [
        'status',
        'access_token',
        'refresh_token'
    ],
    title: 'RefreshResponse'
} as const;

export const RegionExtractRequestSchema = {
    properties: {
        page: {
            type: 'integer',
            minimum: 1,
            title: 'Page'
        },
        x0: {
            type: 'number',
            minimum: 0,
            title: 'X0'
        },
        y0: {
            type: 'number',
            minimum: 0,
            title: 'Y0'
        },
        x1: {
            type: 'number',
            minimum: 0,
            title: 'X1'
        },
        y1: {
            type: 'number',
            minimum: 0,
            title: 'Y1'
        },
        text: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Text'
        }
    },
    type: 'object',
    required: [
        'page',
        'x0',
        'y0',
        'x1',
        'y1'
    ],
    title: 'RegionExtractRequest',
    description: 'A reviewer-selected region of the report to extract from, in the same\nPDF-point coordinate space as ``BBox``.\n\n``text`` is set when the frontend already holds the selection — digital\nPDFs expose a text layer, so the browser knows exactly what was\nhighlighted and sending it avoids re-resolving server-side. Leave it unset\nfor scanned pages (a drawn rectangle over an image): the region text is\nthen resolved from the page itself.'
} as const;

export const RegionExtractResultSchema = {
    properties: {
        extraction: {
            $ref: '#/components/schemas/ExtractionResult'
        },
        entity_ids: {
            $ref: '#/components/schemas/ExtractionEntityIds'
        },
        bboxes: {
            $ref: '#/components/schemas/ExtractionBBoxes'
        },
        entity_confidence: {
            $ref: '#/components/schemas/ExtractionEntityConfidence'
        },
        duplicates: {
            $ref: '#/components/schemas/ExtractionDuplicates'
        },
        region_text: {
            type: 'string',
            title: 'Region Text'
        },
        ocr_used: {
            type: 'boolean',
            title: 'Ocr Used'
        },
        text_truncated: {
            type: 'boolean',
            title: 'Text Truncated',
            default: false
        }
    },
    type: 'object',
    required: [
        'extraction',
        'region_text',
        'ocr_used'
    ],
    title: 'RegionExtractResult',
    description: 'Candidate entities found in a selected region, for the reviewer to approve.\n\nDeliberately not persisted here: the frontend merges approved candidates\ninto the draft (``entity_origin`` ``"region"``) and saves them through the\ndraft endpoint, so this endpoint adds no storage and no extra ingest path.\n``bboxes`` anchors each candidate to the span its quote was located at,\nfalling back to the selection itself when the quote cannot be placed.\n``region_text`` is echoed back so the UI can show what was actually read\n— the honest explanation when a selection yields nothing.'
} as const;

export const RegionSchemaSchema = {
    properties: {
        region_enum_value: {
            type: 'integer',
            minimum: 0,
            title: 'Region Enum Value'
        },
        region_name: {
            type: 'string',
            title: 'Region Name'
        },
        region_code: {
            type: 'string',
            title: 'Region Code'
        }
    },
    type: 'object',
    required: [
        'region_enum_value',
        'region_name',
        'region_code'
    ],
    title: 'RegionSchema'
} as const;

export const RelativeDeltaModelSchema = {
    properties: {
        years: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Years'
        },
        months: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Months'
        },
        days: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Days'
        },
        hours: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Hours'
        },
        minutes: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Minutes'
        },
        seconds: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seconds'
        },
        microseconds: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Microseconds'
        }
    },
    type: 'object',
    title: 'RelativeDeltaModel',
    description: 'Simple `relativedelta` serializer.'
} as const;

export const ReportTypeSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'ReportType',
    description: ' - 0: medical_record\n - 1: hospitalization_record\n - 2: ecg\n - 3: labs\n - 4: single_exam',
    'x-enum-varnames': [
        'medical_record',
        'hospitalization_record',
        'ecg',
        'labs',
        'single_exam'
    ]
} as const;

export const RequestTokenLoginResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        message: {
            type: 'string',
            title: 'Message'
        }
    },
    type: 'object',
    required: [
        'status',
        'message'
    ],
    title: 'RequestTokenLoginResponse'
} as const;

export const ResetPasswordSchemaSchema = {
    properties: {
        token: {
            type: 'string',
            title: 'Token'
        },
        expiry_token: {
            type: 'string',
            minLength: 1,
            title: 'Expiry Token'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'token',
        'expiry_token'
    ],
    title: 'ResetPasswordSchema'
} as const;

export const ResetUserResponseSchema = {
    properties: {
        password_reset: {
            type: 'boolean',
            const: true,
            title: 'Password Reset',
            default: true
        },
        reset_mail_sent: {
            type: 'boolean',
            title: 'Reset Mail Sent'
        },
        ses_send_mail_task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 32,
                    minLength: 32
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ses Send Mail Task Id'
        }
    },
    type: 'object',
    required: [
        'reset_mail_sent',
        'ses_send_mail_task_id'
    ],
    title: 'ResetUserResponse'
} as const;

export const RespiratoryRateSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'respiratory rate',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 70,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'bpm',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RespiratoryRateSchemaResponse'
} as const;

export const ResultPersistenceAlgorithmResultSchema = {
    properties: {
        algorithm_info: {
            $ref: '#/components/schemas/AlgorithmInfo'
        },
        dataset: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGDatasetMetadata'
                },
                {
                    $ref: '#/components/schemas/ECGDatasetMetadata'
                },
                {
                    $ref: '#/components/schemas/PatientDatasetMetadata'
                }
            ],
            title: 'Dataset'
        },
        started: {
            type: 'string',
            format: 'date-time',
            title: 'Started'
        },
        execution_time: {
            type: 'number',
            title: 'Execution Time'
        },
        computed: {
            type: 'boolean',
            title: 'Computed'
        },
        saved_to_db: {
            type: 'boolean',
            title: 'Saved To Db'
        },
        computed_uuids: {
            items: {
                type: 'string',
                format: 'uuid4'
            },
            type: 'array',
            title: 'Computed Uuids'
        }
    },
    type: 'object',
    required: [
        'algorithm_info',
        'dataset',
        'started',
        'execution_time',
        'computed',
        'saved_to_db'
    ],
    title: 'ResultPersistenceAlgorithmResult'
} as const;

export const RevokeAccessTokenSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'The access token has been revoked',
            title: 'Status'
        }
    },
    type: 'object',
    required: [
        'status'
    ],
    title: 'RevokeAccessToken'
} as const;

export const RevokeRefreshTokenSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'The refresh token has been revoked',
            title: 'Status'
        }
    },
    type: 'object',
    required: [
        'status'
    ],
    title: 'RevokeRefreshToken'
} as const;

export const RhcArterialOxygenSaturationSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC arterial oxygen saturation',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcArterialOxygenSaturationSchemaResponse'
} as const;

export const RhcCardiacIndexFickSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac index - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 20,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcCardiacIndexFickSchemaResponse'
} as const;

export const RhcCardiacIndexTdSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac index - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 20,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcCardiacIndexTdSchemaResponse'
} as const;

export const RhcCardiacOutputFickSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac output - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 25,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcCardiacOutputFickSchemaResponse'
} as const;

export const RhcCardiacOutputTdSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC cardiac output - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 25,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'l/min',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcCardiacOutputTdSchemaResponse'
} as const;

export const RhcDiastolicTranspulmonaryGradientSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC diastolic transpulmonary gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcDiastolicTranspulmonaryGradientSchemaResponse'
} as const;

export const RhcMixedVenousOxygenSaturationSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC mixed venous oxygen saturation',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcMixedVenousOxygenSaturationSchemaResponse'
} as const;

export const RhcPulmonaryArterialComplianceSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary arterial compliance',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml/mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryArterialComplianceSchemaResponse'
} as const;

export const RhcPulmonaryArteryPressureDiastolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary artery pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryArteryPressureDiastolicSchemaResponse'
} as const;

export const RhcPulmonaryArteryPressureMeanSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary artery pressure mean',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryArteryPressureMeanSchemaResponse'
} as const;

export const RhcPulmonaryArteryPressureSystolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary artery pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 150,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryArteryPressureSystolicSchemaResponse'
} as const;

export const RhcPulmonaryCapillaryWedgePressureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary capillary wedge pressure',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryCapillaryWedgePressureSchemaResponse'
} as const;

export const RhcPulmonaryVascularResistanceFickSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary vascular resistance - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 30,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryVascularResistanceFickSchemaResponse'
} as const;

export const RhcPulmonaryVascularResistanceIndexSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary vascular resistance index',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 20,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryVascularResistanceIndexSchemaResponse'
} as const;

export const RhcPulmonaryVascularResistanceTdSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC pulmonary vascular resistance - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 30,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcPulmonaryVascularResistanceTdSchemaResponse'
} as const;

export const RhcRightAtrialPressureMeanSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC right atrial pressure mean',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcRightAtrialPressureMeanSchemaResponse'
} as const;

export const RhcStrokeVolumeFickSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC stroke volume - Fick',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 400,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcStrokeVolumeFickSchemaResponse'
} as const;

export const RhcStrokeVolumeIndexSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC stroke volume index',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml/m2',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcStrokeVolumeIndexSchemaResponse'
} as const;

export const RhcStrokeVolumeTdSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC stroke volume - TD',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 400,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcStrokeVolumeTdSchemaResponse'
} as const;

export const RhcSystemicBloodPressureDiastolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC systemic blood pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 300,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcSystemicBloodPressureDiastolicSchemaResponse'
} as const;

export const RhcSystemicBloodPressureSystolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC systemic blood pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcSystemicBloodPressureSystolicSchemaResponse'
} as const;

export const RhcTotalPulmonaryResistanceSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC total pulmonary resistance',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 30,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'WU',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcTotalPulmonaryResistanceSchemaResponse'
} as const;

export const RhcTranspulmonaryGradientSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC transpulmonary gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcTranspulmonaryGradientSchemaResponse'
} as const;

export const RhcVentricularPressureDiastolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC ventricular pressure diastolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcVentricularPressureDiastolicSchemaResponse'
} as const;

export const RhcVentricularPressureSystolicSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RHC ventricular pressure systolic',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 200,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RhcVentricularPressureSystolicSchemaResponse'
} as const;

export const RvspSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'RVSP',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'RvspSchemaResponse'
} as const;

export const SGLT2_inhibitorsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'SGLT2 inhibitors',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'SGLT2 inhibitors'
} as const;

export const SamsungWatchDeviceStatusSchema = {
    properties: {
        signal_gaps: {
            additionalProperties: {
                $ref: '#/components/schemas/SignalGaps'
            },
            type: 'object',
            title: 'Signal Gaps'
        }
    },
    type: 'object',
    required: [
        'signal_gaps'
    ],
    title: 'SamsungWatchDeviceStatus',
    description: 'Each wavelength arrives on its own timestamp stream, so gaps are per\nwavelength.'
} as const;

export const ScheduleCheckListResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        schedule_checks: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/FullMeasurementScheduleCheckResponse'
                    },
                    {
                        $ref: '#/components/schemas/SingleScheduleCheckResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Schedule Checks'
        },
        all_done: {
            type: 'boolean',
            title: 'All Done',
            readOnly: true
        },
        number_missed: {
            type: 'integer',
            minimum: 0,
            title: 'Number Missed',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'schedule_checks',
        'all_done',
        'number_missed'
    ],
    title: 'ScheduleCheckListResponse'
} as const;

export const ScheduleTypesSchema = {
    type: 'string',
    enum: [
        'medicaldata',
        'symptoms',
        'ppg',
        'ecg',
        'full_measurement'
    ],
    title: 'ScheduleTypes'
} as const;

export const ScreeningUserOutSchema = {
    properties: {
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_status: {
            $ref: '#/components/schemas/UserStatus'
        },
        has_payment_permission: {
            type: 'boolean',
            title: 'Has Payment Permission'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid',
            description: 'Physician record whose `connected_user_uuid` points at this login - read from the column, not a relationship.'
        },
        physician_in_policy: {
            type: 'boolean',
            title: 'Physician In Policy',
            description: 'Whether that physician is also on the account\'s own access policy. Visibility resolves through either link; both are written at provisioning.',
            default: false
        },
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Patient Ids'
        },
        account_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Account Uuid'
        },
        balance: {
            type: 'integer',
            minimum: 0,
            title: 'Balance'
        },
        attempts_paid: {
            type: 'integer',
            minimum: 0,
            title: 'Attempts Paid',
            description: 'Credits spent: one per flow whose result was delivered.'
        },
        measurements: {
            type: 'integer',
            minimum: 0,
            title: 'Measurements',
            description: 'PPG recordings this account stored, charged or not.'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        missing: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Missing',
            description: 'What this account still needs to work: `permission`, `physician`, `patient`, `account`.'
        }
    },
    type: 'object',
    required: [
        'user_uuid',
        'username',
        'email',
        'user_status',
        'has_payment_permission',
        'patient_ids',
        'account_uuid',
        'balance',
        'attempts_paid',
        'measurements',
        'created_at',
        'missing'
    ],
    title: 'ScreeningUserOut',
    description: 'One screening user and everything provisioning should have given them.\n`missing` is empty exactly when the account is usable end to end, so an\naccount broken after provisioning is visible at a glance.'
} as const;

export const SectionContentSchema = {
    properties: {
        section_name: {
            type: 'string',
            title: 'Section Name'
        },
        section_key: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QuickLookSection'
                },
                {
                    type: 'null'
                }
            ]
        },
        plots: {
            items: {
                $ref: '#/components/schemas/PlotData'
            },
            type: 'array',
            title: 'Plots'
        },
        tables: {
            items: {
                $ref: '#/components/schemas/DataTable'
            },
            type: 'array',
            title: 'Tables'
        },
        statistical_tests: {
            items: {
                $ref: '#/components/schemas/StatisticalTestResult'
            },
            type: 'array',
            title: 'Statistical Tests'
        },
        empty: {
            type: 'boolean',
            title: 'Empty',
            default: false
        },
        empty_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Empty Reason'
        }
    },
    type: 'object',
    required: [
        'section_name'
    ],
    title: 'SectionContent',
    description: 'Content for one report section.'
} as const;

export const SeerlinqComputedRelatedResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_measurement_quality_index: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Index'
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version'
    ],
    title: 'SeerlinqComputedRelatedResponse'
} as const;

export const SeerlinqComputedWithPPGResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppgs: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PPGDataRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppgs'
        },
        final_value: {
            type: 'number',
            title: 'Final Value',
            description: 'Return value of PPG derived data - when\n`seerlinq_measurement_quality_flag` is set to `manual_value_override`,\nit will return the manual `override_value`, otherwise return\n`measurement_value`.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'final_value'
    ],
    title: 'SeerlinqComputedWithPPGResponse'
} as const;

export const SeerlinqEmailSchema = {
    properties: {
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        full_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Full Name'
        }
    },
    type: 'object',
    required: [
        'email'
    ],
    title: 'SeerlinqEmail'
} as const;

export const SeerlinqEnvironmentsSchema = {
    type: 'string',
    enum: [
        'local',
        'test',
        'stage',
        'prod',
        'github'
    ],
    title: 'SeerlinqEnvironments'
} as const;

export const SeerlinqQueuesSchema = {
    type: 'string',
    enum: [
        'algorithms-low',
        'algorithms-mid',
        'algorithms-torch-low',
        'algorithms-torch-mid',
        'on-demand-high'
    ],
    title: 'SeerlinqQueues'
} as const;

export const SeerlinqStudyTextResponseSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType',
            default: 'telemonitoring'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        study_text: {
            type: 'string',
            title: 'Study Text'
        },
        study_text_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StudyTextLanguage-Output'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Study Text Language'
        },
        study_text_markup: {
            $ref: '#/components/schemas/StudyTextMarkup'
        },
        study_text_version: {
            type: 'string',
            title: 'Study Text Version'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        content_hash: {
            type: 'string',
            title: 'Content Hash'
        },
        study_text_template: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Text Template'
        },
        patient_specific: {
            type: 'boolean',
            title: 'Patient Specific',
            default: false
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'study_text',
        'study_text_language',
        'study_text_markup',
        'study_text_version',
        'uuid',
        'content_hash',
        'created_at',
        'updated_at'
    ],
    title: 'SeerlinqStudyTextResponse'
} as const;

export const SeerlinqTaskNamesSchema = {
    type: 'string',
    enum: [
        'dummy',
        'hf_dri',
        'hf_dri_monitor',
        'afib',
        'hr_rr',
        'hr_rr_monitor',
        'ppg_preprocess',
        'ppg_sqi',
        'artefact_removal',
        'ppg_timeseries_ql',
        'ppg_auc_annotated_ql',
        'ppg_beats_investigation_ql',
        'ppg_afib_ql',
        'ecg_timeseries_ql',
        'hf_dri_timeseries_ql',
        'lvfp_timeseries_ql',
        'heartcore_report',
        'heartcore_master_ql'
    ],
    title: 'SeerlinqTaskNames'
} as const;

export const SeerlinqWorkflowNamesSchema = {
    type: 'string',
    enum: [
        'ppg_sqi_hr_rr',
        'ppg_sqi_hf_dri',
        'ppg_sqi_afib',
        'ppg_hf_dri_monitor',
        'ppg_filter_artefact_removal',
        'ppg_filter_artefact_removal_hf_dri'
    ],
    title: 'SeerlinqWorkflowNames'
} as const;

export const SelectorSchema = {
    properties: {
        default: {
            type: 'string',
            title: 'Default'
        },
        options: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Options',
            default: []
        }
    },
    type: 'object',
    required: [
        'default'
    ],
    title: 'Selector',
    description: 'A client-side selector with its default and the options present in the\nrequested range.'
} as const;

export const SendPatientPushRequestSchema = {
    properties: {
        title: {
            type: 'string',
            maxLength: 200,
            title: 'Title',
            description: 'Notification title'
        },
        body: {
            type: 'string',
            maxLength: 500,
            title: 'Body',
            description: 'Notification body'
        },
        data: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Data'
        },
        priority: {
            $ref: '#/components/schemas/NotificationPriority',
            default: 'default'
        },
        sound: {
            $ref: '#/components/schemas/NotificationSound',
            default: 'default'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        path: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 500,
                    minLength: 1,
                    pattern: '^/(?:[\\w?&=-][\\w/?&=-]*)?$',
                    description: 'In-app route the mobile client opens when the notification is tapped, validated for shape only rather than against the app\'s routes; a second leading slash is refused, since `//host` is a destination rather than a route'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Path'
        },
        language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/LocaleTag'
                },
                {
                    type: 'null'
                }
            ],
            description: 'Language the hand-typed copy is written in, kept on every ledger row of the send; defaults to the `preferred_language` of an account the push reaches'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'title',
        'body',
        'patient_id'
    ],
    title: 'SendPatientPushRequest',
    description: 'Send to a single patient by ID (looks up the connected user\'s tokens).'
} as const;

export const SendPushNotificationRequestSchema = {
    properties: {
        title: {
            type: 'string',
            maxLength: 200,
            title: 'Title',
            description: 'Notification title'
        },
        body: {
            type: 'string',
            maxLength: 500,
            title: 'Body',
            description: 'Notification body'
        },
        data: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Data'
        },
        priority: {
            $ref: '#/components/schemas/NotificationPriority',
            default: 'default'
        },
        sound: {
            $ref: '#/components/schemas/NotificationSound',
            default: 'default'
        },
        user_uuids: {
            items: {
                type: 'string',
                format: 'uuid4'
            },
            type: 'array',
            minItems: 1,
            title: 'User Uuids'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'title',
        'body',
        'user_uuids'
    ],
    title: 'SendPushNotificationRequest',
    description: 'Send to multiple users by UUID (looks up users\' registered tokens).'
} as const;

export const SendTemplatedPushRequestSchema = {
    properties: {
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid',
            description: 'Account to notify; must be connected to a patient'
        },
        message: {
            $ref: '#/components/schemas/NotificationMessage',
            description: 'Message of `notifications.json` to render'
        },
        params: {
            additionalProperties: {
                type: 'string'
            },
            type: 'object',
            title: 'Params',
            description: 'Placeholder values the copy needs, e.g. `{"battery": "12"}`'
        },
        language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/LocaleTag'
                },
                {
                    type: 'null'
                }
            ],
            description: 'Overrides the account\'s own `preferred_language`'
        },
        force: {
            type: 'boolean',
            title: 'Force',
            description: 'Send outside the 05:00-21:59 window, so copy can be reviewed at any hour',
            default: false
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'user_uuid',
        'message'
    ],
    title: 'SendTemplatedPushRequest',
    description: 'Send one of the engine\'s own messages to a patient, bypassing the checks\nthat would normally decide whether it is due.'
} as const;

export const ServiceAccountResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        user_type: {
            type: 'string',
            const: 'service',
            title: 'User Type',
            default: 'service'
        },
        status: {
            $ref: '#/components/schemas/ServiceAccountStatus'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        owner_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Owner Uuid'
        },
        added_by: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Added By'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponse'
        },
        api_keys: {
            items: {
                $ref: '#/components/schemas/ApiKeyResponse'
            },
            type: 'array',
            title: 'Api Keys'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'username',
        'status',
        'description',
        'email',
        'owner_uuid',
        'added_by',
        'access_policy',
        'api_keys',
        'created_at',
        'updated_at'
    ],
    title: 'ServiceAccountResponse'
} as const;

export const ServiceAccountStatusSchema = {
    type: 'integer',
    enum: [
        100,
        101,
        110,
        111
    ],
    title: 'ServiceAccountStatus',
    'x-enum-varnames': [
        'not_active',
        'active',
        'owner_deactivated',
        'retired'
    ],
    description: ' - 100: not_active\n - 101: active\n - 110: owner_deactivated\n - 111: retired'
} as const;

export const ServiceHealthSchema = {
    type: 'string',
    enum: [
        'ok',
        'degraded',
        'critical'
    ],
    title: 'ServiceHealth'
} as const;

export const ServiceLoginResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        access_token: {
            type: 'string',
            title: 'Access Token'
        },
        token_type: {
            type: 'string',
            const: 'bearer',
            title: 'Token Type',
            default: 'bearer'
        },
        expires_in: {
            type: 'integer',
            title: 'Expires In'
        }
    },
    type: 'object',
    required: [
        'status',
        'access_token',
        'expires_in'
    ],
    title: 'ServiceLoginResponse'
} as const;

export const SessionKindSchema = {
    type: 'string',
    enum: [
        'full',
        'partial',
        'standalone'
    ],
    title: 'SessionKind',
    description: 'Kind of a measurement session within a day.'
} as const;

export const SetCreditsRequestSchema = {
    properties: {
        balance: {
            type: 'integer',
            maximum: 1000000,
            minimum: 0,
            title: 'Balance',
            description: 'Credits the account should hold after this call.'
        },
        note: {
            type: 'string',
            maxLength: 500,
            minLength: 3,
            title: 'Note'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'balance',
        'note'
    ],
    title: 'SetCreditsRequest',
    description: 'The balance an account should have after the call, not a change to it.'
} as const;

export const SexSchema = {
    type: 'string',
    enum: [
        'M',
        'F',
        'other'
    ],
    title: 'Sex'
} as const;

export const ShareQuickLookAnalysisResponseSchema = {
    properties: {
        shareable_link: {
            type: 'string',
            title: 'Shareable Link'
        }
    },
    type: 'object',
    required: [
        'shareable_link'
    ],
    title: 'ShareQuickLookAnalysisResponse'
} as const;

export const ShortnessOfBreathSchemaResponseSchema = {
    properties: {
        symptom_name: {
            type: 'string',
            const: 'shortness of breath',
            title: 'Symptom Name'
        },
        symptom_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Symptom Datetime'
        },
        symptom_value: {
            type: 'integer',
            maximum: 4,
            minimum: 1,
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            type: 'boolean',
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'symptom_name',
        'symptom_datetime',
        'symptom_value',
        'symptom_change_in_last_six_m',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ShortnessOfBreathSchemaResponse'
} as const;

export const ShouldFillQuestionnaireSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        should_fill: {
            type: 'boolean',
            title: 'Should Fill'
        },
        already_filled: {
            type: 'integer',
            minimum: 0,
            title: 'Already Filled'
        },
        last_filled: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Filled'
        },
        next_fill_schedule: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Next Fill Schedule'
        },
        skippable: {
            type: 'boolean',
            title: 'Skippable'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'should_fill',
        'already_filled',
        'last_filled',
        'next_fill_schedule',
        'skippable'
    ],
    title: 'ShouldFillQuestionnaire'
} as const;

export const ShouldFillQuestionnaireResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        should_fill: {
            type: 'boolean',
            title: 'Should Fill'
        },
        already_filled: {
            type: 'integer',
            minimum: 0,
            title: 'Already Filled'
        },
        last_filled: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Filled'
        },
        next_fill_schedule: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Next Fill Schedule'
        },
        skippable: {
            type: 'boolean',
            title: 'Skippable'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'questionnaire_id',
        'should_fill',
        'already_filled',
        'last_filled',
        'next_fill_schedule',
        'skippable'
    ],
    title: 'ShouldFillQuestionnaireResponse'
} as const;

export const ShouldFillQuestionnairesResponseSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        n_checked: {
            type: 'integer',
            minimum: 0,
            title: 'N Checked'
        },
        checks: {
            items: {
                $ref: '#/components/schemas/ShouldFillQuestionnaire'
            },
            type: 'array',
            title: 'Checks'
        },
        should_fill_questionnaires: {
            items: {
                $ref: '#/components/schemas/QuestionnaireTypes'
            },
            type: 'array',
            title: 'Should Fill Questionnaires',
            description: 'List of questionnaire IDs / types that patient should fill.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'n_checked',
        'checks',
        'should_fill_questionnaires'
    ],
    title: 'ShouldFillQuestionnairesResponse'
} as const;

export const SideEffectExecutionResultSchema = {
    properties: {
        side_effect_name: {
            type: 'string',
            title: 'Side Effect Name'
        },
        results: {
            $ref: '#/components/schemas/SideEffectMetrics'
        }
    },
    type: 'object',
    required: [
        'side_effect_name',
        'results'
    ],
    title: 'SideEffectExecutionResult',
    description: 'Result of a single side effect execution.\n\nside_effect_name: name of the side effect as defined in study JSON config\nresults: tagged/skipped counts for the executed side effect'
} as const;

export const SideEffectMetricsSchema = {
    properties: {
        tagged: {
            type: 'integer',
            minimum: 0,
            title: 'Tagged'
        },
        skipped: {
            type: 'integer',
            minimum: 0,
            title: 'Skipped'
        }
    },
    type: 'object',
    required: [
        'tagged',
        'skipped'
    ],
    title: 'SideEffectMetrics',
    description: 'Standard metrics returned by side effect handlers.\n\ntagged: number of items newly associated to the visit\nskipped: number of items already associated, skipped to avoid duplicates'
} as const;

export const SignalGapsSchema = {
    properties: {
        n_gaps_over_100ms: {
            type: 'integer',
            minimum: 0,
            title: 'N Gaps Over 100Ms'
        },
        max_gap_ms: {
            type: 'number',
            minimum: 0,
            title: 'Max Gap Ms'
        },
        max_gap_at_s: {
            type: 'number',
            minimum: 0,
            title: 'Max Gap At S'
        },
        total_gap_ms: {
            type: 'number',
            minimum: 0,
            title: 'Total Gap Ms'
        },
        missing_samples: {
            type: 'integer',
            minimum: 0,
            title: 'Missing Samples'
        },
        gappy: {
            type: 'boolean',
            title: 'Gappy'
        }
    },
    type: 'object',
    required: [
        'n_gaps_over_100ms',
        'max_gap_ms',
        'max_gap_at_s',
        'total_gap_ms',
        'missing_samples',
        'gappy'
    ],
    title: 'SignalGaps',
    description: 'Timestamp gaps in a raw PPG recording, measured before interpolation.\n`gappy` flags a recording with enough missing signal that interpolation\nfabricates a meaningful part of it.'
} as const;

export const SignalPointSchema = {
    properties: {
        seconds: {
            type: 'number',
            title: 'Seconds'
        },
        value: {
            type: 'number',
            title: 'Value'
        }
    },
    type: 'object',
    required: [
        'seconds',
        'value'
    ],
    title: 'SignalPoint',
    description: 'Single point in a raw PPG/ECG signal (seconds from recording start).'
} as const;

export const SignalTraceSchema = {
    properties: {
        points: {
            items: {
                $ref: '#/components/schemas/SignalPoint'
            },
            type: 'array',
            title: 'Points'
        },
        label: {
            type: 'string',
            title: 'Label'
        },
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        }
    },
    type: 'object',
    required: [
        'points',
        'label'
    ],
    title: 'SignalTrace',
    description: 'A single raw-signal trace (e.g. one wavelength/lead), with facet identity.'
} as const;

export const SimpleAccountResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        user_type: {
            type: 'string',
            enum: [
                'human',
                'service'
            ],
            title: 'User Type'
        },
        role: {
            anyOf: [
                {
                    $ref: '#/components/schemas/UserRole'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'uuid',
        'username',
        'user_type'
    ],
    title: 'SimpleAccountResponse',
    description: 'Minimal account info, valid for humans and service accounts alike.\n\nUse wherever the referenced account is not guaranteed to be human: a\nServiceAccount\'s `role` is NULL, which SimpleUserResponse requires.'
} as const;

export const SimpleAdverseEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'adverse_event',
            title: 'Event Category',
            default: 'adverse_event'
        },
        event_category_type: {
            type: 'string',
            const: 'symptomatic_hypotension',
            title: 'Event Category Type',
            default: 'symptomatic_hypotension'
        },
        event_category_details: {
            type: 'null',
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_description',
        'patient_id'
    ],
    title: 'SimpleAdverseEventSchema'
} as const;

export const SimpleEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            enum: [
                'medical_call',
                'technical_call',
                'medication_change',
                'patient_non_adherence'
            ],
            title: 'Event Category'
        },
        event_category_type: {
            type: 'null',
            title: 'Event Category Type'
        },
        event_category_details: {
            type: 'null',
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category',
        'event_description',
        'patient_id'
    ],
    title: 'SimpleEventSchema'
} as const;

export const SimpleHealthCheckResponseSchema = {
    properties: {
        message: {
            type: 'string',
            const: 'Yes, I work, good job!',
            title: 'Message'
        }
    },
    type: 'object',
    required: [
        'message'
    ],
    title: 'SimpleHealthCheckResponse'
} as const;

export const SimpleUserResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status'
    ],
    title: 'SimpleUserResponse',
    description: 'Minimal schema for returning user info as a response.'
} as const;

export const SimpleUserWithFlagsResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status'
    ],
    title: 'SimpleUserWithFlagsResponse',
    description: 'The same read for monitoring callers, who also get `status`.'
} as const;

export const SingleChoiceQuestionSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_text: {
            type: 'string',
            title: 'Question Text'
        },
        question_type: {
            type: 'string',
            const: 'polytomous_single_choice',
            title: 'Question Type',
            default: 'polytomous_single_choice'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        question_options: {
            items: {
                $ref: '#/components/schemas/QuestionOption'
            },
            type: 'array',
            minItems: 2,
            title: 'Question Options'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_text',
        'question_options'
    ],
    title: 'SingleChoiceQuestion'
} as const;

export const SingleChoiceQuestionnaireResponseSchema = {
    properties: {
        question_id: {
            type: 'string',
            description: 'Question ID as `${questionnaire_id}-${question}`.'
        },
        question_type: {
            type: 'string',
            enum: [
                'dichotomous',
                'polytomous_single_choice'
            ],
            title: 'Question Type'
        },
        question_response: {
            type: 'string',
            description: 'Question option ID as `${questionnaire_id}-${question}-${option}`.'
        }
    },
    type: 'object',
    required: [
        'question_id',
        'question_type',
        'question_response'
    ],
    title: 'SingleChoiceQuestionnaireResponse'
} as const;

export const SingleMessageStatusSchema = {
    type: 'string',
    enum: [
        'ok',
        'no_messages',
        'expired_message'
    ],
    title: 'SingleMessageStatus'
} as const;

export const SinglePatientMessageResponseSchema = {
    properties: {
        status: {
            $ref: '#/components/schemas/SingleMessageStatus'
        },
        message: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMessageResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status'
    ],
    title: 'SinglePatientMessageResponse'
} as const;

export const SingleScheduleCheckResponseSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_time: {
            type: 'string',
            format: 'time',
            title: 'Scheduled Time'
        },
        check_timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Check Timezone'
        },
        allowed_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Allowed Timedelta'
        },
        measured_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measured Datetime'
        },
        time_difference: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Time Difference',
            readOnly: true
        },
        schedule_fulfilled: {
            type: 'boolean',
            title: 'Schedule Fulfilled',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_time',
        'check_timezone',
        'allowed_timedelta',
        'measured_datetime',
        'time_difference',
        'schedule_fulfilled'
    ],
    title: 'SingleScheduleCheckResponse'
} as const;

export const SingleScheduleComplianceSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_times: {
            items: {
                type: 'string',
                format: 'time'
            },
            type: 'array',
            title: 'Scheduled Times'
        },
        total_expected: {
            type: 'integer',
            minimum: 0,
            title: 'Total Expected'
        },
        total_completed: {
            type: 'integer',
            minimum: 0,
            title: 'Total Completed'
        },
        total_pending: {
            type: 'integer',
            minimum: 0,
            title: 'Total Pending',
            default: 0
        },
        compliance_ratio: {
            type: 'number',
            title: 'Compliance Ratio'
        },
        soft_total_completed: {
            type: 'integer',
            minimum: 0,
            title: 'Soft Total Completed'
        },
        soft_compliance_ratio: {
            type: 'number',
            title: 'Soft Compliance Ratio'
        },
        soft_tolerance_days: {
            type: 'integer',
            minimum: 0,
            title: 'Soft Tolerance Days'
        },
        daily_counts: {
            items: {
                $ref: '#/components/schemas/DailyCount'
            },
            type: 'array',
            title: 'Daily Counts'
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_times',
        'total_expected',
        'total_completed',
        'compliance_ratio',
        'soft_total_completed',
        'soft_compliance_ratio',
        'soft_tolerance_days',
        'daily_counts'
    ],
    title: 'SingleScheduleCompliance'
} as const;

export const SmsEnrollBeginSchemaSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        },
        phone_number: {
            type: 'string',
            format: 'phone',
            title: 'Phone Number'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'phone_number'
    ],
    title: 'SmsEnrollBeginSchema'
} as const;

export const SmsEnrollConfirmSchemaSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'SmsEnrollConfirmSchema'
} as const;

export const SmsSendResponseSchema = {
    properties: {
        sent: {
            type: 'boolean',
            title: 'Sent'
        },
        retry_after_seconds: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Retry After Seconds'
        },
        phone_masked: {
            type: 'string',
            title: 'Phone Masked'
        },
        reason: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'cooldown',
                        'challenge_cap',
                        'daily_cap'
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reason'
        }
    },
    type: 'object',
    required: [
        'sent',
        'phone_masked'
    ],
    title: 'SmsSendResponse',
    description: '`retry_after_seconds`: seconds until the next send is accepted, None when\na cap refused it. `phone_masked`: the number the code goes to.\n`reason`: why a send was refused - `challenge_cap` clears on a new login,\nor for a signed-in enrollment `MFA_ENROLLMENT_EXPIRES_IN` minutes after\nits first send; `daily_cap` only the next day.'
} as const;

export const Spo2SchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'SpO2',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 30,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: '%',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'Spo2SchemaResponse'
} as const;

export const StatisticalTestResultSchema = {
    properties: {
        test_type: {
            type: 'string',
            enum: [
                'anova',
                'anova+posthoc',
                'ttest'
            ],
            title: 'Test Type'
        },
        test_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Test Name'
        },
        between: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Between'
        },
        within: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Within'
        },
        statistic: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Statistic'
        },
        p_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'P Value'
        },
        degrees_of_freedom: {
            anyOf: [
                {
                    items: {
                        type: 'integer'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Degrees Of Freedom'
        },
        posthoc_results: {
            anyOf: [
                {
                    items: {
                        additionalProperties: true,
                        type: 'object'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Posthoc Results'
        },
        correction_method: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'none',
                        'bonf',
                        'sidak',
                        'holm',
                        'fdr_bh',
                        'fdr_by'
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Correction Method'
        }
    },
    type: 'object',
    required: [
        'test_type'
    ],
    title: 'StatisticalTestResult',
    description: 'Result of a statistical test (pingouin output).'
} as const;

export const StrictPPGConditionsSchema = {
    type: 'string',
    enum: [
        'Condition 1',
        'Condition 2',
        'Condition 3',
        'Condition 4',
        'Condition 5',
        'Condition 6'
    ],
    title: 'StrictPPGConditions'
} as const;

export const StrokeVolumeOnEchoSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'stroke volume on echo',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 400,
            minimum: 1,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'ml',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'StrokeVolumeOnEchoSchemaResponse'
} as const;

export const StudyCategorySchema = {
    type: 'string',
    enum: [
        'observational',
        'interventional',
        'diagnostic',
        'synthesis'
    ],
    title: 'StudyCategory'
} as const;

export const StudyControlStructureSchema = {
    type: 'string',
    enum: [
        'controlled',
        'uncontrolled'
    ],
    title: 'StudyControlStructure'
} as const;

export const StudyRandomizationSchema = {
    type: 'string',
    enum: [
        'randomized',
        'non-randomized'
    ],
    title: 'StudyRandomization'
} as const;

export const StudyTemporalDimensionSchema = {
    type: 'string',
    enum: [
        'prospective',
        'retrospective',
        'cross-sectional'
    ],
    title: 'StudyTemporalDimension'
} as const;

export const StudyTextLanguage_InputSchema = {
    properties: {
        language: {
            type: 'string',
            enum: [
                'ar',
                'az',
                'cs',
                'da',
                'de',
                'el',
                'en',
                'es',
                'fr',
                'he',
                'hi',
                'hu',
                'it',
                'ja',
                'lt',
                'lv',
                'nb',
                'pl',
                'pt',
                'ro',
                'ru',
                'sk',
                'sv',
                'tr',
                'uk',
                'zh-Hans',
                'zh-Hant'
            ],
            title: 'SupportedLanguages',
            description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
            'x-seerlinq-languages': [
                {
                    alpha2: 'ar',
                    alpha3: 'ara',
                    code: 'ar',
                    dir: 'rtl',
                    name: 'Arabic',
                    native_name: 'العربية',
                    regions: [
                        'SA'
                    ]
                },
                {
                    alpha2: 'az',
                    alpha3: 'aze',
                    code: 'az',
                    dir: 'ltr',
                    name: 'Azerbaijani',
                    native_name: 'Azərbaycan dili',
                    regions: []
                },
                {
                    alpha2: 'cs',
                    alpha3: 'ces',
                    code: 'cs',
                    dir: 'ltr',
                    name: 'Czech',
                    native_name: 'Čeština',
                    regions: []
                },
                {
                    alpha2: 'da',
                    alpha3: 'dan',
                    code: 'da',
                    dir: 'ltr',
                    name: 'Danish',
                    native_name: 'Dansk',
                    regions: []
                },
                {
                    alpha2: 'de',
                    alpha3: 'deu',
                    code: 'de',
                    dir: 'ltr',
                    name: 'German',
                    native_name: 'Deutsch',
                    regions: [
                        'DE',
                        'AT'
                    ]
                },
                {
                    alpha2: 'el',
                    alpha3: 'ell',
                    code: 'el',
                    dir: 'ltr',
                    name: 'Greek',
                    native_name: 'Ελληνικά',
                    regions: []
                },
                {
                    alpha2: 'en',
                    alpha3: 'eng',
                    code: 'en',
                    dir: 'ltr',
                    name: 'English',
                    native_name: 'English',
                    regions: [
                        'GB'
                    ]
                },
                {
                    alpha2: 'es',
                    alpha3: 'spa',
                    code: 'es',
                    dir: 'ltr',
                    name: 'Spanish',
                    native_name: 'Español',
                    regions: [
                        'ES'
                    ]
                },
                {
                    alpha2: 'fr',
                    alpha3: 'fra',
                    code: 'fr',
                    dir: 'ltr',
                    name: 'French',
                    native_name: 'Français',
                    regions: [
                        'FR'
                    ]
                },
                {
                    alpha2: 'he',
                    alpha3: 'heb',
                    code: 'he',
                    dir: 'rtl',
                    name: 'Hebrew',
                    native_name: 'עברית',
                    regions: []
                },
                {
                    alpha2: 'hi',
                    alpha3: 'hin',
                    code: 'hi',
                    dir: 'ltr',
                    name: 'Hindi',
                    native_name: 'हिन्दी',
                    regions: []
                },
                {
                    alpha2: 'hu',
                    alpha3: 'hun',
                    code: 'hu',
                    dir: 'ltr',
                    name: 'Hungarian',
                    native_name: 'Magyar',
                    regions: []
                },
                {
                    alpha2: 'it',
                    alpha3: 'ita',
                    code: 'it',
                    dir: 'ltr',
                    name: 'Italian',
                    native_name: 'Italiano',
                    regions: []
                },
                {
                    alpha2: 'ja',
                    alpha3: 'jpn',
                    code: 'ja',
                    dir: 'ltr',
                    name: 'Japanese',
                    native_name: '日本語',
                    regions: []
                },
                {
                    alpha2: 'lt',
                    alpha3: 'lit',
                    code: 'lt',
                    dir: 'ltr',
                    name: 'Lithuanian',
                    native_name: 'Lietuvių',
                    regions: []
                },
                {
                    alpha2: 'lv',
                    alpha3: 'lav',
                    code: 'lv',
                    dir: 'ltr',
                    name: 'Latvian',
                    native_name: 'Latviešu',
                    regions: []
                },
                {
                    alpha2: 'nb',
                    alpha3: 'nob',
                    code: 'nb',
                    dir: 'ltr',
                    name: 'Norwegian Bokmål',
                    native_name: 'Norsk bokmål',
                    regions: []
                },
                {
                    alpha2: 'pl',
                    alpha3: 'pol',
                    code: 'pl',
                    dir: 'ltr',
                    name: 'Polish',
                    native_name: 'Polski',
                    regions: []
                },
                {
                    alpha2: 'pt',
                    alpha3: 'por',
                    code: 'pt',
                    dir: 'ltr',
                    name: 'Portuguese',
                    native_name: 'Português',
                    regions: [
                        'PT'
                    ]
                },
                {
                    alpha2: 'ro',
                    alpha3: 'ron',
                    code: 'ro',
                    dir: 'ltr',
                    name: 'Romanian',
                    native_name: 'Română',
                    regions: []
                },
                {
                    alpha2: 'ru',
                    alpha3: 'rus',
                    code: 'ru',
                    dir: 'ltr',
                    name: 'Russian',
                    native_name: 'Русский',
                    regions: []
                },
                {
                    alpha2: 'sk',
                    alpha3: 'slk',
                    code: 'sk',
                    dir: 'ltr',
                    name: 'Slovak',
                    native_name: 'Slovenčina',
                    regions: []
                },
                {
                    alpha2: 'sv',
                    alpha3: 'swe',
                    code: 'sv',
                    dir: 'ltr',
                    name: 'Swedish',
                    native_name: 'Svenska',
                    regions: []
                },
                {
                    alpha2: 'tr',
                    alpha3: 'tur',
                    code: 'tr',
                    dir: 'ltr',
                    name: 'Turkish',
                    native_name: 'Türkçe',
                    regions: []
                },
                {
                    alpha2: 'uk',
                    alpha3: 'ukr',
                    code: 'uk',
                    dir: 'ltr',
                    name: 'Ukrainian',
                    native_name: 'Українська',
                    regions: []
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hans',
                    dir: 'ltr',
                    name: 'Chinese (Simplified)',
                    native_name: '简体中文',
                    regions: [
                        'CN'
                    ]
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hant',
                    dir: 'ltr',
                    name: 'Chinese (Traditional)',
                    native_name: '繁體中文',
                    regions: [
                        'TW'
                    ]
                }
            ]
        },
        locale: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Locale'
        }
    },
    type: 'object',
    required: [
        'language'
    ],
    title: 'StudyTextLanguage'
} as const;

export const StudyTextLanguage_OutputSchema = {
    type: 'string'
} as const;

export const StudyTextMarkupSchema = {
    type: 'string',
    enum: [
        'html',
        'md'
    ],
    title: 'StudyTextMarkup'
} as const;

export const StudyVisitCalendarSchema = {
    properties: {
        studies: {
            items: {
                $ref: '#/components/schemas/ClinicalStudies'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Studies'
        },
        datetime_from: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Datetime From'
        },
        datetime_to: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Datetime To'
        },
        events: {
            items: {
                $ref: '#/components/schemas/VisitCalendarEvent'
            },
            type: 'array',
            title: 'Events'
        }
    },
    type: 'object',
    required: [
        'studies',
        'datetime_from',
        'datetime_to',
        'events'
    ],
    title: 'StudyVisitCalendar'
} as const;

export const StudyVisitLocationSchema = {
    type: 'string',
    enum: [
        'in-person',
        'remote'
    ],
    title: 'StudyVisitLocation'
} as const;

export const StudyVisitSchedule_InputSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Input'
        },
        visit_location_type: {
            $ref: '#/components/schemas/StudyVisitLocation'
        },
        exact_date: {
            type: 'string',
            format: 'date',
            title: 'Exact Date'
        },
        exact_date_status: {
            $ref: '#/components/schemas/ExactDateStatus'
        },
        corrected_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Corrected Date'
        },
        min_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Min Date'
        },
        max_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Max Date'
        },
        scheduled_date: {
            type: 'string',
            format: 'date',
            title: 'Scheduled Date'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'visit_type',
        'visit_location_type',
        'exact_date',
        'exact_date_status',
        'scheduled_date'
    ],
    title: 'StudyVisitSchedule'
} as const;

export const StudyVisitSchedule_OutputSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_location_type: {
            $ref: '#/components/schemas/StudyVisitLocation'
        },
        exact_date: {
            type: 'string',
            format: 'date',
            title: 'Exact Date'
        },
        exact_date_status: {
            $ref: '#/components/schemas/ExactDateStatus'
        },
        corrected_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Corrected Date'
        },
        min_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Min Date'
        },
        max_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Max Date'
        },
        scheduled_date: {
            type: 'string',
            format: 'date',
            title: 'Scheduled Date'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'visit_type',
        'visit_location_type',
        'exact_date',
        'exact_date_status',
        'scheduled_date'
    ],
    title: 'StudyVisitSchedule'
} as const;

export const StudyVisitTypeSchema = {
    type: 'string',
    enum: [
        'pre-screening',
        'screening',
        'baseline',
        'follow-up'
    ],
    title: 'StudyVisitType'
} as const;

export const SubmitPPGQuickLookTaskSchemaSchema = {
    properties: {
        task: {
            $ref: '#/components/schemas/QuickLookTasks'
        },
        queue_type: {
            type: 'null',
            title: 'Queue Type'
        },
        report_format: {
            $ref: '#/components/schemas/QLReportFormat',
            default: 'html'
        },
        uuids: {
            items: {
                type: 'string'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Uuids'
        }
    },
    additionalProperties: true,
    type: 'object',
    required: [
        'task',
        'uuids'
    ],
    title: 'SubmitPPGQuickLookTaskSchema'
} as const;

export const SubmitPPGTaskResponseSchema = {
    properties: {
        submitted: {
            type: 'boolean',
            title: 'Submitted'
        },
        task_kwargs: {
            additionalProperties: true,
            type: 'object',
            title: 'Task Kwargs'
        },
        task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 32
                },
                {
                    type: 'string',
                    format: 'uuid4'
                }
            ],
            title: 'Task Id'
        },
        state: {
            $ref: '#/components/schemas/CeleryTaskStates'
        },
        task_name: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        task_queue: {
            $ref: '#/components/schemas/SeerlinqQueues'
        },
        task_worker: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Task Worker'
        },
        ppg_uuids: {
            items: {
                type: 'string'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Ppg Uuids'
        }
    },
    type: 'object',
    required: [
        'submitted',
        'task_kwargs',
        'task_id',
        'state',
        'task_name',
        'task_queue',
        'task_worker',
        'ppg_uuids'
    ],
    title: 'SubmitPPGTaskResponse'
} as const;

export const SubmitPPGTaskSchemaSchema = {
    properties: {
        task: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        queue_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QueueTypes'
                },
                {
                    type: 'null'
                }
            ],
            default: 'mid'
        },
        uuids: {
            items: {
                type: 'string'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Uuids'
        }
    },
    additionalProperties: true,
    type: 'object',
    required: [
        'task',
        'uuids'
    ],
    title: 'SubmitPPGTaskSchema'
} as const;

export const SubmitPPGWorkflowResponseSchema = {
    properties: {
        submitted: {
            type: 'boolean',
            title: 'Submitted'
        },
        task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 32
                },
                {
                    type: 'string',
                    format: 'uuid4'
                }
            ],
            title: 'Task Id'
        },
        state: {
            $ref: '#/components/schemas/CeleryTaskStates'
        }
    },
    type: 'object',
    required: [
        'submitted',
        'task_id',
        'state'
    ],
    title: 'SubmitPPGWorkflowResponse'
} as const;

export const SubmitPPGWorkflowSchemaSchema = {
    properties: {
        uuids: {
            items: {
                type: 'string'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Uuids'
        },
        workflow: {
            $ref: '#/components/schemas/SeerlinqWorkflowNames'
        },
        queue_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QueueTypes'
                },
                {
                    type: 'null'
                }
            ],
            default: 'mid'
        }
    },
    type: 'object',
    required: [
        'uuids',
        'workflow'
    ],
    title: 'SubmitPPGWorkflowSchema'
} as const;

export const SubmitPatientQuickLookTaskSchemaSchema = {
    properties: {
        task: {
            $ref: '#/components/schemas/QuickLookTasks'
        },
        queue_type: {
            type: 'null',
            title: 'Queue Type'
        },
        report_format: {
            $ref: '#/components/schemas/QLReportFormat',
            default: 'html'
        },
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            uniqueItems: true,
            title: 'Patient Ids'
        }
    },
    additionalProperties: true,
    type: 'object',
    required: [
        'task',
        'patient_ids'
    ],
    title: 'SubmitPatientQuickLookTaskSchema'
} as const;

export const SubmitPatientTaskResponseSchema = {
    properties: {
        submitted: {
            type: 'boolean',
            title: 'Submitted'
        },
        task_kwargs: {
            additionalProperties: true,
            type: 'object',
            title: 'Task Kwargs'
        },
        task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 32
                },
                {
                    type: 'string',
                    format: 'uuid4'
                }
            ],
            title: 'Task Id'
        },
        state: {
            $ref: '#/components/schemas/CeleryTaskStates'
        },
        task_name: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        task_queue: {
            $ref: '#/components/schemas/SeerlinqQueues'
        },
        task_worker: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Task Worker'
        },
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            uniqueItems: true,
            title: 'Patient Ids'
        }
    },
    type: 'object',
    required: [
        'submitted',
        'task_kwargs',
        'task_id',
        'state',
        'task_name',
        'task_queue',
        'task_worker',
        'patient_ids'
    ],
    title: 'SubmitPatientTaskResponse'
} as const;

export const SubmitPatientTaskSchemaSchema = {
    properties: {
        task: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        queue_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QueueTypes'
                },
                {
                    type: 'null'
                }
            ],
            default: 'mid'
        },
        patient_ids: {
            items: {
                type: 'integer',
                exclusiveMinimum: 0
            },
            type: 'array',
            uniqueItems: true,
            title: 'Patient Ids'
        }
    },
    additionalProperties: true,
    type: 'object',
    required: [
        'task',
        'patient_ids'
    ],
    title: 'SubmitPatientTaskSchema'
} as const;

export const SupportContactSchema = {
    properties: {
        phone: {
            type: 'string',
            format: 'phone',
            title: 'Phone'
        },
        email: {
            $ref: '#/components/schemas/SeerlinqEmail'
        }
    },
    type: 'object',
    required: [
        'phone',
        'email'
    ],
    title: 'SupportContact'
} as const;

export const SupportedDiagnosisSchema = {
    properties: {
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_value: {
            $ref: '#/components/schemas/EnumDataSchema'
        }
    },
    type: 'object',
    required: [
        'data_name',
        'data_value'
    ],
    title: 'SupportedDiagnosis'
} as const;

export const SupportedLanguagesSchema = {
    type: 'string',
    enum: [
        'ar',
        'az',
        'cs',
        'da',
        'de',
        'el',
        'en',
        'es',
        'fr',
        'he',
        'hi',
        'hu',
        'it',
        'ja',
        'lt',
        'lv',
        'nb',
        'pl',
        'pt',
        'ro',
        'ru',
        'sk',
        'sv',
        'tr',
        'uk',
        'zh-Hans',
        'zh-Hant'
    ],
    title: 'SupportedLanguages',
    description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
    'x-seerlinq-languages': [
        {
            alpha2: 'ar',
            alpha3: 'ara',
            code: 'ar',
            dir: 'rtl',
            name: 'Arabic',
            native_name: 'العربية',
            regions: [
                'SA'
            ]
        },
        {
            alpha2: 'az',
            alpha3: 'aze',
            code: 'az',
            dir: 'ltr',
            name: 'Azerbaijani',
            native_name: 'Azərbaycan dili',
            regions: []
        },
        {
            alpha2: 'cs',
            alpha3: 'ces',
            code: 'cs',
            dir: 'ltr',
            name: 'Czech',
            native_name: 'Čeština',
            regions: []
        },
        {
            alpha2: 'da',
            alpha3: 'dan',
            code: 'da',
            dir: 'ltr',
            name: 'Danish',
            native_name: 'Dansk',
            regions: []
        },
        {
            alpha2: 'de',
            alpha3: 'deu',
            code: 'de',
            dir: 'ltr',
            name: 'German',
            native_name: 'Deutsch',
            regions: [
                'DE',
                'AT'
            ]
        },
        {
            alpha2: 'el',
            alpha3: 'ell',
            code: 'el',
            dir: 'ltr',
            name: 'Greek',
            native_name: 'Ελληνικά',
            regions: []
        },
        {
            alpha2: 'en',
            alpha3: 'eng',
            code: 'en',
            dir: 'ltr',
            name: 'English',
            native_name: 'English',
            regions: [
                'GB'
            ]
        },
        {
            alpha2: 'es',
            alpha3: 'spa',
            code: 'es',
            dir: 'ltr',
            name: 'Spanish',
            native_name: 'Español',
            regions: [
                'ES'
            ]
        },
        {
            alpha2: 'fr',
            alpha3: 'fra',
            code: 'fr',
            dir: 'ltr',
            name: 'French',
            native_name: 'Français',
            regions: [
                'FR'
            ]
        },
        {
            alpha2: 'he',
            alpha3: 'heb',
            code: 'he',
            dir: 'rtl',
            name: 'Hebrew',
            native_name: 'עברית',
            regions: []
        },
        {
            alpha2: 'hi',
            alpha3: 'hin',
            code: 'hi',
            dir: 'ltr',
            name: 'Hindi',
            native_name: 'हिन्दी',
            regions: []
        },
        {
            alpha2: 'hu',
            alpha3: 'hun',
            code: 'hu',
            dir: 'ltr',
            name: 'Hungarian',
            native_name: 'Magyar',
            regions: []
        },
        {
            alpha2: 'it',
            alpha3: 'ita',
            code: 'it',
            dir: 'ltr',
            name: 'Italian',
            native_name: 'Italiano',
            regions: []
        },
        {
            alpha2: 'ja',
            alpha3: 'jpn',
            code: 'ja',
            dir: 'ltr',
            name: 'Japanese',
            native_name: '日本語',
            regions: []
        },
        {
            alpha2: 'lt',
            alpha3: 'lit',
            code: 'lt',
            dir: 'ltr',
            name: 'Lithuanian',
            native_name: 'Lietuvių',
            regions: []
        },
        {
            alpha2: 'lv',
            alpha3: 'lav',
            code: 'lv',
            dir: 'ltr',
            name: 'Latvian',
            native_name: 'Latviešu',
            regions: []
        },
        {
            alpha2: 'nb',
            alpha3: 'nob',
            code: 'nb',
            dir: 'ltr',
            name: 'Norwegian Bokmål',
            native_name: 'Norsk bokmål',
            regions: []
        },
        {
            alpha2: 'pl',
            alpha3: 'pol',
            code: 'pl',
            dir: 'ltr',
            name: 'Polish',
            native_name: 'Polski',
            regions: []
        },
        {
            alpha2: 'pt',
            alpha3: 'por',
            code: 'pt',
            dir: 'ltr',
            name: 'Portuguese',
            native_name: 'Português',
            regions: [
                'PT'
            ]
        },
        {
            alpha2: 'ro',
            alpha3: 'ron',
            code: 'ro',
            dir: 'ltr',
            name: 'Romanian',
            native_name: 'Română',
            regions: []
        },
        {
            alpha2: 'ru',
            alpha3: 'rus',
            code: 'ru',
            dir: 'ltr',
            name: 'Russian',
            native_name: 'Русский',
            regions: []
        },
        {
            alpha2: 'sk',
            alpha3: 'slk',
            code: 'sk',
            dir: 'ltr',
            name: 'Slovak',
            native_name: 'Slovenčina',
            regions: []
        },
        {
            alpha2: 'sv',
            alpha3: 'swe',
            code: 'sv',
            dir: 'ltr',
            name: 'Swedish',
            native_name: 'Svenska',
            regions: []
        },
        {
            alpha2: 'tr',
            alpha3: 'tur',
            code: 'tr',
            dir: 'ltr',
            name: 'Turkish',
            native_name: 'Türkçe',
            regions: []
        },
        {
            alpha2: 'uk',
            alpha3: 'ukr',
            code: 'uk',
            dir: 'ltr',
            name: 'Ukrainian',
            native_name: 'Українська',
            regions: []
        },
        {
            alpha2: 'zh',
            alpha3: 'zho',
            code: 'zh-Hans',
            dir: 'ltr',
            name: 'Chinese (Simplified)',
            native_name: '简体中文',
            regions: [
                'CN'
            ]
        },
        {
            alpha2: 'zh',
            alpha3: 'zho',
            code: 'zh-Hant',
            dir: 'ltr',
            name: 'Chinese (Traditional)',
            native_name: '繁體中文',
            regions: [
                'TW'
            ]
        }
    ]
} as const;

export const SupportedMedicalDataSchema = {
    properties: {
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_value: {
            oneOf: [
                {
                    $ref: '#/components/schemas/FloatDataSchema'
                },
                {
                    $ref: '#/components/schemas/IntDataSchema'
                },
                {
                    $ref: '#/components/schemas/EnumDataSchema'
                }
            ],
            title: 'Data Value',
            discriminator: {
                propertyName: 'value_type',
                mapping: {
                    enum: '#/components/schemas/EnumDataSchema',
                    float: '#/components/schemas/FloatDataSchema',
                    integer: '#/components/schemas/IntDataSchema'
                }
            }
        },
        data_category: {
            $ref: '#/components/schemas/MedicalDataCategory'
        },
        primary_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Primary Unit'
        },
        alternative_units: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Units'
        },
        can_self_report: {
            type: 'boolean',
            title: 'Can Self Report',
            default: false
        },
        all_units: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'All Units',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'data_name',
        'data_value',
        'data_category',
        'all_units'
    ],
    title: 'SupportedMedicalData'
} as const;

export const SupportedMedicationSchema = {
    properties: {
        medication_group: {
            $ref: '#/components/schemas/MedicationGroup'
        },
        gdmt: {
            anyOf: [
                {
                    $ref: '#/components/schemas/GDMTInfo'
                },
                {
                    type: 'null'
                }
            ]
        },
        free_text: {
            type: 'boolean',
            title: 'Free Text',
            default: true
        },
        medications: {
            additionalProperties: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/MedicationEntry'
                    },
                    {
                        type: 'null'
                    }
                ]
            },
            type: 'object',
            title: 'Medications'
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medications'
    ],
    title: 'SupportedMedication'
} as const;

export const SupportedPushNotificationPlatformSchema = {
    type: 'string',
    enum: [
        'iOS',
        'Android'
    ],
    title: 'SupportedPushNotificationPlatform',
    description: 'Supported device platforms for push notifications.'
} as const;

export const SupportedSymptomSchema = {
    properties: {
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_value: {
            $ref: '#/components/schemas/IntDataSchema'
        },
        can_self_report: {
            type: 'boolean',
            title: 'Can Self Report',
            default: true
        }
    },
    type: 'object',
    required: [
        'data_name',
        'data_value'
    ],
    title: 'SupportedSymptom'
} as const;

export const SuppressingDiagnosisSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            title: 'Diagnosis Name'
        },
        suppressing_values: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Suppressing Values'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'suppressing_values'
    ],
    title: 'SuppressingDiagnosis',
    description: 'A supported diagnosis (with a subset of its values) whose presence\nsuppresses an alert threshold. Used to mute alerts for patients with a\ngiven comorbidity.'
} as const;

export const TOTPEnrollBeginResponseSchema = {
    properties: {
        secret: {
            type: 'string',
            title: 'Secret'
        },
        provisioning_uri: {
            type: 'string',
            title: 'Provisioning Uri'
        },
        qr_code_data_uri: {
            type: 'string',
            title: 'Qr Code Data Uri'
        },
        issuer: {
            type: 'string',
            title: 'Issuer'
        }
    },
    type: 'object',
    required: [
        'secret',
        'provisioning_uri',
        'qr_code_data_uri',
        'issuer'
    ],
    title: 'TOTPEnrollBeginResponse'
} as const;

export const TOTPEnrollBeginSchemaSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'TOTPEnrollBeginSchema'
} as const;

export const TOTPEnrollConfirmResponseSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        backup_codes: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Backup Codes'
        },
        access_token: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Access Token'
        },
        refresh_token: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Refresh Token'
        }
    },
    type: 'object',
    required: [
        'status',
        'backup_codes'
    ],
    title: 'TOTPEnrollConfirmResponse'
} as const;

export const TOTPEnrollConfirmSchemaSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'TOTPEnrollConfirmSchema'
} as const;

export const TagDiagnosesToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitDiagnosisAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagDiagnosesToClinicalStudyVisitBulk'
} as const;

export const TagEventToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitEventAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagEventToClinicalStudyVisitBulk'
} as const;

export const TagMedicalDataToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitMedicalDataAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagMedicalDataToClinicalStudyVisitBulk'
} as const;

export const TagMedicalReportToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitMedicalReportAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagMedicalReportToClinicalStudyVisitBulk'
} as const;

export const TagMedicationToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitMedicationAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagMedicationToClinicalStudyVisitBulk'
} as const;

export const TagPPGDerivedToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitPPGDerivedAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagPPGDerivedToClinicalStudyVisitBulk'
} as const;

export const TagPPGToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitPPGAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagPPGToClinicalStudyVisitBulk'
} as const;

export const TagQuestionnaireToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitQuestionnaireAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagQuestionnaireToClinicalStudyVisitBulk'
} as const;

export const TagSeerlinqComputedToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitSeerlinqComputedAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagSeerlinqComputedToClinicalStudyVisitBulk'
} as const;

export const TagSymptomToClinicalStudyVisitBulkSchema = {
    properties: {
        number_of_datapoints: {
            type: 'integer',
            title: 'Number Of Datapoints'
        },
        associations: {
            items: {
                $ref: '#/components/schemas/CreateClinicalStudyVisitSymptomAssociation'
            },
            type: 'array',
            title: 'Associations'
        }
    },
    type: 'object',
    required: [
        'number_of_datapoints',
        'associations'
    ],
    title: 'TagSymptomToClinicalStudyVisitBulk'
} as const;

export const TaskAPIReadyResponseSchema = {
    properties: {
        task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Task Id'
        },
        ready: {
            type: 'boolean',
            title: 'Ready'
        }
    },
    type: 'object',
    required: [
        'task_id',
        'ready'
    ],
    title: 'TaskAPIReadyResponse'
} as const;

export const TaskAPIResultResponseSchema = {
    properties: {
        task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Task Id'
        },
        is_error: {
            type: 'boolean',
            title: 'Is Error'
        },
        error: {
            $ref: '#/components/schemas/ExceptionRepr',
            title: 'Error'
        },
        full_result: {
            anyOf: [
                {
                    $ref: '#/components/schemas/TaskiqResult'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'task_id',
        'is_error',
        'error'
    ],
    title: 'TaskAPIResultResponse'
} as const;

export const TaskCeleryResultAdminResponseSchema = {
    properties: {
        task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 32
                },
                {
                    type: 'string',
                    format: 'uuid4'
                }
            ],
            title: 'Task Id'
        },
        state: {
            $ref: '#/components/schemas/CeleryTaskStates'
        },
        datetime_done: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Datetime Done'
        },
        handled_exception: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HandledExceptionAlgorithmResult'
                },
                {
                    type: 'null'
                }
            ]
        },
        task_name: {
            $ref: '#/components/schemas/SeerlinqTaskNames'
        },
        queue: {
            $ref: '#/components/schemas/SeerlinqQueues'
        },
        args: {
            items: {},
            type: 'array',
            title: 'Args'
        },
        kwargs: {
            additionalProperties: true,
            type: 'object',
            title: 'Kwargs'
        },
        worker: {
            type: 'string',
            title: 'Worker'
        },
        error: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Error'
        },
        full_result: {
            anyOf: [
                {
                    $ref: '#/components/schemas/BaseAlgorithmResult'
                },
                {
                    $ref: '#/components/schemas/PreprocessAlgorithmResult'
                },
                {
                    $ref: '#/components/schemas/ResultPersistenceAlgorithmResult'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Full Result'
        }
    },
    type: 'object',
    required: [
        'task_id',
        'state',
        'datetime_done',
        'handled_exception',
        'task_name',
        'queue',
        'args',
        'kwargs',
        'worker',
        'error',
        'full_result'
    ],
    title: 'TaskCeleryResultAdminResponse'
} as const;

export const TaskCeleryResultBasicResponseSchema = {
    properties: {
        task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 32
                },
                {
                    type: 'string',
                    format: 'uuid4'
                }
            ],
            title: 'Task Id'
        },
        state: {
            $ref: '#/components/schemas/CeleryTaskStates'
        },
        datetime_done: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Datetime Done'
        },
        handled_exception: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HandledExceptionAlgorithmResult'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'task_id',
        'state',
        'datetime_done',
        'handled_exception'
    ],
    title: 'TaskCeleryResultBasicResponse'
} as const;

export const TaskCeleryStatusResponseSchema = {
    properties: {
        task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 64,
                    minLength: 32
                },
                {
                    type: 'string',
                    format: 'uuid4'
                }
            ],
            title: 'Task Id'
        },
        state: {
            $ref: '#/components/schemas/CeleryTaskStates'
        }
    },
    type: 'object',
    required: [
        'task_id',
        'state'
    ],
    title: 'TaskCeleryStatusResponse'
} as const;

export const TaskInputTypesSchema = {
    type: 'string',
    enum: [
        'ppg_task',
        'ecg_task',
        'patient_task',
        'mixed_task'
    ],
    title: 'TaskInputTypes'
} as const;

export const TaskSchemaSchema = {
    properties: {
        type: {
            type: 'string',
            const: 'Task',
            title: 'Type'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        args: {
            items: {},
            type: 'array',
            title: 'Args'
        },
        kwargs: {
            additionalProperties: true,
            type: 'object',
            title: 'Kwargs'
        }
    },
    type: 'object',
    required: [
        'type',
        'name'
    ],
    title: 'TaskSchema'
} as const;

export const TaskiqResultSchema = {
    properties: {
        is_err: {
            type: 'boolean',
            title: 'Is Err'
        },
        log: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Log'
        },
        return_value: {
            title: 'Return Value'
        },
        execution_time: {
            type: 'number',
            title: 'Execution Time'
        },
        labels: {
            additionalProperties: true,
            type: 'object',
            title: 'Labels'
        },
        error: {
            title: 'Error'
        }
    },
    type: 'object',
    required: [
        'is_err',
        'return_value',
        'execution_time'
    ],
    title: 'TaskiqResult',
    description: 'Result of a remote task invocation.'
} as const;

export const TechnicalWaitingRoomAlertsSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            minLength: 1,
            title: 'Name'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region'
        },
        patient_state: {
            $ref: '#/components/schemas/PatientState'
        },
        clinical_risk_score: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Clinical Risk Score'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        ppg_quality_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Ppg Quality Alerts'
        },
        other_technical_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Other Technical Alerts'
        },
        compliance_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponse'
            },
            type: 'array',
            title: 'Compliance Alerts'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        monitoring_watch_kind: {
            $ref: '#/components/schemas/PatientMonitoringWatchKind'
        },
        monitoring_watch_status: {
            $ref: '#/components/schemas/PatientMonitoringWatchStatus'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'name',
        'country',
        'region',
        'patient_state',
        'clinical_risk_score',
        'ppg_quality_alerts',
        'other_technical_alerts',
        'compliance_alerts',
        'monitoring_watch_kind',
        'monitoring_watch_status'
    ],
    title: 'TechnicalWaitingRoomAlerts'
} as const;

export const TechnicalWaitingRoomResponseSchema = {
    properties: {
        fetched: {
            type: 'string',
            format: 'date-time',
            title: 'Fetched'
        },
        waiting_room: {
            items: {
                $ref: '#/components/schemas/TechnicalWaitingRoomAlerts'
            },
            type: 'array',
            title: 'Waiting Room'
        },
        waiting_room_timespan: {
            type: 'string',
            format: 'duration',
            title: 'Waiting Room Timespan'
        }
    },
    type: 'object',
    required: [
        'fetched',
        'waiting_room',
        'waiting_room_timespan'
    ],
    title: 'TechnicalWaitingRoomResponse'
} as const;

export const TemperatureSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'temperature',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 50,
            minimum: 20,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'C',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'TemperatureSchemaResponse'
} as const;

export const TemporaryPasswordResponseSchema = {
    properties: {
        temporary_password: {
            type: 'string',
            title: 'Temporary Password'
        },
        valid_to: {
            type: 'string',
            format: 'date-time',
            title: 'Valid To'
        }
    },
    type: 'object',
    required: [
        'temporary_password',
        'valid_to'
    ],
    title: 'TemporaryPasswordResponse'
} as const;

export const Thiazide_diureticsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Thiazide diuretics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Thiazide diuretics'
} as const;

export const ThresholdHistorySchema = {
    properties: {
        change_date: {
            type: 'string',
            format: 'date',
            title: 'Change Date'
        },
        old_value: {
            type: 'number',
            title: 'Old Value'
        },
        old_settings: {
            oneOf: [
                {
                    $ref: '#/components/schemas/ValueBasedThresholdSettings'
                },
                {
                    $ref: '#/components/schemas/ChangeBasedThresholdSettings'
                }
            ],
            title: 'Old Settings',
            discriminator: {
                propertyName: 'threshold_trigger',
                mapping: {
                    change_based: '#/components/schemas/ChangeBasedThresholdSettings',
                    value_based: '#/components/schemas/ValueBasedThresholdSettings'
                }
            }
        }
    },
    type: 'object',
    required: [
        'change_date',
        'old_value',
        'old_settings'
    ],
    title: 'ThresholdHistory'
} as const;

export const ThresholdInfoSchema = {
    properties: {
        crossed: {
            type: 'boolean',
            title: 'Crossed'
        },
        type: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Type'
        },
        reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reason'
        },
        alert_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Uuid'
        },
        seen: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seen'
        }
    },
    type: 'object',
    required: [
        'crossed'
    ],
    title: 'ThresholdInfo',
    description: 'Whether a value crossed an alerting threshold, from persisted alerts.'
} as const;

export const ThresholdTablesSchema = {
    type: 'string',
    enum: [
        'patients',
        'medicaldata',
        'symptoms',
        'ppg_derived',
        'sq_computed',
        'ppg'
    ],
    title: 'ThresholdTables'
} as const;

export const ThresholdTriggersSchema = {
    type: 'string',
    enum: [
        'value_based',
        'change_based'
    ],
    title: 'ThresholdTriggers'
} as const;

export const ThresholdTypesSchema = {
    type: 'string',
    enum: [
        'low',
        'high'
    ],
    title: 'ThresholdTypes'
} as const;

export const ThyreopathySchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Thyreopathy',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ThyreopathySchemaResponse'
} as const;

export const Thyroid__substitute_therapySchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Thyroid: substitute therapy',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Thyroid: substitute therapy'
} as const;

export const Thyroid__thyrostaticsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Thyroid: thyrostatics',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Thyroid: thyrostatics'
} as const;

export const TimeSeriesPlotSchema = {
    properties: {
        x_column: {
            type: 'string',
            title: 'X Column'
        },
        y_column: {
            type: 'string',
            title: 'Y Column'
        },
        traces: {
            items: {
                $ref: '#/components/schemas/TimeSeriesTrace'
            },
            type: 'array',
            title: 'Traces'
        },
        column_info: {
            additionalProperties: {
                type: 'string'
            },
            type: 'object',
            title: 'Column Info'
        },
        time_range: {
            anyOf: [
                {
                    prefixItems: [
                        {
                            type: 'string',
                            format: 'date-time'
                        },
                        {
                            type: 'string',
                            format: 'date-time'
                        }
                    ],
                    type: 'array',
                    maxItems: 2,
                    minItems: 2
                },
                {
                    type: 'null'
                }
            ],
            title: 'Time Range'
        },
        time_range_unix: {
            anyOf: [
                {
                    prefixItems: [
                        {
                            type: 'integer'
                        },
                        {
                            type: 'integer'
                        }
                    ],
                    type: 'array',
                    maxItems: 2,
                    minItems: 2
                },
                {
                    type: 'null'
                }
            ],
            title: 'Time Range Unix',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'x_column',
        'y_column',
        'traces',
        'column_info',
        'time_range_unix'
    ],
    title: 'TimeSeriesPlot',
    description: 'Time series plot data. Pure data — no colors, symbols, or styling.'
} as const;

export const TimeSeriesPointSchema = {
    properties: {
        timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Timestamp'
        },
        value: {
            type: 'number',
            title: 'Value'
        },
        error_x: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Error X'
        },
        error_y: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Error Y'
        },
        channels: {
            anyOf: [
                {
                    additionalProperties: {
                        anyOf: [
                            {
                                type: 'number'
                            },
                            {
                                type: 'string'
                            }
                        ]
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Channels'
        },
        metadata: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Metadata'
        },
        timestamp_unix: {
            type: 'integer',
            title: 'Timestamp Unix',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'timestamp',
        'value',
        'timestamp_unix'
    ],
    title: 'TimeSeriesPoint',
    description: 'Single point in a time series.'
} as const;

export const TimeSeriesTraceSchema = {
    properties: {
        points: {
            items: {
                $ref: '#/components/schemas/TimeSeriesPoint'
            },
            type: 'array',
            title: 'Points'
        },
        label: {
            type: 'string',
            title: 'Label'
        },
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        }
    },
    type: 'object',
    required: [
        'points',
        'label'
    ],
    title: 'TimeSeriesTrace',
    description: 'A single trace/line in a plot.'
} as const;

export const TokenLoginSchemaSchema = {
    properties: {
        token: {
            type: 'string',
            title: 'Token'
        },
        expiry_token: {
            type: 'string',
            minLength: 1,
            title: 'Expiry Token'
        }
    },
    type: 'object',
    required: [
        'token',
        'expiry_token'
    ],
    title: 'TokenLoginSchema'
} as const;

export const TrGradientSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'TR gradient',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmHg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'TrGradientSchemaResponse'
} as const;

export const TrSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'TR',
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'TrSchemaResponse'
} as const;

export const TrVelocitySchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'TR velocity',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'm/s',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'TrVelocitySchemaResponse'
} as const;

export const UnplannedHospitalizationEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'hospitalization',
            title: 'Event Category',
            default: 'hospitalization'
        },
        event_category_type: {
            type: 'string',
            const: 'unplanned',
            title: 'Event Category Type',
            default: 'unplanned'
        },
        event_category_details: {
            $ref: '#/components/schemas/HospitalizationEventReason'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_details',
        'event_description',
        'patient_id'
    ],
    title: 'UnplannedHospitalizationEventSchema'
} as const;

export const UnplannedVisitEventSchemaSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date',
            default: '2026-11-02'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp',
            default: '2026-11-02T16:17:18.069278Z'
        },
        event_category: {
            type: 'string',
            const: 'visit',
            title: 'Event Category',
            default: 'visit'
        },
        event_category_type: {
            type: 'string',
            const: 'unplanned',
            title: 'Event Category Type',
            default: 'unplanned'
        },
        event_category_details: {
            $ref: '#/components/schemas/VisitEventUnplannedCategory'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'event_category_details',
        'event_description',
        'patient_id'
    ],
    title: 'UnplannedVisitEventSchema'
} as const;

export const UpdateAccessLimiterSchema = {
    properties: {
        limiter_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AccessLimiterTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        limiter_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AccessLimiterType'
                },
                {
                    type: 'null'
                }
            ]
        },
        limit_permission_bit: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PermissionBit'
                },
                {
                    type: 'null'
                }
            ]
        },
        limit_username: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Limit Username'
        },
        allowed_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Allowed Items'
        },
        limit_whole_table: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Limit Whole Table'
        },
        forbidden_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Forbidden Items'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAccessLimiter'
} as const;

export const UpdateAlertSchemaSchema = {
    properties: {
        alert_validation_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Validation Comment'
        },
        append_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Tags'
        },
        remove_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Tags'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAlertSchema'
} as const;

export const UpdateAorSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'AoR'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAorSchema'
} as const;

export const UpdateAosSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'AoS'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAosSchema'
} as const;

export const UpdateArrhythmiaTreatmentSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Arrhythmia treatment'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Cardioversion',
                        'Interventional',
                        'Surgical'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateArrhythmiaTreatmentSchema'
} as const;

export const UpdateArterialHypertensionSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Arterial hypertension'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateArterialHypertensionSchema'
} as const;

export const UpdateAscitesSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ascites'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Minor',
                        'Major'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAscitesSchema'
} as const;

export const UpdateAsthmaSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Asthma'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAsthmaSchema'
} as const;

export const UpdateAtrialFibrillationSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Atrial fibrillation'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Paroxysmal',
                        'Persistent',
                        'Permanent',
                        'Unknown',
                        'Other',
                        'Seerlinq-suspected'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 6,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAtrialFibrillationSchema'
} as const;

export const UpdateAtrialFlutterSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Atrial flutter'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Paroxysmal',
                        'Persistent',
                        'Permanent',
                        'Unknown',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 5,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateAtrialFlutterSchema'
} as const;

export const UpdateBLinesOnLungUltrasoundSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'B-lines on lung-ultrasound'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateBLinesOnLungUltrasoundSchema'
} as const;

export const UpdateBloodPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'blood pressure diastolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateBloodPressureDiastolicSchema'
} as const;

export const UpdateBloodPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'blood pressure systolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 350,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateBloodPressureSystolicSchema'
} as const;

export const UpdateBnpSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'BNP'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100000,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'pg/ml'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateBnpSchema'
} as const;

export const UpdateCkdSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'CKD'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'K/DIGO: G1',
                        'K/DIGO: G2',
                        'K/DIGO: G3a',
                        'K/DIGO: G3b',
                        'K/DIGO: G4',
                        'K/DIGO: G5',
                        'K/DIGO: Unknown'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 7,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateCkdSchema'
} as const;

export const UpdateClinicSchemaSchema = {
    properties: {
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        append_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Physicians'
        },
        delete_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Physicians'
        },
        assigned_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Users'
        },
        append_assigned_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Assigned Users'
        },
        delete_assigned_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Assigned Users'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateClinicSchema'
} as const;

export const UpdateClinicalStudyAdminProcedureSchema = {
    properties: {
        procedure: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure'
        },
        procedure_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure Date'
        },
        procedure_done: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure Done'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        procedure_done_by_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Procedure Done By Uuid'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateClinicalStudyAdminProcedure'
} as const;

export const UpdateClinicalStudyVisitSchema = {
    properties: {
        visit_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visit Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        schedule_timedelta: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Timedelta'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateClinicalStudyVisit'
} as const;

export const UpdateCopdSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'COPD'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateCopdSchema'
} as const;

export const UpdateCoronaryArteryDiseaseSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Coronary artery disease'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Chronic coronary syndrome',
                        'Previous ACS',
                        'Previous PCI',
                        'Previous CABG'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateCoronaryArteryDiseaseSchema'
} as const;

export const UpdateCreatinineSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'creatinine'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 1500,
                    minimum: 10
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'umol/l'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateCreatinineSchema'
} as const;

export const UpdateDefaultDiagnosisSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultDiagnosisSchema'
} as const;

export const UpdateDefaultECGDataLevel2SchemaSchema = {
    properties: {
        measurement_mode: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGMeasurementMode'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultECGDataLevel2Schema'
} as const;

export const UpdateDefaultECGDataLevel3SchemaSchema = {
    properties: {
        measurement_mode: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGMeasurementMode'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultECGDataLevel3Schema'
} as const;

export const UpdateDefaultECGDataLevel4SchemaSchema = {
    properties: {
        measurement_mode: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGMeasurementMode'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_device: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device'
        },
        leads_placement: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Leads Placement'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultECGDataLevel4Schema'
} as const;

export const UpdateDefaultMedicalDataSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultMedicalDataSchema'
} as const;

export const UpdateDefaultMedicationSchemaSchema = {
    properties: {
        medication_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            anyOf: [
                {
                    items: {
                        type: 'number',
                        multipleOf: 0.25,
                        minimum: 0
                    },
                    type: 'array',
                    maxItems: 4,
                    minItems: 4
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every'
        },
        medication_frequency_regular_repeat_every_unit: {
            anyOf: [
                {
                    $ref: '#/components/schemas/FrequencyUnit'
                },
                {
                    type: 'null'
                }
            ]
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultMedicationSchema'
} as const;

export const UpdateDefaultPPGDataAccessLevel2SchemaSchema = {
    properties: {
        measurement_condition: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StrictPPGConditions'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_mode: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGMeasurementMode'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultPPGDataAccessLevel2Schema'
} as const;

export const UpdateDefaultPPGDataAccessLevel3SchemaSchema = {
    properties: {
        measurement_condition: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StrictPPGConditions'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_mode: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGMeasurementMode'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultPPGDataAccessLevel3Schema'
} as const;

export const UpdateDefaultPPGDataAccessLevel4SchemaSchema = {
    properties: {
        measurement_condition: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Condition'
        },
        measurement_mode: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGMeasurementMode'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_device: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device'
        },
        measurement_origin: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultPPGDataAccessLevel4Schema'
} as const;

export const UpdateDefaultSymptomSchemaSchema = {
    properties: {
        symptom_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Name'
        },
        symptom_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Datetime'
        },
        symptom_value: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDefaultSymptomSchema'
} as const;

export const UpdateDiabetesMellitusSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Diabetes mellitus'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Type 1',
                        'Type 2'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDiabetesMellitusSchema'
} as const;

export const UpdateDryWeightSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'dry weight'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 350,
                    minimum: 30
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'kg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDryWeightSchema'
} as const;

export const UpdateDyslipidemiaSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Dyslipidemia'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateDyslipidemiaSchema'
} as const;

export const UpdateESchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'E'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 500,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'cm/s'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateESchema'
} as const;

export const UpdateE_ASchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'E/A'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateE/ASchema'
} as const;

export const UpdateE_E_AverageSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'E/e\' average'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 50,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateE/E\'AverageSchema'
} as const;

export const UpdateEcgPrematureContractionsSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ECG premature contractions'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateEcgPrematureContractionsSchema'
} as const;

export const UpdateEcgRhythmSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ECG rhythm'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'SR',
                        'AF',
                        'Paced',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateEcgRhythmSchema'
} as const;

export const UpdateElevatedLvFillingPressureFinalOutcomeSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'elevated LV filling pressure final outcome'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateElevatedLvFillingPressureFinalOutcomeSchema'
} as const;

export const UpdateElevatedLvFillingPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'elevated LV filling pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateElevatedLvFillingPressureSchema'
} as const;

export const UpdateFatigueScoreSchemaSchema = {
    properties: {
        symptom_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'fatigue score'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Name'
        },
        symptom_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Datetime'
        },
        symptom_value: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateFatigueScoreSchema'
} as const;

export const UpdateFilledQuestionnaireAdminSchemaSchema = {
    properties: {
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        questionnaire_id: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QuestionnaireTypes'
                },
                {
                    type: 'null'
                }
            ]
        },
        questionnaire_language: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'ar',
                        'az',
                        'cs',
                        'da',
                        'de',
                        'el',
                        'en',
                        'es',
                        'fr',
                        'he',
                        'hi',
                        'hu',
                        'it',
                        'ja',
                        'lt',
                        'lv',
                        'nb',
                        'pl',
                        'pt',
                        'ro',
                        'ru',
                        'sk',
                        'sv',
                        'tr',
                        'uk',
                        'zh-Hans',
                        'zh-Hant'
                    ],
                    title: 'SupportedLanguages',
                    description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
                    'x-seerlinq-languages': [
                        {
                            alpha2: 'ar',
                            alpha3: 'ara',
                            code: 'ar',
                            dir: 'rtl',
                            name: 'Arabic',
                            native_name: 'العربية',
                            regions: [
                                'SA'
                            ]
                        },
                        {
                            alpha2: 'az',
                            alpha3: 'aze',
                            code: 'az',
                            dir: 'ltr',
                            name: 'Azerbaijani',
                            native_name: 'Azərbaycan dili',
                            regions: []
                        },
                        {
                            alpha2: 'cs',
                            alpha3: 'ces',
                            code: 'cs',
                            dir: 'ltr',
                            name: 'Czech',
                            native_name: 'Čeština',
                            regions: []
                        },
                        {
                            alpha2: 'da',
                            alpha3: 'dan',
                            code: 'da',
                            dir: 'ltr',
                            name: 'Danish',
                            native_name: 'Dansk',
                            regions: []
                        },
                        {
                            alpha2: 'de',
                            alpha3: 'deu',
                            code: 'de',
                            dir: 'ltr',
                            name: 'German',
                            native_name: 'Deutsch',
                            regions: [
                                'DE',
                                'AT'
                            ]
                        },
                        {
                            alpha2: 'el',
                            alpha3: 'ell',
                            code: 'el',
                            dir: 'ltr',
                            name: 'Greek',
                            native_name: 'Ελληνικά',
                            regions: []
                        },
                        {
                            alpha2: 'en',
                            alpha3: 'eng',
                            code: 'en',
                            dir: 'ltr',
                            name: 'English',
                            native_name: 'English',
                            regions: [
                                'GB'
                            ]
                        },
                        {
                            alpha2: 'es',
                            alpha3: 'spa',
                            code: 'es',
                            dir: 'ltr',
                            name: 'Spanish',
                            native_name: 'Español',
                            regions: [
                                'ES'
                            ]
                        },
                        {
                            alpha2: 'fr',
                            alpha3: 'fra',
                            code: 'fr',
                            dir: 'ltr',
                            name: 'French',
                            native_name: 'Français',
                            regions: [
                                'FR'
                            ]
                        },
                        {
                            alpha2: 'he',
                            alpha3: 'heb',
                            code: 'he',
                            dir: 'rtl',
                            name: 'Hebrew',
                            native_name: 'עברית',
                            regions: []
                        },
                        {
                            alpha2: 'hi',
                            alpha3: 'hin',
                            code: 'hi',
                            dir: 'ltr',
                            name: 'Hindi',
                            native_name: 'हिन्दी',
                            regions: []
                        },
                        {
                            alpha2: 'hu',
                            alpha3: 'hun',
                            code: 'hu',
                            dir: 'ltr',
                            name: 'Hungarian',
                            native_name: 'Magyar',
                            regions: []
                        },
                        {
                            alpha2: 'it',
                            alpha3: 'ita',
                            code: 'it',
                            dir: 'ltr',
                            name: 'Italian',
                            native_name: 'Italiano',
                            regions: []
                        },
                        {
                            alpha2: 'ja',
                            alpha3: 'jpn',
                            code: 'ja',
                            dir: 'ltr',
                            name: 'Japanese',
                            native_name: '日本語',
                            regions: []
                        },
                        {
                            alpha2: 'lt',
                            alpha3: 'lit',
                            code: 'lt',
                            dir: 'ltr',
                            name: 'Lithuanian',
                            native_name: 'Lietuvių',
                            regions: []
                        },
                        {
                            alpha2: 'lv',
                            alpha3: 'lav',
                            code: 'lv',
                            dir: 'ltr',
                            name: 'Latvian',
                            native_name: 'Latviešu',
                            regions: []
                        },
                        {
                            alpha2: 'nb',
                            alpha3: 'nob',
                            code: 'nb',
                            dir: 'ltr',
                            name: 'Norwegian Bokmål',
                            native_name: 'Norsk bokmål',
                            regions: []
                        },
                        {
                            alpha2: 'pl',
                            alpha3: 'pol',
                            code: 'pl',
                            dir: 'ltr',
                            name: 'Polish',
                            native_name: 'Polski',
                            regions: []
                        },
                        {
                            alpha2: 'pt',
                            alpha3: 'por',
                            code: 'pt',
                            dir: 'ltr',
                            name: 'Portuguese',
                            native_name: 'Português',
                            regions: [
                                'PT'
                            ]
                        },
                        {
                            alpha2: 'ro',
                            alpha3: 'ron',
                            code: 'ro',
                            dir: 'ltr',
                            name: 'Romanian',
                            native_name: 'Română',
                            regions: []
                        },
                        {
                            alpha2: 'ru',
                            alpha3: 'rus',
                            code: 'ru',
                            dir: 'ltr',
                            name: 'Russian',
                            native_name: 'Русский',
                            regions: []
                        },
                        {
                            alpha2: 'sk',
                            alpha3: 'slk',
                            code: 'sk',
                            dir: 'ltr',
                            name: 'Slovak',
                            native_name: 'Slovenčina',
                            regions: []
                        },
                        {
                            alpha2: 'sv',
                            alpha3: 'swe',
                            code: 'sv',
                            dir: 'ltr',
                            name: 'Swedish',
                            native_name: 'Svenska',
                            regions: []
                        },
                        {
                            alpha2: 'tr',
                            alpha3: 'tur',
                            code: 'tr',
                            dir: 'ltr',
                            name: 'Turkish',
                            native_name: 'Türkçe',
                            regions: []
                        },
                        {
                            alpha2: 'uk',
                            alpha3: 'ukr',
                            code: 'uk',
                            dir: 'ltr',
                            name: 'Ukrainian',
                            native_name: 'Українська',
                            regions: []
                        },
                        {
                            alpha2: 'zh',
                            alpha3: 'zho',
                            code: 'zh-Hans',
                            dir: 'ltr',
                            name: 'Chinese (Simplified)',
                            native_name: '简体中文',
                            regions: [
                                'CN'
                            ]
                        },
                        {
                            alpha2: 'zh',
                            alpha3: 'zho',
                            code: 'zh-Hant',
                            dir: 'ltr',
                            name: 'Chinese (Traditional)',
                            native_name: '繁體中文',
                            regions: [
                                'TW'
                            ]
                        }
                    ]
                },
                {
                    type: 'null'
                }
            ]
        },
        questionnaire_version: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Questionnaire Version'
        },
        filling_start: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Filling Start'
        },
        filling_end: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Filling End'
        },
        responses: {
            anyOf: [
                {
                    items: {
                        oneOf: [
                            {
                                $ref: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                            },
                            {
                                $ref: '#/components/schemas/OrdinalQuestionnaireResponse'
                            },
                            {
                                $ref: '#/components/schemas/MultiChoiceQuestionnaireResponse-Input'
                            },
                            {
                                $ref: '#/components/schemas/ContinuousQuestionnaireResponse'
                            },
                            {
                                $ref: '#/components/schemas/OpenTextQuestionnaireResponse'
                            }
                        ],
                        discriminator: {
                            propertyName: 'question_type',
                            mapping: {
                                continuous: '#/components/schemas/ContinuousQuestionnaireResponse',
                                dichotomous: '#/components/schemas/SingleChoiceQuestionnaireResponse',
                                open_text: '#/components/schemas/OpenTextQuestionnaireResponse',
                                ordinal: '#/components/schemas/OrdinalQuestionnaireResponse',
                                polytomous_multi_choice: '#/components/schemas/MultiChoiceQuestionnaireResponse-Input',
                                polytomous_single_choice: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                            }
                        }
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Responses'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateFilledQuestionnaireAdminSchema'
} as const;

export const UpdateFilledQuestionnaireBasicSchemaSchema = {
    properties: {
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateFilledQuestionnaireBasicSchema'
} as const;

export const UpdateHeartRateSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'heart rate'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 250,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'bpm'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHeartRateSchema'
} as const;

export const UpdateHematocritSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'hematocrit'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHematocritSchema'
} as const;

export const UpdateHemoglobinSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'hemoglobin'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 500,
                    minimum: 10
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'g/l'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHemoglobinSchema'
} as const;

export const UpdateHfEtiologySchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'HF etiology'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Ischemic',
                        'Non-ischemic',
                        'Unknown'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHfEtiologySchema'
} as const;

export const UpdateHfTypeSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'HF type'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'HFpEF',
                        'HFmrEF',
                        'HFrEF'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHfTypeSchema'
} as const;

export const UpdateHistoryOfAlcoholAbuseSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'History of alcohol abuse'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHistoryOfAlcoholAbuseSchema'
} as const;

export const UpdateHistoryOfCardiotoxicDrugsSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'History of cardiotoxic drugs'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHistoryOfCardiotoxicDrugsSchema'
} as const;

export const UpdateHistoryOfHtxSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'History of HTx'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHistoryOfHtxSchema'
} as const;

export const UpdateHistoryOfSmokingSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'History of smoking'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateHistoryOfSmokingSchema'
} as const;

export const UpdateImplantedDevicesSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Implanted devices'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'PM',
                        'ICD',
                        'CRT-D',
                        'CRT-P',
                        'PA-sensor',
                        'VAD'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 6,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateImplantedDevicesSchema'
} as const;

export const UpdateIronDeficiency_AnemiaSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Iron deficiency/anemia'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateIronDeficiency/AnemiaSchema'
} as const;

export const UpdateJugularVenousDistentionSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'jugular venous distention'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateJugularVenousDistentionSchema'
} as const;

export const UpdateLaviSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LAVi'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ml/m2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLaviSchema'
} as const;

export const UpdateLegEdemaSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'leg edema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLegEdemaSchema'
} as const;

export const UpdateLhcAorticDiastolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC aortic diastolic pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 150,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcAorticDiastolicPressureSchema'
} as const;

export const UpdateLhcAorticMeanPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC aortic mean pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 200,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcAorticMeanPressureSchema'
} as const;

export const UpdateLhcAorticOxygenSaturationSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC aortic oxygen saturation'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: '%'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcAorticOxygenSaturationSchema'
} as const;

export const UpdateLhcAorticSystolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC aortic systolic pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcAorticSystolicPressureSchema'
} as const;

export const UpdateLhcCardiacIndexSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC cardiac index'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'l/min/m2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcCardiacIndexSchema'
} as const;

export const UpdateLhcCardiacOutputSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC cardiac output'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 15,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'l/min'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcCardiacOutputSchema'
} as const;

export const UpdateLhcDp_DtSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC dP/dt'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 6000,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg/s'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcDp/DtSchema'
} as const;

export const UpdateLhcEjectionFractionSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC ejection fraction'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: '%'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcEjectionFractionSchema'
} as const;

export const UpdateLhcLeftAtrialMeanPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC left atrial mean pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 50,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcLeftAtrialMeanPressureSchema'
} as const;

export const UpdateLhcLeftVentricularEndDiastolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC left ventricular end-diastolic pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 50,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcLeftVentricularEndDiastolicPressureSchema'
} as const;

export const UpdateLhcLeftVentricularSystolicPressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC left ventricular systolic pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcLeftVentricularSystolicPressureSchema'
} as const;

export const UpdateLhcMeanAorticValveGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC mean aortic valve gradient'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 150,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcMeanAorticValveGradientSchema'
} as const;

export const UpdateLhcMeanMitralValveGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LHC mean mitral valve gradient'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 50,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLhcMeanMitralValveGradientSchema'
} as const;

export const UpdateLvefSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'LVEF'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 75,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: '%'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateLvefSchema'
} as const;

export const UpdateMalignancySchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Malignancy'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMalignancySchema'
} as const;

export const UpdateMedicalReportSchemaSchema = {
    properties: {
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        report_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Report Date'
        },
        report_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ReportType'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        original_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Original Name'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMedicalReportSchema'
} as const;

export const UpdateMonitoringSettingsSchema = {
    properties: {
        new_patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        auto_schedule: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auto Schedule'
        },
        requires_measurement_config: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Requires Measurement Config'
        },
        auto_alerts: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auto Alerts'
        },
        patient_push_notifications: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Push Notifications'
        },
        consent_type: {
            anyOf: [
                {
                    type: 'integer',
                    enum: [
                        2,
                        1
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Consent Type',
            description: '**Deprecated** - use `required_consents`.',
            deprecated: true
        },
        required_consents: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ConsentType'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Consents'
        },
        is_monitored: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Is Monitored'
        },
        any_new_data_activate: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Any New Data Activate'
        },
        ppg_ecg_new_data_activate: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppg Ecg New Data Activate'
        },
        auto_deactivate_after: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auto Deactivate After'
        },
        status_change_email: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Status Change Email'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMonitoringSettings'
} as const;

export const UpdateMrSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'MR'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMrSchema'
} as const;

export const UpdateMsSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'MS'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMsSchema'
} as const;

export const UpdateMultipleECGDerivedSchemaSchema = {
    properties: {
        seerlinq_measurement_quality_flag: {
            anyOf: [
                {
                    type: 'integer',
                    enum: [
                        0,
                        1,
                        2,
                        3
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Flag'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMultipleECGDerivedSchema'
} as const;

export const UpdateMultiplePPGDerivedSchemaSchema = {
    properties: {
        seerlinq_measurement_quality_flag: {
            anyOf: [
                {
                    type: 'integer',
                    enum: [
                        0,
                        1,
                        2,
                        3
                    ]
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Flag'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateMultiplePPGDerivedSchema'
} as const;

export const UpdateNtProbnpSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'NT-proBNP'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100000,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'pg/ml'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateNtProbnpSchema'
} as const;

export const UpdateNyhaClassSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'NYHA class'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'I',
                        'II',
                        'III',
                        'IV'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 3,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateNyhaClassSchema'
} as const;

export const UpdatePatientConsentSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        consent_history: {
            items: {
                $ref: '#/components/schemas/ConsentHistory'
            },
            type: 'array',
            title: 'Consent History'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'informed_consent',
        'consent_history',
        'updated_at'
    ],
    title: 'UpdatePatientConsent'
} as const;

export const UpdatePatientConsentTypeSchema = {
    properties: {
        granted: {
            type: 'boolean',
            title: 'Granted'
        },
        method: {
            $ref: '#/components/schemas/ConsentMethod'
        },
        study_text_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Text Uuid'
        },
        content_hash: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^[0-9a-fA-F]{64}$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Content Hash'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'granted',
        'method'
    ],
    title: 'UpdatePatientConsentType'
} as const;

export const UpdatePatientEventLevel3SchemaSchema = {
    properties: {
        event_description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientEventLevel3Schema'
} as const;

export const UpdatePatientEventLevel4SchemaSchema = {
    properties: {
        event_description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        event_category_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Type'
        },
        event_category_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Details'
        },
        event_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Date'
        },
        event_timestamp: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Timestamp'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientEventLevel4Schema'
} as const;

export const UpdatePatientLevel2SchemaSchema = {
    properties: {
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Input'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientLevel2Schema'
} as const;

export const UpdatePatientLevel3SchemaSchema = {
    properties: {
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Input'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        insurance_policy_details: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Insurance Policy Details'
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientLevel3Schema'
} as const;

export const UpdatePatientLevel4SchemaSchema = {
    properties: {
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-Input'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        insurance_policy_details: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Insurance Policy Details'
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        given_name: {
            anyOf: [
                {
                    type: 'string',
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            anyOf: [
                {
                    type: 'string',
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Order Given First'
        },
        residence: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Residence'
        },
        health_insurance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Health Insurance'
        },
        id_number: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 11,
                    minLength: 7,
                    pattern: '^[0-9,\\/]*$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Id Number'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientLevel4Schema'
} as const;

export const UpdatePatientMessageSchemaSchema = {
    properties: {
        message_body: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Body'
        },
        message_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Active'
        },
        message_expiry: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Expiry'
        },
        message_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Message Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientMessageSchema'
} as const;

export const UpdatePatientScheduleSchema = {
    properties: {
        schedule_times: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'time'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Times'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        schedule_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ScheduleTypes'
                },
                {
                    type: 'null'
                }
            ]
        },
        specific_schedule_data_name: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Specific Schedule Data Name'
        },
        schedule_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Frequency Regular Repeat Every'
        },
        schedule_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Frequency On Days'
        },
        start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Start Date'
        },
        schedule_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Active'
        },
        schedule_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientSchedule'
} as const;

export const UpdatePatientScheduleSelfSchema = {
    properties: {
        schedule_times: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'time'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule Times'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientScheduleSelf'
} as const;

export const UpdatePatientThresholdSchemaSchema = {
    properties: {
        threshold_settings: {
            anyOf: [
                {
                    oneOf: [
                        {
                            $ref: '#/components/schemas/ValueBasedThresholdSettings'
                        },
                        {
                            $ref: '#/components/schemas/ChangeBasedThresholdSettings'
                        }
                    ],
                    discriminator: {
                        propertyName: 'threshold_trigger',
                        mapping: {
                            change_based: '#/components/schemas/ChangeBasedThresholdSettings',
                            value_based: '#/components/schemas/ValueBasedThresholdSettings'
                        }
                    }
                },
                {
                    type: 'null'
                }
            ],
            title: 'Threshold Settings'
        },
        threshold_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Threshold Value'
        },
        threshold_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Threshold Comment'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePatientThresholdSchema'
} as const;

export const UpdatePerceivedHealthChangeSchemaSchema = {
    properties: {
        symptom_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'perceived health change'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Name'
        },
        symptom_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Datetime'
        },
        symptom_value: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePerceivedHealthChangeSchema'
} as const;

export const UpdatePeripheralArterialDiseaseSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Peripheral arterial disease'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePeripheralArterialDiseaseSchema'
} as const;

export const UpdatePhysicianSchemaSchema = {
    properties: {
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        given_name: {
            anyOf: [
                {
                    type: 'string',
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            anyOf: [
                {
                    type: 'string',
                    minLength: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Order Given First'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        connected_user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected User Uuid'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        phone: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'phone'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        append_phone: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'phone'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Phone'
        },
        delete_phone: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'phone'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Phone'
        },
        clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinics'
        },
        append_clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Clinics'
        },
        delete_clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Clinics'
        },
        patients: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patients'
        },
        append_patients: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Patients'
        },
        delete_patients: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Patients'
        },
        bypass_geography_checks: {
            anyOf: [
                {
                    prefixItems: [
                        {
                            type: 'boolean'
                        },
                        {
                            type: 'boolean'
                        }
                    ],
                    type: 'array',
                    maxItems: 2,
                    minItems: 2
                },
                {
                    type: 'null'
                }
            ],
            title: 'Bypass Geography Checks'
        },
        assigned_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Users'
        },
        append_assigned_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Assigned Users'
        },
        delete_assigned_users: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Assigned Users'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePhysicianSchema'
} as const;

export const UpdatePleuralEffusionSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'pleural effusion'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Minor',
                        'Major'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 2,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePleuralEffusionSchema'
} as const;

export const UpdatePreviousHfhSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Previous HFH'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePreviousHfhSchema'
} as const;

export const UpdatePreviousStroke_TiaSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Previous stroke/TIA'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePreviousStroke/TiaSchema'
} as const;

export const UpdatePurSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'PuR'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdatePurSchema'
} as const;

export const UpdateQuestionnaireAutomatonSettingsSchema = {
    properties: {
        first_when_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'First When Active'
        },
        filling_frequency: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FrequencyIntervalItem'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Filling Frequency'
        },
        first_after_active: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        stop_after_n: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Stop After N'
        },
        stop_after_time_from_first: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        skippable: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Skippable'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateQuestionnaireAutomatonSettings'
} as const;

export const UpdateRespiratoryRateSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'respiratory rate'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 70,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'bpm'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRespiratoryRateSchema'
} as const;

export const UpdateRhcArterialOxygenSaturationSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC arterial oxygen saturation'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: '%'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcArterialOxygenSaturationSchema'
} as const;

export const UpdateRhcCardiacIndexFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC cardiac index - Fick'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 20,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'l/min/m2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcCardiacIndexFickSchema'
} as const;

export const UpdateRhcCardiacIndexTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC cardiac index - TD'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 20,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'l/min/m2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcCardiacIndexTdSchema'
} as const;

export const UpdateRhcCardiacOutputFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC cardiac output - Fick'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 25,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'l/min'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcCardiacOutputFickSchema'
} as const;

export const UpdateRhcCardiacOutputTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC cardiac output - TD'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 25,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'l/min'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcCardiacOutputTdSchema'
} as const;

export const UpdateRhcDiastolicTranspulmonaryGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC diastolic transpulmonary gradient'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcDiastolicTranspulmonaryGradientSchema'
} as const;

export const UpdateRhcMixedVenousOxygenSaturationSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC mixed venous oxygen saturation'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: '%'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcMixedVenousOxygenSaturationSchema'
} as const;

export const UpdateRhcPulmonaryArterialComplianceSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary arterial compliance'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ml/mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryArterialComplianceSchema'
} as const;

export const UpdateRhcPulmonaryArteryPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary artery pressure diastolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryArteryPressureDiastolicSchema'
} as const;

export const UpdateRhcPulmonaryArteryPressureMeanSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary artery pressure mean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryArteryPressureMeanSchema'
} as const;

export const UpdateRhcPulmonaryArteryPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary artery pressure systolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 150,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryArteryPressureSystolicSchema'
} as const;

export const UpdateRhcPulmonaryCapillaryWedgePressureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary capillary wedge pressure'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryCapillaryWedgePressureSchema'
} as const;

export const UpdateRhcPulmonaryVascularResistanceFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary vascular resistance - Fick'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 30,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'WU'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryVascularResistanceFickSchema'
} as const;

export const UpdateRhcPulmonaryVascularResistanceIndexSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary vascular resistance index'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 20,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'WU/m2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryVascularResistanceIndexSchema'
} as const;

export const UpdateRhcPulmonaryVascularResistanceTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC pulmonary vascular resistance - TD'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 30,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'WU'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcPulmonaryVascularResistanceTdSchema'
} as const;

export const UpdateRhcRightAtrialPressureMeanSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC right atrial pressure mean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 50,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcRightAtrialPressureMeanSchema'
} as const;

export const UpdateRhcStrokeVolumeFickSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC stroke volume - Fick'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 400,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ml'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcStrokeVolumeFickSchema'
} as const;

export const UpdateRhcStrokeVolumeIndexSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC stroke volume index'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ml/m2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcStrokeVolumeIndexSchema'
} as const;

export const UpdateRhcStrokeVolumeTdSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC stroke volume - TD'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 400,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ml'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcStrokeVolumeTdSchema'
} as const;

export const UpdateRhcSystemicBloodPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC systemic blood pressure diastolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcSystemicBloodPressureDiastolicSchema'
} as const;

export const UpdateRhcSystemicBloodPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC systemic blood pressure systolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 350,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcSystemicBloodPressureSystolicSchema'
} as const;

export const UpdateRhcTotalPulmonaryResistanceSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC total pulmonary resistance'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 30,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'WU'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcTotalPulmonaryResistanceSchema'
} as const;

export const UpdateRhcTranspulmonaryGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC transpulmonary gradient'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcTranspulmonaryGradientSchema'
} as const;

export const UpdateRhcVentricularPressureDiastolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC ventricular pressure diastolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcVentricularPressureDiastolicSchema'
} as const;

export const UpdateRhcVentricularPressureSystolicSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RHC ventricular pressure systolic'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 200,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRhcVentricularPressureSystolicSchema'
} as const;

export const UpdateRvspSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'RVSP'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateRvspSchema'
} as const;

export const UpdateSeerlinqComputedAdminSchemaSchema = {
    properties: {
        seerlinq_measurement_quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGRelatedQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        append_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Tags'
        },
        remove_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Tags'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_algorithm: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Algorithm Version'
        },
        seerlinq_measurement_quality_index: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Index'
        },
        ground_truth_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ComputedGroundTruthTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        ground_truth_data_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ground Truth Data Name'
        },
        alert: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert'
        },
        append_alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Alert Tags'
        },
        remove_alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Alert Tags'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateSeerlinqComputedAdminSchema'
} as const;

export const UpdateSeerlinqComputedUsersSchemaSchema = {
    properties: {
        seerlinq_measurement_quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGRelatedQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        append_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Tags'
        },
        remove_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Tags'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateSeerlinqComputedUsersSchema'
} as const;

export const UpdateServiceAccountOwnerSchemaSchema = {
    properties: {
        owner_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Owner Uuid'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'owner_uuid'
    ],
    title: 'UpdateServiceAccountOwnerSchema',
    description: 'Required, not defaulted: an omitted field must not quietly orphan an\naccount. `None` is the deliberate "org-owned" answer.'
} as const;

export const UpdateServiceAccountStatusSchemaSchema = {
    properties: {
        status: {
            type: 'integer',
            enum: [
                101,
                100,
                111
            ],
            title: 'Status'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'status'
    ],
    title: 'UpdateServiceAccountStatusSchema',
    description: 'No `owner_deactivated`: it claims the owner is gone, and only the\nowner-lifecycle hooks know that.'
} as const;

export const UpdateShortnessOfBreathSchemaSchema = {
    properties: {
        symptom_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'shortness of breath'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Name'
        },
        symptom_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Datetime'
        },
        symptom_value: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value'
        },
        symptom_change_in_last_six_m: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change In Last Six M'
        },
        symptom_change_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Change Date'
        },
        symptom_value_before: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Symptom Value Before'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateShortnessOfBreathSchema'
} as const;

export const UpdateSingleECGDerivedSchemaSchema = {
    properties: {
        seerlinq_measurement_quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ECGRelatedQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateSingleECGDerivedSchema'
} as const;

export const UpdateSinglePPGDerivedSchemaSchema = {
    properties: {
        seerlinq_measurement_quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PPGRelatedQualityFlag'
                },
                {
                    type: 'null'
                }
            ]
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateSinglePPGDerivedSchema'
} as const;

export const UpdateSpo2SchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'SpO2'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 30
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: '%'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateSpo2Schema'
} as const;

export const UpdateStrokeVolumeOnEchoSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'stroke volume on echo'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 400,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'ml'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateStrokeVolumeOnEchoSchema'
} as const;

export const UpdateStudyAssociationSchema = {
    properties: {
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        include: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Include'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateStudyAssociation'
} as const;

export const UpdateStudyEnrollmentSchema = {
    properties: {
        enrollment_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Enrollment Date'
        },
        enrollment_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Enrollment Active'
        },
        enrollment_activity_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Enrollment Activity Reason'
        },
        schedule: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/StudyVisitSchedule-Input'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Schedule'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        strict_limits_check: {
            type: 'boolean',
            title: 'Strict Limits Check',
            default: true
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateStudyEnrollment'
} as const;

export const UpdateTemperatureSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'temperature'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 50,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'C'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateTemperatureSchema'
} as const;

export const UpdateThyreopathySchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Thyreopathy'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'Yes'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 1,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateThyreopathySchema'
} as const;

export const UpdateTrGradientSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'TR gradient'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmHg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateTrGradientSchema'
} as const;

export const UpdateTrSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'TR'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'None',
                        'Trace',
                        'Mild',
                        'Moderate',
                        'Severe'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'null',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateTrSchema'
} as const;

export const UpdateTrVelocitySchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'TR velocity'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'm/s'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateTrVelocitySchema'
} as const;

export const UpdateUreaSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'urea'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'mmol/l'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateUreaSchema'
} as const;

export const UpdateUserAccessPolicySchemaSchema = {
    properties: {
        regions: {
            anyOf: [
                {
                    additionalProperties: {
                        items: {
                            type: 'integer',
                            minimum: 0
                        },
                        type: 'array',
                        uniqueItems: true
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Regions'
        },
        permissions: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PermissionSet'
                },
                {
                    type: 'null'
                }
            ]
        },
        countries: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Countries'
        },
        add_permissions: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PermissionSet'
                },
                {
                    type: 'null'
                }
            ]
        },
        remove_permissions: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PermissionSet'
                },
                {
                    type: 'null'
                }
            ]
        },
        clinic_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuids'
        },
        append_clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Clinics'
        },
        remove_clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Clinics'
        },
        physician_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuids'
        },
        append_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Physicians'
        },
        remove_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Physicians'
        },
        visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patient Ids'
        },
        append_visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Visible Patient Ids'
        },
        remove_visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Visible Patient Ids'
        },
        monitoring_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Active'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateUserAccessPolicySchema',
    description: 'Schema for updating user access policy with flexible operations.'
} as const;

export const UpdateUserAdminSchemaSchema = {
    properties: {
        username: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Username'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/LocaleTag'
                },
                {
                    type: 'null'
                }
            ]
        },
        feature_flags: {
            anyOf: [
                {
                    additionalProperties: {
                        $ref: '#/components/schemas/FeatureFlagUpdate'
                    },
                    propertyNames: {
                        $ref: '#/components/schemas/UserFeatureFlag'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Feature Flags'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        role: {
            anyOf: [
                {
                    $ref: '#/components/schemas/UserRole'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patient Ids'
        },
        append_visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Visible Patient Ids'
        },
        delete_visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Visible Patient Ids'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        append_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Physicians'
        },
        delete_physicians: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Physicians'
        },
        clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinics'
        },
        append_clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Clinics'
        },
        delete_clinics: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Delete Clinics'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateUserAdminSchema'
} as const;

export const UpdateUserBasicSchemaSchema = {
    properties: {
        username: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Username'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/LocaleTag'
                },
                {
                    type: 'null'
                }
            ]
        },
        feature_flags: {
            anyOf: [
                {
                    additionalProperties: {
                        $ref: '#/components/schemas/FeatureFlagUpdate'
                    },
                    propertyNames: {
                        $ref: '#/components/schemas/UserFeatureFlag'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Feature Flags'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateUserBasicSchema'
} as const;

export const UpdateValvularInterventionSchemaSchema = {
    properties: {
        diagnosis_name: {
            anyOf: [
                {
                    type: 'string',
                    const: 'Valvular intervention'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'SAVR',
                        'TAVR',
                        'MVR',
                        'MVP',
                        'TEER',
                        'TVR',
                        'TVP',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 8,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence'
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateValvularInterventionSchema'
} as const;

export const UpdateWaitingRoomPatientSchemaSchema = {
    properties: {
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateWaitingRoomPatientSchema'
} as const;

export const UpdateWeightSchemaSchema = {
    properties: {
        measurement_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Datetime'
        },
        measurement_type: {
            anyOf: [
                {
                    type: 'string',
                    const: 'weight'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Type'
        },
        measurement_value: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 350,
                    minimum: 30
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string',
                    const: 'kg'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Patient Id'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    additionalProperties: false,
    type: 'object',
    title: 'UpdateWeightSchema'
} as const;

export const UploadStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4
    ],
    title: 'UploadStatus',
    'x-enum-varnames': [
        'pending',
        'success',
        'failed',
        'quarantined',
        'expired'
    ],
    description: ' - 0: pending\n - 1: success\n - 2: failed\n - 3: quarantined\n - 4: expired'
} as const;

export const UploadedBySchema = {
    type: 'string',
    enum: [
        'clinic',
        'patient'
    ],
    title: 'UploadedBy',
    description: 'Who uploaded a medical report, set from the caller\'s role at create.'
} as const;

export const UreaSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'urea',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 100,
            minimum: 0,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'mmol/l',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'UreaSchemaResponse'
} as const;

export const UserAccessPolicyResponseSchema = {
    properties: {
        regions: {
            anyOf: [
                {
                    additionalProperties: {
                        items: {
                            type: 'integer',
                            minimum: 0
                        },
                        type: 'array'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Regions'
        },
        permissions: {
            $ref: '#/components/schemas/PermissionSet'
        },
        countries: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Countries'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        stored_permissions: {
            $ref: '#/components/schemas/PermissionSet',
            description: 'The bits stored on the policy, without the ones `permissions` adds by implication. Send these back to `PUT /policies/{uuid}` to keep an implied bit implied: a bit sent in `permissions` is stored explicitly and outlives the bit that implied it.'
        },
        connected_user: {
            $ref: '#/components/schemas/SimpleAccountResponse'
        },
        visible_patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientWithUuidSchema'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patients'
        },
        monitoring_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Active'
        },
        clinics: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ClinicResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinics'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PhysicianResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'permissions',
        'uuid',
        'connected_user',
        'created_at',
        'updated_at'
    ],
    title: 'UserAccessPolicyResponse',
    description: 'Schema for user access policy responses.\nPermissions are returned with implied permissions expanded.'
} as const;

export const UserConnectedPatientResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        user_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'User Uuid'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'patient_id',
        'monitoring_type',
        'user_uuid'
    ],
    title: 'UserConnectedPatientResponse'
} as const;

export const UserFeatureFlagSchema = {
    type: 'string',
    enum: [
        'fe_daily_monitoring_dashboard',
        'fe_app_only_user',
        'app_rhythm_monitoring',
        'app_confirm_manual_entry',
        'app_ui_v2',
        'app_kiosk_mode',
        'fe_ai_triage'
    ],
    title: 'UserFeatureFlag',
    description: 'Boolean feature flags stored per user. The prefix marks the consumer:\n`app_` (mobile app), `fe_` (CLV frontend), `shared_` (both).'
} as const;

export const UserPasswordStatusSchema = {
    type: 'string',
    enum: [
        'not_active',
        'regular',
        'reset_by_user',
        'reset_by_admin',
        'temporary_valid',
        'temporary_expired',
        'temporary_error',
        'archived'
    ],
    title: 'UserPasswordStatus'
} as const;

export const UserPatientRoleResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponse'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        connected_patient: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientBaseSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        password_status: {
            $ref: '#/components/schemas/UserPasswordStatus',
            description: 'Return status of user\'s password.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at',
        'password_status'
    ],
    title: 'UserPatientRoleResponse'
} as const;

export const UserReferenceResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid'
    ],
    title: 'UserReferenceResponse',
    description: 'Use wherever the reader is not entitled to the account\'s state.'
} as const;

export const UserReferenceWithFlagsResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid'
    ],
    title: 'UserReferenceWithFlagsResponse',
    description: '`UserReferenceResponse` plus the merged feature-flag registry - used\nonly on patient-scoped reads (`connected_users`), so a plain physician\n(`MEDICAL_READ`) can see what they can already set via the\n`MEDICAL_UPDATE` carve-out, without needing `MONITORING_READ`.'
} as const;

export const UserResponseSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponse'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at'
    ],
    title: 'UserResponse',
    description: 'Schema for returning user info as a response.'
} as const;

export const UserResponseManagingSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponse'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        visible_patients: {
            items: {
                $ref: '#/components/schemas/UserConnectedPatientResponse'
            },
            type: 'array',
            title: 'Visible Patients',
            description: 'Get managed patients from access policy.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at',
        'visible_patients'
    ],
    title: 'UserResponseManaging'
} as const;

export const UserRoleSchema = {
    type: 'string',
    enum: [
        'patient-ppg-app',
        'physician-ppg-app',
        'study-ppg-app',
        'patient',
        'physician',
        'study-physician',
        'seerlinq-user',
        'admin',
        'screening-user'
    ],
    title: 'UserRole'
} as const;

export const UserStatusSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2,
        3,
        4,
        5
    ],
    title: 'UserStatus',
    'x-enum-varnames': [
        'not_active',
        'active',
        'reset_by_user',
        'reset_by_admin',
        'temporary_password',
        'archived'
    ],
    description: ' - 0: not_active\n - 1: active\n - 2: reset_by_user\n - 3: reset_by_admin\n - 4: temporary_password\n - 5: archived'
} as const;

export const ValidateAlertSchemaSchema = {
    properties: {
        alert_seen: {
            type: 'boolean',
            const: true,
            title: 'Alert Seen',
            default: true
        },
        alert_validation: {
            $ref: '#/components/schemas/AlertType'
        },
        alert_validation_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Validation Comment'
        },
        alert_validated_by_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Validated By Uuid'
        },
        append_alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Append Alert Tags'
        },
        remove_alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remove Alert Tags'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'alert_validation'
    ],
    title: 'ValidateAlertSchema'
} as const;

export const ValidationErrorSchema = {
    properties: {
        loc: {
            items: {
                anyOf: [
                    {
                        type: 'string'
                    },
                    {
                        type: 'integer'
                    }
                ]
            },
            type: 'array',
            title: 'Location'
        },
        msg: {
            type: 'string',
            title: 'Message'
        },
        type: {
            type: 'string',
            title: 'Error Type'
        },
        input: {
            title: 'Input'
        },
        ctx: {
            type: 'object',
            title: 'Context'
        }
    },
    type: 'object',
    required: [
        'loc',
        'msg',
        'type'
    ],
    title: 'ValidationError'
} as const;

export const ValueBasedThresholdSettingsSchema = {
    properties: {
        threshold_trigger: {
            type: 'string',
            const: 'value_based',
            title: 'Threshold Trigger',
            default: 'value_based'
        },
        at_least: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'At Least'
        },
        out_of: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Out Of'
        },
        maximum_measurements: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Maximum Measurements',
            description: 'Default is the same as `out_of`'
        }
    },
    type: 'object',
    required: [
        'at_least',
        'out_of'
    ],
    title: 'ValueBasedThresholdSettings'
} as const;

export const ValueCellSchema = {
    properties: {
        value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Value'
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        measured_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measured At'
        },
        quality_flag: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QualityFlagInfo'
                },
                {
                    type: 'null'
                }
            ]
        },
        threshold: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ThresholdInfo'
                },
                {
                    type: 'null'
                }
            ]
        },
        duplicates: {
            type: 'integer',
            minimum: 0,
            title: 'Duplicates',
            default: 0
        }
    },
    type: 'object',
    title: 'ValueCell',
    description: 'A single scalar value rendered in one column of one session.'
} as const;

export const ValvularInterventionSchemaResponseSchema = {
    properties: {
        diagnosis_name: {
            type: 'string',
            const: 'Valvular intervention',
            title: 'Diagnosis Name'
        },
        diagnosis_value: {
            anyOf: [
                {
                    type: 'string',
                    enum: [
                        'No',
                        'SAVR',
                        'TAVR',
                        'MVR',
                        'MVP',
                        'TEER',
                        'TVR',
                        'TVP',
                        'Other'
                    ]
                },
                {
                    type: 'integer',
                    maximum: 8,
                    minimum: 0
                }
            ],
            title: 'Diagnosis Value'
        },
        diagnosis_confidence: {
            anyOf: [
                {
                    type: 'integer',
                    multipleOf: 25,
                    maximum: 100,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosis Confidence',
            default: 100
        },
        diagnosed_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Diagnosed At'
        },
        remission: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Remission'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'diagnosis_name',
        'diagnosis_value',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ValvularInterventionSchemaResponse'
} as const;

export const VasodilatorsSchema = {
    properties: {
        medication_group: {
            type: 'string',
            const: 'Vasodilators',
            title: 'Medication Group'
        },
        medication_name: {
            type: 'string',
            title: 'Medication Name'
        },
        medication_started: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Started'
        },
        medication_ended: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Ended'
        },
        medication_dose: {
            anyOf: [
                {
                    type: 'number',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Dose'
        },
        medication_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Unit'
        },
        medication_dosage: {
            items: {
                type: 'number',
                multipleOf: 0.25,
                minimum: 0
            },
            type: 'array',
            maxItems: 4,
            minItems: 4,
            title: 'Medication Dosage'
        },
        medication_frequency_regular_repeat_every: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 50,
                    minimum: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency Regular Repeat Every',
            default: 1
        },
        medication_frequency_regular_repeat_every_unit: {
            $ref: '#/components/schemas/FrequencyUnit',
            default: 'days'
        },
        medication_frequency_on_days: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Days'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medication Frequency On Days'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        medication_change_auto_event: {
            type: 'boolean',
            title: 'Medication Change Auto Event',
            default: false
        }
    },
    type: 'object',
    required: [
        'medication_group',
        'medication_name',
        'medication_dosage',
        'patient_id'
    ],
    title: 'Vasodilators'
} as const;

export const ViatomBatteryStateSchema = {
    type: 'integer',
    enum: [
        0,
        1,
        2
    ],
    title: 'ViatomBatteryState',
    'x-enum-varnames': [
        'not_charging',
        'charging',
        'charge_complete'
    ],
    description: ' - 0: not_charging\n - 1: charging\n - 2: charge_complete'
} as const;

export const ViatomDeviceStatusSchema = {
    properties: {
        battery: {
            type: 'integer',
            maximum: 100,
            minimum: 0,
            title: 'Battery'
        },
        battery_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ViatomBatteryState'
                },
                {
                    type: 'null'
                }
            ]
        },
        state_stats: {
            additionalProperties: {
                type: 'integer',
                minimum: 0
            },
            propertyNames: {
                enum: [
                    'lead_off',
                    'lead_on'
                ]
            },
            type: 'object',
            title: 'State Stats'
        },
        max_gap_ms: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Max Gap Ms'
        },
        signal_gaps: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SignalGaps'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'battery',
        'state_stats'
    ],
    title: 'ViatomDeviceStatus'
} as const;

export const VisitAdminProcedureSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'admin',
            title: 'Data Type',
            default: 'admin'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitAdminRequiredData'
        },
        associated_data: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicalStudyAdminProcedureResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'associated_data'
    ],
    title: 'VisitAdminProcedure'
} as const;

export const VisitAdminRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'admin',
            title: 'Data Type',
            default: 'admin'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        procedure: {
            type: 'string',
            title: 'Procedure'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'procedure',
        'name'
    ],
    title: 'VisitAdminRequiredData'
} as const;

export const VisitAvailableComputedDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'computed',
            title: 'Data Type',
            default: 'computed'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitComputedRequiredData'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponse'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponse'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableComputedData'
} as const;

export const VisitAvailableDiagnosesSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'diagnosis',
            title: 'Data Type',
            default: 'diagnosis'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitDiagnosisRequiredData'
        },
        existing_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableDiagnoses'
} as const;

export const VisitAvailableMedicalDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitMedicalRequiredData'
        },
        existing_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Existing Data'
        },
        existing_sq_computed: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponse'
            },
            type: 'array',
            title: 'Existing Sq Computed'
        },
        existing_ppg_derived: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponse'
            },
            type: 'array',
            title: 'Existing Ppg Derived'
        },
        associated_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'existing_sq_computed',
        'existing_ppg_derived',
        'associated_data'
    ],
    title: 'VisitAvailableMedicalData'
} as const;

export const VisitAvailablePPGDerivedDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'derived',
            title: 'Data Type',
            default: 'derived'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitPPGDerivedRequiredData'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponse'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponse'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailablePPGDerivedData'
} as const;

export const VisitAvailablePPGsSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitPPGRequiredData'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailablePPGs'
} as const;

export const VisitAvailableQuestionnairesSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'questionnaire',
            title: 'Data Type',
            default: 'questionnaire'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitQuestionnaireRequiredData'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponse'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponse'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableQuestionnaires'
} as const;

export const VisitAvailableSymptomsSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitSymptomRequiredData'
        },
        existing_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableSymptoms'
} as const;

export const VisitCalendarEventSchema = {
    properties: {
        event_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Event Datetime'
        },
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        pretty_event_name: {
            type: 'string',
            title: 'Pretty Event Name',
            description: 'Return reasonable event name as a pretty single string.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'event_datetime',
        'study_acronym',
        'visit_type',
        'patient_id',
        'name',
        'pretty_event_name'
    ],
    title: 'VisitCalendarEvent'
} as const;

export const VisitComputedRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'computed',
            title: 'Data Type',
            default: 'computed'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            type: 'string',
            const: 'computed',
            title: 'Required Data Source',
            default: 'computed'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        minimum_sqi: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Minimum Sqi',
            default: 3
        },
        required_devices: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Devices'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'name'
    ],
    title: 'VisitComputedRequiredData'
} as const;

export const VisitDiagnosisRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'diagnosis',
            title: 'Data Type',
            default: 'diagnosis'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        category: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Category'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'name'
    ],
    title: 'VisitDiagnosisRequiredData'
} as const;

export const VisitEventPlannedCategorySchema = {
    type: 'string',
    enum: [
        'outpatient',
        'outpatient_seerlinq',
        'er_seerlinq',
        'study_visit'
    ],
    title: 'VisitEventPlannedCategory'
} as const;

export const VisitEventSchemaSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/UnplannedVisitEventSchema'
        },
        {
            $ref: '#/components/schemas/PlannedVisitEventSchema'
        }
    ],
    discriminator: {
        propertyName: 'event_category_type',
        mapping: {
            planned: '#/components/schemas/PlannedVisitEventSchema',
            unplanned: '#/components/schemas/UnplannedVisitEventSchema'
        }
    }
} as const;

export const VisitEventTypeSchema = {
    type: 'string',
    enum: [
        'unplanned',
        'planned'
    ],
    title: 'VisitEventType'
} as const;

export const VisitEventUnplannedCategorySchema = {
    type: 'string',
    enum: [
        'er_urgent_hf_iv_diuretics',
        'outpatient',
        'study_visit',
        'er_cv',
        'er_non_cv'
    ],
    title: 'VisitEventUnplannedCategory'
} as const;

export const VisitMedicalRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesFull'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        category: {
            $ref: '#/components/schemas/MedicalDataCategory'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'category',
        'name'
    ],
    title: 'VisitMedicalRequiredData',
    description: 'Can be used for data derived or computed from the PPG, but this data must be\nalready part of our supported data list (e.g., SpO2 or heart rate).'
} as const;

export const VisitPPGDerivedRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'derived',
            title: 'Data Type',
            default: 'derived'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            type: 'string',
            const: 'derived',
            title: 'Required Data Source',
            default: 'derived'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        measurement_condition: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Condition'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length'
        },
        minimum_sqi: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Minimum Sqi',
            default: 3
        },
        required_devices: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Devices'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'measurement_condition',
        'minimum_length',
        'name'
    ],
    title: 'VisitPPGDerivedRequiredData'
} as const;

export const VisitPPGRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesSimple'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length'
        },
        minimum_sqi: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Minimum Sqi',
            default: 3
        },
        required_devices: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Devices'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'measurement_condition',
        'minimum_length',
        'name'
    ],
    title: 'VisitPPGRequiredData'
} as const;

export const VisitQuestionnaireRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'questionnaire',
            title: 'Data Type',
            default: 'questionnaire'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesSimple'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'questionnaire_id',
        'name'
    ],
    title: 'VisitQuestionnaireRequiredData'
} as const;

export const VisitRequiredDataSourcesFullSchema = {
    type: 'string',
    enum: [
        'patient',
        'physician',
        'derived',
        'computed'
    ],
    title: 'VisitRequiredDataSourcesFull'
} as const;

export const VisitRequiredDataSourcesSimpleSchema = {
    type: 'string',
    enum: [
        'patient',
        'physician'
    ],
    title: 'VisitRequiredDataSourcesSimple'
} as const;

export const VisitSymptomRequiredDataSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesSimple'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        category: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Category'
        },
        name: {
            type: 'string',
            title: 'Name',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'name'
    ],
    title: 'VisitSymptomRequiredData'
} as const;

export const WaitingRoomEventsSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date'
        },
        event_category: {
            $ref: '#/components/schemas/EventCategory'
        },
        event_category_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Type'
        },
        event_category_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        added_by: {
            type: 'string',
            title: 'Added By'
        },
        event_color_hex: {
            type: 'string',
            title: 'Event Color Hex',
            description: 'Event marker color as a HEX value.',
            readOnly: true
        },
        event_shape: {
            type: 'string',
            title: 'Event Shape',
            description: 'Event marker shape as a named string.',
            readOnly: true
        }
    },
    type: 'object',
    required: [
        'event_date',
        'event_category',
        'event_category_type',
        'event_category_details',
        'event_description',
        'added_by',
        'event_color_hex',
        'event_shape'
    ],
    title: 'WaitingRoomEvents'
} as const;

export const WebAuthnCredentialResponseSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid',
            title: 'Uuid'
        },
        device_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Device Name'
        },
        aaguid: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Aaguid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        last_used_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Used At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'device_name',
        'aaguid',
        'created_at',
        'last_used_at'
    ],
    title: 'WebAuthnCredentialResponse'
} as const;

export const WebAuthnOptionsResponseSchema = {
    properties: {
        options: {
            type: 'string',
            title: 'Options'
        }
    },
    type: 'object',
    required: [
        'options'
    ],
    title: 'WebAuthnOptionsResponse',
    description: '`options` is the raw JSON string from `webauthn.options_to_json()`,\npassed through as-is to `navigator.credentials.create()`/`.get()` -\nkept opaque here rather than mirrored field-by-field to avoid tracking\nthe `webauthn` library\'s own ceremony-options shape in a second place.'
} as const;

export const WeightSchemaResponseSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            const: 'weight',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            maximum: 350,
            minimum: 30,
            title: 'Measurement Value'
        },
        measurement_unit: {
            type: 'string',
            const: 'kg',
            title: 'Measurement Unit'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        update_history: {
            items: {
                $ref: '#/components/schemas/ItemHistory'
            },
            type: 'array',
            title: 'Update History'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'measurement_unit',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'WeightSchemaResponse'
} as const;

export const WindowStatSchema = {
    properties: {
        window_days: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Window Days'
        },
        average: {
            type: 'number',
            title: 'Average'
        },
        pct_change: {
            type: 'number',
            title: 'Pct Change'
        }
    },
    type: 'object',
    required: [
        'window_days',
        'average',
        'pct_change'
    ],
    title: 'WindowStat',
    description: 'Mean over a window ending now and % change of the line\'s last value vs that\nmean. NaN when the window has too few readings.'
} as const;

export const AuditRoomResponseWritableSchema = {
    properties: {
        fetched: {
            type: 'string',
            format: 'date-time',
            title: 'Fetched'
        },
        audit_room: {
            items: {
                $ref: '#/components/schemas/AuditRoomRowWritable'
            },
            type: 'array',
            title: 'Audit Room'
        },
        audit_room_timespan: {
            type: 'string',
            format: 'duration',
            title: 'Audit Room Timespan'
        }
    },
    type: 'object',
    required: [
        'fetched',
        'audit_room',
        'audit_room_timespan'
    ],
    title: 'AuditRoomResponse'
} as const;

export const AuditRoomRowWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            minLength: 1,
            title: 'Name'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region'
        },
        patient_state: {
            $ref: '#/components/schemas/PatientState'
        },
        clinical_risk_score: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Clinical Risk Score'
        },
        audit_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Audit Alerts'
        },
        last_events: {
            items: {
                $ref: '#/components/schemas/WaitingRoomEventsWritable'
            },
            type: 'array',
            title: 'Last Events'
        },
        monitoring_watch_kind: {
            $ref: '#/components/schemas/PatientMonitoringWatchKind'
        },
        monitoring_watch_status: {
            $ref: '#/components/schemas/PatientMonitoringWatchStatus'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'name',
        'country',
        'region',
        'patient_state',
        'clinical_risk_score',
        'audit_alerts',
        'last_events',
        'monitoring_watch_kind',
        'monitoring_watch_status'
    ],
    title: 'AuditRoomRow'
} as const;

export const ClinicWithAssignedUsersResponseWritableSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            type: 'string',
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        physicians: {
            items: {
                $ref: '#/components/schemas/PhysicianResponseWritable'
            },
            type: 'array',
            title: 'Physicians'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/SimpleUserResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        }
    },
    type: 'object',
    required: [
        'country',
        'name',
        'address',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicWithAssignedUsersResponse'
} as const;

export const ClinicalEventWritableSchema = {
    properties: {
        date: {
            type: 'string',
            format: 'date-time',
            title: 'Date'
        },
        timestamp: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Timestamp'
        },
        event_category: {
            $ref: '#/components/schemas/EventCategory'
        },
        event_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Type'
        },
        event_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Details'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        }
    },
    type: 'object',
    required: [
        'date',
        'event_category'
    ],
    title: 'ClinicalEvent',
    description: 'Clinical event marker.'
} as const;

export const ClinicalStudyDefinitionWritableSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        full_title: {
            type: 'string',
            title: 'Full Title'
        },
        protocol_version: {
            type: 'string',
            title: 'Protocol Version'
        },
        protocol_date: {
            type: 'string',
            format: 'date',
            title: 'Protocol Date'
        },
        study_category: {
            $ref: '#/components/schemas/StudyCategory'
        },
        study_temporal_dimension: {
            $ref: '#/components/schemas/StudyTemporalDimension'
        },
        study_control_structure: {
            $ref: '#/components/schemas/StudyControlStructure'
        },
        study_randomization: {
            $ref: '#/components/schemas/StudyRandomization'
        },
        study_stage: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 4,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Stage'
        },
        planned_start: {
            type: 'string',
            format: 'date',
            title: 'Planned Start'
        },
        planned_end: {
            type: 'string',
            format: 'date',
            title: 'Planned End'
        },
        inclusion_criteria: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Inclusion Criteria'
        },
        exclusion_criteria: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Exclusion Criteria'
        },
        planned_patients: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Planned Patients'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        admin_procedures: {
            anyOf: [
                {
                    items: {
                        additionalProperties: true,
                        type: 'object'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Admin Procedures'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitDefinitionWritable'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'full_title',
        'protocol_version',
        'protocol_date',
        'study_category',
        'study_temporal_dimension',
        'study_control_structure',
        'study_randomization',
        'planned_start',
        'planned_end',
        'inclusion_criteria',
        'exclusion_criteria',
        'planned_patients',
        'study_visits'
    ],
    title: 'ClinicalStudyDefinition'
} as const;

export const ClinicalStudyEnrollmentResponseWritableSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        enrollment_date: {
            type: 'string',
            format: 'date',
            title: 'Enrollment Date'
        },
        enrollment_active: {
            type: 'boolean',
            title: 'Enrollment Active'
        },
        enrollment_activity_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Enrollment Activity Reason'
        },
        schedule: {
            items: {
                $ref: '#/components/schemas/StudyVisitSchedule-Output'
            },
            type: 'array',
            title: 'Schedule'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitResponseWritable'
            },
            type: 'array',
            title: 'Study Visits'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'enrollment_date',
        'enrollment_active',
        'schedule',
        'patient_id',
        'uuid',
        'study_visits',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicalStudyEnrollmentResponse'
} as const;

export const ClinicalStudyVisitDefinitionWritableSchema = {
    properties: {
        parent_study: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_schedule: {
            type: 'string',
            format: 'duration',
            title: 'Visit Schedule'
        },
        visit_schedule_limits: {
            prefixItems: [
                {
                    anyOf: [
                        {
                            type: 'string',
                            format: 'duration'
                        },
                        {
                            type: 'null'
                        }
                    ]
                },
                {
                    anyOf: [
                        {
                            type: 'string',
                            format: 'duration'
                        },
                        {
                            type: 'null'
                        }
                    ]
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Visit Schedule Limits'
        },
        visit_location_type: {
            $ref: '#/components/schemas/StudyVisitLocation'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        data_list: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/VisitAdminRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitDiagnosisRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitMedicalRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitSymptomRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitQuestionnaireRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitPPGRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitComputedRequiredDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitPPGDerivedRequiredDataWritable'
                    }
                ],
                discriminator: {
                    propertyName: 'data_type',
                    mapping: {
                        admin: '#/components/schemas/VisitAdminRequiredDataWritable',
                        computed: '#/components/schemas/VisitComputedRequiredDataWritable',
                        derived: '#/components/schemas/VisitPPGDerivedRequiredDataWritable',
                        diagnosis: '#/components/schemas/VisitDiagnosisRequiredDataWritable',
                        'medical-data': '#/components/schemas/VisitMedicalRequiredDataWritable',
                        ppg: '#/components/schemas/VisitPPGRequiredDataWritable',
                        questionnaire: '#/components/schemas/VisitQuestionnaireRequiredDataWritable',
                        symptom: '#/components/schemas/VisitSymptomRequiredDataWritable'
                    }
                }
            },
            type: 'array',
            title: 'Data List'
        }
    },
    type: 'object',
    required: [
        'parent_study',
        'visit_type',
        'visit_schedule',
        'visit_schedule_limits',
        'visit_location_type',
        'data_list'
    ],
    title: 'ClinicalStudyVisitDefinition'
} as const;

export const ClinicalStudyVisitPrefillDataResponseWritableSchema = {
    properties: {
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Visit Datetime'
        },
        study_visit_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Visit Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        data_list: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/VisitAdminProcedureWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableDiagnosesWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableMedicalDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableSymptomsWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableQuestionnairesWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailablePPGsWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailableComputedDataWritable'
                    },
                    {
                        $ref: '#/components/schemas/VisitAvailablePPGDerivedDataWritable'
                    }
                ],
                discriminator: {
                    propertyName: 'data_type',
                    mapping: {
                        admin: '#/components/schemas/VisitAdminProcedureWritable',
                        computed: '#/components/schemas/VisitAvailableComputedDataWritable',
                        derived: '#/components/schemas/VisitAvailablePPGDerivedDataWritable',
                        diagnosis: '#/components/schemas/VisitAvailableDiagnosesWritable',
                        'medical-data': '#/components/schemas/VisitAvailableMedicalDataWritable',
                        ppg: '#/components/schemas/VisitAvailablePPGsWritable',
                        questionnaire: '#/components/schemas/VisitAvailableQuestionnairesWritable',
                        symptom: '#/components/schemas/VisitAvailableSymptomsWritable'
                    }
                }
            },
            type: 'array',
            title: 'Data List'
        }
    },
    type: 'object',
    required: [
        'visit_type',
        'visit_datetime',
        'study_visit_uuid',
        'patient_id',
        'data_list'
    ],
    title: 'ClinicalStudyVisitPrefillDataResponse'
} as const;

export const ClinicalStudyVisitResponseWritableSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        protocol_version: {
            type: 'string',
            title: 'Protocol Version'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Visit Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        study_enrollment_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Study Enrollment Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        schedule_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Schedule Timedelta'
        },
        admin_procedures: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyAdminProcedureResponse'
            },
            type: 'array',
            title: 'Admin Procedures'
        },
        diagnoses: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Diagnoses'
        },
        medical_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Medical Data'
        },
        medical_reports: {
            items: {
                $ref: '#/components/schemas/MedicalReportResponse'
            },
            type: 'array',
            title: 'Medical Reports'
        },
        medications: {
            items: {
                $ref: '#/components/schemas/DefaultMedicationSchemaResponse'
            },
            type: 'array',
            title: 'Medications'
        },
        symptoms: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Symptoms'
        },
        questionnaires: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponseWritable'
            },
            type: 'array',
            title: 'Questionnaires'
        },
        events: {
            items: {
                $ref: '#/components/schemas/DefaultPatientEventResponseWritable'
            },
            type: 'array',
            title: 'Events'
        },
        ppgs: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Ppgs'
        },
        sq_computed_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponseWritable'
            },
            type: 'array',
            title: 'Sq Computed Data'
        },
        ppg_derived_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponseWritable'
            },
            type: 'array',
            title: 'Ppg Derived Data'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'protocol_version',
        'visit_type',
        'visit_datetime',
        'study_enrollment_uuid',
        'patient_id',
        'uuid',
        'schedule_timedelta',
        'admin_procedures',
        'diagnoses',
        'medical_data',
        'medical_reports',
        'medications',
        'symptoms',
        'questionnaires',
        'events',
        'ppgs',
        'sq_computed_data',
        'ppg_derived_data',
        'created_at',
        'updated_at'
    ],
    title: 'ClinicalStudyVisitResponse'
} as const;

export const ComplianceResponseWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        date_from: {
            type: 'string',
            format: 'date',
            title: 'Date From'
        },
        date_to: {
            type: 'string',
            format: 'date',
            title: 'Date To'
        },
        timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Timezone'
        },
        schedules: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/FullMeasurementCompliance'
                    },
                    {
                        $ref: '#/components/schemas/SingleScheduleCompliance'
                    }
                ]
            },
            type: 'array',
            title: 'Schedules'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'date_from',
        'date_to',
        'timezone',
        'schedules'
    ],
    title: 'ComplianceResponse'
} as const;

export const CountryRegionsResponseWritableSchema = {
    properties: {
        countries: {
            additionalProperties: {
                $ref: '#/components/schemas/CountrySchemaWritable'
            },
            type: 'object',
            title: 'Countries'
        }
    },
    type: 'object',
    required: [
        'countries'
    ],
    title: 'CountryRegionsResponse'
} as const;

export const CountrySchemaWritableSchema = {
    properties: {
        country_name: {
            type: 'string',
            title: 'Country Name'
        },
        regions: {
            items: {
                $ref: '#/components/schemas/RegionSchema'
            },
            type: 'array',
            title: 'Regions'
        }
    },
    type: 'object',
    required: [
        'country_name',
        'regions'
    ],
    title: 'CountrySchema'
} as const;

export const CreatePatientAndUserResponseWritableSchema = {
    properties: {
        patient: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DefaultPatientResponseWritable'
                },
                {
                    $ref: '#/components/schemas/Level3PatientResponseWritable'
                }
            ],
            title: 'Patient'
        },
        new_user: {
            $ref: '#/components/schemas/UserResponseWritable'
        },
        qr_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^data:image/png;base64,.*'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Qr Code'
        },
        ses_send_mail_task_id: {
            type: 'string',
            maxLength: 32,
            minLength: 32,
            title: 'Ses Send Mail Task Id'
        }
    },
    type: 'object',
    required: [
        'patient',
        'new_user',
        'ses_send_mail_task_id'
    ],
    title: 'CreatePatientAndUserResponse'
} as const;

export const CreatePaymentUserRequestWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        initial_credits: {
            type: 'integer',
            maximum: 1000000,
            minimum: 0,
            title: 'Initial Credits',
            description: 'Credits the account is opened with.',
            default: 101
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'username',
        'password',
        'email'
    ],
    title: 'CreatePaymentUserRequest',
    description: 'A paid-screening user: the login and its starting credits. The patient\nand physician rows the account needs are filled in by provisioning, since\nnone of it describes a real person.'
} as const;

export const CreateUserSchemaWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        visible_patient_ids: {
            anyOf: [
                {
                    items: {
                        type: 'integer',
                        exclusiveMinimum: 0
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patient Ids'
        },
        assigned_physicians_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Physicians Uuids'
        },
        assigned_clinics_uuids: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        format: 'uuid4'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Assigned Clinics Uuids'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'password'
    ],
    title: 'CreateUserSchema'
} as const;

export const DefaultClinicalPatientResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientFacingPhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        diagnoses: {
            type: 'integer',
            title: 'Diagnoses'
        },
        medical_data: {
            type: 'integer',
            title: 'Medical Data'
        },
        medications: {
            type: 'integer',
            title: 'Medications'
        },
        symptoms: {
            type: 'integer',
            title: 'Symptoms'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-OutputWritable'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'diagnoses',
        'medical_data',
        'medications',
        'symptoms'
    ],
    title: 'DefaultClinicalPatientResponse'
} as const;

export const DefaultECGDerivedDataSchemaResponseWritableSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/ECGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    $ref: '#/components/schemas/BeatAnnotationWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Type'
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataAggregationFunction'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/ECGRelatedQualityFlag',
            default: 1
        },
        custom_statement: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CustomECGStatement'
                },
                {
                    type: 'null'
                }
            ]
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'window_type',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'custom_statement',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultECGDerivedDataSchemaResponse'
} as const;

export const DefaultFullECGDataSchemaResponseWritableSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        leads_placement: {
            items: {
                type: 'string'
            },
            type: 'array',
            title: 'Leads Placement'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/ECGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_device_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AliveCorECGDeviceStatus'
                },
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        measurement_mode: {
            $ref: '#/components/schemas/ECGMeasurementMode',
            default: 3
        },
        leads: {
            items: {
                $ref: '#/components/schemas/ECGLead'
            },
            type: 'array',
            maxItems: 12,
            minItems: 1,
            title: 'Leads'
        },
        sampling_frequency: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Sampling Frequency'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        ecg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Ecg'
        },
        auxiliary_time_series: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/AuxiliaryTimeSeriesRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auxiliary Time Series'
        },
        derived_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ECGDerivedDataResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_device',
        'leads_placement',
        'patient_id',
        'uuid',
        'leads',
        'sampling_frequency',
        'length',
        'created_at',
        'updated_at',
        'ecg'
    ],
    title: 'DefaultFullECGDataSchemaResponse'
} as const;

export const DefaultFullPPGDataSchemaResponseWritableSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        measurement_device: {
            type: 'string',
            title: 'Measurement Device'
        },
        measurement_origin: {
            type: 'string',
            title: 'Measurement Origin'
        },
        facility: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facility'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        quality_flag: {
            $ref: '#/components/schemas/PPGQualityFlag',
            default: 1
        },
        computed_sqi: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Computed Sqi'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_device_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/BerryOxiDeviceStatus'
                },
                {
                    $ref: '#/components/schemas/ViatomDeviceStatus'
                },
                {
                    $ref: '#/components/schemas/SamsungWatchDeviceStatus'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Device Status'
        },
        measurement_mode: {
            $ref: '#/components/schemas/PPGMeasurementMode',
            default: 1
        },
        wavelengths: {
            items: {
                $ref: '#/components/schemas/PPGWavelength'
            },
            type: 'array',
            minItems: 1,
            title: 'Wavelengths'
        },
        sampling_frequency: {
            items: {
                type: 'number',
                exclusiveMinimum: 0
            },
            type: 'array',
            title: 'Sampling Frequency'
        },
        length: {
            type: 'number',
            exclusiveMinimum: 0,
            title: 'Length'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppg: {
            items: {
                items: {
                    type: 'number'
                },
                type: 'array',
                minItems: 1
            },
            type: 'array',
            title: 'Ppg'
        },
        auxiliary_time_series: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/AuxiliaryTimeSeriesRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auxiliary Time Series'
        },
        sq_computed_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SeerlinqComputedRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Sq Computed Data'
        },
        derived_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PPGDerivedDataResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_condition',
        'measurement_device',
        'measurement_origin',
        'patient_id',
        'uuid',
        'wavelengths',
        'sampling_frequency',
        'length',
        'created_at',
        'updated_at',
        'ppg'
    ],
    title: 'DefaultFullPPGDataSchemaResponse'
} as const;

export const DefaultMeasurementConfigResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        config: {
            items: {
                $ref: '#/components/schemas/MeasurementRequiredData-OutputWritable'
            },
            type: 'array',
            minItems: 1,
            title: 'Config'
        }
    },
    type: 'object',
    required: [
        'status',
        'config'
    ],
    title: 'DefaultMeasurementConfigResponse'
} as const;

export const DefaultPPGDerivedDataSchemaResponseWritableSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/PPGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            $ref: '#/components/schemas/DerivedDataAggregationFunction'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultPPGDerivedDataSchemaResponse'
} as const;

export const DefaultPatientAlertResponseWritableSchema = {
    properties: {
        alert_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Alert Timestamp'
        },
        alert_kind: {
            $ref: '#/components/schemas/AlertKind'
        },
        alert_variable: {
            type: 'string',
            title: 'Alert Variable'
        },
        alert_type: {
            $ref: '#/components/schemas/AlertType'
        },
        alert_value: {
            type: 'number',
            title: 'Alert Value'
        },
        alert_reason: {
            type: 'string',
            title: 'Alert Reason'
        },
        alert_table: {
            $ref: '#/components/schemas/AlertTables'
        },
        alert_item_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Alert Item Uuid'
        },
        alert_tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        alert_seen: {
            type: 'boolean',
            title: 'Alert Seen'
        },
        alert_validated_by: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        alert_validation: {
            anyOf: [
                {
                    $ref: '#/components/schemas/AlertType'
                },
                {
                    type: 'null'
                }
            ]
        },
        alert_validation_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alert Validation Comment'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'alert_timestamp',
        'alert_kind',
        'alert_variable',
        'alert_type',
        'alert_value',
        'alert_reason',
        'alert_table',
        'alert_item_uuid',
        'patient_id',
        'uuid',
        'alert_seen',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultPatientAlertResponse'
} as const;

export const DefaultPatientEventResponseWritableSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date'
        },
        event_timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Event Timestamp'
        },
        event_category: {
            $ref: '#/components/schemas/EventCategory'
        },
        event_category_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Type'
        },
        event_category_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        event_comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Comment'
        },
        event_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/EventTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        event_item_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Item Uuid'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'event_date',
        'event_timestamp',
        'event_category',
        'event_description',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultPatientEventResponse'
} as const;

export const DefaultPatientResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/UserReferenceResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientFacingPhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-OutputWritable'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id'
    ],
    title: 'DefaultPatientResponse'
} as const;

export const DefaultSeerlinqComputedBasicResponseWritableSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultSeerlinqComputedBasicResponse'
} as const;

export const DefaultSeerlinqComputedFullResponseWritableSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppgs: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PPGDataRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppgs'
        },
        seerlinq_measurement_quality_index: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Seerlinq Measurement Quality Index'
        },
        ground_truth_table: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ComputedGroundTruthTables'
                },
                {
                    type: 'null'
                }
            ]
        },
        ground_truth_data_name: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ground Truth Data Name'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'DefaultSeerlinqComputedFullResponse'
} as const;

export const ECGDerivedDataResponseWritableSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/ECGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    $ref: '#/components/schemas/BeatAnnotationWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Type'
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataAggregationFunction'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/ECGRelatedQualityFlag',
            default: 1
        },
        custom_statement: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CustomECGStatement'
                },
                {
                    type: 'null'
                }
            ]
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'window_type',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'custom_statement',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'ECGDerivedDataResponse'
} as const;

export const ECGDerivedDataSchemaWithECGResponseWritableSchema = {
    properties: {
        ecg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ecg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/ECGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    $ref: '#/components/schemas/BeatAnnotationWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Type'
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataAggregationFunction'
                },
                {
                    type: 'null'
                }
            ]
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/ECGRelatedQualityFlag',
            default: 1
        },
        custom_statement: {
            anyOf: [
                {
                    $ref: '#/components/schemas/CustomECGStatement'
                },
                {
                    type: 'null'
                }
            ]
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        ecg: {
            $ref: '#/components/schemas/ECGDataRelatedResponse'
        }
    },
    type: 'object',
    required: [
        'ecg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'window_type',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'custom_statement',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'ecg'
    ],
    title: 'ECGDerivedDataSchemaWithECGResponse'
} as const;

export const ErrorCodeResponseWritableSchema = {
    additionalProperties: {
        type: 'string'
    },
    propertyNames: {
        $ref: '#/components/schemas/ErrorCode'
    },
    type: 'object',
    title: 'ErrorCodeResponse'
} as const;

export const FilledQuestionnaireResponseWritableSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        questionnaire_language: {
            type: 'string',
            enum: [
                'ar',
                'az',
                'cs',
                'da',
                'de',
                'el',
                'en',
                'es',
                'fr',
                'he',
                'hi',
                'hu',
                'it',
                'ja',
                'lt',
                'lv',
                'nb',
                'pl',
                'pt',
                'ro',
                'ru',
                'sk',
                'sv',
                'tr',
                'uk',
                'zh-Hans',
                'zh-Hant'
            ],
            title: 'SupportedLanguages',
            description: 'Languages supported by Seerlinq, as BCP 47 language tags (ISO 639-1 code,\nplus a script subtag for Chinese). The `x-seerlinq-languages` schema\nextension lists one record per language: ISO codes, English and native\nname, text direction and target regions (first one is the default\nvariant).',
            'x-seerlinq-languages': [
                {
                    alpha2: 'ar',
                    alpha3: 'ara',
                    code: 'ar',
                    dir: 'rtl',
                    name: 'Arabic',
                    native_name: 'العربية',
                    regions: [
                        'SA'
                    ]
                },
                {
                    alpha2: 'az',
                    alpha3: 'aze',
                    code: 'az',
                    dir: 'ltr',
                    name: 'Azerbaijani',
                    native_name: 'Azərbaycan dili',
                    regions: []
                },
                {
                    alpha2: 'cs',
                    alpha3: 'ces',
                    code: 'cs',
                    dir: 'ltr',
                    name: 'Czech',
                    native_name: 'Čeština',
                    regions: []
                },
                {
                    alpha2: 'da',
                    alpha3: 'dan',
                    code: 'da',
                    dir: 'ltr',
                    name: 'Danish',
                    native_name: 'Dansk',
                    regions: []
                },
                {
                    alpha2: 'de',
                    alpha3: 'deu',
                    code: 'de',
                    dir: 'ltr',
                    name: 'German',
                    native_name: 'Deutsch',
                    regions: [
                        'DE',
                        'AT'
                    ]
                },
                {
                    alpha2: 'el',
                    alpha3: 'ell',
                    code: 'el',
                    dir: 'ltr',
                    name: 'Greek',
                    native_name: 'Ελληνικά',
                    regions: []
                },
                {
                    alpha2: 'en',
                    alpha3: 'eng',
                    code: 'en',
                    dir: 'ltr',
                    name: 'English',
                    native_name: 'English',
                    regions: [
                        'GB'
                    ]
                },
                {
                    alpha2: 'es',
                    alpha3: 'spa',
                    code: 'es',
                    dir: 'ltr',
                    name: 'Spanish',
                    native_name: 'Español',
                    regions: [
                        'ES'
                    ]
                },
                {
                    alpha2: 'fr',
                    alpha3: 'fra',
                    code: 'fr',
                    dir: 'ltr',
                    name: 'French',
                    native_name: 'Français',
                    regions: [
                        'FR'
                    ]
                },
                {
                    alpha2: 'he',
                    alpha3: 'heb',
                    code: 'he',
                    dir: 'rtl',
                    name: 'Hebrew',
                    native_name: 'עברית',
                    regions: []
                },
                {
                    alpha2: 'hi',
                    alpha3: 'hin',
                    code: 'hi',
                    dir: 'ltr',
                    name: 'Hindi',
                    native_name: 'हिन्दी',
                    regions: []
                },
                {
                    alpha2: 'hu',
                    alpha3: 'hun',
                    code: 'hu',
                    dir: 'ltr',
                    name: 'Hungarian',
                    native_name: 'Magyar',
                    regions: []
                },
                {
                    alpha2: 'it',
                    alpha3: 'ita',
                    code: 'it',
                    dir: 'ltr',
                    name: 'Italian',
                    native_name: 'Italiano',
                    regions: []
                },
                {
                    alpha2: 'ja',
                    alpha3: 'jpn',
                    code: 'ja',
                    dir: 'ltr',
                    name: 'Japanese',
                    native_name: '日本語',
                    regions: []
                },
                {
                    alpha2: 'lt',
                    alpha3: 'lit',
                    code: 'lt',
                    dir: 'ltr',
                    name: 'Lithuanian',
                    native_name: 'Lietuvių',
                    regions: []
                },
                {
                    alpha2: 'lv',
                    alpha3: 'lav',
                    code: 'lv',
                    dir: 'ltr',
                    name: 'Latvian',
                    native_name: 'Latviešu',
                    regions: []
                },
                {
                    alpha2: 'nb',
                    alpha3: 'nob',
                    code: 'nb',
                    dir: 'ltr',
                    name: 'Norwegian Bokmål',
                    native_name: 'Norsk bokmål',
                    regions: []
                },
                {
                    alpha2: 'pl',
                    alpha3: 'pol',
                    code: 'pl',
                    dir: 'ltr',
                    name: 'Polish',
                    native_name: 'Polski',
                    regions: []
                },
                {
                    alpha2: 'pt',
                    alpha3: 'por',
                    code: 'pt',
                    dir: 'ltr',
                    name: 'Portuguese',
                    native_name: 'Português',
                    regions: [
                        'PT'
                    ]
                },
                {
                    alpha2: 'ro',
                    alpha3: 'ron',
                    code: 'ro',
                    dir: 'ltr',
                    name: 'Romanian',
                    native_name: 'Română',
                    regions: []
                },
                {
                    alpha2: 'ru',
                    alpha3: 'rus',
                    code: 'ru',
                    dir: 'ltr',
                    name: 'Russian',
                    native_name: 'Русский',
                    regions: []
                },
                {
                    alpha2: 'sk',
                    alpha3: 'slk',
                    code: 'sk',
                    dir: 'ltr',
                    name: 'Slovak',
                    native_name: 'Slovenčina',
                    regions: []
                },
                {
                    alpha2: 'sv',
                    alpha3: 'swe',
                    code: 'sv',
                    dir: 'ltr',
                    name: 'Swedish',
                    native_name: 'Svenska',
                    regions: []
                },
                {
                    alpha2: 'tr',
                    alpha3: 'tur',
                    code: 'tr',
                    dir: 'ltr',
                    name: 'Turkish',
                    native_name: 'Türkçe',
                    regions: []
                },
                {
                    alpha2: 'uk',
                    alpha3: 'ukr',
                    code: 'uk',
                    dir: 'ltr',
                    name: 'Ukrainian',
                    native_name: 'Українська',
                    regions: []
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hans',
                    dir: 'ltr',
                    name: 'Chinese (Simplified)',
                    native_name: '简体中文',
                    regions: [
                        'CN'
                    ]
                },
                {
                    alpha2: 'zh',
                    alpha3: 'zho',
                    code: 'zh-Hant',
                    dir: 'ltr',
                    name: 'Chinese (Traditional)',
                    native_name: '繁體中文',
                    regions: [
                        'TW'
                    ]
                }
            ]
        },
        questionnaire_version: {
            type: 'string',
            title: 'Questionnaire Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        filling_start: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Filling Start'
        },
        filling_end: {
            type: 'string',
            format: 'date-time',
            title: 'Filling End'
        },
        responses: {
            items: {
                oneOf: [
                    {
                        $ref: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/OrdinalQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/MultiChoiceQuestionnaireResponse-Output'
                    },
                    {
                        $ref: '#/components/schemas/ContinuousQuestionnaireResponse'
                    },
                    {
                        $ref: '#/components/schemas/OpenTextQuestionnaireResponse'
                    }
                ],
                discriminator: {
                    propertyName: 'question_type',
                    mapping: {
                        continuous: '#/components/schemas/ContinuousQuestionnaireResponse',
                        dichotomous: '#/components/schemas/SingleChoiceQuestionnaireResponse',
                        open_text: '#/components/schemas/OpenTextQuestionnaireResponse',
                        ordinal: '#/components/schemas/OrdinalQuestionnaireResponse',
                        polytomous_multi_choice: '#/components/schemas/MultiChoiceQuestionnaireResponse-Output',
                        polytomous_single_choice: '#/components/schemas/SingleChoiceQuestionnaireResponse'
                    }
                }
            },
            type: 'array',
            minItems: 1,
            title: 'Responses'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'questionnaire_language',
        'questionnaire_version',
        'filling_end',
        'responses',
        'patient_id',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'FilledQuestionnaireResponse'
} as const;

export const FinishRegistrationWritableSchema = {
    properties: {
        new_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'New Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        repeat_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Repeat Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        token: {
            type: 'string',
            title: 'Token'
        },
        expiry_token: {
            type: 'string',
            minLength: 1,
            title: 'Expiry Token'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'new_password',
        'repeat_password',
        'token',
        'expiry_token'
    ],
    title: 'FinishRegistration'
} as const;

export const FinishRegistrationResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        new_user: {
            $ref: '#/components/schemas/UserResponseWritable'
        }
    },
    type: 'object',
    required: [
        'status',
        'new_user'
    ],
    title: 'FinishRegistrationResponse'
} as const;

export const FullClinicResponseWritableSchema = {
    properties: {
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        name: {
            type: 'string',
            title: 'Name'
        },
        ambulance: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ambulance'
        },
        address: {
            type: 'string',
            title: 'Address'
        },
        phone: {
            anyOf: [
                {
                    type: 'string',
                    format: 'phone'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Phone'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        physicians: {
            items: {
                $ref: '#/components/schemas/PhysicianResponseWritable'
            },
            type: 'array',
            title: 'Physicians'
        }
    },
    type: 'object',
    required: [
        'country',
        'name',
        'address',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'FullClinicResponse'
} as const;

export const FullMeasurementScheduleCheckResponseWritableSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_time: {
            type: 'string',
            format: 'time',
            title: 'Scheduled Time'
        },
        check_timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Check Timezone'
        },
        allowed_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Allowed Timedelta'
        },
        measured_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measured Datetime'
        },
        is_complete: {
            type: 'boolean',
            title: 'Is Complete'
        },
        missing_items: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Missing Items'
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_time',
        'check_timezone',
        'allowed_timedelta',
        'measured_datetime',
        'is_complete'
    ],
    title: 'FullMeasurementScheduleCheckResponse'
} as const;

export const FullPhysicianResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicResponse'
            },
            type: 'array',
            title: 'Clinics'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/SimpleUserResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'FullPhysicianResponse'
} as const;

export const FullUserResponseWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponseWritable'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        added_by: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Added By'
        },
        connected_patient: {
            anyOf: [
                {
                    $ref: '#/components/schemas/UserConnectedPatientResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/FullPhysicianResponseWritable'
                },
                {
                    type: 'null'
                }
            ]
        },
        assigned_physicians: {
            items: {
                $ref: '#/components/schemas/FullPhysicianResponseWritable'
            },
            type: 'array',
            title: 'Assigned Physicians'
        },
        assigned_clinics: {
            items: {
                $ref: '#/components/schemas/FullClinicResponseWritable'
            },
            type: 'array',
            title: 'Assigned Clinics'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at'
    ],
    title: 'FullUserResponse'
} as const;

export const Level3ClinicalPatientResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        diagnoses: {
            type: 'integer',
            title: 'Diagnoses'
        },
        medical_data: {
            type: 'integer',
            title: 'Medical Data'
        },
        medications: {
            type: 'integer',
            title: 'Medications'
        },
        symptoms: {
            type: 'integer',
            title: 'Symptoms'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-OutputWritable'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        insurance_policy_details: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Insurance Policy Details'
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        alerts: {
            type: 'integer',
            title: 'Alerts'
        },
        thresholds: {
            type: 'integer',
            title: 'Thresholds'
        },
        events: {
            type: 'integer',
            title: 'Events'
        },
        gdmt_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 9,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Gdmt Score'
        },
        ai_summary: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientAISummary'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id',
        'diagnoses',
        'medical_data',
        'medications',
        'symptoms',
        'alerts',
        'thresholds',
        'events'
    ],
    title: 'Level3ClinicalPatientResponse'
} as const;

export const Level3MinimalPatientResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        gdmt_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 9,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Gdmt Score'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id'
    ],
    title: 'Level3MinimalPatientResponse'
} as const;

export const Level3PatientResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        date_of_birth: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Date Of Birth'
        },
        sex: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Sex'
                },
                {
                    type: 'null'
                }
            ]
        },
        race: {
            anyOf: [
                {
                    $ref: '#/components/schemas/Race'
                },
                {
                    type: 'null'
                }
            ]
        },
        height: {
            anyOf: [
                {
                    type: 'number',
                    maximum: 300,
                    minimum: 20
                },
                {
                    type: 'null'
                }
            ],
            title: 'Height'
        },
        patient_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        monitoring_watch_kind: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchKind'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_watch_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringWatchStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_call_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringCallStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_report_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringReportStatus'
                },
                {
                    type: 'null'
                }
            ]
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        connected_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserWithFlagsResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Users'
        },
        managing_users: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SimpleUserResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Managing Users'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        },
        self_measurement_config: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/MeasurementRequiredData-OutputWritable'
                    },
                    type: 'array',
                    minItems: 1
                },
                {
                    type: 'null'
                }
            ],
            title: 'Self Measurement Config'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        insurance_policy_details: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Insurance Policy Details'
        },
        patient_state: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientState'
                },
                {
                    type: 'null'
                }
            ]
        },
        monitoring_muted: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Muted'
        },
        monitoring_mute_start_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute Start Date'
        },
        monitoring_mute_end_date: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Mute End Date'
        },
        gdmt_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 9,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Gdmt Score'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id'
    ],
    title: 'Level3PatientResponse'
} as const;

export const ListBasicComputedDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        computed_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponseWritable'
            },
            type: 'array',
            title: 'Computed Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'computed_data'
    ],
    title: 'ListBasicComputedDataResponse'
} as const;

export const ListClinicalStudyEnrollmentsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        study_enrollments: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyEnrollmentResponseWritable'
            },
            type: 'array',
            title: 'Study Enrollments'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'study_enrollments'
    ],
    title: 'ListClinicalStudyEnrollmentsResponse'
} as const;

export const ListClinicalStudyVisitResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitResponseWritable'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'study_visits'
    ],
    title: 'ListClinicalStudyVisitResponse'
} as const;

export const ListClinicsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicWithAssignedUsersResponseWritable'
            },
            type: 'array',
            title: 'Clinics'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'clinics'
    ],
    title: 'ListClinicsResponse'
} as const;

export const ListComputedWithPPGDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        computed_data: {
            items: {
                $ref: '#/components/schemas/SeerlinqComputedWithPPGResponseWritable'
            },
            type: 'array',
            title: 'Computed Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'computed_data'
    ],
    title: 'ListComputedWithPPGDataResponse'
} as const;

export const ListECGDerivedDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/DefaultECGDerivedDataSchemaResponseWritable'
            },
            type: 'array',
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListECGDerivedDataResponse',
    description: 'Schema for listing ECG Derived data entries.'
} as const;

export const ListECGDerivedDataWithECGDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/ECGDerivedDataSchemaWithECGResponseWritable'
            },
            type: 'array',
            title: 'Derived Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListECGDerivedDataWithECGDataResponse'
} as const;

export const ListFilledQuestionnaireResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        questionnaires: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponseWritable'
            },
            type: 'array',
            title: 'Questionnaires'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'questionnaires'
    ],
    title: 'ListFilledQuestionnaireResponse'
} as const;

export const ListMQuestionnaireAutomatonSettingsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        questionnaire_automaton_settings: {
            items: {
                $ref: '#/components/schemas/QuestionnaireAutomatonSettingsResponseWritable'
            },
            type: 'array',
            title: 'Questionnaire Automaton Settings'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'questionnaire_automaton_settings'
    ],
    title: 'ListMQuestionnaireAutomatonSettingsResponse'
} as const;

export const ListMaybeFullPPGDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        ppg_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/DefaultFullPPGDataSchemaResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
                    },
                    type: 'array'
                }
            ],
            title: 'Ppg Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'ppg_data'
    ],
    title: 'ListMaybeFullPPGDataResponse',
    description: 'Schema for listing (maybe) full PPG data entries.'
} as const;

export const ListMonitoringSettingsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        monitoring_settings: {
            items: {
                $ref: '#/components/schemas/MonitoringSettingsResponseWritable'
            },
            type: 'array',
            title: 'Monitoring Settings'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'monitoring_settings'
    ],
    title: 'ListMonitoringSettingsResponse'
} as const;

export const ListPPGDerivedDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponseWritable'
            },
            type: 'array',
            title: 'Derived Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListPPGDerivedDataResponse',
    description: 'Schema for listing PPG Derived data entries.'
} as const;

export const ListPPGDerivedDataWithPPGDataResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        derived_data: {
            items: {
                $ref: '#/components/schemas/PPGDerivedDataSchemaWithPPGResponseWritable'
            },
            type: 'array',
            title: 'Derived Data'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'derived_data'
    ],
    title: 'ListPPGDerivedDataWithPPGDataResponse'
} as const;

export const ListPatientAlertsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Alerts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'alerts'
    ],
    title: 'ListPatientAlertsResponse'
} as const;

export const ListPatientEventsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        events: {
            items: {
                $ref: '#/components/schemas/DefaultPatientEventResponseWritable'
            },
            type: 'array',
            title: 'Events'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'events'
    ],
    title: 'ListPatientEventsResponse'
} as const;

export const ListPatientsMinimalResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Level3MinimalPatientResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/MinimalPatientResponseWritable'
                    },
                    type: 'array'
                }
            ],
            title: 'Patients'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patients'
    ],
    title: 'ListPatientsMinimalResponse'
} as const;

export const ListPatientsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/Level3ClinicalPatientResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/DefaultClinicalPatientResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/Level3PatientResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/DefaultPatientResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/Level3MinimalPatientResponseWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/MinimalPatientResponseWritable'
                    },
                    type: 'array'
                }
            ],
            title: 'Patients'
        },
        pagination: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PaginationResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patients'
    ],
    title: 'ListPatientsResponse'
} as const;

export const ListPendingConsentsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        patients: {
            items: {
                $ref: '#/components/schemas/PendingConsentPatientWritable'
            },
            type: 'array',
            title: 'Patients'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'patients'
    ],
    title: 'ListPendingConsentsResponse'
} as const;

export const ListPhysiciansResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        physicians: {
            items: {
                $ref: '#/components/schemas/PhysicianWithPatientsResponseWritable'
            },
            type: 'array',
            title: 'Physicians'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'physicians'
    ],
    title: 'ListPhysiciansResponse'
} as const;

export const ListSeerlinqStudyTextResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        study_texts: {
            items: {
                $ref: '#/components/schemas/SeerlinqStudyTextResponseWritable'
            },
            type: 'array',
            title: 'Study Texts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'study_texts'
    ],
    title: 'ListSeerlinqStudyTextResponse'
} as const;

export const ListServiceAccountsResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        service_accounts: {
            items: {
                $ref: '#/components/schemas/ServiceAccountResponseWritable'
            },
            type: 'array',
            title: 'Service Accounts'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'service_accounts'
    ],
    title: 'ListServiceAccountsResponse'
} as const;

export const ListSupportedMedicalDataWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        medical_data: {
            items: {
                $ref: '#/components/schemas/SupportedMedicalDataWritable'
            },
            type: 'array',
            title: 'Medical Data'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'medical_data'
    ],
    title: 'ListSupportedMedicalData'
} as const;

export const ListUserAccessPoliciesResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        policies: {
            items: {
                $ref: '#/components/schemas/UserAccessPolicyResponseWritable'
            },
            type: 'array',
            title: 'Policies'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'policies'
    ],
    title: 'ListUserAccessPoliciesResponse',
    description: 'Schema for listing user access policies.'
} as const;

export const ListUsersResponseWritableSchema = {
    properties: {
        status: {
            type: 'string',
            const: 'success',
            title: 'Status'
        },
        results: {
            type: 'integer',
            minimum: 0,
            title: 'Results'
        },
        users: {
            items: {
                $ref: '#/components/schemas/FullUserResponseWritable'
            },
            type: 'array',
            title: 'Users'
        }
    },
    type: 'object',
    required: [
        'status',
        'results',
        'users'
    ],
    title: 'ListUsersResponse'
} as const;

export const LoginUserSchemaWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Password',
            writeOnly: true
        }
    },
    type: 'object',
    required: [
        'username',
        'password'
    ],
    title: 'LoginUserSchema'
} as const;

export const MFAVerifyBackupCodeSchemaWritableSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        method: {
            type: 'string',
            const: 'backup_code',
            title: 'Method'
        },
        code: {
            type: 'string',
            format: 'password',
            title: 'Code',
            writeOnly: true
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'method',
        'code'
    ],
    title: 'MFAVerifyBackupCodeSchema'
} as const;

export const MFAVerifySmsSchemaWritableSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        method: {
            type: 'string',
            const: 'sms',
            title: 'Method'
        },
        code: {
            type: 'string',
            format: 'password',
            title: 'Code',
            writeOnly: true
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'method',
        'code'
    ],
    title: 'MFAVerifySmsSchema'
} as const;

export const MFAVerifyTOTPSchemaWritableSchema = {
    properties: {
        challenge_id: {
            type: 'string',
            maxLength: 1024,
            minLength: 20,
            pattern: '^[A-Za-z0-9_-]+$',
            title: 'Challenge Id'
        },
        method: {
            type: 'string',
            const: 'totp',
            title: 'Method'
        },
        code: {
            type: 'string',
            format: 'password',
            title: 'Code',
            writeOnly: true
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'challenge_id',
        'method',
        'code'
    ],
    title: 'MFAVerifyTOTPSchema'
} as const;

export const MeasurementAliveCorECGRequiredData_OutputWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ecg',
            title: 'Data Type',
            default: 'ecg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 20,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 30
        },
        required_device: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^AliveCor '
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Device'
        },
        ecg_device_type: {
            type: 'string',
            const: 'alivecor',
            title: 'Ecg Device Type',
            default: 'alivecor'
        }
    },
    type: 'object',
    required: [
        'order'
    ],
    title: 'MeasurementAliveCorECGRequiredData'
} as const;

export const MeasurementBerryPPGRequiredData_OutputWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            type: 'string',
            const: 'BerryMed BM1000B',
            title: 'Required Device',
            default: 'BerryMed BM1000B'
        },
        ppg_device_type: {
            type: 'string',
            const: 'berry',
            title: 'Ppg Device Type',
            default: 'berry'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition'
    ],
    title: 'MeasurementBerryPPGRequiredData'
} as const;

export const MeasurementECGRequiredData_OutputWritableSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementAliveCorECGRequiredData-OutputWritable'
        }
    ],
    discriminator: {
        propertyName: 'ecg_device_type',
        mapping: {
            alivecor: '#/components/schemas/MeasurementAliveCorECGRequiredData-OutputWritable'
        }
    }
} as const;

export const MeasurementMedicalRequiredData_OutputWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        }
    },
    type: 'object',
    required: [
        'order',
        'data_name'
    ],
    title: 'MeasurementMedicalRequiredData'
} as const;

export const MeasurementPPGRequiredData_OutputWritableSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementBerryPPGRequiredData-OutputWritable'
        },
        {
            $ref: '#/components/schemas/MeasurementSamsungPPGRequiredData-OutputWritable'
        },
        {
            $ref: '#/components/schemas/MeasurementSmartCarePPGRequiredData-OutputWritable'
        }
    ],
    discriminator: {
        propertyName: 'ppg_device_type',
        mapping: {
            berry: '#/components/schemas/MeasurementBerryPPGRequiredData-OutputWritable',
            samsung: '#/components/schemas/MeasurementSamsungPPGRequiredData-OutputWritable',
            smartcare: '#/components/schemas/MeasurementSmartCarePPGRequiredData-OutputWritable'
        }
    }
} as const;

export const MeasurementRequiredData_OutputWritableSchema = {
    oneOf: [
        {
            $ref: '#/components/schemas/MeasurementMedicalRequiredData-OutputWritable'
        },
        {
            $ref: '#/components/schemas/MeasurementSymptomRequiredData-OutputWritable'
        },
        {
            $ref: '#/components/schemas/MeasurementPPGRequiredData-OutputWritable'
        },
        {
            $ref: '#/components/schemas/MeasurementECGRequiredData-OutputWritable'
        }
    ],
    discriminator: {
        propertyName: 'data_type',
        mapping: {
            ecg: '#/components/schemas/MeasurementECGRequiredData-OutputWritable',
            'medical-data': '#/components/schemas/MeasurementMedicalRequiredData-OutputWritable',
            ppg: '#/components/schemas/MeasurementPPGRequiredData-OutputWritable',
            symptom: '#/components/schemas/MeasurementSymptomRequiredData-OutputWritable'
        }
    }
} as const;

export const MeasurementSamsungPPGRequiredData_OutputWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^Samsung '
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Device'
        },
        ppg_device_type: {
            type: 'string',
            const: 'samsung',
            title: 'Ppg Device Type',
            default: 'samsung'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition'
    ],
    title: 'MeasurementSamsungPPGRequiredData'
} as const;

export const MeasurementSmartCarePPGRequiredData_OutputWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length',
            default: 120
        },
        required_device: {
            type: 'string',
            enum: [
                'SmartCare',
                'SmartCare BM2000A'
            ],
            title: 'Required Device',
            default: 'SmartCare BM2000A'
        },
        ppg_device_type: {
            type: 'string',
            const: 'smartcare',
            title: 'Ppg Device Type',
            default: 'smartcare'
        },
        measurement_condition: {
            $ref: '#/components/schemas/StrictPPGConditions'
        }
    },
    type: 'object',
    required: [
        'order',
        'measurement_condition'
    ],
    title: 'MeasurementSmartCarePPGRequiredData'
} as const;

export const MeasurementSymptomRequiredData_OutputWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required',
            default: true
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        }
    },
    type: 'object',
    required: [
        'order',
        'data_name'
    ],
    title: 'MeasurementSymptomRequiredData'
} as const;

export const MedicalWaitingRoomAlertsWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            minLength: 1,
            title: 'Name'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region'
        },
        patient_state: {
            $ref: '#/components/schemas/PatientState'
        },
        clinical_risk_score: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Clinical Risk Score'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        alert_risk_score: {
            type: 'number',
            minimum: 0,
            title: 'Alert Risk Score'
        },
        lvfp_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Lvfp Alerts'
        },
        vitals_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Vitals Alerts'
        },
        symptom_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Symptom Alerts'
        },
        ppg_quality_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Ppg Quality Alerts'
        },
        compliance_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Compliance Alerts'
        },
        last_events: {
            items: {
                $ref: '#/components/schemas/WaitingRoomEventsWritable'
            },
            type: 'array',
            title: 'Last Events'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        monitoring_watch_kind: {
            $ref: '#/components/schemas/PatientMonitoringWatchKind'
        },
        monitoring_watch_status: {
            $ref: '#/components/schemas/PatientMonitoringWatchStatus'
        },
        monitoring_call_status: {
            $ref: '#/components/schemas/PatientMonitoringCallStatus'
        },
        monitoring_report_status: {
            $ref: '#/components/schemas/PatientMonitoringReportStatus'
        },
        ai_summary: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientAISummary'
                },
                {
                    type: 'null'
                }
            ]
        },
        ai_triage: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientAITriage'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'name',
        'country',
        'region',
        'patient_state',
        'clinical_risk_score',
        'alert_risk_score',
        'lvfp_alerts',
        'vitals_alerts',
        'symptom_alerts',
        'ppg_quality_alerts',
        'compliance_alerts',
        'last_events',
        'monitoring_watch_kind',
        'monitoring_watch_status',
        'monitoring_call_status',
        'monitoring_report_status'
    ],
    title: 'MedicalWaitingRoomAlerts'
} as const;

export const MedicalWaitingRoomResponseWritableSchema = {
    properties: {
        fetched: {
            type: 'string',
            format: 'date-time',
            title: 'Fetched'
        },
        waiting_room: {
            items: {
                $ref: '#/components/schemas/MedicalWaitingRoomAlertsWritable'
            },
            type: 'array',
            title: 'Waiting Room'
        },
        waiting_room_timespan: {
            type: 'string',
            format: 'duration',
            title: 'Waiting Room Timespan'
        },
        risk_factor_exponential_discount: {
            type: 'number',
            title: 'Risk Factor Exponential Discount'
        }
    },
    type: 'object',
    required: [
        'fetched',
        'waiting_room',
        'waiting_room_timespan',
        'risk_factor_exponential_discount'
    ],
    title: 'MedicalWaitingRoomResponse'
} as const;

export const MinimalPatientResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        monitoring_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientMonitoringType'
                },
                {
                    type: 'null'
                }
            ]
        },
        informed_consent: {
            $ref: '#/components/schemas/InformedConsent',
            default: 0,
            deprecated: true,
            description: '**Deprecated** - use the `/patients/{patient_id}/consents` endpoints.'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinical_risk_score: {
            anyOf: [
                {
                    type: 'integer',
                    maximum: 10,
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinical Risk Score'
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        consents: {
            items: {
                $ref: '#/components/schemas/ListedConsentItem'
            },
            type: 'array',
            title: 'Consents',
            description: 'Per-type consent state, filled on the patient lists and `GET /patients/{patient_id}`; empty on every other response, which `GET /patients/{patient_id}/consents` answers.'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'patient_id'
    ],
    title: 'MinimalPatientResponse'
} as const;

export const MonitoringSettingsResponseWritableSchema = {
    properties: {
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        new_patient_status: {
            $ref: '#/components/schemas/PatientStatus',
            default: 1
        },
        auto_schedule: {
            type: 'boolean',
            title: 'Auto Schedule',
            default: false
        },
        requires_measurement_config: {
            type: 'boolean',
            title: 'Requires Measurement Config',
            default: false
        },
        auto_alerts: {
            type: 'boolean',
            title: 'Auto Alerts',
            default: false
        },
        patient_push_notifications: {
            type: 'boolean',
            title: 'Patient Push Notifications',
            default: false
        },
        consent_type: {
            type: 'integer',
            enum: [
                2,
                1
            ],
            title: 'Consent Type',
            description: '**Deprecated** - use `required_consents`.',
            default: 2,
            deprecated: true
        },
        required_consents: {
            items: {
                $ref: '#/components/schemas/ConsentType'
            },
            type: 'array',
            title: 'Required Consents',
            default: [
                'research'
            ]
        },
        is_monitored: {
            type: 'boolean',
            title: 'Is Monitored',
            default: false
        },
        any_new_data_activate: {
            type: 'boolean',
            title: 'Any New Data Activate',
            default: false
        },
        ppg_ecg_new_data_activate: {
            type: 'boolean',
            title: 'Ppg Ecg New Data Activate',
            default: true
        },
        auto_deactivate_after: {
            anyOf: [
                {
                    type: 'string',
                    format: 'duration'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Auto Deactivate After',
            default: 'P60D'
        },
        status_change_email: {
            type: 'boolean',
            title: 'Status Change Email',
            default: false
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponseWritable'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'MonitoringSettingsResponse'
} as const;

export const NextMeasurementListResponseWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        per_schedule_next_measurement: {
            items: {
                $ref: '#/components/schemas/PerScheduleNextMeasurementWritable'
            },
            type: 'array',
            title: 'Per Schedule Next Measurement'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'per_schedule_next_measurement'
    ],
    title: 'NextMeasurementListResponse'
} as const;

export const OverallStudyOverviewResponseWritableSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        full_title: {
            type: 'string',
            title: 'Full Title'
        },
        planned_patients: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Planned Patients'
        },
        no_planned_visits: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'No Planned Visits'
        },
        enrolled_patients: {
            type: 'integer',
            minimum: 0,
            title: 'Enrolled Patients'
        },
        visits_per_patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PerPatientStudyStatsSimpleDetailedWritable'
                    },
                    type: 'array'
                },
                {
                    items: {
                        $ref: '#/components/schemas/PerPatientStudyStatsSimpleWritable'
                    },
                    type: 'array'
                }
            ],
            title: 'Visits Per Patients'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'full_title',
        'planned_patients',
        'no_planned_visits',
        'enrolled_patients',
        'visits_per_patients'
    ],
    title: 'OverallStudyOverviewResponse'
} as const;

export const PPGDerivedDataResponseWritableSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/PPGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            $ref: '#/components/schemas/DerivedDataAggregationFunction'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PPGDerivedDataResponse'
} as const;

export const PPGDerivedDataSchemaWithPPGResponseWritableSchema = {
    properties: {
        ppg_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Ppg Uuid'
        },
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        data_group: {
            $ref: '#/components/schemas/PPGDerivedDataGroup'
        },
        rolling: {
            $ref: '#/components/schemas/DerivedDataRollingType'
        },
        window_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DerivedDataWindowTypeSchema'
                },
                {
                    type: 'null'
                }
            ]
        },
        window_id: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Window Id'
        },
        aggregation_function: {
            $ref: '#/components/schemas/DerivedDataAggregationFunction'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppg: {
            $ref: '#/components/schemas/PPGWithSessionResponse'
        }
    },
    type: 'object',
    required: [
        'ppg_uuid',
        'measurement_datetime',
        'patient_id',
        'data_group',
        'rolling',
        'aggregation_function',
        'measurement_type',
        'measurement_value',
        'override_value',
        'uuid',
        'created_at',
        'updated_at',
        'ppg'
    ],
    title: 'PPGDerivedDataSchemaWithPPGResponse'
} as const;

export const PatientFacingPhysicianResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicResponse'
            },
            type: 'array',
            title: 'Clinics'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/UserReferenceResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PatientFacingPhysicianResponse'
} as const;

export const PatientStudyOverviewResponseWritableSchema = {
    properties: {
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        visits: {
            items: {
                $ref: '#/components/schemas/PerVisitStudyStatsWritable'
            },
            type: 'array',
            title: 'Visits'
        }
    },
    type: 'object',
    required: [
        'study_acronym',
        'patient_id',
        'visits'
    ],
    title: 'PatientStudyOverviewResponse'
} as const;

export const PendingConsentPatientWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region',
            default: 0
        },
        created_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Created At'
        },
        updated_at: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Updated At'
        },
        pending: {
            items: {
                $ref: '#/components/schemas/PendingConsentItem'
            },
            type: 'array',
            title: 'Pending'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'patient_id',
        'country',
        'pending'
    ],
    title: 'PendingConsentPatient'
} as const;

export const PerPatientStudyStatsSimpleWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_status: {
            $ref: '#/components/schemas/OverallVisitStatus'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'visit_type',
        'visit_status'
    ],
    title: 'PerPatientStudyStatsSimple'
} as const;

export const PerPatientStudyStatsSimpleDetailedWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_status: {
            $ref: '#/components/schemas/DataVisitStatus'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'visit_type',
        'data_name',
        'data_status'
    ],
    title: 'PerPatientStudyStatsSimpleDetailed'
} as const;

export const PerScheduleNextMeasurementWritableSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_time: {
            type: 'string',
            format: 'time',
            title: 'Scheduled Time'
        },
        check_timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Check Timezone'
        },
        last_measurement: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Measurement'
        },
        next_measurement: {
            type: 'string',
            format: 'date-time',
            title: 'Next Measurement'
        },
        allowed_window: {
            type: 'string',
            format: 'duration',
            title: 'Allowed Window'
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_time',
        'check_timezone',
        'last_measurement',
        'next_measurement',
        'allowed_window'
    ],
    title: 'PerScheduleNextMeasurement'
} as const;

export const PerVisitStudyStatsWritableSchema = {
    properties: {
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        visit_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visit Datetime'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_status: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DataVisitStatus'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'visit_type',
        'visit_datetime',
        'data_name',
        'data_status'
    ],
    title: 'PerVisitStudyStats'
} as const;

export const PermissionSetWritableSchema = {
    items: {
        $ref: '#/components/schemas/PermissionBit'
    },
    type: 'array',
    uniqueItems: true,
    title: 'PermissionSet',
    description: 'Set of permissions.'
} as const;

export const PhysicianResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PhysicianResponse'
} as const;

export const PhysicianWithPatientsResponseWritableSchema = {
    properties: {
        given_name: {
            type: 'string',
            minLength: 1,
            title: 'Given Name'
        },
        middle_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Middle Names'
        },
        name_particle: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Particle'
        },
        family_name: {
            type: 'string',
            minLength: 1,
            title: 'Family Name'
        },
        additional_family_names: {
            anyOf: [
                {
                    items: {
                        type: 'string',
                        minLength: 1
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Additional Family Names'
        },
        name_suffix: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Name Suffix'
        },
        title_before: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title Before'
        },
        title_after: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Title After'
        },
        name_order_given_first: {
            type: 'boolean',
            title: 'Name Order Given First',
            default: true
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'CountryRegions',
            description: 'Regions based on country `Enum` - all Enums have a default `0` (`unknown`), then the regions depends on the specific country.'
        },
        email: {
            type: 'string',
            format: 'email',
            title: 'Email'
        },
        phone: {
            items: {
                type: 'string',
                format: 'phone'
            },
            type: 'array',
            title: 'Phone'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        bypass_geography_checks: {
            prefixItems: [
                {
                    type: 'boolean'
                },
                {
                    type: 'boolean'
                }
            ],
            type: 'array',
            maxItems: 2,
            minItems: 2,
            title: 'Bypass Geography Checks',
            default: [
                false,
                false
            ]
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        clinics: {
            items: {
                $ref: '#/components/schemas/ClinicResponse'
            },
            type: 'array',
            title: 'Clinics'
        },
        assigned_monitoring_users: {
            items: {
                $ref: '#/components/schemas/SimpleUserResponse'
            },
            type: 'array',
            title: 'Assigned Monitoring Users'
        },
        connected_user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleUserResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        patients: {
            items: {
                $ref: '#/components/schemas/PatientBaseSchema'
            },
            type: 'array',
            title: 'Patients'
        }
    },
    type: 'object',
    required: [
        'given_name',
        'family_name',
        'country',
        'email',
        'phone',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'PhysicianWithPatientsResponse'
} as const;

export const PlotDataWritableSchema = {
    properties: {
        plot_type: {
            type: 'string',
            enum: [
                'timeseries',
                'signal_annotated',
                'box',
                'bar'
            ],
            title: 'Plot Type'
        },
        timeseries: {
            anyOf: [
                {
                    $ref: '#/components/schemas/TimeSeriesPlotWritable'
                },
                {
                    type: 'null'
                }
            ]
        },
        signal_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/SignalTrace'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Signal Data'
        },
        beat_annotations: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/BeatAnnotation'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Beat Annotations'
        },
        quality_bands: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/QualityBand'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Quality Bands'
        },
        box_data: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/BoxTrace'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Box Data'
        },
        thresholds: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientThreshold'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Thresholds'
        },
        reference_lines: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ReferenceLine'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reference Lines'
        },
        reference_bands: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ReferenceBand'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Reference Bands'
        },
        events: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ClinicalEventWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Events'
        },
        afib_probability: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/TimeSeriesPointWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Afib Probability'
        },
        afib_probability_recording: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Afib Probability Recording'
        },
        line_stats: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/LineStats'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Line Stats'
        },
        delta_color_rule: {
            anyOf: [
                {
                    $ref: '#/components/schemas/DeltaColorRule'
                },
                {
                    type: 'null'
                }
            ]
        },
        last_value_threshold_gt: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Value Threshold Gt'
        }
    },
    type: 'object',
    required: [
        'plot_type'
    ],
    title: 'PlotData',
    description: 'Container for plot data.'
} as const;

export const PreregisterUserResponseWritableSchema = {
    properties: {
        new_user: {
            $ref: '#/components/schemas/UserResponseWritable'
        },
        qr_code: {
            anyOf: [
                {
                    type: 'string',
                    pattern: '^data:image/png;base64,.*'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Qr Code'
        },
        ses_send_mail_task_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 32,
                    minLength: 32
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ses Send Mail Task Id'
        }
    },
    type: 'object',
    required: [
        'new_user'
    ],
    title: 'PreregisterUserResponse'
} as const;

export const PublishedStudyTextResponseWritableSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType',
            default: 'telemonitoring'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        study_text: {
            type: 'string',
            title: 'Study Text'
        },
        study_text_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StudyTextLanguage-Output'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Study Text Language'
        },
        study_text_markup: {
            $ref: '#/components/schemas/StudyTextMarkup'
        },
        study_text_version: {
            type: 'string',
            title: 'Study Text Version'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        content_hash: {
            type: 'string',
            title: 'Content Hash'
        },
        study_text_template: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Text Template'
        },
        patient_specific: {
            type: 'boolean',
            title: 'Patient Specific',
            default: false
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponseWritable'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        dry_run: {
            type: 'boolean',
            title: 'Dry Run'
        },
        patients_to_resign: {
            type: 'integer',
            minimum: 0,
            title: 'Patients To Resign'
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'study_text',
        'study_text_language',
        'study_text_markup',
        'study_text_version',
        'uuid',
        'content_hash',
        'created_at',
        'updated_at',
        'dry_run',
        'patients_to_resign'
    ],
    title: 'PublishedStudyTextResponse'
} as const;

export const QuestionnaireAutomatonSettingsResponseWritableSchema = {
    properties: {
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        first_when_active: {
            type: 'boolean',
            title: 'First When Active'
        },
        filling_frequency: {
            items: {
                $ref: '#/components/schemas/FrequencyIntervalItem'
            },
            type: 'array',
            minItems: 1,
            title: 'Filling Frequency'
        },
        first_after_active: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        stop_after_n: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Stop After N'
        },
        stop_after_time_from_first: {
            anyOf: [
                {
                    $ref: '#/components/schemas/RelativeDeltaModel'
                },
                {
                    type: 'null'
                }
            ]
        },
        skippable: {
            type: 'boolean',
            title: 'Skippable',
            default: false
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponseWritable'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'questionnaire_id',
        'monitoring_type',
        'first_when_active',
        'filling_frequency',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'QuestionnaireAutomatonSettingsResponse'
} as const;

export const QuickLookReportWritableSchema = {
    properties: {
        report_id: {
            type: 'string',
            title: 'Report Id'
        },
        report_type: {
            type: 'string',
            enum: [
                'ppg_timeseries_ql',
                'ppg_auc_annotated_ql',
                'ppg_beats_investigation_ql',
                'ppg_afib_ql',
                'ecg_timeseries_ql',
                'hf_dri_timeseries_ql',
                'lvfp_timeseries_ql',
                'heartcore_report',
                'heartcore_master_ql'
            ],
            title: 'Report Type'
        },
        title: {
            type: 'string',
            title: 'Title'
        },
        patient_info: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientInfo'
                },
                {
                    type: 'null'
                }
            ]
        },
        algorithm_name: {
            type: 'string',
            title: 'Algorithm Name'
        },
        algorithm_version: {
            type: 'string',
            title: 'Algorithm Version'
        },
        generated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Generated At'
        },
        sections: {
            items: {
                $ref: '#/components/schemas/SectionContentWritable'
            },
            type: 'array',
            title: 'Sections'
        }
    },
    type: 'object',
    required: [
        'report_type',
        'title',
        'algorithm_name',
        'algorithm_version',
        'generated_at',
        'sections'
    ],
    title: 'QuickLookReport',
    description: 'Complete QuickLook report.'
} as const;

export const ResetPasswordSchemaWritableSchema = {
    properties: {
        new_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'New Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        repeat_password: {
            type: 'string',
            maxLength: 256,
            format: 'password',
            title: 'Repeat Password',
            description: 'Passwords must have at least 10 characters, at most 256, and at least one of: uppercase letter, lowercase letter, and digit.',
            writeOnly: true
        },
        token: {
            type: 'string',
            title: 'Token'
        },
        expiry_token: {
            type: 'string',
            minLength: 1,
            title: 'Expiry Token'
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'new_password',
        'repeat_password',
        'token',
        'expiry_token'
    ],
    title: 'ResetPasswordSchema'
} as const;

export const ScheduleCheckListResponseWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        schedule_checks: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/FullMeasurementScheduleCheckResponseWritable'
                    },
                    {
                        $ref: '#/components/schemas/SingleScheduleCheckResponseWritable'
                    }
                ]
            },
            type: 'array',
            title: 'Schedule Checks'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'schedule_checks'
    ],
    title: 'ScheduleCheckListResponse'
} as const;

export const SectionContentWritableSchema = {
    properties: {
        section_name: {
            type: 'string',
            title: 'Section Name'
        },
        section_key: {
            anyOf: [
                {
                    $ref: '#/components/schemas/QuickLookSection'
                },
                {
                    type: 'null'
                }
            ]
        },
        plots: {
            items: {
                $ref: '#/components/schemas/PlotDataWritable'
            },
            type: 'array',
            title: 'Plots'
        },
        tables: {
            items: {
                $ref: '#/components/schemas/DataTable'
            },
            type: 'array',
            title: 'Tables'
        },
        statistical_tests: {
            items: {
                $ref: '#/components/schemas/StatisticalTestResult'
            },
            type: 'array',
            title: 'Statistical Tests'
        },
        empty: {
            type: 'boolean',
            title: 'Empty',
            default: false
        },
        empty_reason: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Empty Reason'
        }
    },
    type: 'object',
    required: [
        'section_name'
    ],
    title: 'SectionContent',
    description: 'Content for one report section.'
} as const;

export const SeerlinqComputedWithPPGResponseWritableSchema = {
    properties: {
        measurement_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Measurement Datetime'
        },
        measurement_type: {
            type: 'string',
            title: 'Measurement Type'
        },
        measurement_value: {
            type: 'number',
            title: 'Measurement Value'
        },
        measurement_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Unit'
        },
        seerlinq_measurement_quality_flag: {
            $ref: '#/components/schemas/PPGRelatedQualityFlag',
            default: 1
        },
        seerlinq_algorithm: {
            type: 'string',
            title: 'Seerlinq Algorithm'
        },
        seerlinq_algorithm_version: {
            type: 'string',
            title: 'Seerlinq Algorithm Version'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        tags: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Tags'
        },
        patient_id: {
            type: 'integer',
            minimum: 0,
            title: 'Patient Id'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        override_value: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Override Value'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        user: {
            anyOf: [
                {
                    $ref: '#/components/schemas/SimpleAccountResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        study_visits: {
            items: {
                $ref: '#/components/schemas/ClinicalStudyVisitsAssociation'
            },
            type: 'array',
            title: 'Study Visits'
        },
        ppgs: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PPGDataRelatedResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Ppgs'
        }
    },
    type: 'object',
    required: [
        'measurement_datetime',
        'measurement_type',
        'measurement_value',
        'seerlinq_algorithm',
        'seerlinq_algorithm_version',
        'patient_id',
        'override_value',
        'uuid',
        'created_at',
        'updated_at'
    ],
    title: 'SeerlinqComputedWithPPGResponse'
} as const;

export const SeerlinqStudyTextResponseWritableSchema = {
    properties: {
        consent_type: {
            $ref: '#/components/schemas/ConsentType',
            default: 'telemonitoring'
        },
        monitoring_type: {
            $ref: '#/components/schemas/PatientMonitoringType'
        },
        country: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Country'
        },
        region: {
            anyOf: [
                {
                    type: 'integer',
                    minimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Region'
        },
        study_text: {
            type: 'string',
            title: 'Study Text'
        },
        study_text_language: {
            anyOf: [
                {
                    $ref: '#/components/schemas/StudyTextLanguage-Output'
                },
                {
                    type: 'string'
                }
            ],
            title: 'Study Text Language'
        },
        study_text_markup: {
            $ref: '#/components/schemas/StudyTextMarkup'
        },
        study_text_version: {
            type: 'string',
            title: 'Study Text Version'
        },
        user_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'User Uuid'
        },
        clinic_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinic Uuid'
        },
        physician_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physician Uuid'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        content_hash: {
            type: 'string',
            title: 'Content Hash'
        },
        study_text_template: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Study Text Template'
        },
        patient_specific: {
            type: 'boolean',
            title: 'Patient Specific',
            default: false
        },
        clinic: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicResponse'
                },
                {
                    type: 'null'
                }
            ]
        },
        physician: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PhysicianResponseWritable'
                },
                {
                    type: 'null'
                }
            ]
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'monitoring_type',
        'study_text',
        'study_text_language',
        'study_text_markup',
        'study_text_version',
        'uuid',
        'content_hash',
        'created_at',
        'updated_at'
    ],
    title: 'SeerlinqStudyTextResponse'
} as const;

export const ServiceAccountResponseWritableSchema = {
    properties: {
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        username: {
            type: 'string',
            title: 'Username'
        },
        user_type: {
            type: 'string',
            const: 'service',
            title: 'User Type',
            default: 'service'
        },
        status: {
            $ref: '#/components/schemas/ServiceAccountStatus'
        },
        description: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Description'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        owner_uuid: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Owner Uuid'
        },
        added_by: {
            anyOf: [
                {
                    type: 'string',
                    format: 'uuid4'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Added By'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponseWritable'
        },
        api_keys: {
            items: {
                $ref: '#/components/schemas/ApiKeyResponse'
            },
            type: 'array',
            title: 'Api Keys'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'uuid',
        'username',
        'status',
        'description',
        'email',
        'owner_uuid',
        'added_by',
        'access_policy',
        'api_keys',
        'created_at',
        'updated_at'
    ],
    title: 'ServiceAccountResponse'
} as const;

export const ShouldFillQuestionnairesResponseWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        n_checked: {
            type: 'integer',
            minimum: 0,
            title: 'N Checked'
        },
        checks: {
            items: {
                $ref: '#/components/schemas/ShouldFillQuestionnaire'
            },
            type: 'array',
            title: 'Checks'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'n_checked',
        'checks'
    ],
    title: 'ShouldFillQuestionnairesResponse'
} as const;

export const SingleScheduleCheckResponseWritableSchema = {
    properties: {
        schedule_uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Schedule Uuid'
        },
        schedule_type: {
            $ref: '#/components/schemas/ScheduleTypes'
        },
        schedule_data_name: {
            type: 'string',
            title: 'Schedule Data Name'
        },
        scheduled_time: {
            type: 'string',
            format: 'time',
            title: 'Scheduled Time'
        },
        check_timezone: {
            type: 'string',
            enum: [
                'Africa/Abidjan',
                'Africa/Accra',
                'Africa/Addis_Ababa',
                'Africa/Algiers',
                'Africa/Asmara',
                'Africa/Asmera',
                'Africa/Bamako',
                'Africa/Bangui',
                'Africa/Banjul',
                'Africa/Bissau',
                'Africa/Blantyre',
                'Africa/Brazzaville',
                'Africa/Bujumbura',
                'Africa/Cairo',
                'Africa/Casablanca',
                'Africa/Ceuta',
                'Africa/Conakry',
                'Africa/Dakar',
                'Africa/Dar_es_Salaam',
                'Africa/Djibouti',
                'Africa/Douala',
                'Africa/El_Aaiun',
                'Africa/Freetown',
                'Africa/Gaborone',
                'Africa/Harare',
                'Africa/Johannesburg',
                'Africa/Juba',
                'Africa/Kampala',
                'Africa/Khartoum',
                'Africa/Kigali',
                'Africa/Kinshasa',
                'Africa/Lagos',
                'Africa/Libreville',
                'Africa/Lome',
                'Africa/Luanda',
                'Africa/Lubumbashi',
                'Africa/Lusaka',
                'Africa/Malabo',
                'Africa/Maputo',
                'Africa/Maseru',
                'Africa/Mbabane',
                'Africa/Mogadishu',
                'Africa/Monrovia',
                'Africa/Nairobi',
                'Africa/Ndjamena',
                'Africa/Niamey',
                'Africa/Nouakchott',
                'Africa/Ouagadougou',
                'Africa/Porto-Novo',
                'Africa/Sao_Tome',
                'Africa/Timbuktu',
                'Africa/Tripoli',
                'Africa/Tunis',
                'Africa/Windhoek',
                'America/Adak',
                'America/Anchorage',
                'America/Anguilla',
                'America/Antigua',
                'America/Araguaina',
                'America/Argentina/Buenos_Aires',
                'America/Argentina/Catamarca',
                'America/Argentina/ComodRivadavia',
                'America/Argentina/Cordoba',
                'America/Argentina/Jujuy',
                'America/Argentina/La_Rioja',
                'America/Argentina/Mendoza',
                'America/Argentina/Rio_Gallegos',
                'America/Argentina/Salta',
                'America/Argentina/San_Juan',
                'America/Argentina/San_Luis',
                'America/Argentina/Tucuman',
                'America/Argentina/Ushuaia',
                'America/Aruba',
                'America/Asuncion',
                'America/Atikokan',
                'America/Atka',
                'America/Bahia',
                'America/Bahia_Banderas',
                'America/Barbados',
                'America/Belem',
                'America/Belize',
                'America/Blanc-Sablon',
                'America/Boa_Vista',
                'America/Bogota',
                'America/Boise',
                'America/Buenos_Aires',
                'America/Cambridge_Bay',
                'America/Campo_Grande',
                'America/Cancun',
                'America/Caracas',
                'America/Catamarca',
                'America/Cayenne',
                'America/Cayman',
                'America/Chicago',
                'America/Chihuahua',
                'America/Ciudad_Juarez',
                'America/Coral_Harbour',
                'America/Cordoba',
                'America/Costa_Rica',
                'America/Coyhaique',
                'America/Creston',
                'America/Cuiaba',
                'America/Curacao',
                'America/Danmarkshavn',
                'America/Dawson',
                'America/Dawson_Creek',
                'America/Denver',
                'America/Detroit',
                'America/Dominica',
                'America/Edmonton',
                'America/Eirunepe',
                'America/El_Salvador',
                'America/Ensenada',
                'America/Fort_Nelson',
                'America/Fort_Wayne',
                'America/Fortaleza',
                'America/Glace_Bay',
                'America/Godthab',
                'America/Goose_Bay',
                'America/Grand_Turk',
                'America/Grenada',
                'America/Guadeloupe',
                'America/Guatemala',
                'America/Guayaquil',
                'America/Guyana',
                'America/Halifax',
                'America/Havana',
                'America/Hermosillo',
                'America/Indiana/Indianapolis',
                'America/Indiana/Knox',
                'America/Indiana/Marengo',
                'America/Indiana/Petersburg',
                'America/Indiana/Tell_City',
                'America/Indiana/Vevay',
                'America/Indiana/Vincennes',
                'America/Indiana/Winamac',
                'America/Indianapolis',
                'America/Inuvik',
                'America/Iqaluit',
                'America/Jamaica',
                'America/Jujuy',
                'America/Juneau',
                'America/Kentucky/Louisville',
                'America/Kentucky/Monticello',
                'America/Knox_IN',
                'America/Kralendijk',
                'America/La_Paz',
                'America/Lima',
                'America/Los_Angeles',
                'America/Louisville',
                'America/Lower_Princes',
                'America/Maceio',
                'America/Managua',
                'America/Manaus',
                'America/Marigot',
                'America/Martinique',
                'America/Matamoros',
                'America/Mazatlan',
                'America/Mendoza',
                'America/Menominee',
                'America/Merida',
                'America/Metlakatla',
                'America/Mexico_City',
                'America/Miquelon',
                'America/Moncton',
                'America/Monterrey',
                'America/Montevideo',
                'America/Montreal',
                'America/Montserrat',
                'America/Nassau',
                'America/New_York',
                'America/Nipigon',
                'America/Nome',
                'America/Noronha',
                'America/North_Dakota/Beulah',
                'America/North_Dakota/Center',
                'America/North_Dakota/New_Salem',
                'America/Nuuk',
                'America/Ojinaga',
                'America/Panama',
                'America/Pangnirtung',
                'America/Paramaribo',
                'America/Phoenix',
                'America/Port-au-Prince',
                'America/Port_of_Spain',
                'America/Porto_Acre',
                'America/Porto_Velho',
                'America/Puerto_Rico',
                'America/Punta_Arenas',
                'America/Rainy_River',
                'America/Rankin_Inlet',
                'America/Recife',
                'America/Regina',
                'America/Resolute',
                'America/Rio_Branco',
                'America/Rosario',
                'America/Santa_Isabel',
                'America/Santarem',
                'America/Santiago',
                'America/Santo_Domingo',
                'America/Sao_Paulo',
                'America/Scoresbysund',
                'America/Shiprock',
                'America/Sitka',
                'America/St_Barthelemy',
                'America/St_Johns',
                'America/St_Kitts',
                'America/St_Lucia',
                'America/St_Thomas',
                'America/St_Vincent',
                'America/Swift_Current',
                'America/Tegucigalpa',
                'America/Thule',
                'America/Thunder_Bay',
                'America/Tijuana',
                'America/Toronto',
                'America/Tortola',
                'America/Vancouver',
                'America/Virgin',
                'America/Whitehorse',
                'America/Winnipeg',
                'America/Yakutat',
                'America/Yellowknife',
                'Antarctica/Casey',
                'Antarctica/Davis',
                'Antarctica/DumontDUrville',
                'Antarctica/Macquarie',
                'Antarctica/Mawson',
                'Antarctica/McMurdo',
                'Antarctica/Palmer',
                'Antarctica/Rothera',
                'Antarctica/South_Pole',
                'Antarctica/Syowa',
                'Antarctica/Troll',
                'Antarctica/Vostok',
                'Arctic/Longyearbyen',
                'Asia/Aden',
                'Asia/Almaty',
                'Asia/Amman',
                'Asia/Anadyr',
                'Asia/Aqtau',
                'Asia/Aqtobe',
                'Asia/Ashgabat',
                'Asia/Ashkhabad',
                'Asia/Atyrau',
                'Asia/Baghdad',
                'Asia/Bahrain',
                'Asia/Baku',
                'Asia/Bangkok',
                'Asia/Barnaul',
                'Asia/Beirut',
                'Asia/Bishkek',
                'Asia/Brunei',
                'Asia/Calcutta',
                'Asia/Chita',
                'Asia/Choibalsan',
                'Asia/Chongqing',
                'Asia/Chungking',
                'Asia/Colombo',
                'Asia/Dacca',
                'Asia/Damascus',
                'Asia/Dhaka',
                'Asia/Dili',
                'Asia/Dubai',
                'Asia/Dushanbe',
                'Asia/Famagusta',
                'Asia/Gaza',
                'Asia/Harbin',
                'Asia/Hebron',
                'Asia/Ho_Chi_Minh',
                'Asia/Hong_Kong',
                'Asia/Hovd',
                'Asia/Irkutsk',
                'Asia/Istanbul',
                'Asia/Jakarta',
                'Asia/Jayapura',
                'Asia/Jerusalem',
                'Asia/Kabul',
                'Asia/Kamchatka',
                'Asia/Karachi',
                'Asia/Kashgar',
                'Asia/Kathmandu',
                'Asia/Katmandu',
                'Asia/Khandyga',
                'Asia/Kolkata',
                'Asia/Krasnoyarsk',
                'Asia/Kuala_Lumpur',
                'Asia/Kuching',
                'Asia/Kuwait',
                'Asia/Macao',
                'Asia/Macau',
                'Asia/Magadan',
                'Asia/Makassar',
                'Asia/Manila',
                'Asia/Muscat',
                'Asia/Nicosia',
                'Asia/Novokuznetsk',
                'Asia/Novosibirsk',
                'Asia/Omsk',
                'Asia/Oral',
                'Asia/Phnom_Penh',
                'Asia/Pontianak',
                'Asia/Pyongyang',
                'Asia/Qatar',
                'Asia/Qostanay',
                'Asia/Qyzylorda',
                'Asia/Rangoon',
                'Asia/Riyadh',
                'Asia/Saigon',
                'Asia/Sakhalin',
                'Asia/Samarkand',
                'Asia/Seoul',
                'Asia/Shanghai',
                'Asia/Singapore',
                'Asia/Srednekolymsk',
                'Asia/Taipei',
                'Asia/Tashkent',
                'Asia/Tbilisi',
                'Asia/Tehran',
                'Asia/Tel_Aviv',
                'Asia/Thimbu',
                'Asia/Thimphu',
                'Asia/Tokyo',
                'Asia/Tomsk',
                'Asia/Ujung_Pandang',
                'Asia/Ulaanbaatar',
                'Asia/Ulan_Bator',
                'Asia/Urumqi',
                'Asia/Ust-Nera',
                'Asia/Vientiane',
                'Asia/Vladivostok',
                'Asia/Yakutsk',
                'Asia/Yangon',
                'Asia/Yekaterinburg',
                'Asia/Yerevan',
                'Atlantic/Azores',
                'Atlantic/Bermuda',
                'Atlantic/Canary',
                'Atlantic/Cape_Verde',
                'Atlantic/Faeroe',
                'Atlantic/Faroe',
                'Atlantic/Jan_Mayen',
                'Atlantic/Madeira',
                'Atlantic/Reykjavik',
                'Atlantic/South_Georgia',
                'Atlantic/St_Helena',
                'Atlantic/Stanley',
                'Australia/ACT',
                'Australia/Adelaide',
                'Australia/Brisbane',
                'Australia/Broken_Hill',
                'Australia/Canberra',
                'Australia/Currie',
                'Australia/Darwin',
                'Australia/Eucla',
                'Australia/Hobart',
                'Australia/LHI',
                'Australia/Lindeman',
                'Australia/Lord_Howe',
                'Australia/Melbourne',
                'Australia/NSW',
                'Australia/North',
                'Australia/Perth',
                'Australia/Queensland',
                'Australia/South',
                'Australia/Sydney',
                'Australia/Tasmania',
                'Australia/Victoria',
                'Australia/West',
                'Australia/Yancowinna',
                'Brazil/Acre',
                'Brazil/DeNoronha',
                'Brazil/East',
                'Brazil/West',
                'CET',
                'CST6CDT',
                'Canada/Atlantic',
                'Canada/Central',
                'Canada/Eastern',
                'Canada/Mountain',
                'Canada/Newfoundland',
                'Canada/Pacific',
                'Canada/Saskatchewan',
                'Canada/Yukon',
                'Chile/Continental',
                'Chile/EasterIsland',
                'Cuba',
                'EET',
                'EST',
                'EST5EDT',
                'Egypt',
                'Eire',
                'Etc/GMT',
                'Etc/GMT+0',
                'Etc/GMT+1',
                'Etc/GMT+10',
                'Etc/GMT+11',
                'Etc/GMT+12',
                'Etc/GMT+2',
                'Etc/GMT+3',
                'Etc/GMT+4',
                'Etc/GMT+5',
                'Etc/GMT+6',
                'Etc/GMT+7',
                'Etc/GMT+8',
                'Etc/GMT+9',
                'Etc/GMT-0',
                'Etc/GMT-1',
                'Etc/GMT-10',
                'Etc/GMT-11',
                'Etc/GMT-12',
                'Etc/GMT-13',
                'Etc/GMT-14',
                'Etc/GMT-2',
                'Etc/GMT-3',
                'Etc/GMT-4',
                'Etc/GMT-5',
                'Etc/GMT-6',
                'Etc/GMT-7',
                'Etc/GMT-8',
                'Etc/GMT-9',
                'Etc/GMT0',
                'Etc/Greenwich',
                'Etc/UCT',
                'Etc/UTC',
                'Etc/Universal',
                'Etc/Zulu',
                'Europe/Amsterdam',
                'Europe/Andorra',
                'Europe/Astrakhan',
                'Europe/Athens',
                'Europe/Belfast',
                'Europe/Belgrade',
                'Europe/Berlin',
                'Europe/Bratislava',
                'Europe/Brussels',
                'Europe/Bucharest',
                'Europe/Budapest',
                'Europe/Busingen',
                'Europe/Chisinau',
                'Europe/Copenhagen',
                'Europe/Dublin',
                'Europe/Gibraltar',
                'Europe/Guernsey',
                'Europe/Helsinki',
                'Europe/Isle_of_Man',
                'Europe/Istanbul',
                'Europe/Jersey',
                'Europe/Kaliningrad',
                'Europe/Kiev',
                'Europe/Kirov',
                'Europe/Kyiv',
                'Europe/Lisbon',
                'Europe/Ljubljana',
                'Europe/London',
                'Europe/Luxembourg',
                'Europe/Madrid',
                'Europe/Malta',
                'Europe/Mariehamn',
                'Europe/Minsk',
                'Europe/Monaco',
                'Europe/Moscow',
                'Europe/Nicosia',
                'Europe/Oslo',
                'Europe/Paris',
                'Europe/Podgorica',
                'Europe/Prague',
                'Europe/Riga',
                'Europe/Rome',
                'Europe/Samara',
                'Europe/San_Marino',
                'Europe/Sarajevo',
                'Europe/Saratov',
                'Europe/Simferopol',
                'Europe/Skopje',
                'Europe/Sofia',
                'Europe/Stockholm',
                'Europe/Tallinn',
                'Europe/Tirane',
                'Europe/Tiraspol',
                'Europe/Ulyanovsk',
                'Europe/Uzhgorod',
                'Europe/Vaduz',
                'Europe/Vatican',
                'Europe/Vienna',
                'Europe/Vilnius',
                'Europe/Volgograd',
                'Europe/Warsaw',
                'Europe/Zagreb',
                'Europe/Zaporozhye',
                'Europe/Zurich',
                'Factory',
                'GB',
                'GB-Eire',
                'GMT',
                'GMT+0',
                'GMT-0',
                'GMT0',
                'Greenwich',
                'HST',
                'Hongkong',
                'Iceland',
                'Indian/Antananarivo',
                'Indian/Chagos',
                'Indian/Christmas',
                'Indian/Cocos',
                'Indian/Comoro',
                'Indian/Kerguelen',
                'Indian/Mahe',
                'Indian/Maldives',
                'Indian/Mauritius',
                'Indian/Mayotte',
                'Indian/Reunion',
                'Iran',
                'Israel',
                'Jamaica',
                'Japan',
                'Kwajalein',
                'Libya',
                'MET',
                'MST',
                'MST7MDT',
                'Mexico/BajaNorte',
                'Mexico/BajaSur',
                'Mexico/General',
                'NZ',
                'NZ-CHAT',
                'Navajo',
                'PRC',
                'PST8PDT',
                'Pacific/Apia',
                'Pacific/Auckland',
                'Pacific/Bougainville',
                'Pacific/Chatham',
                'Pacific/Chuuk',
                'Pacific/Easter',
                'Pacific/Efate',
                'Pacific/Enderbury',
                'Pacific/Fakaofo',
                'Pacific/Fiji',
                'Pacific/Funafuti',
                'Pacific/Galapagos',
                'Pacific/Gambier',
                'Pacific/Guadalcanal',
                'Pacific/Guam',
                'Pacific/Honolulu',
                'Pacific/Johnston',
                'Pacific/Kanton',
                'Pacific/Kiritimati',
                'Pacific/Kosrae',
                'Pacific/Kwajalein',
                'Pacific/Majuro',
                'Pacific/Marquesas',
                'Pacific/Midway',
                'Pacific/Nauru',
                'Pacific/Niue',
                'Pacific/Norfolk',
                'Pacific/Noumea',
                'Pacific/Pago_Pago',
                'Pacific/Palau',
                'Pacific/Pitcairn',
                'Pacific/Pohnpei',
                'Pacific/Ponape',
                'Pacific/Port_Moresby',
                'Pacific/Rarotonga',
                'Pacific/Saipan',
                'Pacific/Samoa',
                'Pacific/Tahiti',
                'Pacific/Tarawa',
                'Pacific/Tongatapu',
                'Pacific/Truk',
                'Pacific/Wake',
                'Pacific/Wallis',
                'Pacific/Yap',
                'Poland',
                'Portugal',
                'ROC',
                'ROK',
                'Singapore',
                'Turkey',
                'UCT',
                'US/Alaska',
                'US/Aleutian',
                'US/Arizona',
                'US/Central',
                'US/East-Indiana',
                'US/Eastern',
                'US/Hawaii',
                'US/Indiana-Starke',
                'US/Michigan',
                'US/Mountain',
                'US/Pacific',
                'US/Samoa',
                'UTC',
                'Universal',
                'W-SU',
                'WET',
                'Zulu',
                'localtime'
            ],
            minLength: 1,
            title: 'Check Timezone'
        },
        allowed_timedelta: {
            type: 'string',
            format: 'duration',
            title: 'Allowed Timedelta'
        },
        measured_datetime: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measured Datetime'
        }
    },
    type: 'object',
    required: [
        'schedule_uuid',
        'schedule_type',
        'schedule_data_name',
        'scheduled_time',
        'check_timezone',
        'allowed_timedelta',
        'measured_datetime'
    ],
    title: 'SingleScheduleCheckResponse'
} as const;

export const SmsEnrollConfirmSchemaWritableSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        },
        code: {
            type: 'string',
            format: 'password',
            title: 'Code',
            writeOnly: true
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'code'
    ],
    title: 'SmsEnrollConfirmSchema'
} as const;

export const StudyVisitCalendarWritableSchema = {
    properties: {
        studies: {
            items: {
                $ref: '#/components/schemas/ClinicalStudies'
            },
            type: 'array',
            uniqueItems: true,
            title: 'Studies'
        },
        datetime_from: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Datetime From'
        },
        datetime_to: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Datetime To'
        },
        events: {
            items: {
                $ref: '#/components/schemas/VisitCalendarEventWritable'
            },
            type: 'array',
            title: 'Events'
        }
    },
    type: 'object',
    required: [
        'studies',
        'datetime_from',
        'datetime_to',
        'events'
    ],
    title: 'StudyVisitCalendar'
} as const;

export const SupportedMedicalDataWritableSchema = {
    properties: {
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        data_value: {
            oneOf: [
                {
                    $ref: '#/components/schemas/FloatDataSchema'
                },
                {
                    $ref: '#/components/schemas/IntDataSchema'
                },
                {
                    $ref: '#/components/schemas/EnumDataSchema'
                }
            ],
            title: 'Data Value',
            discriminator: {
                propertyName: 'value_type',
                mapping: {
                    enum: '#/components/schemas/EnumDataSchema',
                    float: '#/components/schemas/FloatDataSchema',
                    integer: '#/components/schemas/IntDataSchema'
                }
            }
        },
        data_category: {
            $ref: '#/components/schemas/MedicalDataCategory'
        },
        primary_unit: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Primary Unit'
        },
        alternative_units: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Units'
        },
        can_self_report: {
            type: 'boolean',
            title: 'Can Self Report',
            default: false
        }
    },
    type: 'object',
    required: [
        'data_name',
        'data_value',
        'data_category'
    ],
    title: 'SupportedMedicalData'
} as const;

export const TOTPEnrollConfirmSchemaWritableSchema = {
    properties: {
        challenge_id: {
            anyOf: [
                {
                    type: 'string',
                    maxLength: 1024,
                    minLength: 20,
                    pattern: '^[A-Za-z0-9_-]+$'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Challenge Id'
        },
        secret: {
            type: 'string',
            format: 'password',
            title: 'Secret',
            writeOnly: true
        },
        code: {
            type: 'string',
            format: 'password',
            title: 'Code',
            writeOnly: true
        }
    },
    additionalProperties: false,
    type: 'object',
    required: [
        'secret',
        'code'
    ],
    title: 'TOTPEnrollConfirmSchema'
} as const;

export const TechnicalWaitingRoomAlertsWritableSchema = {
    properties: {
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            minLength: 1,
            title: 'Name'
        },
        country: {
            type: 'string',
            title: 'Country'
        },
        region: {
            type: 'integer',
            minimum: 0,
            title: 'Region'
        },
        patient_state: {
            $ref: '#/components/schemas/PatientState'
        },
        clinical_risk_score: {
            type: 'integer',
            maximum: 10,
            minimum: 0,
            title: 'Clinical Risk Score'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/FullPhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        ppg_quality_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Ppg Quality Alerts'
        },
        other_technical_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Other Technical Alerts'
        },
        compliance_alerts: {
            items: {
                $ref: '#/components/schemas/DefaultPatientAlertResponseWritable'
            },
            type: 'array',
            title: 'Compliance Alerts'
        },
        medical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Medical Monitoring Note'
        },
        technical_monitoring_note: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Technical Monitoring Note'
        },
        monitoring_watch_kind: {
            $ref: '#/components/schemas/PatientMonitoringWatchKind'
        },
        monitoring_watch_status: {
            $ref: '#/components/schemas/PatientMonitoringWatchStatus'
        }
    },
    type: 'object',
    required: [
        'patient_id',
        'name',
        'country',
        'region',
        'patient_state',
        'clinical_risk_score',
        'ppg_quality_alerts',
        'other_technical_alerts',
        'compliance_alerts',
        'monitoring_watch_kind',
        'monitoring_watch_status'
    ],
    title: 'TechnicalWaitingRoomAlerts'
} as const;

export const TechnicalWaitingRoomResponseWritableSchema = {
    properties: {
        fetched: {
            type: 'string',
            format: 'date-time',
            title: 'Fetched'
        },
        waiting_room: {
            items: {
                $ref: '#/components/schemas/TechnicalWaitingRoomAlertsWritable'
            },
            type: 'array',
            title: 'Waiting Room'
        },
        waiting_room_timespan: {
            type: 'string',
            format: 'duration',
            title: 'Waiting Room Timespan'
        }
    },
    type: 'object',
    required: [
        'fetched',
        'waiting_room',
        'waiting_room_timespan'
    ],
    title: 'TechnicalWaitingRoomResponse'
} as const;

export const TimeSeriesPlotWritableSchema = {
    properties: {
        x_column: {
            type: 'string',
            title: 'X Column'
        },
        y_column: {
            type: 'string',
            title: 'Y Column'
        },
        traces: {
            items: {
                $ref: '#/components/schemas/TimeSeriesTraceWritable'
            },
            type: 'array',
            title: 'Traces'
        },
        column_info: {
            additionalProperties: {
                type: 'string'
            },
            type: 'object',
            title: 'Column Info'
        },
        time_range: {
            anyOf: [
                {
                    prefixItems: [
                        {
                            type: 'string',
                            format: 'date-time'
                        },
                        {
                            type: 'string',
                            format: 'date-time'
                        }
                    ],
                    type: 'array',
                    maxItems: 2,
                    minItems: 2
                },
                {
                    type: 'null'
                }
            ],
            title: 'Time Range'
        }
    },
    type: 'object',
    required: [
        'x_column',
        'y_column',
        'traces',
        'column_info'
    ],
    title: 'TimeSeriesPlot',
    description: 'Time series plot data. Pure data — no colors, symbols, or styling.'
} as const;

export const TimeSeriesPointWritableSchema = {
    properties: {
        timestamp: {
            type: 'string',
            format: 'date-time',
            title: 'Timestamp'
        },
        value: {
            type: 'number',
            title: 'Value'
        },
        error_x: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Error X'
        },
        error_y: {
            anyOf: [
                {
                    type: 'number'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Error Y'
        },
        channels: {
            anyOf: [
                {
                    additionalProperties: {
                        anyOf: [
                            {
                                type: 'number'
                            },
                            {
                                type: 'string'
                            }
                        ]
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Channels'
        },
        metadata: {
            anyOf: [
                {
                    additionalProperties: true,
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Metadata'
        }
    },
    type: 'object',
    required: [
        'timestamp',
        'value'
    ],
    title: 'TimeSeriesPoint',
    description: 'Single point in a time series.'
} as const;

export const TimeSeriesTraceWritableSchema = {
    properties: {
        points: {
            items: {
                $ref: '#/components/schemas/TimeSeriesPointWritable'
            },
            type: 'array',
            title: 'Points'
        },
        label: {
            type: 'string',
            title: 'Label'
        },
        facets: {
            anyOf: [
                {
                    additionalProperties: {
                        type: 'string'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Facets'
        }
    },
    type: 'object',
    required: [
        'points',
        'label'
    ],
    title: 'TimeSeriesTrace',
    description: 'A single trace/line in a plot.'
} as const;

export const UserAccessPolicyResponseWritableSchema = {
    properties: {
        regions: {
            anyOf: [
                {
                    additionalProperties: {
                        items: {
                            type: 'integer',
                            minimum: 0
                        },
                        type: 'array'
                    },
                    type: 'object'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Regions'
        },
        permissions: {
            $ref: '#/components/schemas/PermissionSetWritable'
        },
        countries: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Countries'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        stored_permissions: {
            $ref: '#/components/schemas/PermissionSetWritable',
            description: 'The bits stored on the policy, without the ones `permissions` adds by implication. Send these back to `PUT /policies/{uuid}` to keep an implied bit implied: a bit sent in `permissions` is stored explicitly and outlives the bit that implied it.'
        },
        connected_user: {
            $ref: '#/components/schemas/SimpleAccountResponse'
        },
        visible_patients: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PatientWithUuidSchema'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Visible Patients'
        },
        monitoring_active: {
            anyOf: [
                {
                    type: 'boolean'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Monitoring Active'
        },
        clinics: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/ClinicResponse'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Clinics'
        },
        physicians: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/PhysicianResponseWritable'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Physicians'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'permissions',
        'uuid',
        'connected_user',
        'created_at',
        'updated_at'
    ],
    title: 'UserAccessPolicyResponse',
    description: 'Schema for user access policy responses.\nPermissions are returned with implied permissions expanded.'
} as const;

export const UserPatientRoleResponseWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponseWritable'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        },
        connected_patient: {
            anyOf: [
                {
                    $ref: '#/components/schemas/PatientBaseSchema'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at'
    ],
    title: 'UserPatientRoleResponse'
} as const;

export const UserResponseWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponseWritable'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at'
    ],
    title: 'UserResponse',
    description: 'Schema for returning user info as a response.'
} as const;

export const UserResponseManagingWritableSchema = {
    properties: {
        username: {
            type: 'string',
            title: 'Username'
        },
        role: {
            $ref: '#/components/schemas/UserRole'
        },
        uuid: {
            type: 'string',
            format: 'uuid4',
            title: 'Uuid'
        },
        status: {
            $ref: '#/components/schemas/UserStatus'
        },
        connected_patient_id: {
            anyOf: [
                {
                    type: 'integer',
                    exclusiveMinimum: 0
                },
                {
                    type: 'null'
                }
            ],
            title: 'Connected Patient Id'
        },
        email: {
            anyOf: [
                {
                    type: 'string',
                    format: 'email'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Email'
        },
        preferred_language: {
            $ref: '#/components/schemas/LocaleTag',
            default: 'en'
        },
        last_login: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Login'
        },
        last_requested_reset: {
            anyOf: [
                {
                    type: 'string',
                    format: 'date-time'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Last Requested Reset'
        },
        access_policy: {
            $ref: '#/components/schemas/UserAccessPolicyResponseWritable'
        },
        feature_flags: {
            additionalProperties: {
                $ref: '#/components/schemas/FeatureFlagState'
            },
            propertyNames: {
                $ref: '#/components/schemas/UserFeatureFlag'
            },
            type: 'object',
            title: 'Feature Flags'
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            title: 'Created At'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            title: 'Updated At'
        }
    },
    type: 'object',
    required: [
        'username',
        'role',
        'uuid',
        'status',
        'access_policy',
        'created_at',
        'updated_at'
    ],
    title: 'UserResponseManaging'
} as const;

export const VisitAdminProcedureWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'admin',
            title: 'Data Type',
            default: 'admin'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitAdminRequiredDataWritable'
        },
        associated_data: {
            anyOf: [
                {
                    $ref: '#/components/schemas/ClinicalStudyAdminProcedureResponse'
                },
                {
                    type: 'null'
                }
            ]
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'associated_data'
    ],
    title: 'VisitAdminProcedure'
} as const;

export const VisitAdminRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'admin',
            title: 'Data Type',
            default: 'admin'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        procedure: {
            type: 'string',
            title: 'Procedure'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'procedure'
    ],
    title: 'VisitAdminRequiredData'
} as const;

export const VisitAvailableComputedDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'computed',
            title: 'Data Type',
            default: 'computed'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitComputedRequiredDataWritable'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponseWritable'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponseWritable'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableComputedData'
} as const;

export const VisitAvailableDiagnosesWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'diagnosis',
            title: 'Data Type',
            default: 'diagnosis'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitDiagnosisRequiredDataWritable'
        },
        existing_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HfEtiologySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HfTypeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousHfhSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CoronaryArteryDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArterialHypertensionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFibrillationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AtrialFlutterSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DiabetesMellitusSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DyslipidemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CkdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ThyreopathySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/IronDeficiency_AnemiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PreviousStroke_TiaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PeripheralArterialDiseaseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CopdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AsthmaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfSmokingSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfAlcoholAbuseSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MalignancySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfCardiotoxicDrugsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ImplantedDevicesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ValvularInterventionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ArrhythmiaTreatmentSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HistoryOfHtxSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultDiagnosisSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableDiagnoses'
} as const;

export const VisitAvailableMedicalDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitMedicalRequiredDataWritable'
        },
        existing_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Existing Data'
        },
        existing_sq_computed: {
            items: {
                $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponseWritable'
            },
            type: 'array',
            title: 'Existing Sq Computed'
        },
        existing_ppg_derived: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponseWritable'
            },
            type: 'array',
            title: 'Existing Ppg Derived'
        },
        associated_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/DefaultSeerlinqComputedBasicResponseWritable'
                    },
                    {
                        $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponseWritable'
                    },
                    {
                        $ref: '#/components/schemas/HeartRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/Spo2SchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/WeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DryWeightSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TemperatureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RespiratoryRateSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NtProbnpSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/UreaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/CreatinineSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HemoglobinSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/HematocritSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgRhythmSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/EcgPrematureContractionsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LegEdemaSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/JugularVenousDistentionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/NyhaClassSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LvefSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ESchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_ASchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/E_E_AverageSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrVelocitySchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LaviSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/StrokeVolumeOnEchoSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AosSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AorSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/MsSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/TrSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PurSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RvspSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/BLinesOnLungUltrasoundSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PleuralEffusionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/AscitesSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcRightAtrialPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcVentricularPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryCapillaryWedgePressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArteryPressureMeanSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcStrokeVolumeIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacOutputTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcCardiacIndexTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceFickSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceTdSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryVascularResistanceIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTotalPulmonaryResistanceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcPulmonaryArterialComplianceSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcDiastolicTranspulmonaryGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcMixedVenousOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcArterialOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureSystolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/RhcSystemicBloodPressureDiastolicSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftVentricularEndDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticSystolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticDiastolicPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanAorticValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcMeanMitralValveGradientSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcLeftAtrialMeanPressureSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacOutputSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcCardiacIndexSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcAorticOxygenSaturationSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcDp_DtSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/LhcEjectionFractionSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/ElevatedLvFillingPressureFinalOutcomeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultMedicalDataSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'existing_sq_computed',
        'existing_ppg_derived',
        'associated_data'
    ],
    title: 'VisitAvailableMedicalData'
} as const;

export const VisitAvailablePPGDerivedDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'derived',
            title: 'Data Type',
            default: 'derived'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitPPGDerivedRequiredDataWritable'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponseWritable'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDerivedDataSchemaResponseWritable'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailablePPGDerivedData'
} as const;

export const VisitAvailablePPGsWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitPPGRequiredDataWritable'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/DefaultPPGDataSchemaResponse'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailablePPGs'
} as const;

export const VisitAvailableQuestionnairesWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'questionnaire',
            title: 'Data Type',
            default: 'questionnaire'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitQuestionnaireRequiredDataWritable'
        },
        existing_data: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponseWritable'
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                $ref: '#/components/schemas/FilledQuestionnaireResponseWritable'
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableQuestionnaires'
} as const;

export const VisitAvailableSymptomsWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        data_definition: {
            $ref: '#/components/schemas/VisitSymptomRequiredDataWritable'
        },
        existing_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Existing Data'
        },
        associated_data: {
            items: {
                anyOf: [
                    {
                        $ref: '#/components/schemas/ShortnessOfBreathSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/FatigueScoreSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/PerceivedHealthChangeSchemaResponse'
                    },
                    {
                        $ref: '#/components/schemas/DefaultSymptomSchemaResponse'
                    }
                ]
            },
            type: 'array',
            title: 'Associated Data'
        }
    },
    type: 'object',
    required: [
        'data_definition',
        'existing_data',
        'associated_data'
    ],
    title: 'VisitAvailableSymptoms'
} as const;

export const VisitCalendarEventWritableSchema = {
    properties: {
        event_datetime: {
            type: 'string',
            format: 'date-time',
            title: 'Event Datetime'
        },
        study_acronym: {
            $ref: '#/components/schemas/ClinicalStudies'
        },
        visit_type: {
            $ref: '#/components/schemas/ClinicalStudyVisit-Output'
        },
        patient_id: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Patient Id'
        },
        name: {
            type: 'string',
            title: 'Name'
        }
    },
    type: 'object',
    required: [
        'event_datetime',
        'study_acronym',
        'visit_type',
        'patient_id',
        'name'
    ],
    title: 'VisitCalendarEvent'
} as const;

export const VisitComputedRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'computed',
            title: 'Data Type',
            default: 'computed'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            type: 'string',
            const: 'computed',
            title: 'Required Data Source',
            default: 'computed'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        minimum_sqi: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Minimum Sqi',
            default: 3
        },
        required_devices: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Devices'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name'
    ],
    title: 'VisitComputedRequiredData'
} as const;

export const VisitDiagnosisRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'diagnosis',
            title: 'Data Type',
            default: 'diagnosis'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        category: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Category'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name'
    ],
    title: 'VisitDiagnosisRequiredData'
} as const;

export const VisitMedicalRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'medical-data',
            title: 'Data Type',
            default: 'medical-data'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesFull'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        category: {
            $ref: '#/components/schemas/MedicalDataCategory'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'category'
    ],
    title: 'VisitMedicalRequiredData',
    description: 'Can be used for data derived or computed from the PPG, but this data must be\nalready part of our supported data list (e.g., SpO2 or heart rate).'
} as const;

export const VisitPPGDerivedRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'derived',
            title: 'Data Type',
            default: 'derived'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            type: 'string',
            const: 'derived',
            title: 'Required Data Source',
            default: 'derived'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        measurement_condition: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Measurement Condition'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length'
        },
        minimum_sqi: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Minimum Sqi',
            default: 3
        },
        required_devices: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Devices'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name',
        'measurement_condition',
        'minimum_length'
    ],
    title: 'VisitPPGDerivedRequiredData'
} as const;

export const VisitPPGRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'ppg',
            title: 'Data Type',
            default: 'ppg'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesSimple'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        measurement_condition: {
            type: 'string',
            title: 'Measurement Condition'
        },
        minimum_length: {
            type: 'number',
            minimum: 30,
            exclusiveMinimum: 0,
            title: 'Minimum Length'
        },
        minimum_sqi: {
            type: 'number',
            maximum: 10,
            minimum: 0,
            title: 'Minimum Sqi',
            default: 3
        },
        required_devices: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array',
                    minItems: 1,
                    uniqueItems: true
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Devices'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'measurement_condition',
        'minimum_length'
    ],
    title: 'VisitPPGRequiredData'
} as const;

export const VisitQuestionnaireRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'questionnaire',
            title: 'Data Type',
            default: 'questionnaire'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesSimple'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        questionnaire_id: {
            $ref: '#/components/schemas/QuestionnaireTypes'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'questionnaire_id'
    ],
    title: 'VisitQuestionnaireRequiredData'
} as const;

export const VisitSymptomRequiredDataWritableSchema = {
    properties: {
        data_type: {
            type: 'string',
            const: 'symptom',
            title: 'Data Type',
            default: 'symptom'
        },
        order: {
            type: 'integer',
            exclusiveMinimum: 0,
            title: 'Order'
        },
        required: {
            type: 'boolean',
            title: 'Required'
        },
        comment: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Comment'
        },
        required_data_source: {
            anyOf: [
                {
                    items: {
                        $ref: '#/components/schemas/VisitRequiredDataSourcesSimple'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Required Data Source'
        },
        datetime_limits: {
            type: 'string',
            format: 'duration',
            title: 'Datetime Limits',
            default: 'P7D'
        },
        alternative_data_names: {
            anyOf: [
                {
                    items: {
                        type: 'string'
                    },
                    type: 'array'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Alternative Data Names'
        },
        data_name: {
            type: 'string',
            title: 'Data Name'
        },
        category: {
            anyOf: [
                {
                    type: 'string'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Category'
        }
    },
    type: 'object',
    required: [
        'order',
        'required',
        'data_name'
    ],
    title: 'VisitSymptomRequiredData'
} as const;

export const WaitingRoomEventsWritableSchema = {
    properties: {
        event_date: {
            type: 'string',
            format: 'date',
            title: 'Event Date'
        },
        event_category: {
            $ref: '#/components/schemas/EventCategory'
        },
        event_category_type: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventType'
                },
                {
                    $ref: '#/components/schemas/DeathEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventType'
                },
                {
                    $ref: '#/components/schemas/LabsEventType'
                },
                {
                    $ref: '#/components/schemas/AdverseEventType'
                },
                {
                    $ref: '#/components/schemas/PhysicianNotifiedEventType'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Type'
        },
        event_category_details: {
            anyOf: [
                {
                    $ref: '#/components/schemas/HospitalizationEventReason'
                },
                {
                    $ref: '#/components/schemas/VisitEventUnplannedCategory'
                },
                {
                    $ref: '#/components/schemas/VisitEventPlannedCategory'
                },
                {
                    $ref: '#/components/schemas/AdverseEventLevel'
                },
                {
                    type: 'null'
                }
            ],
            title: 'Event Category Details'
        },
        event_description: {
            type: 'string',
            title: 'Event Description'
        },
        added_by: {
            type: 'string',
            title: 'Added By'
        }
    },
    type: 'object',
    required: [
        'event_date',
        'event_category',
        'event_category_type',
        'event_category_details',
        'event_description',
        'added_by'
    ],
    title: 'WaitingRoomEvents'
} as const;
